import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import AdminPollsPanel from './AdminPollsPanel';
import SabbaticalPanel from '../../components/SabbaticalPanel';

// ─── Shared Admin Components ─────────────────────────────
function AdminPageHeader({ title, icon, children }) {
  return (
    <div style={{
      background: 'white',
      borderBottom: '1px solid var(--border)',
      padding: '16px 16px 14px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', right: 8, top: '50%',
        transform: 'translateY(-50%)',
        fontFamily: "'Rajdhani', sans-serif", fontWeight: 900,
        fontSize: '4rem', color: 'var(--navy)', opacity: 0.04,
        pointerEvents: 'none', userSelect: 'none',
      }}>UCC</div>
      <div style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: '0.65rem', fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--text-muted)', marginBottom: 4,
      }}>ADMIN · SEASON 2026</div>
      <div style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: '1.6rem', fontWeight: 700,
        color: 'var(--navy)', lineHeight: 1.1,
      }}>{icon} {title}</div>
      {children && <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>{children}</div>}
    </div>
  );
}

function AdminCard({ title, children, action }) {
  return (
    <div className="card mb-md">
      <div className="card__header">
        <span className="card__title">{title}</span>
        {action}
      </div>
      <div className="card__body">{children}</div>
    </div>
  );
}

// ─── AdminDashboard ───────────────────────────────────────
export function AdminDashboard() {
  const { member, isSuperAdmin, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcomingMatch, setUpcomingMatch] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard').catch(() => ({ data: null })),
      api.get('/matches/upcoming-sunday').catch(() => ({ data: null })),
    ]).then(([dRes, mRes]) => {
      setStats(dRes.data);
      setUpcomingMatch(mRes.data?.match || null);
    }).finally(() => setLoading(false));
  }, []);

  // Live visitor count, refreshed every 30s while the panel is open. Silent on
  // failure — a broken counter should never disturb the admin screen.
  useEffect(() => {
    let alive = true;
    const poll = () => api.get('/admin/live-visitors')
      .then(r => { if (alive) setLive(r.data); })
      .catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const ALL_MODULES = [
    { path: '/admin/members',      icon: '👥', label: 'Members',      desc: 'Manage all members',                  key: 'members' },
    { path: '/admin/matches',      icon: '🏏', label: 'Matches',      desc: 'Manage Sundays & import scores',      key: 'matches' },
    // Always-visible entry point to the live-scoring flow. Lists all
    // non-cancelled matches with one tap to /matches/:id/setup. Backed by
    // the 'matches' permission (anyone who can manage matches can score).
    { path: '/admin/match-control', icon: '🎯', label: 'Setup & Score', desc: 'UCC App live scoring console',       key: 'matches' },
    { path: '/admin/attendance',   icon: '📅', label: 'Attendance',   desc: 'Mark & override attendance',          key: 'attendance' },
    { path: '/admin/availability', icon: '✋', label: 'Availability', desc: 'Review responses, opt-outs, resting', key: 'availability' },
    { path: '/admin/leave',        icon: '🏖️', label: 'Leave',        desc: 'Review member leave applications', key: 'availability' },
    { path: '/admin/squad-config', icon: '⚙️', label: 'Squad Config',  desc: 'Special conditions & rested reasons', key: 'availability' },
    { path: '/admin/fees',         icon: '💰', label: 'Fees',         desc: 'Monthly fees & generation',           key: 'fees' },
    { path: '/admin/fines',        icon: '⚠️', label: 'Fines',        desc: 'Issue & manage fines',                key: 'fines' },
    { path: '/admin/adjustments',  icon: '💠', label: 'Charges & Rebates', desc: 'Sponsorships and rebates on fees', key: 'fees' },
    { path: '/admin/penalty-adjustments', icon: '📉', label: 'Season Penalties', desc: 'Manual point deductions per year', key: 'fines' },
    { path: '/admin/payments',     icon: '🏦', label: 'Payments',     desc: 'Bank statement reconciliation',       key: 'payments' },
    { path: '/admin/scorecards',   icon: '📊', label: 'Scorecards',   desc: 'Import CricHeroes, MOTM',             key: 'scorecards' },
    { path: '/admin/videos',       icon: '🎥', label: 'Match Videos',  desc: 'Add & manage YouTube match videos',  key: 'matches' },
    { path: '/admin/youtube',      icon: '📺', label: 'YouTube Live',  desc: 'Schedule, go live & end match streams', key: 'matches' },
    { path: '/admin/notices',      icon: '🔔', label: 'Noticeboard',  desc: 'Post notices & polls',                key: 'notices' },
    { path: '/admin/celebrations', icon: '🎊', label: 'Celebrations', desc: 'Custom celebrations & MOTM',          key: 'celebrations' },
    { path: '/admin/drop-catches', icon: '🧤', label: 'Drop Catches',    desc: 'Record dropped catches',               key: 'drop_catches' },
    { path: '/admin/season-import', icon: '📦', label: 'Season Import',  desc: 'Import historical seasons from CricHeroes', key: 'season_import' },
    { path: '/admin/captaincy',    icon: '🎖️', label: 'Captaincy',    desc: 'Pool & Wagon Wheel draw',             key: 'captaincy' },
    { path: '/admin/floaters',     icon: '🔄', label: 'Floaters',     desc: 'Record floater selections',           key: 'captaincy' },
    { path: '/admin/series',       icon: '🥇', label: 'Series',       desc: 'Manage series & fixtures',            key: 'series' },
    { path: '/admin/expenses',     icon: '💼', label: 'Expenses',     desc: 'Club expenses & collections',         key: 'expenses' },
    { path: '/admin/lunch',        icon: '🍱', label: 'Lunch',        desc: 'Upload menu & view headcount',        key: 'lunch' },
    { path: '/admin/member-report', icon: '📊', label: 'Member Report', desc: 'Attendance, fines & dues per member', key: 'member_report' },
    ...(isSuperAdmin ? [
      { path: '/admin/alerts',   icon: '🚨', label: 'Alerts',    desc: 'Missed replies, check-ins & unpaid fees', key: '__super' },
      { path: '/admin/grievances', icon: '📮', label: 'Grievances', desc: 'Members\u2019 complaints (named or anonymous)', key: '__super' },
      { path: '/admin/auction', icon: '🔨', label: 'Super Series Auction', desc: 'Teams, owners, retentions, player pool', key: '__super' },
      { path: '/admin/roles',    icon: '🔑', label: 'Roles',     desc: 'Manage admin permissions', key: '__super' },
      { path: '/admin/scorers',  icon: '🎯', label: 'Scorers',   desc: 'Manage live scoring users', key: '__super' },
      { path: '/admin/audit',    icon: '📋', label: 'Audit Log', desc: 'Full financial history',   key: '__super' },
      { path: '/admin/chatbot',  icon: '🤖', label: 'Ask UCC',   desc: 'Chatbot activity & settings', key: '__super' },
      { path: '/admin/wheel-setup', icon: '🎡', label: 'Wagon Wheel', desc: 'Custom shots & field positions', key: '__super' },
    ] : []),
  ];
  const adminModules = isSuperAdmin
    ? ALL_MODULES
    : ALL_MODULES.filter(m => m.key === '__super' ? false : hasPermission(m.key, 'can_view'));

  return (
    <div>
      <AdminPageHeader title="Admin Panel" icon="⚙️">
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>
          Welcome, {member?.display_name?.split(' ')[0]}. Season 2026.
        </p>
      </AdminPageHeader>

      <div style={{ padding: '16px' }}>
        {/* Upcoming-match banner. Shows the next non-cancelled match with one
            tap to /matches/:id/setup so admin never has to hunt for the URL.
            Quietly hidden when the next match is already past. */}
        {!loading && upcomingMatch && !upcomingMatch.is_cancelled && (() => {
          const matchDate = new Date(upcomingMatch.match_date);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          // Show banner from match_date up to 1 day after (so post-match
          // night, admin can still reach the scoring console for edits).
          const cutoff = new Date(matchDate);
          cutoff.setDate(cutoff.getDate() + 1);
          if (today > cutoff) return null;
          const isToday = matchDate.toDateString() === new Date().toDateString();
          const label = isToday
            ? 'Today'
            : matchDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
          return (
            <div style={{
              background: 'linear-gradient(135deg, #1E3A6E 0%, #2c4a8e 100%)',
              borderRadius: 12, padding: '14px 16px', color: 'white',
              boxShadow: '0 2px 8px rgba(30,58,110,0.25)',
              marginBottom: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.65rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, letterSpacing: '0.1em', color: '#F5C518', textTransform: 'uppercase' }}>
                  🏏 {label}'s Match
                </div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', marginTop: 2, lineHeight: 1.2 }}>
                  {upcomingMatch.capt1_team || 'Side A'} <span style={{ color: 'rgba(255,255,255,0.5)' }}>vs</span> {upcomingMatch.capt2_team || 'Side B'}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                  {upcomingMatch.overs} overs · Report: {upcomingMatch.reporting_time?.slice(0, 5) || '—'}
                </div>
              </div>
              <Link
                to={`/matches/${upcomingMatch.id}/setup`}
                style={{
                  background: '#F5C518', color: '#1E3A6E',
                  padding: '10px 14px', borderRadius: 8,
                  textDecoration: 'none', fontWeight: 800, fontSize: '0.75rem',
                  fontFamily: 'Rajdhani,sans-serif', letterSpacing: '0.04em',
                  whiteSpace: 'nowrap', textTransform: 'uppercase',
                  flexShrink: 0,
                }}
              >
                🎯 Setup & Score
              </Link>
            </div>
          );
        })()}

        {live?.available && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            background:'white', border:'1px solid var(--border)', borderLeft:'4px solid #16a34a',
            borderRadius:12, padding:'12px 14px', marginTop:16,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{
                width:9, height:9, borderRadius:'50%', background:'#16a34a',
                boxShadow:'0 0 7px #16a34a', display:'inline-block',
              }} />
              <div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                  fontSize:'1.05rem', color:'var(--navy)', lineHeight:1.1 }}>
                  {live.active} on the site now
                </div>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:2 }}>
                  {live.last_hour} in the last hour · {live.last_24h}{live.partial_day ? ' since the last deploy' : ' today'}
                </div>
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                fontSize:'1.05rem', color:'var(--navy)', lineHeight:1.1 }}>
                {live.requests}
              </div>
              <div style={{ fontSize:'0.66rem', color:'var(--text-muted)' }}>requests</div>
            </div>
          </div>
        )}

        {!loading && stats && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16, marginTop: 16 }}>
            {[
              { v: stats.stats?.total_members, l: 'Members', c: 'var(--navy)' },
              { v: stats.stats?.total_inactive, l: 'Ex-Members', c: 'var(--text-muted)' },
              { v: stats.stats?.pending_payments, l: 'Pending Pays', c: 'var(--amber)' },
              { v: stats.stats?.unpaid_fines, l: 'Unpaid Fines', c: 'var(--red)' },
              { v: stats.stats?.total_notices ?? stats.stats?.unread_notices, l: 'Notices', c: 'var(--blue)' },
              { v: '2026', l: 'Season', c: 'var(--gold)' },
            ].map(s => (
              <div key={s.l} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.6rem', color: s.c, lineHeight: 1, marginBottom: 4 }}>{s.v ?? 0}</div>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280' }}>{s.l}</div>
              </div>
            ))}
          </div>
        )}

        {(() => {
          const tileStyle = { background:'white', border:'1px solid #e5e7eb', borderBottom:'3px solid #1E3A6E', borderRadius:12, padding:'14px 8px', display:'flex', flexDirection:'column', alignItems:'center', gap:6, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', minHeight:80, cursor:'pointer' };
          const remainder = adminModules.length % 3;
          const fullTiles = remainder === 0 ? adminModules : adminModules.slice(0, -remainder);
          const lastTiles = remainder === 0 ? [] : adminModules.slice(-remainder);
          return (<>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
              {fullTiles.map(m => (
                <Link key={m.path} to={m.path} style={{ textDecoration:'none' }}>
                  <div style={tileStyle}>
                    <span style={{ fontSize:'1.5rem' }}>{m.icon}</span>
                    <span style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#1E3A6E', textAlign:'center' }}>{m.label}</span>
                  </div>
                </Link>
              ))}
            </div>
            {lastTiles.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:`repeat(${lastTiles.length}, 1fr)`, gap:10, marginTop:10 }}>
                {lastTiles.map(m => (
                  <Link key={m.path} to={m.path} style={{ textDecoration:'none' }}>
                    <div style={{ ...tileStyle, padding:'18px 8px' }}>
                      <span style={{ fontSize:'1.8rem' }}>{m.icon}</span>
                      <span style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#1E3A6E', textAlign:'center' }}>{m.label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>);
        })()}
      </div>
    </div>
  );
}

// ─── AdminMembers ─────────────────────────────────────────
export function AdminMembers() {
  const { isSuperAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [ledgerModal, setLedgerModal] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [otpResetMember, setOtpResetMember] = useState(null);
  // Sabbaticals: which member's panel is open, and a member_id → period map
  // used to show the badge in the list without a request per row.
  const [sabbaticalMember, setSabbaticalMember] = useState(null);
  const [openSabs, setOpenSabs] = useState({});
  const [showSabbatical, setShowSabbatical] = useState(false);
  const [form, setForm] = useState({ display_name:'', email:'', mobile:'', jersey_no:'', dob:'', anniversary:'', spouse_name:'', role:'member', is_observer:false, ucc_sheets_name:'', cricheroes_name:'', joining_month_year:'', emergency_name:'', emergency_mobile:'', emergency_relation:'' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      // Load active + inactive members only. Ghost-observer records stay hidden
      // from the Members page; they still own historical stats in DB.
      const res = await api.get('/members?includeInactive=true');
      setMembers(res.data.members || []);

      // Sabbaticals covering today, for the badge. "current" not "open" — a
      // period with a future end date is still current. Non-fatal: if the
      // endpoint isn't deployed the page still works, just without badges.
      try {
        const sab = await api.get('/sabbaticals?current=true');
        const map = {};
        for (const s of (sab.data.sabbaticals || [])) map[s.member_id] = s;
        setOpenSabs(map);
      } catch (e) { /* ignore — badges are cosmetic */ }
    } finally { setLoading(false); }
  }

  async function addMember() {
    if (!form.display_name || !form.email) return alert('Name and email required');
    setSaving(true);
    try {
      await api.post('/members', form);
      alert('Member added!');
      setShowAdd(false);
      setForm({ display_name:'', email:'', mobile:'', jersey_no:'', dob:'', anniversary:'', role:'member', is_observer:false, ucc_sheets_name:'', cricheroes_name:'', joining_month_year:'', emergency_name:'', emergency_mobile:'', emergency_relation:'' });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to add'); }
    finally { setSaving(false); }
  }

  async function saveEdit() {
    if (!editModal) return;
    setSaving(true);
    try {
      await api.put('/members/' + editModal.id, editModal);
      alert('Member updated!');
      setEditModal(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update'); }
    finally { setSaving(false); }
  }

  async function toggleActive(id, isActive) {
    await api.patch('/members/' + id + '/activate', { is_active: !isActive });
    load();
  }

  // Retire = leaves the club for good. Records the date; fees are NOT touched,
  // so anything unpaid stays as debt against his name.
  async function retireMember(m) {
    const todayISO = new Date().toISOString().slice(0, 10);
    const input = window.prompt(
      `Retire ${m.display_name} from active participation?\n\n` +
      `He becomes an ex-member and keeps all his stats and history.\n` +
      `Unpaid fees are NOT cancelled.\n\n` +
      `Date of retirement (YYYY-MM-DD):`,
      todayISO
    );
    if (input === null) return;
    const retired_on = input.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(retired_on)) {
      return alert('Please use the format YYYY-MM-DD, e.g. ' + todayISO);
    }
    try {
      const r = await api.patch('/members/' + m.id + '/retire', { retired_on });
      const owed = Number(r.data.outstanding || 0);
      alert(
        `${m.display_name} retired on ${retired_on}.` +
        (owed > 0 ? `\n\n⚠️ Still owes ₹${owed.toLocaleString('en-IN')} — this has NOT been written off.` : '') +
        (r.data.closed_sabbatical ? `\n\nHis open sabbatical was closed on the same date.` : '')
      );
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to retire member');
    }
  }

  async function openLedger(m) {
    setLedgerModal(m);
    setLedgerData(null);
    try {
      const [feesRes, finesRes] = await Promise.all([
        api.get('/fees/member/' + m.id),
        api.get('/fines?member_id=' + m.id),
      ]);
      // Load confirmed payments separately — non-fatal if route not yet deployed
      let payments = [];
      try {
        const paymentsRes = await api.get('/payments/confirmed?member_id=' + m.id);
        payments = paymentsRes.data.payments || [];
      } catch (_) {}
      setLedgerData({
        fees: feesRes.data.fees || [],
        fines: finesRes.data.fines || [],
        payments,
      });
    } catch (err) {
      setLedgerData({ fees: [], fines: [], payments: [], error: err.response?.data?.error || 'Failed to load' });
    }
  }

  async function resetOtp(m) {
    setOtpResetMember(m);
  }

  async function confirmOtpReset() {
    try {
      const res = await api.post('/auth/request-otp', { email: otpResetMember.email });
      alert('Login code sent to ' + otpResetMember.email + '. Ask them to check their email.');
      setOtpResetMember(null);
    } catch (err) { alert(err.response?.data?.error || 'Failed to send OTP'); }
  }

  const filtered = members.filter(m => {
    // Hide pending signups (UCC### with is_active=false) — they're shown separately
    // in the "Pending Access Requests" block above.
    const isPendingSignup = !m.is_active && !m.is_observer && (m.ucc_id || '').startsWith('UCC');
    if (isPendingSignup) return false;
    // Apply chip filter: Active/Inactive toggles. If both off, show nothing.
    if (m.is_active && !showActive) return false;
    if (!m.is_active && !showInactive) return false;
    // Sabbatical chip narrows the list to members currently away.
    if (showSabbatical && !openSabs[m.id]) return false;
    // Search match
    if (search) {
      const s = search.toLowerCase();
      if (!(m.display_name?.toLowerCase().includes(s) ||
            m.email?.toLowerCase().includes(s) ||
            m.ucc_id?.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const FORM_FIELDS = [
    { label:'Full Name *', key:'display_name', type:'text' },
    { label:'Email *', key:'email', type:'email' },
    { label:'Mobile', key:'mobile', type:'tel' },
    { label:'Jersey No', key:'jersey_no', type:'number' },
    { label:'Date of Birth', key:'dob', type:'date' },
    { label:'Anniversary', key:'anniversary', type:'date' },
    { label:'Sheets Name', key:'ucc_sheets_name', type:'text' },
    { label:'CricHeroes Name', key:'cricheroes_name', type:'text' },
    { label:'Joining (Mon-Year)', key:'joining_month_year', type:'text', placeholder:'e.g. Jan-2024' },
    { label:'Spouse / Partner Name', key:'spouse_name', type:'text', placeholder:'e.g. Deepika Bhalla' },
    { label:'Emergency Contact Name', key:'emergency_name', type:'text' },
    { label:'Emergency Mobile', key:'emergency_mobile', type:'tel' },
    { label:'Emergency Relation', key:'emergency_relation', type:'text', placeholder:'e.g. Wife, Father' },
  ];

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <AdminPageHeader title="Members">
        <div style={{ display:'flex', gap:8 }}>
          {isSuperAdmin && <button className="btn btn-gold btn-sm" onClick={() => setShowAdd(!showAdd)}>+ Add</button>}
        </div>
      </AdminPageHeader>

      <div style={{ padding:'16px' }}>
        <input className="form-control mb-md" placeholder="Search by name, email, UCC ID…"
          value={search} onChange={e => setSearch(e.target.value)} />

        {/* Add member form */}
        {showAdd && (
          <div className="card mb-md">
            <div className="card__header"><span className="card__title">Add Member</span></div>
            <div className="card__body">
              {FORM_FIELDS.map(f => (
                <div key={f.key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input type={f.type} className="form-control" placeholder={f.placeholder || ''}
                    value={form[f.key] || ''} onChange={e => setForm(ff => ({ ...ff, [f.key]: e.target.value }))} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="observer">Inactive Member</option>
                </select>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ flex:2 }} onClick={addMember} disabled={saving}>
                  {saving ? 'Adding…' : 'Add Member'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending access requests — only UCC### pending signups, NOT INA### inactives */}
        {members.filter(m => !m.is_active && !m.is_observer && (m.ucc_id || '').startsWith('UCC')).length > 0 && (
          <div style={{ background:'#fffbeb', border:'2px solid #f59e0b', borderRadius:12, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.85rem', color:'#d97706', marginBottom:8 }}>
              🆕 PENDING ACCESS REQUESTS ({members.filter(m => !m.is_active && !m.is_observer && (m.ucc_id || '').startsWith('UCC')).length})
            </div>
            {members.filter(m => !m.is_active && !m.is_observer && (m.ucc_id || '').startsWith('UCC')).map(m => (
              <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #fde68a' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1E3A6E' }}>{m.display_name}</div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>{m.email} · {m.mobile}</div>
                </div>
                <button onClick={async () => {
                    await api.patch('/members/' + m.id + '/activate', { is_active: true });
                    load();
                  }}
                  style={{ padding:'5px 14px', borderRadius:6, border:'none', background:'#16a34a', color:'white', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
                  ✅ Approve
                </button>
                <button onClick={async () => {
                    if (!window.confirm('Reject and permanently delete this access request from ' + m.display_name + '?')) return;
                    try {
                      const res = await api.delete('/members/' + m.id);
                      const action = res && res.data ? res.data.action : null;
                      if (action === 'archived') {
                        alert(m.display_name + ' has existing cricket/financial records, so their records were kept and the request was removed from this list.');
                      } else {
                        alert('Access request from ' + m.display_name + ' was permanently deleted.');
                      }
                      load();
                    } catch (e) {
                      const msg = (e && e.response && e.response.data && (e.response.data.error || e.response.data.detail)) || (e && e.message) || 'unknown error';
                      alert('Could not reject this request: ' + msg);
                    }
                  }}
                  style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'#fee2e2', color:'#dc2626', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
                  ✕ Reject
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Active / Inactive chips */}
        <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
          <button
            onClick={() => setShowActive(v => !v)}
            style={{
              padding:'6px 14px', borderRadius:20,
              border: showActive ? '1.5px solid #16a34a' : '1.5px solid #e5e7eb',
              background: showActive ? '#f0fdf4' : 'white',
              color: showActive ? '#166534' : '#6b7280',
              fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
            }}>
            {showActive ? '✓ ' : ''}Active ({members.filter(m => m.is_active && !m.is_observer).length})
          </button>
          <button
            onClick={() => setShowInactive(v => !v)}
            style={{
              padding:'6px 14px', borderRadius:20,
              border: showInactive ? '1.5px solid #d97706' : '1.5px solid #e5e7eb',
              background: showInactive ? '#fffbeb' : 'white',
              color: showInactive ? '#92400e' : '#6b7280',
              fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
            }}>
            {showInactive ? '✓ ' : ''}Inactive ({members.filter(m => !m.is_active && !m.is_observer && !(m.ucc_id || '').startsWith('UCC')).length})
          </button>
          {Object.keys(openSabs).length > 0 && (
            <button
              onClick={() => setShowSabbatical(v => !v)}
              style={{
                padding:'6px 14px', borderRadius:20,
                border: showSabbatical ? '1.5px solid #d97706' : '1.5px solid #e5e7eb',
                background: showSabbatical ? '#fef3c7' : 'white',
                color: showSabbatical ? '#92400e' : '#6b7280',
                fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
              }}>
              {showSabbatical ? '✓ ' : ''}🏖️ Sabbatical ({Object.keys(openSabs).length})
            </button>
          )}
        </div>

        {/* Members list */}
        <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#6b7280', letterSpacing:'0.08em',
          textTransform:'uppercase', marginBottom:8 }}>{filtered.length} shown</div>
        <div className="card">
          {filtered.map(m => (
            <div key={m.id} style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', flexShrink:0, overflow:'hidden',
                  background: m.is_observer ? '#e5e7eb' : '#1E3A6E',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {m.photo_url
                    ? <img src={m.photo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <span style={{ color:'white', fontWeight:700, fontSize:'1rem' }}>{m.display_name?.charAt(0)}</span>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:'0.9rem',
                      color: m.is_active ? '#111827' : '#9ca3af',
                      textDecoration: m.is_active ? 'none' : 'line-through' }}>
                      {m.display_name}
                    </span>
                    {!m.is_active && <span style={{ fontSize:'0.6rem', fontWeight:700, background:'#fee2e2', color:'#dc2626', padding:'1px 6px', borderRadius:10 }}>{m.retired_on ? 'RETIRED' : 'INACTIVE'}</span>}
                    {openSabs[m.id] && <span style={{ fontSize:'0.6rem', fontWeight:700, background:'#fef3c7', color:'#92400e', padding:'1px 6px', borderRadius:10 }}>🏖️ SABBATICAL</span>}
                    {m.role === 'admin' && <span style={{ fontSize:'0.6rem', fontWeight:700, background:'#dbeafe', color:'#1d4ed8', padding:'1px 6px', borderRadius:10 }}>ADMIN</span>}
                    {m.role === 'super_admin' && <span style={{ fontSize:'0.6rem', fontWeight:700, background:'#fef3c7', color:'#d97706', padding:'1px 6px', borderRadius:10 }}>CHAIRMAN</span>}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>
                    {m.ucc_id} · {m.email}
                    {m.jersey_no ? ` · #${m.jersey_no}` : ''}
                  </div>
                  {!m.is_active && m.retired_on && (
                    <div style={{ fontSize:'0.68rem', color:'#dc2626', fontWeight:600 }}>
                      Retired {new Date(m.retired_on)
                        .toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                    </div>
                  )}
                  {openSabs[m.id] && (
                    <div style={{ fontSize:'0.68rem', color:'#92400e', fontWeight:600 }}>
                      On sabbatical since {new Date(openSabs[m.id].start_date)
                        .toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                      {openSabs[m.id].end_date
                        ? ` until ${new Date(openSabs[m.id].end_date)
                            .toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`
                        : ''}
                      {' '}· no fees, no availability
                    </div>
                  )}
                </div>
              </div>
              {/* Action buttons */}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <button onClick={() => openLedger(m)}
                  style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #e5e7eb',
                    background:'white', color:'#374151', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', minWidth:60 }}>
                  📊 Ledger
                </button>
                <button onClick={() => setEditModal({ ...m })}
                  style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #dbeafe',
                    background:'#eff6ff', color:'#1d4ed8', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', minWidth:60 }}>
                  ✏️ Edit
                </button>
                <button onClick={() => resetOtp(m)}
                  style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #e5e7eb',
                    background:'white', color:'#374151', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', minWidth:60 }}>
                  🔑 OTP
                </button>
                {isSuperAdmin && m.role !== 'super_admin' && (<>
                  <button onClick={() => setSabbaticalMember(m)}
                    style={{ flex:1, padding:'5px 0', borderRadius:6,
                      border: openSabs[m.id] ? '1px solid #fde68a' : '1px solid #e5e7eb',
                      background: openSabs[m.id] ? '#fffbeb' : 'white',
                      color: openSabs[m.id] ? '#92400e' : '#374151',
                      fontSize:'0.7rem', fontWeight:600, cursor:'pointer', minWidth:60 }}>
                    🏖️ {openSabs[m.id] ? 'Away' : 'Sabbatical'}
                  </button>
                  <button onClick={() => m.is_active ? retireMember(m) : toggleActive(m.id, m.is_active)}
                    style={{ flex:1, padding:'5px 0', borderRadius:6,
                      border: m.is_active ? '1px solid #fca5a5' : '1px solid #bbf7d0',
                      background: m.is_active ? '#fff1f2' : '#f0fdf4',
                      color: m.is_active ? '#dc2626' : '#16a34a',
                      fontSize:'0.7rem', fontWeight:600, cursor:'pointer', minWidth:60 }}>
                    {m.is_active ? 'Retire' : 'Reactivate'}
                  </button>
                  <button onClick={() => {
                      const msg = m.is_active
                        ? `Permanently delete ${m.display_name}? This cannot be undone. All their data (fees, fines, attendance) will be lost.`
                        : `Permanently delete ${m.display_name}?`;
                      if(window.confirm(msg)) api.delete('/members/'+m.id).then(load);
                    }}
                    style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #fca5a5',
                      background:'#fff1f2', color:'#dc2626',
                      fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
                    🗑️
                  </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sabbatical Panel */}
      {sabbaticalMember && (
        <SabbaticalPanel
          member={sabbaticalMember}
          onClose={() => setSabbaticalMember(null)}
          onSaved={load}
        />
      )}

      {/* Edit Modal */}
      {editModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
                      <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:16 }}>
                ✏️ Edit Member
              </div>
              {FORM_FIELDS.map(f => (
                <div key={f.key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input type={f.type} className="form-control" placeholder={f.placeholder || ''}
                    value={f.type === 'date'
                      ? (editModal[f.key] ? editModal[f.key].split('T')[0] : '')
                      : (editModal[f.key] || '')}
                    onChange={e => setEditModal(m => ({ ...m, [f.key]: e.target.value }))} />
                </div>
              ))}
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-control" value={editModal.role}
                    onChange={e => setEditModal(m => ({ ...m, role: e.target.value }))}>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="observer">Inactive Member</option>
                  </select>
                </div>
              )}
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">Active from</label>
                  <input type="date" className="form-control" value={editModal.active_from || ''}
                    onChange={e => setEditModal(m => ({ ...m, active_from: e.target.value || null }))} />
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:4 }}>
                    The date he became (or became again) an active playing member. Alerts ignore anything before it:
                    no availability, check-in or fee alerts for earlier dates. Leave empty if he has always been active.
                    Filled in automatically when a member is activated.
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Profile Photo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {editModal.photo_url && (
                    <img src={editModal.photo_url} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid #F5C518', flexShrink: 0 }} />
                  )}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#1E3A6E', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    {editModal._photoUploading ? '⏳ Uploading…' : '📷 Choose Photo'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setEditModal(m => ({ ...m, _photoUploading: true }));
                        try {
                          const fd = new FormData();
                          fd.append('file', file);
                          const apiBase = process.env.REACT_APP_API_URL || '/api';
                          const res = await fetch(apiBase + '/upload/image', {
                            method: 'POST',
                            headers: { Authorization: 'Bearer ' + localStorage.getItem('ucc_token') },
                            body: fd,
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || 'Upload failed');
                          setEditModal(m => ({ ...m, photo_url: data.url, _photoUploading: false }));
                          toast.success('Photo uploaded ✅');
                        } catch (err) {
                          toast.error(err.message || 'Upload failed');
                          setEditModal(m => ({ ...m, _photoUploading: false }));
                        }
                      }} />
                  </label>
                  {editModal.photo_url && (
                    <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setEditModal(m => ({ ...m, photo_url: '' }))}>✕ Remove</button>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding:'12px 20px', paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', background:'white', display:'flex', gap:8, flexShrink:0 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      {ledgerModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setLedgerModal(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
              <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:4 }}>
                📊 {ledgerModal.display_name}
              </div>
              <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:16 }}>{ledgerModal.ucc_id}</div>
              {!ledgerData ? (
                <div className="loading-spinner"><div className="spinner" /></div>
              ) : ledgerData.error ? (
                <div style={{ color: '#dc2626', fontSize: '0.85rem', padding: 16 }}>
                  Failed to load ledger: {ledgerData.error}
                </div>
              ) : (<>
                {/* Fees */}
                <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#374151', marginBottom:8 }}>
                  Fees ({ledgerData.fees.length})
                </div>
                {ledgerData.fees.length === 0
                  ? <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginBottom:16 }}>No fee records</div>
                  : ledgerData.fees.map(f => (
                    <div key={f.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', fontSize:'0.82rem' }}>
                      <div>
                        <div style={{ fontWeight:600 }}>
                          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][f.month-1]} {f.year}
                        </div>
                        <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>{Number(f.matches_held) === 0 ? '🏖️ Holiday' : `${f.sessions_attended} sessions`}</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:700, color: f.status==='paid' ? '#16a34a' : f.status==='overdue' ? '#dc2626' : '#1E3A6E' }}>
                          ₹{parseFloat(f.amount_due).toLocaleString('en-IN')}
                        </div>
                        {(['paid','waived'].includes(f.status) ? 0 : Math.max(0, parseFloat(f.balance || f.amount_due || 0))) > 0 && <div style={{ fontSize:'0.68rem', color:'#dc2626' }}>₹{Math.max(0, parseFloat(f.balance || f.amount_due || 0)).toLocaleString('en-IN')} due</div>}
                        <div style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', color:'#6b7280' }}>{f.status}</div>
                      </div>
                    </div>
                  ))
                }
                {/* Fines */}
                <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#374151', marginTop:16, marginBottom:8 }}>
                  Fines ({ledgerData.fines.length})
                </div>
                {ledgerData.fines.length === 0
                  ? <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginBottom:16 }}>No fines</div>
                  : ledgerData.fines.map(f => (
                    <div key={f.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', fontSize:'0.82rem' }}>
                      <div>
                        <div style={{ fontWeight:600 }}>{f.reason}</div>
                        <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>{new Date(f.issued_at).toLocaleDateString('en-IN')}</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:700, color: ['paid','waived'].includes(f.status) ? '#16a34a' : '#dc2626',
                          textDecoration: f.status==='cancelled' ? 'line-through' : 'none' }}>
                          ₹{parseFloat(f.amount||0).toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', color:'#6b7280' }}>{f.status}</div>
                      </div>
                    </div>
                  ))
                }
                {/* Confirmed Payments */}
                {ledgerData.payments && ledgerData.payments.length > 0 && (<>
                  <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#374151', marginTop:16, marginBottom:8 }}>
                    Confirmed Payments ({ledgerData.payments.length})
                  </div>
                  {ledgerData.payments.map(p => {
                    const isAdvance = p.allocation_note && p.allocation_note.includes('ADVANCE_CREDIT');
                    const advanceAmt = isAdvance ? p.allocation_note.match(/ADVANCE_CREDIT:([\d.]+)/)?.[1] : null;
                    const paidBy = p.paid_by_name && p.paid_by_name !== ledgerModal.display_name ? p.paid_by_name : null;
                    return (
                      <div key={p.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', fontSize:'0.82rem' }}>
                        <div>
                          <div style={{ fontWeight:600 }}>UTR: {p.utr}</div>
                          <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>{new Date(p.submitted_at).toLocaleDateString('en-IN')}</div>
                          {paidBy && <div style={{ fontSize:'0.68rem', color:'#7c3aed', fontWeight:600 }}>Paid by {paidBy}</div>}
                          {isAdvance && advanceAmt && <div style={{ fontSize:'0.68rem', color:'#d97706', fontWeight:700 }}>⚡ ₹{parseFloat(advanceAmt).toLocaleString('en-IN')} advance credit pending allocation</div>}
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontWeight:700, color:'#16a34a' }}>₹{parseFloat(p.amount).toLocaleString('en-IN')}</div>
                          <div style={{ fontSize:'0.65rem', color:'#16a34a', fontWeight:700 }}>CONFIRMED</div>
                        </div>
                      </div>
                    );
                  })}
                </>)}

                {/* Advance Credit Summary */}
                {ledgerData.payments && ledgerData.payments.some(p => p.allocation_note && p.allocation_note.includes('ADVANCE_CREDIT')) && (
                  <div style={{ background:'#fffbeb', border:'1px solid #f59e0b', borderRadius:8, padding:10, marginTop:8, fontSize:'0.8rem' }}>
                    <span style={{ fontWeight:700, color:'#d97706' }}>⚡ Advance credit on account — will auto-apply when next fee/fine is generated.</span>
                  </div>
                )}

                {/* Summary */}
                <div style={{ background:'#f8fafc', borderRadius:10, padding:12, marginTop:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:6 }}>
                    <span style={{ color:'#6b7280' }}>Total Fees Due</span>
                    <span style={{ fontWeight:700, color:'#dc2626' }}>
                      ₹{ledgerData.fees.reduce((s,f) => {
                        if (['paid','waived','cancelled'].includes(f.status)) return s;
                        return s + Math.max(0, parseFloat(f.balance||0));
                      }, 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem' }}>
                    <span style={{ color:'#6b7280' }}>Unpaid Fines</span>
                    <span style={{ fontWeight:700, color:'#dc2626' }}>
                      ₹{ledgerData.fines.filter(f=>!['paid','waived','cancelled'].includes(f.status)).reduce((s,f) => s + parseFloat(f.amount||0), 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </>)}
            </div>
            <div style={{ padding:'12px 20px', paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', background:'white', flexShrink:0 }}>
              <button className="btn btn-outline btn-block" onClick={() => setLedgerModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Reset Modal */}
      {otpResetMember && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setOtpResetMember(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', padding:'20px',
            paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))', width:'100%' }}>
            <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:8 }}>
              🔑 Send OTP to {otpResetMember.display_name}
            </div>
            <div style={{ fontSize:'0.85rem', color:'#6b7280', marginBottom:8 }}>
              Email: <strong>{otpResetMember.email}</strong>
            </div>
            {otpResetMember.email?.includes('placeholder') && (
              <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.8rem', color:'#92400e' }}>
                ⚠️ This member has a placeholder email. They need to log in first to set their real email.
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setOtpResetMember(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={confirmOtpReset}
                disabled={otpResetMember.email?.includes('placeholder')}>
                Send OTP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AdminFees ────────────────────────────────────────────
export function AdminFees() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState({ month: new Date().getMonth() + 1, year: 2026, status: '' });
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [feeModal, setFeeModal] = useState(null); // individual fee action
  // Phase 1 (c.3): edit due-date modal
  const [dueDateModal, setDueDateModal] = useState(null); // { fee, value }
  const [bulkDue, setBulkDue]           = useState(null);  // { value, note } — bulk due-date dialog
  const [savingBulkDue, setSavingBulkDue] = useState(false);
  const [savingDueDate, setSavingDueDate] = useState(false);
  // Record a payment against one fee: { fee, amount, paid_on, mode, reference, note, history, saving }
  const [payModal, setPayModal] = useState(null);
  // Generate-fees dialog: { useDefault: bool, custom: 'YYYY-MM-DD' }
  const [genModal, setGenModal] = useState(null);

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const STATUS_COLORS = { paid:'#16a34a', partial:'#d97706', pending:'#2563eb', overdue:'#dc2626', waived:'#6b7280' };
  const STATUS_BG = { paid:'#dcfce7', partial:'#fef3c7', pending:'#dbeafe', overdue:'#fee2e2', waived:'#f3f4f6' };

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = new URLSearchParams({
        year: filter.year,
        ...(filter.month && { month: filter.month }),
        ...(filter.status && { status: filter.status }),
      });
      const res = await api.get('/fees?' + params);
      setFees(res.data.fees || []);
    } finally { setLoading(false); }
  }

  // Default due date = 1st Sunday of the month after the fee month (same rule
  // as the server). Returned as YYYY-MM-DD.
  function defaultDueFor(month, year) {
    const m = parseInt(month), y = parseInt(year);
    const nm = m === 12 ? 1 : m + 1, ny = m === 12 ? y + 1 : y;
    const d = new Date(Date.UTC(ny, nm - 1, 1));
    d.setUTCDate(1 + ((7 - d.getUTCDay()) % 7));
    return d.toISOString().slice(0, 10);
  }
  const fmtDay = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' });

  function generateFees() {
    if (!filter.month) return alert('Select a month first');
    setGenModal({ useDefault: true, custom: defaultDueFor(filter.month, filter.year) });
  }

  async function confirmGenerate() {
    const gm = genModal;
    if (!gm) return;
    if (!gm.useDefault && !gm.custom) return alert('Pick the due date');
    setGenerating(true);
    try {
      const body = { month: filter.month, year: filter.year };
      if (!gm.useDefault) body.due_date = gm.custom;
      const res = await api.post('/fees/generate', body);
      setGenModal(null);
      const sab = res.data.on_sabbatical || [];
      alert(
        'Generated ' + res.data.generated + ' fee records' +
        (res.data.due_date ? ', due ' + fmtDay(res.data.due_date) + (res.data.custom_due_date ? '' : ' (default)') : '') + '. ' +
        (res.data.skipped ? res.data.skipped + ' already existed. ' : '') +
        (sab.length
          ? '\n\n🏖️ Skipped ' + sab.length + ' on sabbatical:\n' +
            sab.map(s => `• ${s.display_name} (${s.ucc_id})`).join('\n')
          : '')
      );
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to generate'); }
    finally { setGenerating(false); }
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selected.size === fees.length) setSelected(new Set());
    else setSelected(new Set(fees.map(f => f.id)));
  }

  async function applyBulkStatus() {
    if (!bulkStatus) return alert('Select a status first');
    if (selected.size === 0) return alert('Select at least one member');
    if (!window.confirm('Set ' + selected.size + ' fee(s) to ' + bulkStatus + '?')) return;
    setApplyingBulk(true);
    try {
      await Promise.all([...selected].map(id =>
        api.patch('/fees/' + id, { status: bulkStatus })
      ));
      alert('Updated ' + selected.size + ' fee records');
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setApplyingBulk(false); }
  }

  // Bulk due date for every selected fee, in one request (POST /fees/bulk-due-date).
  async function saveBulkDue() {
    if (!bulkDue?.value || selected.size === 0) return;
    setSavingBulkDue(true);
    try {
      const r = await api.post('/fees/bulk-due-date', {
        fee_ids: [...selected], due_date: bulkDue.value, note: (bulkDue.note || '').trim() || undefined,
      });
      const d = r.data;
      const when = new Date(d.due_date + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
      let msg = `Due date set to ${when} for ${d.updated} fee${d.updated === 1 ? '' : 's'}.`;
      if (d.reopened) msg += ` ${d.reopened} no longer overdue.`;
      if (d.skipped)  msg += ` Skipped ${d.skipped} paid/waived: ${d.skipped_names.slice(0, 5).join(', ')}${d.skipped > 5 ? '…' : ''}.`;
      alert(msg);
      setBulkDue(null);
      setSelected(new Set());
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update due dates');
    } finally {
      setSavingBulkDue(false);
    }
  }

  // Phase 1 (c.3): individual due-date override (calls PATCH /fees/:id/due-date)
  async function saveDueDate() {
    if (!dueDateModal || !dueDateModal.value) return;
    setSavingDueDate(true);
    try {
      await api.patch('/fees/' + dueDateModal.fee.id + '/due-date',
        { due_date: dueDateModal.value });
      setDueDateModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update due date');
    } finally {
      setSavingDueDate(false);
    }
  }

  // ── Record a payment (exact amount + date paid) ──────────
  const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  async function loadPayHistory(feeId) {
    try {
      const r = await api.get('/payments/fee/' + feeId);
      setPayModal(m => (m && m.fee.id === feeId) ? { ...m, history: r.data.payments || [] } : m);
    } catch { setPayModal(m => m ? { ...m, history: [] } : m); }
  }
  function openPayModal(fee) {
    const bal = Math.max(0, parseFloat(fee.amount_due || 0) - parseFloat(fee.amount_paid || 0));
    setPayModal({ fee, amount: bal > 0 ? String(Math.round(bal)) : '', paid_on: todayIST(),
                  mode: 'upi', reference: '', note: '', history: null, saving: false });
    loadPayHistory(fee.id);
  }
  async function savePayment() {
    const m = payModal;
    if (!m) return;
    const amt = parseFloat(m.amount);
    if (!(amt > 0)) return alert('Enter the amount paid');
    if (!m.paid_on) return alert('Enter the date it was paid');
    if (!window.confirm(`Record ₹${amt.toLocaleString('en-IN')} paid by ${m.fee.display_name} on ${m.paid_on}?`)) return;
    setPayModal({ ...m, saving: true });
    try {
      const r = await api.post('/payments/admin-record', {
        fee_id: m.fee.id, amount: amt, paid_on: m.paid_on, mode: m.mode,
        reference: m.reference.trim() || undefined, note: m.note.trim() || undefined,
      });
      const d = r.data;
      let msg = `Recorded ₹${amt.toLocaleString('en-IN')}.`;
      if (d.applied > 0) msg += ` ₹${Math.round(d.applied).toLocaleString('en-IN')} applied to the fee.`;
      if (d.advance_credit > 0) msg += ` ₹${Math.round(d.advance_credit).toLocaleString('en-IN')} kept as advance credit.`;
      if (d.fee) msg += ` Fee is now ${String(d.fee.status).toUpperCase()}.`;
      alert(msg);
      setPayModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record payment');
      setPayModal(x => x ? { ...x, saving: false } : x);
    }
  }
  async function undoPayment(p) {
    if (!window.confirm(`Undo the ₹${Math.round(p.payment_amount).toLocaleString('en-IN')} payment recorded for ${new Date(p.submitted_at).toLocaleDateString('en-IN')}? The money comes back off this fee.`)) return;
    try {
      await api.post('/payments/admin-record/' + p.submission_id + '/reverse');
      setPayModal(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to undo'); }
  }

  const effectiveBalance = (f) => ['paid','waived'].includes(f.status) ? 0 : Math.max(0, parseFloat(f.balance || f.amount_due || 0));
  const totalDue = fees.reduce((s, f) => s + effectiveBalance(f), 0);
  const totalCollected = fees.reduce((s, f) => f.status === 'paid' ? s + Math.max(0, parseFloat(f.amount_paid || 0)) : s, 0);
  const totalGenerated = fees.reduce((s, f) => s + parseFloat(f.amount_due || 0), 0);

  return (
    <div>
      <AdminPageHeader title="Fee Management" />
      <div style={{ padding:'16px' }}>

        {/* Filters */}
        <div className="card mb-md">
          <div className="card__body">
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              <select className="form-control" style={{ flex:2 }} value={filter.month}
                onChange={e => setFilter(f => ({ ...f, month: e.target.value }))}>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <select className="form-control" style={{ flex:1 }} value={filter.year}
                onChange={e => setFilter(f => ({ ...f, year: e.target.value }))}>
                {[2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="form-control" style={{ flex:2 }} value={filter.status}
                onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Status</option>
                {['paid','partial','pending','overdue','waived'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={load}>🔍 Filter</button>
              <button className="btn btn-gold" style={{ flex:1 }} onClick={generateFees} disabled={generating}>
                {generating ? 'Generating…' : '⚡ Generate Fees'}
              </button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
          <div style={{ background:'#fee2e2', borderRadius:12, padding:'12px 14px' }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.3rem', color:'#dc2626' }}>
              ₹{Math.round(totalDue).toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize:'0.68rem', color:'#dc2626', textTransform:'uppercase', letterSpacing:'0.06em' }}>
              Outstanding ({fees.filter(f => effectiveBalance(f) > 0).length} members)
            </div>
          </div>
          <div style={{ background:'#dcfce7', borderRadius:12, padding:'12px 14px' }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.3rem', color:'#16a34a' }}>
              ₹{Math.round(totalCollected).toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize:'0.68rem', color:'#16a34a', textTransform:'uppercase', letterSpacing:'0.06em' }}>
              Collected ({fees.filter(f => f.status === 'paid').length} paid)
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {fees.length > 0 && (
          <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', marginBottom:12, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <button onClick={selectAll}
              style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e5e7eb', background: selected.size === fees.length ? '#1E3A6E' : 'white',
                color: selected.size === fees.length ? 'white' : '#374151', fontWeight:600, fontSize:'0.75rem', cursor:'pointer', flexShrink:0 }}>
              {selected.size === fees.length ? '☑ All' : `☐ All (${fees.length})`}
            </button>
            {selected.size > 0 && (
              <>
                <span style={{ fontSize:'0.75rem', color:'#6b7280' }}>{selected.size} selected</span>
                <select className="form-control" style={{ flex:1, minWidth:100, fontSize:'0.8rem' }}
                  value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
                  <option value="">Set status…</option>
                  {['paid','partial','pending','overdue','waived'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={applyBulkStatus} disabled={applyingBulk || !bulkStatus}
                  style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'#1E3A6E',
                    color:'white', fontWeight:700, fontSize:'0.75rem', cursor:'pointer', flexShrink:0 }}>
                  {applyingBulk ? 'Applying…' : 'Apply'}
                </button>
                <button onClick={() => setBulkDue({ value: '', note: '' })}
                  style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #1E3A6E', background:'white',
                    color:'#1E3A6E', fontWeight:700, fontSize:'0.75rem', cursor:'pointer', flexShrink:0 }}>
                  📅 Due date
                </button>
              </>
            )}
          </div>
        )}

        {/* Fee list */}
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="card">
            {fees.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__title">No fees found</div>
                <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginTop:4 }}>
                  Click "Generate Fees" to create fee records for this month
                </div>
              </div>
            )}
            {fees.map(fee => (
              <div key={fee.id} style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6',
                background: selected.has(fee.id) ? '#f0f9ff' : 'white',
                display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}
                onClick={() => toggleSelect(fee.id)}>
                <div style={{ width:20, height:20, borderRadius:4, border:'2px solid',
                  borderColor: selected.has(fee.id) ? '#1E3A6E' : '#d1d5db',
                  background: selected.has(fee.id) ? '#1E3A6E' : 'white',
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {selected.has(fee.id) && <span style={{ color:'white', fontSize:'0.7rem' }}>✓</span>}
                </div>
                <div className="avatar" style={{ flexShrink:0 }}>{fee.display_name?.charAt(0)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.88rem' }}>
                    {fee.display_name}
                    <span style={{ fontSize:'0.72rem', color:'#6b7280', fontWeight:400, marginLeft:6 }}>({fee.ucc_id})</span>
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>
                    {Number(fee.matches_held) === 0
                      ? '🏖️ Holiday — no matches'
                      : `${fee.sessions_attended ?? 0} sessions attended`}
                  </div>
                  {/* Phase 1 (c.3): show + edit due_date */}
                  <div style={{ fontSize:'0.7rem', color:'#6b7280', marginTop:1, display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                    Due: <strong>{fee.due_date ? new Date(fee.due_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</strong>
                    {!['paid','waived'].includes(fee.status) && (
                      <button onClick={(e) => {
                          e.stopPropagation();
                          setDueDateModal({
                            fee,
                            value: fee.due_date ? new Date(fee.due_date).toISOString().split('T')[0] : ''
                          });
                        }}
                        title="Change due date for this fee"
                        style={{ fontSize:'0.6rem', padding:'1px 6px', borderRadius:4, border:'1px solid #d1d5db', background:'white', color:'#6b7280', cursor:'pointer' }}>
                        ✏️ edit
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem',
                    color: STATUS_COLORS[fee.status] || '#374151' }}>
                    ₹{parseFloat(fee.amount_due||0).toLocaleString('en-IN')}
                  </div>
                  {parseFloat(fee.balance||0) > 0 && (
                    <div style={{ fontSize:'0.68rem', color:'#dc2626' }}>₹{parseFloat(fee.balance).toLocaleString('en-IN')} due</div>
                  )}
                  <span style={{ fontSize:'0.6rem', fontWeight:700, textTransform:'uppercase',
                    background: STATUS_BG[fee.status] || '#f3f4f6',
                    color: STATUS_COLORS[fee.status] || '#374151',
                    padding:'1px 6px', borderRadius:10 }}>
                    {fee.status}
                  </span>
                  <div style={{ marginTop:4 }}>
                    <button onClick={(e) => { e.stopPropagation(); openPayModal(fee); }}
                      title="Record an exact amount paid, with the date"
                      style={{ fontSize:'0.65rem', padding:'2px 8px', borderRadius:6, border:'1px solid #16a34a',
                        background:'white', color:'#16a34a', fontWeight:700, cursor:'pointer' }}>
                      💰 Payment
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate fees dialog — choose the due date */}
      {genModal && (() => {
        const def = defaultDueFor(filter.month, filter.year);
        const opt = (on) => ({ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px', borderRadius:10, cursor:'pointer',
          border: on ? '2px solid #1E3A6E' : '1px solid #e5e7eb', background: on ? '#f0f5ff' : 'white', marginBottom:8 });
        return (
          <div onClick={() => !generating && setGenModal(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex',
              alignItems:'center', justifyContent:'center', padding:16, zIndex:60 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'white', borderRadius:12, padding:20, width:'100%', maxWidth:420 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E', marginBottom:4 }}>
                Generate Fees — {MONTHS[filter.month - 1]} {filter.year}
              </div>
              <div style={{ fontSize:'0.78rem', color:'#6b7280', marginBottom:12 }}>
                Choose when these fees are due. Members are told the due date in their fee notice.
              </div>

              <div style={opt(genModal.useDefault)} onClick={() => setGenModal({ ...genModal, useDefault: true })}>
                <input type="radio" readOnly checked={genModal.useDefault} style={{ marginTop:3 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1E3A6E' }}>Default — {fmtDay(def)}</div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>1st Sunday of next month. Members with a personal due day keep theirs.</div>
                </div>
              </div>
              <div style={opt(!genModal.useDefault)} onClick={() => setGenModal({ ...genModal, useDefault: false })}>
                <input type="radio" readOnly checked={!genModal.useDefault} style={{ marginTop:3 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1E3A6E' }}>Custom date</div>
                  <input type="date" className="form-control" style={{ marginTop:6 }}
                    min={`${filter.year}-${String(filter.month).padStart(2, '0')}-01`}
                    value={genModal.custom}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setGenModal({ ...genModal, useDefault: false, custom: e.target.value })} />
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:4 }}>Applies to every fee generated now, including members with a personal due day.</div>
                </div>
              </div>

              <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginBottom:4 }}>
                Members who already have a fee for this month are skipped. To change their due date, select them and use 📅 Due date.
              </div>
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button onClick={() => setGenModal(null)} disabled={generating}
                  style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                    background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={confirmGenerate} disabled={generating || (!genModal.useDefault && !genModal.custom)}
                  style={{ flex:2, padding:'12px', borderRadius:10, border:'none',
                    background: generating ? '#9ca3af' : '#F5C518', color:'#1E3A6E',
                    fontWeight:800, fontSize:'0.9rem', cursor:'pointer', fontFamily:'Rajdhani,sans-serif' }}>
                  {generating ? 'Generating…' : `⚡ Generate — due ${fmtDay(genModal.useDefault ? def : (genModal.custom || def))}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Record payment dialog */}
      {payModal && (() => {
        const m = payModal;
        const due  = parseFloat(m.fee.amount_due || 0);
        const paid = parseFloat(m.fee.amount_paid || 0);
        const bal  = Math.max(0, due - paid);
        const set  = (k) => (e) => setPayModal({ ...m, [k]: e.target.value });
        const lbl  = { fontSize:'0.72rem', fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' };
        return (
          <div onClick={() => !m.saving && setPayModal(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex',
              alignItems:'center', justifyContent:'center', padding:16, zIndex:60 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'white', borderRadius:12, padding:20, width:'100%', maxWidth:440, maxHeight:'90vh', overflowY:'auto' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E', marginBottom:4 }}>
                Record Payment
              </div>
              <div style={{ fontSize:'0.8rem', color:'#374151', marginBottom:12 }}>
                <strong>{m.fee.display_name}</strong> · {MONTHS[m.fee.month - 1]} {m.fee.year} fee<br />
                Fee ₹{due.toLocaleString('en-IN')} · Paid ₹{paid.toLocaleString('en-IN')} ·{' '}
                <span style={{ color: bal > 0 ? '#dc2626' : '#16a34a', fontWeight:700 }}>Balance ₹{bal.toLocaleString('en-IN')}</span>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Amount paid (₹)</label>
                  <input type="number" min="1" className="form-control" value={m.amount} onChange={set('amount')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date paid</label>
                  <input type="date" className="form-control" max={todayIST()} value={m.paid_on} onChange={set('paid_on')} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Paid by</label>
                  <select className="form-control" value={m.mode} onChange={set('mode')}>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">UTR / Ref (optional)</label>
                  <input className="form-control" placeholder="e.g. 612345678901" value={m.reference} onChange={set('reference')} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <input className="form-control" placeholder="e.g. Paid to treasurer at ground" value={m.note} onChange={set('note')} />
              </div>
              {parseFloat(m.amount) > bal && bal >= 0 && (
                <div style={{ fontSize:'0.75rem', color:'#92400e', background:'#fef3c7', borderRadius:8, padding:'6px 10px', marginBottom:8 }}>
                  ₹{Math.round(parseFloat(m.amount) - bal).toLocaleString('en-IN')} is more than the balance — it will be kept as the member's advance credit for future fees.
                </div>
              )}

              <div style={{ display:'flex', gap:8, marginTop:6 }}>
                <button onClick={() => setPayModal(null)} disabled={m.saving}
                  style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                    background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={savePayment} disabled={m.saving || !(parseFloat(m.amount) > 0) || !m.paid_on}
                  style={{ flex:2, padding:'12px', borderRadius:10, border:'none',
                    background: (m.saving || !(parseFloat(m.amount) > 0) || !m.paid_on) ? '#9ca3af' : '#16a34a',
                    color:'white', fontWeight:700, fontSize:'0.9rem', cursor:'pointer', fontFamily:'Rajdhani,sans-serif' }}>
                  {m.saving ? 'Saving…' : 'Record Payment'}
                </button>
              </div>

              <div style={{ ...lbl, marginTop:16, marginBottom:6 }}>Payments on this fee</div>
              {m.history === null ? (
                <div style={{ fontSize:'0.78rem', color:'#9ca3af' }}>Loading…</div>
              ) : m.history.length === 0 ? (
                <div style={{ fontSize:'0.78rem', color:'#9ca3af' }}>None yet.</div>
              ) : m.history.map((p, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderTop:'1px solid #f3f4f6',
                  opacity: p.reversed_at ? 0.5 : 1 }}>
                  <div style={{ flex:1, minWidth:0, fontSize:'0.75rem', color:'#374151' }}>
                    <strong>₹{Math.round(p.applied).toLocaleString('en-IN')}</strong>
                    {' · '}{new Date(p.submitted_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                    {' · '}{p.from_credit ? 'from advance credit' : p.admin_recorded ? 'recorded by admin' : `UTR ${String(p.utr).split('_SPLIT_')[0]}`}
                    {p.reversed_at && ' · undone'}
                  </div>
                  {p.admin_recorded && !p.reversed_at && p.status === 'confirmed' && (
                    <button onClick={() => undoPayment(p)}
                      style={{ fontSize:'0.65rem', padding:'2px 8px', borderRadius:6, border:'1px solid #dc2626',
                        background:'white', color:'#dc2626', fontWeight:700, cursor:'pointer', flexShrink:0 }}>
                      Undo
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Bulk due-date dialog */}
      {bulkDue && (() => {
        const chosen = fees.filter(f => selected.has(f.id));
        const locked = chosen.filter(f => ['paid', 'waived'].includes(f.status));
        const willChange = chosen.length - locked.length;
        return (
          <div onClick={() => !savingBulkDue && setBulkDue(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex',
              alignItems:'center', justifyContent:'center', padding:16, zIndex:60 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'white', borderRadius:12, padding:20, width:'100%', maxWidth:420 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E', marginBottom:6 }}>
                Change Due Date — {chosen.length} selected
              </div>
              <div style={{ fontSize:'0.78rem', color:'#6b7280', marginBottom:14 }}>
                {willChange} fee{willChange === 1 ? '' : 's'} will move to the new date.
                {locked.length > 0 && <> {locked.length} paid/waived will be left as they are.</>}
              </div>

              <div className="form-group">
                <label className="form-label">New Due Date</label>
                <input type="date" className="form-control" value={bulkDue.value}
                  onChange={e => setBulkDue({ ...bulkDue, value: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <input className="form-control" placeholder="e.g. Extended after Dubai tour"
                  value={bulkDue.note} onChange={e => setBulkDue({ ...bulkDue, note: e.target.value })} />
              </div>
              <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:-4 }}>
                A fee marked overdue goes back to pending if the new date is today or later.
                Late charges already added stay.
              </div>

              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                <button onClick={() => setBulkDue(null)} disabled={savingBulkDue}
                  style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                    background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={saveBulkDue} disabled={savingBulkDue || !bulkDue.value || willChange === 0}
                  style={{ flex:2, padding:'12px', borderRadius:10, border:'none',
                    background: (savingBulkDue || !bulkDue.value || willChange === 0) ? '#9ca3af' : '#1E3A6E',
                    color:'white', fontWeight:700, fontSize:'0.9rem',
                    cursor: (savingBulkDue || !bulkDue.value || willChange === 0) ? 'not-allowed' : 'pointer',
                    fontFamily:'Rajdhani,sans-serif' }}>
                  {savingBulkDue ? 'Saving…' : `Set for ${willChange} fee${willChange === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 1 (c.3): Edit-due-date modal */}
      {dueDateModal && (
        <div onClick={() => !savingDueDate && setDueDateModal(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex',
            alignItems:'center', justifyContent:'center', padding:16, zIndex:60 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'white', borderRadius:12, padding:20, width:'100%', maxWidth:400 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E', marginBottom:6 }}>
              Edit Fee Due Date
            </div>
            <div style={{ fontSize:'0.78rem', color:'#6b7280', marginBottom:14 }}>
              <strong>{dueDateModal.fee.display_name}</strong> · {MONTHS[dueDateModal.fee.month - 1]} {dueDateModal.fee.year} · ₹{parseFloat(dueDateModal.fee.amount_due || 0).toLocaleString('en-IN')}
            </div>

            <div className="form-group">
              <label className="form-label">New Due Date</label>
              <input type="date" className="form-control"
                value={dueDateModal.value}
                onChange={e => setDueDateModal({ ...dueDateModal, value: e.target.value })} />
              <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:4 }}>
                One-off override. To make a permanent rule for a member, use the member's <code>fee_due_day_override</code> setting.
              </div>
            </div>

            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button onClick={() => setDueDateModal(null)} disabled={savingDueDate}
                style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                  background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={saveDueDate} disabled={savingDueDate || !dueDateModal.value}
                style={{ flex:2, padding:'12px', borderRadius:10, border:'none',
                  background: (savingDueDate || !dueDateModal.value) ? '#9ca3af' : '#1E3A6E',
                  color:'white', fontWeight:700, fontSize:'0.9rem',
                  cursor: (savingDueDate || !dueDateModal.value) ? 'not-allowed' : 'pointer',
                  fontFamily:'Rajdhani,sans-serif' }}>
                {savingDueDate ? 'Saving…' : 'Save Due Date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AdminFines ───────────────────────────────────────────
// ─── Member charges & rebates ─────────────────────────────
// A CHARGE collects extra alongside the monthly fee — a sponsorship, a kit
// contribution, whatever the club calls it. A REBATE reduces the fee until it
// is used up. Either can be taken in one go or over monthly instalments, and
// BOTH the amount and the number of instalments may differ per member
// (SB, 2 Sep 2026) — hence the per-member table rather than one figure.
export function AdminAdjustments() {
  const MONTH_NAMES = ['January','February','March','April','May','June',
                       'July','August','September','October','November','December'];
  const now = new Date();

  const [tab, setTab]           = useState('new');   // new | active
  const [members, setMembers]   = useState([]);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [problems, setProblems] = useState([]);

  const [head, setHead] = useState({
    kind: 'charge', label: '', notes: '',
    start_month: now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2,   // next month
    start_year:  now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear(),
  });

  // Defaults typed once and pushed across the selected rows.
  const [bulk, setBulk] = useState({ total_amount: '', mode: 'lumpsum', instalments: 3 });

  // member_id -> { total_amount, mode, instalments }
  const [picked, setPicked] = useState({});

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [mRes, aRes] = await Promise.all([
        api.get('/members'),
        api.get('/adjustments').catch(() => ({ data: { adjustments: [] } })),
      ]);
      setMembers((mRes.data.members || []).filter(m => m.is_active));
      setExisting(aRes.data.adjustments || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }

  const chosen = Object.keys(picked);

  function toggleMember(id) {
    setPicked(p => {
      if (p[id]) { const n = { ...p }; delete n[id]; return n; }
      return { ...p, [id]: { ...bulk } };
    });
  }

  function selectAll() {
    const next = {};
    for (const m of members) next[m.id] = { ...bulk };
    setPicked(next);
  }

  function applyBulkToChosen() {
    if (!chosen.length) return toast.error('Pick some members first');
    setPicked(p => {
      const n = { ...p };
      for (const id of Object.keys(n)) n[id] = { ...bulk };
      return n;
    });
    toast.success(`Applied to ${chosen.length} member${chosen.length === 1 ? '' : 's'}`);
  }

  function patchRow(id, patch) {
    setPicked(p => ({ ...p, [id]: { ...p[id], ...patch } }));
  }

  // What each member ends up paying per month, shown live so a wrong figure is
  // obvious before submitting rather than after.
  function scheduleFor(row) {
    const total = parseFloat(row.total_amount);
    if (!total || total <= 0) return null;
    if (row.mode === 'lumpsum') return `₹${total.toLocaleString('en-IN')} in one go`;
    const n = Math.max(1, parseInt(row.instalments, 10) || 1);
    const per = Math.floor(total / n);
    const last = total - per * (n - 1);
    return per === last
      ? `${n} × ₹${per.toLocaleString('en-IN')}`
      : `${n - 1} × ₹${per.toLocaleString('en-IN')} + ₹${last.toLocaleString('en-IN')}`;
  }

  const grandTotal = chosen.reduce((t, id) => t + (parseFloat(picked[id]?.total_amount) || 0), 0);

  async function submit() {
    setProblems([]);
    if (!head.label.trim()) return toast.error('A description is required — the member sees it');
    if (!chosen.length)     return toast.error('Select at least one member');

    const items = chosen.map(id => ({
      member_id: id,
      total_amount: picked[id].total_amount,
      mode: picked[id].mode,
      instalments: picked[id].mode === 'instalments' ? picked[id].instalments : null,
    }));

    setSaving(true);
    try {
      const r = await api.post('/adjustments', { ...head, items });
      toast.success(r.data.message || 'Created', { duration: 6000 });
      setPicked({});
      setHead(h => ({ ...h, label: '', notes: '' }));
      await load();
      setTab('active');
    } catch (err) {
      const d = err.response?.data;
      if (Array.isArray(d?.problems)) setProblems(d.problems);
      toast.error(d?.error || 'Failed to create');
    } finally { setSaving(false); }
  }

  async function cancelAdj(a) {
    const reason = window.prompt(
      `Cancel "${a.label}" for ${a.display_name}?\n\nFuture instalments stop. Anything already on a fee stays.\n\nReason:`
    );
    if (!reason || !reason.trim()) return;
    try {
      const r = await api.post(`/adjustments/${a.id}/cancel`, { reason: reason.trim() });
      toast.success(`Cancelled. ₹${Number(r.data.already_applied).toLocaleString('en-IN')} already applied stays.`);
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to cancel'); }
  }

  // One-off catch-up for plans created before applying-on-create existed.
  const [applyBusy, setApplyBusy] = useState(false);
  async function applyExisting() {
    if (!window.confirm(
      'Apply every active charge and rebate to the fees already generated for their months?\n\n' +
      'Charges go on first, then rebates (a rebate only reduces the unpaid part of a fee). ' +
      'Fees that already carry a plan are left as they are. Safe to run more than once.')) return;
    setApplyBusy(true);
    try {
      const r = await api.post('/adjustments/apply-existing', {});
      const lines = (r.data.results || []).map(x =>
        `${x.member}: ${x.kind === 'charge' ? '+' : '−'}₹${Number(x.amount).toLocaleString('en-IN')} (${x.label}, ${x.months.join(', ')})`);
      toast.success(r.data.message + (lines.length ? '\n' + lines.slice(0, 8).join('\n') + (lines.length > 8 ? `\n…and ${lines.length - 8} more` : '') : ''),
                    { duration: 12000, style: { whiteSpace: 'pre-line', maxWidth: 520 } });
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to apply', { duration: 8000 }); }
    finally { setApplyBusy(false); }
  }

  // ── Edit ──
  // Wording can always change. Amount / schedule / start month can change too:
  // the server takes the plan off the fees it touched and applies it again.
  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  function startEdit(a) {
    setEditId(a.id);
    setEditForm({
      label: a.label || '', notes: a.notes || '',
      total_amount: String(parseFloat(a.total_amount)), mode: a.mode,
      instalments: a.instalments || 3,
      start_month: a.start_month, start_year: a.start_year,
    });
  }
  async function saveEdit(a) {
    const f = editForm;
    const body = {};
    if (f.label.trim() !== (a.label || '')) body.label = f.label.trim();
    if ((f.notes || '').trim() !== (a.notes || '')) body.notes = f.notes;
    if (a.status !== 'cancelled') {
      if (parseFloat(f.total_amount) !== parseFloat(a.total_amount)) body.total_amount = parseFloat(f.total_amount);
      if (f.mode !== a.mode) body.mode = f.mode;
      if (f.mode === 'instalments' && parseInt(f.instalments, 10) !== parseInt(a.instalments || 0, 10)) body.instalments = parseInt(f.instalments, 10);
      if (parseInt(f.start_month, 10) !== a.start_month) body.start_month = parseInt(f.start_month, 10);
      if (parseInt(f.start_year, 10) !== a.start_year) body.start_year = parseInt(f.start_year, 10);
    }
    if (!Object.keys(body).length) { setEditId(null); return; }
    if (body.mode === 'instalments' && body.instalments === undefined) body.instalments = parseInt(f.instalments, 10);
    setEditBusy(true);
    try {
      const r = await api.patch(`/adjustments/${a.id}`, body);
      toast.success(r.data.fees_changed
        ? `Saved. ${r.data.fees_changed} fee(s) recalculated.`
        : 'Saved.', { duration: 6000 });
      setEditId(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save', { duration: 8000 }); }
    finally { setEditBusy(false); }
  }

  // Delete removes the plan AND takes its amounts back off every fee it was
  // applied to. Cancel (above) only stops future instalments.
  async function deleteAdj(a) {
    const applied = parseFloat(a.applied_amount || 0);
    const what = a.kind === 'charge' ? 'charge' : 'rebate';
    const effect = applied > 0
      ? `₹${applied.toLocaleString('en-IN')} already applied over ${a.applied_count} month(s) will be taken back off those fees` +
        (a.kind === 'charge' ? ' (the member will owe less).' : ' (the member will owe more).')
      : 'It has not been applied to any fee yet.';
    const reason = window.prompt(
      `DELETE the ${what} "${a.label}" for ${a.display_name}?\n\n${effect}\n\nThis cannot be undone. Reason:`
    );
    if (!reason || !reason.trim()) return;
    try {
      const r = await api.delete(`/adjustments/${a.id}`, { data: { reason: reason.trim() } });
      toast.success(r.data.fees_changed
        ? `Deleted. ₹${Number(r.data.removed_amount).toLocaleString('en-IN')} removed from ${r.data.fees_changed} fee(s).`
        : 'Deleted.');
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete', { duration: 8000 }); }
  }

  const isCharge = head.kind === 'charge';
  const accent   = isCharge ? '#b45309' : '#166534';

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Charges & Rebates" icon="💠" />

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {[['new','➕ New'], ['active',`📋 Existing (${existing.filter(a=>a.status==='active').length})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ flex: 1, padding: 12, border: 'none', cursor: 'pointer',
              background: tab === k ? 'var(--navy)' : 'transparent',
              color: tab === k ? 'white' : 'var(--text-muted)',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem' }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}

      {/* ── NEW ── */}
      {!loading && tab === 'new' && (
        <div style={{ padding: 16 }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[['charge','Charge','Collect extra'], ['rebate','Rebate','Reduce the fee']].map(([k, lbl, sub]) => (
                <button key={k} onClick={() => setHead(h => ({ ...h, kind: k }))}
                  style={{ flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${head.kind === k ? (k === 'charge' ? '#b45309' : '#166534') : 'var(--border)'}`,
                    background: head.kind === k ? (k === 'charge' ? '#fffbeb' : '#f0fdf4') : 'white' }}>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem',
                    color: head.kind === k ? (k === 'charge' ? '#b45309' : '#166534') : 'var(--navy)' }}>{lbl}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{sub}</div>
                </button>
              ))}
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <input className="form-control" value={head.label}
                placeholder={isCharge ? 'e.g. Ground sponsorship' : 'e.g. Tour rebate'}
                onChange={e => setHead(h => ({ ...h, label: e.target.value }))} />
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                Members see this on their fee, so write it as you want it read.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label className="form-label">Starts</label>
                <select className="form-control" value={head.start_month}
                  onChange={e => setHead(h => ({ ...h, start_month: Number(e.target.value) }))}>
                  {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Year</label>
                <input type="number" className="form-control" value={head.start_year}
                  onChange={e => setHead(h => ({ ...h, start_year: Number(e.target.value) }))} />
              </div>
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>
              A month the member isn't charged — sabbatical, for instance — is skipped and the plan runs on.
            </div>
          </div>

          {/* Defaults */}
          <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--navy)', marginBottom: 8 }}>Amount &amp; schedule</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.72rem' }}>Amount (₹)</label>
                <input type="number" className="form-control" value={bulk.total_amount}
                  onChange={e => setBulk(b => ({ ...b, total_amount: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.72rem' }}>Taken as</label>
                <select className="form-control" value={bulk.mode}
                  onChange={e => setBulk(b => ({ ...b, mode: e.target.value }))}>
                  <option value="lumpsum">One go</option>
                  <option value="instalments">Instalments</option>
                </select>
              </div>
            </div>
            {bulk.mode === 'instalments' && (
              <div style={{ marginTop: 10 }}>
                <label className="form-label" style={{ fontSize: '0.72rem' }}>Number of months</label>
                <input type="number" min="1" max="36" className="form-control" value={bulk.instalments}
                  onChange={e => setBulk(b => ({ ...b, instalments: e.target.value }))} />
              </div>
            )}
            <button onClick={applyBulkToChosen}
              style={{ width: '100%', marginTop: 10, padding: 9, borderRadius: 8, cursor: 'pointer',
                border: '1px solid var(--navy)', background: 'white', color: 'var(--navy)',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>
              Apply to everyone selected
            </button>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 6 }}>
              A starting point — change any member's amount or months individually below.
            </div>
          </div>

          {/* Members */}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--navy)' }}>
                Members ({chosen.length} of {members.length})
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={selectAll} style={{ border: 'none', background: 'none', color: 'var(--navy)',
                  fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>Select all</button>
                <button onClick={() => setPicked({})} style={{ border: 'none', background: 'none', color: '#dc2626',
                  fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>Clear</button>
              </div>
            </div>

            {members.map(m => {
              const row = picked[m.id];
              return (
                <div key={m.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '9px 14px',
                  background: row ? 'rgba(30,58,110,0.03)' : 'white' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={!!row} onChange={() => toggleMember(m.id)} />
                    <div style={{ flex: 1, fontWeight: 600, color: 'var(--navy)', fontSize: '0.85rem' }}>
                      {m.display_name}
                      {m.ucc_id && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.72rem' }}> · {m.ucc_id}</span>}
                    </div>
                    {row && (
                      <div style={{ fontSize: '0.7rem', color: accent, fontWeight: 700, textAlign: 'right' }}>
                        {scheduleFor(row) || '—'}
                      </div>
                    )}
                  </div>

                  {row && (
                    <div style={{ display: 'grid', gridTemplateColumns: row.mode === 'instalments' ? '1fr 1fr 1fr' : '1fr 1fr',
                      gap: 8, marginTop: 8, paddingLeft: 26 }}>
                      <input type="number" className="form-control" placeholder="Amount ₹"
                        value={row.total_amount} onChange={e => patchRow(m.id, { total_amount: e.target.value })}
                        style={{ fontSize: '0.78rem' }} />
                      <select className="form-control" value={row.mode}
                        onChange={e => patchRow(m.id, { mode: e.target.value })} style={{ fontSize: '0.78rem' }}>
                        <option value="lumpsum">One go</option>
                        <option value="instalments">Instalments</option>
                      </select>
                      {row.mode === 'instalments' && (
                        <input type="number" min="1" max="36" className="form-control" placeholder="Months"
                          value={row.instalments} onChange={e => patchRow(m.id, { instalments: e.target.value })}
                          style={{ fontSize: '0.78rem' }} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {problems.length > 0 && (
            <div style={{ background: '#fff1f2', border: '1px solid #fca5a5', borderRadius: 10,
              padding: '10px 12px', marginTop: 10 }}>
              <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#991b1b', marginBottom: 5 }}>
                Nothing was created — fix these first
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.75rem', color: '#991b1b' }}>
                {problems.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          {chosen.length > 0 && (
            <div style={{ background: 'var(--navy)', color: 'white', borderRadius: 12, padding: '12px 14px', marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.8rem' }}>
                  {chosen.length} member{chosen.length === 1 ? '' : 's'}
                </div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.15rem' }}>
                  {isCharge ? '+' : '−'} ₹{grandTotal.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          )}

          <button onClick={submit} disabled={saving || !chosen.length}
            style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 10, border: 'none',
              background: chosen.length ? 'var(--gold)' : '#e5e7eb',
              color: chosen.length ? 'var(--navy)' : '#9ca3af',
              fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem',
              cursor: chosen.length ? 'pointer' : 'default' }}>
            {saving ? 'Saving…' : `Create ${isCharge ? 'charge' : 'rebate'} for ${chosen.length || 0}`}
          </button>
        </div>
      )}

      {/* ── EXISTING ── */}
      {!loading && tab === 'active' && (
        <div style={{ padding: 16 }}>
          {existing.some(a => a.status === 'active') && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                          background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 10,
                          padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                New plans apply to fees already generated automatically. For older ones, apply them now:
              </div>
              <button className="btn btn-outline btn-sm" onClick={applyExisting} disabled={applyBusy}
                      style={{ flexShrink: 0 }}>
                {applyBusy ? 'Applying…' : 'Apply to fees already generated'}
              </button>
            </div>
          )}
          {existing.length === 0 && (
            <div className="empty-state">
              <div className="empty-state__title">Nothing yet</div>
              <div className="empty-state__text">Charges and rebates you create will be listed here.</div>
            </div>
          )}
          {existing.map(a => {
            const applied   = parseFloat(a.applied_amount || 0);
            const remaining = parseFloat(a.remaining_amount || 0);
            const total     = parseFloat(a.total_amount || 0);
            const pct       = total > 0 ? Math.min(100, Math.round((applied / total) * 100)) : 0;
            const charge    = a.kind === 'charge';
            return (
              <div key={a.id} style={{ background: 'white', border: '1px solid var(--border)',
                borderLeft: `4px solid ${charge ? '#b45309' : '#166534'}`,
                borderRadius: 10, padding: '12px 14px', marginBottom: 10,
                opacity: a.status === 'active' ? 1 : 0.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '0.9rem' }}>{a.display_name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{a.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {a.mode === 'lumpsum' ? 'One go' : `${a.instalments} instalments`}
                      {' · from '}{MONTH_NAMES[a.start_month - 1]} {a.start_year}
                      {a.status !== 'active' && ` · ${a.status.toUpperCase()}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem',
                      color: charge ? '#b45309' : '#166534' }}>
                      {charge ? '+' : '−'}₹{total.toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      ₹{remaining.toLocaleString('en-IN')} left
                    </div>
                  </div>
                </div>

                <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, marginTop: 9, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: charge ? '#b45309' : '#166534' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    ₹{applied.toLocaleString('en-IN')} applied over {a.applied_count} month{a.applied_count === '1' ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    {editId !== a.id && (
                      <button onClick={() => startEdit(a)} title="Change the description, amount or schedule"
                        style={{ border: 'none', background: 'none',
                        color: 'var(--navy)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        ✏️ Edit
                      </button>
                    )}
                    {a.status === 'active' && (
                      <button onClick={() => cancelAdj(a)} title="Stop future instalments; keep what was applied"
                        style={{ border: 'none', background: 'none',
                        color: '#b45309', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        Cancel
                      </button>
                    )}
                    <button onClick={() => deleteAdj(a)} title="Remove completely, including from fees"
                      style={{ border: 'none', background: 'none',
                      color: '#dc2626', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                      🗑 Delete
                    </button>
                  </div>
                </div>

                {editId === a.id && editForm && (() => {
                  const locked = a.status === 'cancelled';
                  const inp = { width: '100%', padding: '7px 9px', border: '1px solid var(--border)',
                                borderRadius: 7, fontSize: '0.85rem', boxSizing: 'border-box' };
                  const lab = { fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
                  const set = (k) => (e) => setEditForm(f => ({ ...f, [k]: e.target.value }));
                  const structuralChanged = !locked && (
                    parseFloat(editForm.total_amount) !== parseFloat(a.total_amount) ||
                    editForm.mode !== a.mode ||
                    (editForm.mode === 'instalments' && parseInt(editForm.instalments, 10) !== parseInt(a.instalments || 0, 10)) ||
                    parseInt(editForm.start_month, 10) !== a.start_month ||
                    parseInt(editForm.start_year, 10) !== a.start_year);
                  return (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)',
                                  display: 'grid', gap: 8 }}>
                      <div>
                        <label style={lab}>Description (the member sees this)</label>
                        <input style={inp} value={editForm.label} onChange={set('label')} />
                      </div>
                      {locked ? (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          Cancelled — only the wording can change. Delete it and create a new one to change the amount.
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <div>
                              <label style={lab}>Total amount (₹)</label>
                              <input style={inp} type="number" min="1" value={editForm.total_amount} onChange={set('total_amount')} />
                            </div>
                            <div>
                              <label style={lab}>How</label>
                              <select style={inp} value={editForm.mode} onChange={set('mode')}>
                                <option value="lumpsum">One go</option>
                                <option value="instalments">Instalments</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: editForm.mode === 'instalments' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
                            {editForm.mode === 'instalments' && (
                              <div>
                                <label style={lab}>Instalments</label>
                                <input style={inp} type="number" min="1" max="36" value={editForm.instalments} onChange={set('instalments')} />
                              </div>
                            )}
                            <div>
                              <label style={lab}>Start month</label>
                              <select style={inp} value={editForm.start_month} onChange={set('start_month')}>
                                {MONTH_NAMES.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={lab}>Year</label>
                              <input style={inp} type="number" value={editForm.start_year} onChange={set('start_year')} />
                            </div>
                          </div>
                        </>
                      )}
                      <div>
                        <label style={lab}>Notes (admin only)</label>
                        <input style={inp} value={editForm.notes} onChange={set('notes')} />
                      </div>
                      {structuralChanged && applied > 0 && (
                        <div style={{ fontSize: '0.72rem', background: '#fffbeb', border: '1px solid #fde68a',
                                      color: '#92400e', borderRadius: 7, padding: '7px 9px' }}>
                          ₹{applied.toLocaleString('en-IN')} is already on {a.applied_count} fee(s). Saving takes it off
                          those fees and applies the new amount to every fee already generated from the start month.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setEditId(null)} disabled={editBusy}>Close</button>
                        <button className="btn btn-primary btn-sm" onClick={() => saveEdit(a)} disabled={editBusy}>
                          {editBusy ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminFines() {
  const [fines, setFines] = useState([]);
  const [pendingFines, setPendingFines] = useState([]);       // Session 15 Turn 2
  const [view, setView] = useState('published');              // 'published' | 'pending'
  const [pendingDrafts, setPendingDrafts] = useState({});     // { [fine_id]: { amount, penalty_pts, reason } }
  const [pendingSelected, setPendingSelected] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ member_id: '', category: '1', reason: '', custom_reason: '', custom_amount: '', penalty_pts: '' });
  const [saving, setSaving] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [actionAmount, setActionAmount] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [actionPts, setActionPts] = useState('0');
  const [actionSaving, setActionSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [customReasons, setCustomReasons] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ucc_custom_fine_reasons') || '[]'); } catch { return []; }
  });

  // Session 17 — global "auto-apply fines to leaderboard" toggle + pending count
  const [autoApply, setAutoApply] = useState(true);
  const [pendingPenaltyCount, setPendingPenaltyCount] = useState(0);
  const [toggleBusy, setToggleBusy] = useState(false);

  const CAT_META = {
    '1': { label: '₹1k',     amount: 1000, color: '#d97706', bg: '#fef3c7' },
    '2': { label: '₹3k',     amount: 3000, color: '#dc2626', bg: '#fee2e2' },
    '3': { label: '₹6k+Ban', amount: 6000, color: '#7c3aed', bg: '#ede9fe' },
    '4': { label: 'Late',    amount: null,  color: '#0891b2', bg: '#cffafe' },
    '5': { label: 'Custom ₹',amount: null,  color: '#374151', bg: '#f3f4f6' },
    '6': { label: 'Points',  amount: 0,     color: '#16a34a', bg: '#dcfce7' },
  };

  const LATE_AMOUNTS = {
    'Late arrival (up to 10 min) — ₹500': 500,
    'Late arrival (10–20 min) — ₹1,500': 1500,
    'Late arrival (20+ min) — ₹2,500': 2500,
  };

  const CAT_PRESETS = {
    '1': ['Improper dress','Littering','Arguing with management','No availability reply','Disobeying captain','Fee receipt not sent','Not wearing UCC jersey','Using phone during match'],
    '2': ['Repeat Cat.1 in quarter','Arguing with umpires','Leaving without permission','Abusing member','Cash deposit without UTR','No-show after YES availability'],
    '3': ['Smoking/drinking on ground','Asset damage','Abusing umpire','Match fixing','Physical fight','Bringing outsiders without permission'],
    '4': ['Late arrival (up to 10 min) — ₹500','Late arrival (10–20 min) — ₹1,500','Late arrival (20+ min) — ₹2,500'],
    '5': [],
    '6': ['Misconduct penalty','Points deduction for rule violation','End-of-year ranking penalty'],
  };

  async function refreshFines() {
    try {
      const [pubRes, pendRes] = await Promise.all([
        api.get('/fines'),
        api.get('/fines?published=false'),
      ]);
      setFines(pubRes.data.fines || []);
      setPendingFines(pendRes.data.fines || []);
    } catch (e) { /* swallow */ }
  }

  useEffect(() => {
    Promise.all([
      api.get('/fines'),
      api.get('/fines?published=false'),
      api.get('/members'),
      api.get('/settings/fines-auto-apply').catch(() => ({ data: { value: true, pending_count: 0 } })),
    ]).then(([pub, pend, m, s]) => {
      setFines(pub.data.fines || []);
      setPendingFines(pend.data.fines || []);
      setMembers((m.data.members || []).filter(mb => !mb.is_observer));
      setAutoApply(s.data?.value !== false);
      setPendingPenaltyCount(s.data?.pending_count || 0);
    }).finally(() => setLoading(false));
  }, []);

  // Session 17 — toggle global auto-apply / bulk apply pending penalty fines
  async function toggleAutoApply(newVal) {
    if (toggleBusy) return;
    setToggleBusy(true);
    try {
      const r = await api.post('/settings/fines-auto-apply', { value: newVal });
      setAutoApply(newVal);
      // After flipping ON, any pending fines just got applied → refetch count
      const s = await api.get('/settings/fines-auto-apply');
      setPendingPenaltyCount(s.data?.pending_count || 0);
      if (r.data?.applied_count > 0) {
        alert(`✓ Auto-apply turned ON. ${r.data.applied_count} pending penalty fines have been applied to leaderboards.`);
      }
    } catch (e) {
      alert('Failed to update setting: ' + (e.response?.data?.error || e.message));
    } finally {
      setToggleBusy(false);
    }
  }

  async function applyPendingPenaltyFines() {
    if (toggleBusy) return;
    if (!window.confirm(`Apply ${pendingPenaltyCount} pending penalty fines to the leaderboard now?`)) return;
    setToggleBusy(true);
    try {
      const r = await api.post('/settings/apply-pending-fines', {});
      alert(`✓ ${r.data.applied_count} pending fines applied.`);
      const s = await api.get('/settings/fines-auto-apply');
      setPendingPenaltyCount(s.data?.pending_count || 0);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setToggleBusy(false);
    }
  }

  // ── Pending fine helpers (Session 15 Turn 2) ───────────
  function draftFor(f) {
    const d = pendingDrafts[f.id];
    return {
      amount: d?.amount !== undefined ? d.amount : (f.system_suggested_amount ?? f.amount ?? ''),
      penalty_pts: d?.penalty_pts !== undefined ? d.penalty_pts : (f.penalty_pts ?? 0),
      reason: d?.reason !== undefined ? d.reason : (f.reason ?? ''),
    };
  }

  function setDraft(id, patch) {
    setPendingDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  }

  function togglePendingSelect(id) {
    setPendingSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAllPending() {
    if (pendingSelected.size === pendingFines.length) setPendingSelected(new Set());
    else setPendingSelected(new Set(pendingFines.map(f => f.id)));
  }

  async function publishPending(mode) {
    if (pendingSelected.size === 0) return;
    const items = pendingFines
      .filter(f => pendingSelected.has(f.id))
      .map(f => {
        const d = draftFor(f);
        return {
          id: f.id,
          amount: parseFloat(d.amount) || 0,
          penalty_pts: parseInt(d.penalty_pts) || 0,
          reason: String(d.reason || '').trim(),
        };
      });

    // Basic client-side validation
    for (const it of items) {
      if (!it.amount || it.amount <= 0) {
        toast.error('All selected fines need a positive amount before publishing');
        return;
      }
      if (!it.reason) {
        toast.error('All selected fines need a reason before publishing');
        return;
      }
    }

    const msg = mode === 'broadcast'
      ? `Publish ${items.length} fine(s) as ONE combined broadcast notice?`
      : `Publish ${items.length} fine(s) as individual notices to each member?`;
    if (!window.confirm(msg)) return;

    setPublishing(true);
    try {
      const res = await api.post('/fines/publish-batch', {
        items,
        as_broadcast: mode === 'broadcast',
      });
      toast.success(`Published ${res.data.published_count} fine(s) ✅`);
      setPendingSelected(new Set());
      setPendingDrafts({});
      await refreshFines();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to publish fines');
    } finally {
      setPublishing(false);
    }
  }

  async function deletePending(id) {
    if (!window.confirm('Delete this pending fine? Cannot be undone.')) return;
    try {
      await api.delete('/fines/' + id);
      toast.success('Pending fine deleted');
      await refreshFines();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  }

  async function bulkDeletePending() {
    if (pendingSelected.size === 0) return;
    if (!window.confirm(`Delete ${pendingSelected.size} pending fine(s)?`)) return;
    setPublishing(true);
    try {
      const ids = [...pendingSelected];
      await Promise.all(ids.map(id => api.delete('/fines/' + id)));
      toast.success(`${ids.length} pending fine(s) deleted`);
      setPendingSelected(new Set());
      await refreshFines();
    } catch (err) {
      toast.error('Bulk delete failed');
    } finally {
      setPublishing(false);
    }
  }

  function getAmount() {
    if (form.category === '1') return 1000;
    if (form.category === '2') return 3000;
    if (form.category === '3') return 6000;
    if (form.category === '4') return LATE_AMOUNTS[form.reason] || parseFloat(form.custom_amount) || 0;
    if (form.category === '5') return parseFloat(form.custom_amount) || 0;
    return 0;
  }

  async function issueFine() {
    const reason = form.custom_reason.trim() || form.reason;
    if (!form.member_id) return alert('Select a member');
    if (!reason) return alert('Enter a reason');
    if (form.category === '6' && !form.penalty_pts) return alert('Enter points to deduct');
    if (['4','5'].includes(form.category) && !getAmount()) return alert('Enter an amount');
    if (form.custom_reason.trim() && !customReasons.includes(form.custom_reason.trim())) {
      const updated = [...customReasons, form.custom_reason.trim()];
      setCustomReasons(updated);
      try { localStorage.setItem('ucc_custom_fine_reasons', JSON.stringify(updated)); } catch {}
    }
    setSaving(true);
    try {
      await api.post('/fines', {
        member_id: form.member_id,
        category: parseInt(form.category),
        amount: getAmount(),
        reason,
        penalty_pts: form.category === '6' ? parseInt(form.penalty_pts) : 0,
      });
      alert('Fine issued successfully');
      setShowAdd(false);
      setForm({ member_id: '', category: '1', reason: '', custom_reason: '', custom_amount: '', penalty_pts: '' });
      const res = await api.get('/fines');
      setFines(res.data.fines || []);
    } catch (err) { alert(err.response?.data?.error || 'Failed to issue fine'); }
    finally { setSaving(false); }
  }

  function openAction(fine, type) {
    setActionModal({ fine, type });
    setActionNote('');
    setActionAmount(fine.amount || '');
    setActionReason(fine.reason || '');
    setActionPts(String(parseInt(fine.penalty_pts) || 0));
  }

  async function submitAction() {
    if (!actionNote.trim()) return alert('Note/reason is required');
    const { fine, type } = actionModal;
    setActionSaving(true);
    try {
      if (type === 'edit') {
        const pts = parseInt(actionPts);
        if (!Number.isFinite(pts) || pts < 0) { setActionSaving(false); return alert('Points must be 0 or more'); }
        await api.patch('/fines/' + fine.id, { reason: actionReason, amount: parseFloat(actionAmount), penalty_pts: pts, note: actionNote });
        alert('Fine updated');
      } else if (type === 'cancel') {
        await api.patch('/fines/' + fine.id + '/cancel', { note: actionNote });
        alert('Fine cancelled');
      } else if (type === 'delete') {
        await api.delete('/fines/' + fine.id);
        alert('Fine deleted');
      }
      setActionModal(null);
      const res = await api.get('/fines');
      setFines(res.data.fines || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally { setActionSaving(false); }
  }

  async function publishFines() {
    try {
      const res = await api.post('/fines/monday-announcement');
      alert('Published ' + (res.data.announced || 0) + ' fines to Noticeboard');
      const r = await api.get('/fines');
      setFines(r.data.fines || []);
    } catch { alert('Failed to publish fines'); }
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    const actionable = fines.filter(f => !['paid','waived','cancelled'].includes(f.status));
    if (selected.size === actionable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(actionable.map(f => f.id)));
    }
  }

  async function bulkAction(action) {
    if (selected.size === 0) return;
    if (!window.confirm(`${action === 'cancel' ? 'Cancel' : 'Delete'} ${selected.size} fine(s)?`)) return;
    setBulkSaving(true);
    try {
      const ids = [...selected];
      await Promise.all(ids.map(id =>
        action === 'cancel'
          ? api.patch(`/fines/${id}/cancel`, { note: 'Bulk cancelled by admin' })
          : api.delete(`/fines/${id}`)
      ));
      toast.success(`${ids.length} fine(s) ${action === 'cancel' ? 'cancelled' : 'deleted'}`);
      setSelected(new Set());
      setBulkMode(false);
      const r = await api.get('/fines');
      setFines(r.data.fines || []);
    } catch { toast.error('Bulk action failed'); }
    finally { setBulkSaving(false); }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const unannounced = fines.filter(f => !f.announced_monday && !['paid','waived'].includes(f.status));
  const actionableFines = fines.filter(f => !['paid','waived','cancelled'].includes(f.status));
  const selectedMember = members.find(m => m.id === form.member_id);
  const allCustomReasons = [...new Set([...customReasons, ...CAT_PRESETS['5']])];

  return (
    <div>
      <AdminPageHeader title="Fine & Discipline" icon="⚠️" />
      <div style={{ padding:'16px' }}>
        {/* Session 15 Turn 2 — Tab toggle: Published vs Pending */}
        <div style={{ display:'flex', gap:0, marginBottom:16, background:'#f3f4f6', borderRadius:10, padding:4 }}>
          <button
            onClick={() => { setView('published'); setPendingSelected(new Set()); }}
            style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'none',
              background: view === 'published' ? '#1E3A6E' : 'transparent',
              color: view === 'published' ? 'white' : '#374151',
              fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
            Published
          </button>
          <button
            onClick={() => { setView('pending'); setShowAdd(false); setBulkMode(false); }}
            style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'none',
              background: view === 'pending' ? '#d97706' : 'transparent',
              color: view === 'pending' ? 'white' : '#374151',
              fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
            ⏳ Pending {pendingFines.length > 0 ? `(${pendingFines.length})` : ''}
          </button>
        </div>

        {view === 'published' && (<>
        {/* Session 17 — Global auto-apply toggle for ranking penalties */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'10px 12px', marginBottom:10, borderRadius:8,
          background: autoApply ? '#dcfce7' : '#fef3c7',
          border:'1px solid', borderColor: autoApply ? '#86efac' : '#fde68a',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:0 }}>
            <span style={{ fontSize:'1rem' }}>{autoApply ? '✅' : '⏸️'}</span>
            <div style={{ minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:'0.78rem', color:'#1f2937' }}>
                {autoApply ? 'Penalty points auto-deduct from leaderboard' : 'Penalty points are PENDING — not deducting'}
              </div>
              {pendingPenaltyCount > 0 && (
                <div style={{ fontSize:'0.68rem', color:'#92400e', marginTop:2 }}>
                  {pendingPenaltyCount} fine{pendingPenaltyCount === 1 ? '' : 's'} waiting to be applied
                </div>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            {!autoApply && pendingPenaltyCount > 0 && (
              <button onClick={applyPendingPenaltyFines} disabled={toggleBusy}
                style={{ padding:'6px 10px', borderRadius:6, border:'none', background:'#1E3A6E', color:'white', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
                Apply All Now
              </button>
            )}
            <button onClick={() => toggleAutoApply(!autoApply)} disabled={toggleBusy}
              style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #1E3A6E', background:'white', color:'#1E3A6E', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
              Turn {autoApply ? 'OFF' : 'ON'}
            </button>
          </div>
        </div>

        {/* Top action bar */}
        <div style={{ display:'flex', gap:8, marginBottom: bulkMode ? 8 : 16 }}>
          <button className="btn btn-gold" style={{ flex:1 }} onClick={() => { setShowAdd(!showAdd); setBulkMode(false); }}>
            {showAdd ? 'Cancel' : '+ Issue Fine'}
          </button>
          <button className="btn btn-primary" style={{ flex:1 }} onClick={publishFines} disabled={unannounced.length === 0}>
            📢 Publish ({unannounced.length})
          </button>
          <button onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); setShowAdd(false); }}
            style={{ padding:'8px 12px', borderRadius:8, border:'2px solid', cursor:'pointer', fontWeight:700, fontSize:'0.75rem',
              borderColor: bulkMode ? '#1E3A6E' : '#e5e7eb',
              background: bulkMode ? '#1E3A6E' : 'white',
              color: bulkMode ? 'white' : '#374151' }}>
            ☑️ Bulk
          </button>
        </div>

        {/* Bulk action bar */}
        {bulkMode && (
          <div style={{ background:'#1E3A6E', borderRadius:10, padding:'10px 14px', marginBottom:12, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <button onClick={selectAll}
              style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'white', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
              {selected.size === actionableFines.length ? '☑ Deselect All' : `☐ Select All (${actionableFines.length})`}
            </button>
            <span style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.72rem' }}>{selected.size} selected</span>
            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
              <button onClick={() => bulkAction('cancel')} disabled={selected.size === 0 || bulkSaving}
                style={{ padding:'5px 12px', borderRadius:6, border:'none', background:'#fbbf24', color:'#1a1a1a', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', opacity: selected.size === 0 ? 0.5 : 1 }}>
                ✕ Cancel
              </button>
              <button onClick={() => bulkAction('delete')} disabled={selected.size === 0 || bulkSaving}
                style={{ padding:'5px 12px', borderRadius:6, border:'none', background:'#dc2626', color:'white', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', opacity: selected.size === 0 ? 0.5 : 1 }}>
                🗑 Delete
              </button>
            </div>
          </div>
        )}

        {showAdd && (
          <div className="card mb-md">
            <div className="card__body">
              <div className="form-group">
                <label className="form-label">Member *</label>
                <select className="form-control" value={form.member_id}
                  onChange={e => setForm(f => ({ ...f, member_id: e.target.value }))}>
                  <option value="">Select member…</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:4 }}>
                  {Object.entries(CAT_META).map(([v, meta]) => (
                    <button key={v} type="button"
                      onClick={() => setForm(f => ({ ...f, category: v, reason: '', custom_reason: '', custom_amount: '', penalty_pts: '' }))}
                      style={{ padding:'8px 4px', borderRadius:8, border:'2px solid ' + (form.category === v ? meta.color : '#e5e7eb'),
                        background: form.category === v ? meta.bg : 'white',
                        color: form.category === v ? meta.color : '#374151',
                        fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                      {meta.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:'0.7rem', color:'var(--ucc-gray-500)', marginBottom:4 }}>
                  {form.category === '1' && 'Fine: ₹1,000'}
                  {form.category === '2' && 'Fine: ₹3,000'}
                  {form.category === '3' && 'Fine: ₹6,000 + 2 match ban'}
                  {form.category === '4' && 'Amount set by late arrival duration'}
                  {form.category === '5' && 'Enter custom amount below'}
                  {form.category === '6' && 'No monetary fine — ranking points deducted at year end'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Reason *</label>
                <select className="form-control" value={form.reason}
                  onChange={e => setForm(f => ({ ...f, reason: e.target.value, custom_reason: '' }))}>
                  <option value="">Select reason…</option>
                  {(form.category === '5' ? allCustomReasons : CAT_PRESETS[form.category] || []).map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <input type="text" className="form-control" style={{ marginTop:6 }}
                  placeholder="Or type custom reason (auto-saved for future)…"
                  value={form.custom_reason}
                  onChange={e => setForm(f => ({ ...f, custom_reason: e.target.value, reason: '' }))} />
              </div>
              {form.category === '4' && form.reason && (
                <div style={{ background:'#cffafe', borderRadius:8, padding:'8px 12px', marginBottom:8, fontSize:'0.8rem', color:'#0891b2', fontWeight:700 }}>
                  Amount: ₹{(LATE_AMOUNTS[form.reason] || 0).toLocaleString('en-IN')}
                </div>
              )}
              {(form.category === '5' || (form.category === '4' && !LATE_AMOUNTS[form.reason])) && (
                <div className="form-group">
                  <label className="form-label">Amount (₹) *</label>
                  <input type="number" className="form-control" placeholder="Enter amount…"
                    value={form.custom_amount} inputMode="numeric"
                    onChange={e => setForm(f => ({ ...f, custom_amount: e.target.value }))} />
                </div>
              )}
              {form.category === '6' && (
                <div className="form-group">
                  <label className="form-label">Points to Deduct *</label>
                  <input type="number" className="form-control" placeholder="e.g. 20"
                    value={form.penalty_pts} inputMode="numeric"
                    onChange={e => setForm(f => ({ ...f, penalty_pts: e.target.value }))} />
                  <div style={{ fontSize:'0.7rem', color:'var(--ucc-gray-500)', marginTop:4 }}>
                    Applied at year end. Member can buy back at ₹500/point.
                  </div>
                </div>
              )}
              {form.member_id && (form.reason || form.custom_reason) && (
                <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', marginBottom:12, fontSize:'0.8rem', border:'1px solid #e5e7eb' }}>
                  <strong>{selectedMember?.display_name}</strong> · {CAT_META[form.category]?.label}<br/>
                  {form.category !== '6'
                    ? <span style={{ color:'#dc2626' }}>Fine: ₹{getAmount().toLocaleString('en-IN')}</span>
                    : <span style={{ color:'#16a34a' }}>Points deducted: {form.penalty_pts || 0}</span>}
                </div>
              )}
              <button className="btn btn-danger btn-block" onClick={issueFine} disabled={saving}>
                {saving ? 'Issuing…' : 'Issue Fine'}
              </button>
            </div>
          </div>
        )}

        <div className="card">
          {fines.length === 0 && <div className="empty-state"><div className="empty-state__title">No fines issued</div></div>}
          {fines.map(fine => {
            const isActionable = !['paid','waived','cancelled'].includes(fine.status);
            const isSelected = selected.has(fine.id);
            return (
            <div key={fine.id} style={{ borderBottom:'1px solid #f3f4f6', padding:'12px',
              background: isSelected ? '#eff6ff' : 'white', cursor: bulkMode && isActionable ? 'pointer' : 'default' }}
              onClick={() => bulkMode && isActionable && toggleSelect(fine.id)}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                {bulkMode && isActionable && (
                  <div style={{ width:20, height:20, borderRadius:4, border:'2px solid', flexShrink:0,
                    borderColor: isSelected ? '#1E3A6E' : '#d1d5db',
                    background: isSelected ? '#1E3A6E' : 'white',
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {isSelected && <span style={{ color:'white', fontSize:'0.7rem', fontWeight:900 }}>✓</span>}
                  </div>
                )}
                <div style={{ width:5, alignSelf:'stretch', borderRadius:3, flexShrink:0,
                  background: fine.category <= 1 ? '#d97706' : fine.category === 2 ? '#dc2626' : fine.category === 3 ? '#7c3aed' : fine.category === 6 ? '#16a34a' : '#0891b2' }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{fine.display_name}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--ucc-gray-500)' }}>{fine.reason}</div>
                  <div style={{ fontSize:'0.68rem', color:'var(--ucc-gray-400)' }}>
                    {new Date(fine.issued_at).toLocaleDateString('en-IN')}
                    {fine.penalty_pts ? ' · −' + fine.penalty_pts + ' pts' : ''}
                    {fine.issued_by_name ? ' · by ' + fine.issued_by_name : ''}
                    {!fine.announced_monday && !['paid','waived','cancelled'].includes(fine.status) && (
                      <span style={{ marginLeft:6, background:'#fef3c7', color:'#d97706',
                        padding:'1px 6px', borderRadius:10, fontSize:'0.6rem', fontWeight:700 }}>UNPUBLISHED</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  {fine.category !== 6 && (
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                      color: ['paid','waived','cancelled'].includes(fine.status) ? '#16a34a' : '#dc2626',
                      textDecoration: fine.status === 'cancelled' ? 'line-through' : 'none' }}>
                      ₹{parseFloat(fine.amount || 0).toLocaleString('en-IN')}
                    </div>
                  )}
                  <span style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase',
                    color: fine.status === 'cancelled' ? '#6b7280' : ['paid','waived'].includes(fine.status) ? '#16a34a' : '#dc2626' }}>
                    {fine.status}
                  </span>
                </div>
              </div>
              {!['paid','waived','cancelled'].includes(fine.status) && !bulkMode && (
                <div style={{ display:'flex', gap:6, marginTop:8, paddingLeft:15 }}>
                  <button onClick={() => openAction(fine, 'edit')}
                    style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #e5e7eb',
                      background:'white', color:'#374151', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => openAction(fine, 'cancel')}
                    style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #fbbf24',
                      background:'#fffbeb', color:'#d97706', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
                    ✕ Cancel
                  </button>
                  <button onClick={() => openAction(fine, 'delete')}
                    style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #fca5a5',
                      background:'#fff1f2', color:'#dc2626', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
                    🗑 Delete
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
        </>)}

        {view === 'pending' && (<>
          {pendingFines.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state__title">No pending fines</div>
                <div className="empty-state__subtitle">Late check-ins, no-reply fines, and admin-created fines will appear here for review before publishing.</div>
              </div>
            </div>
          ) : (<>
            {/* Select-all bar */}
            <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:10, padding:'10px 14px', marginBottom:12,
              display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <button onClick={selectAllPending}
                style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #d97706', background:'white',
                  color:'#d97706', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
                {pendingSelected.size === pendingFines.length && pendingFines.length > 0 ? '☑ Deselect All' : `☐ Select All (${pendingFines.length})`}
              </button>
              <span style={{ color:'#92400e', fontSize:'0.72rem', fontWeight:600 }}>{pendingSelected.size} selected</span>
              <div style={{ fontSize:'0.68rem', color:'#78350f', flexBasis:'100%', marginTop:4 }}>
                ℹ️ Members have been notified that a fine is under review. Amount stays hidden until you publish.
              </div>
            </div>

            {/* Pending list */}
            <div className="card" style={{ marginBottom: pendingSelected.size > 0 ? 120 : 16 }}>
              {pendingFines.map(fine => {
                const isSel = pendingSelected.has(fine.id);
                const draft = draftFor(fine);
                const triggerLabel =
                  fine.category === 4 ? '⏰ Late' :
                  fine.category === 1 && fine.reason && fine.reason.toLowerCase().includes('no availability') ? '🙋 No-Reply' :
                  fine.parent_fine_id ? '↻ Escalation' :
                  '✏️ Manual';
                const triggerBg =
                  fine.category === 4 ? '#cffafe' :
                  fine.category === 1 && fine.reason && fine.reason.toLowerCase().includes('no availability') ? '#e0e7ff' :
                  fine.parent_fine_id ? '#fee2e2' :
                  '#f3f4f6';
                const triggerColor =
                  fine.category === 4 ? '#0891b2' :
                  fine.category === 1 && fine.reason && fine.reason.toLowerCase().includes('no availability') ? '#4338ca' :
                  fine.parent_fine_id ? '#dc2626' :
                  '#374151';
                return (
                  <div key={fine.id} style={{ borderBottom:'1px solid #f3f4f6', padding:'12px',
                    background: isSel ? '#fef3c7' : 'white' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                      <div onClick={() => togglePendingSelect(fine.id)}
                        style={{ width:22, height:22, borderRadius:4, border:'2px solid', flexShrink:0, cursor:'pointer', marginTop:2,
                          borderColor: isSel ? '#d97706' : '#d1d5db',
                          background: isSel ? '#d97706' : 'white',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {isSel && <span style={{ color:'white', fontSize:'0.78rem', fontWeight:900 }}>✓</span>}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:'0.92rem', color:'#1E3A6E' }}>{fine.display_name}</div>
                        <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:3, flexWrap:'wrap' }}>
                          <span style={{ background: triggerBg, color: triggerColor, padding:'2px 8px', borderRadius:10, fontSize:'0.62rem', fontWeight:700 }}>
                            {triggerLabel}
                          </span>
                          <span style={{ fontSize:'0.68rem', color:'#6b7280' }}>
                            {new Date(fine.issued_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => deletePending(fine.id)}
                        style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #fca5a5',
                          background:'white', color:'#dc2626', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
                        🗑
                      </button>
                    </div>

                    {/* Editable fields — system suggestions pre-filled */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:6 }}>
                      <div>
                        <label style={{ fontSize:'0.65rem', fontWeight:700, color:'#6b7280', display:'block', marginBottom:2 }}>
                          Amount (₹)
                        </label>
                        <input type="number" className="form-control" inputMode="numeric"
                          style={{ fontSize:'0.85rem', padding:'6px 10px' }}
                          value={draft.amount}
                          onChange={e => setDraft(fine.id, { amount: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ fontSize:'0.65rem', fontWeight:700, color:'#6b7280', display:'block', marginBottom:2 }}>
                          Penalty Pts
                        </label>
                        <input type="number" className="form-control" inputMode="numeric"
                          style={{ fontSize:'0.85rem', padding:'6px 10px' }}
                          value={draft.penalty_pts}
                          onChange={e => setDraft(fine.id, { penalty_pts: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize:'0.65rem', fontWeight:700, color:'#6b7280', display:'block', marginBottom:2 }}>
                        Reason
                      </label>
                      <input type="text" className="form-control"
                        style={{ fontSize:'0.8rem', padding:'6px 10px' }}
                        value={draft.reason}
                        onChange={e => setDraft(fine.id, { reason: e.target.value })} />
                    </div>
                    {fine.system_suggested_amount !== null && fine.system_suggested_amount !== undefined && (
                      <div style={{ fontSize:'0.62rem', color:'#92400e', marginTop:4, fontStyle:'italic' }}>
                        System suggested: ₹{parseFloat(fine.system_suggested_amount).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sticky footer action bar — only when something is selected */}
            {pendingSelected.size > 0 && (
              <div style={{ position:'fixed', bottom:64, left:0, right:0, background:'white',
                borderTop:'2px solid #d97706', padding:'10px 14px', zIndex:150,
                boxShadow:'0 -4px 16px rgba(0,0,0,0.1)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:'0.75rem', fontWeight:700, color:'#92400e', marginRight:4 }}>
                  {pendingSelected.size} selected
                </span>
                <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap' }}>
                  <button onClick={() => publishPending('individual')} disabled={publishing}
                    style={{ padding:'8px 12px', borderRadius:8, border:'none',
                      background: publishing ? '#9ca3af' : '#1E3A6E', color:'white',
                      fontSize:'0.72rem', fontWeight:700, cursor: publishing ? 'wait' : 'pointer' }}>
                    📨 Publish Individual
                  </button>
                  <button onClick={() => publishPending('broadcast')} disabled={publishing}
                    style={{ padding:'8px 12px', borderRadius:8, border:'none',
                      background: publishing ? '#9ca3af' : '#16a34a', color:'white',
                      fontSize:'0.72rem', fontWeight:700, cursor: publishing ? 'wait' : 'pointer' }}>
                    📢 Publish Combined
                  </button>
                  <button onClick={bulkDeletePending} disabled={publishing}
                    style={{ padding:'8px 12px', borderRadius:8, border:'none',
                      background:'#dc2626', color:'white',
                      fontSize:'0.72rem', fontWeight:700, cursor: publishing ? 'wait' : 'pointer' }}>
                    🗑 Delete
                  </button>
                </div>
              </div>
            )}
          </>)}
        </>)}
      </div>

      {actionModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200,
          display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setActionModal(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%',
            maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            {/* Scrollable content */}
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
              <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:4 }}>
                {actionModal.type === 'edit' ? '✏️ Edit Fine' : actionModal.type === 'cancel' ? '✕ Cancel Fine' : '🗑 Delete Fine'}
              </div>
              <div style={{ fontSize:'0.85rem', color:'#6b7280', marginBottom:16 }}>
                {actionModal.fine.display_name} · ₹{parseFloat(actionModal.fine.amount||0).toLocaleString('en-IN')} · {actionModal.fine.reason}
              </div>
              {actionModal.type === 'edit' && (<>
                <div className="form-group">
                  <label className="form-label">Reason</label>
                  <input type="text" className="form-control" value={actionReason} onChange={e => setActionReason(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount (₹)</label>
                  <input type="number" className="form-control" value={actionAmount} onChange={e => setActionAmount(e.target.value)} inputMode="numeric" />
                </div>
                <div className="form-group">
                  <label className="form-label">Ranking penalty points</label>
                  <input type="number" min="0" className="form-control" value={actionPts} onChange={e => setActionPts(e.target.value)} inputMode="numeric" />
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:4 }}>
                    {(() => {
                      const was = parseInt(actionModal.fine.penalty_pts) || 0, now = parseInt(actionPts) || 0;
                      if (now === was) return `Currently ${was} point${was === 1 ? '' : 's'}. Rankings update as soon as you save.`;
                      return now > was
                        ? `${now - was} more point${now - was === 1 ? '' : 's'} will be deducted (${was} → ${now}). The member gets a notice.`
                        : `${was - now} point${was - now === 1 ? '' : 's'} will be given back (${was} → ${now}). The member gets a notice.`;
                    })()}
                  </div>
                </div>
              </>)}
              {actionModal.type === 'cancel' && (
                <div style={{ background:'#fffbeb', border:'1px solid #fbbf24', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.8rem', color:'#92400e' }}>
                  Fine will be marked CANCELLED but kept in records.
                </div>
              )}
              {actionModal.type === 'delete' && (
                <div style={{ background:'#fff1f2', border:'1px solid #fca5a5', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.8rem', color:'#991b1b' }}>
                  Fine will be permanently deleted. Logged in audit trail.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">
                  {actionModal.type === 'edit' ? 'Reason for edit *' : actionModal.type === 'cancel' ? 'Reason for cancellation *' : 'Reason for deletion *'}
                </label>
                <textarea className="form-control" rows={2}
                  placeholder="Required — logged against your name…"
                  value={actionNote} onChange={e => setActionNote(e.target.value)} />
              </div>
            </div>
            {/* Buttons always visible */}
            <div style={{ padding:'12px 20px', paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', background:'white', display:'flex', gap:8, flexShrink:0 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setActionModal(null)}>Back</button>
              <button onClick={submitAction} disabled={actionSaving}
                style={{ flex:2, padding:'12px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:'0.9rem',
                  background: actionModal.type === 'delete' ? '#dc2626' : actionModal.type === 'cancel' ? '#d97706' : '#1E3A6E',
                  color:'white' }}>
                {actionSaving ? 'Saving…' : actionModal.type === 'edit' ? 'Save Changes' : actionModal.type === 'cancel' ? 'Cancel Fine' : 'Delete Fine'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AdminPayments ─────────────────────────────────────────
export function AdminPayments() {
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [reviewItems, setReviewItems] = useState([]); // Phase 1 (c.3): submissions in 'review' status
  const [members, setMembers] = useState([]);
  const [fees, setFees] = useState([]);
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvContent, setCsvContent] = useState('');
  const [fileB64, setFileB64] = useState('');     // XLSX statement, base64
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [editingUtr, setEditingUtr] = useState(null); // { id, value }
  const [savingUtr, setSavingUtr] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [selectedBank, setSelectedBank] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [manualSubmit, setManualSubmit] = useState(false);
  const [manualForm, setManualForm] = useState({
    utr:'', amount:'', beneficiary_member_id:'', allocation_type:'fees',
    fee_id:'', fine_id:'', is_partial:false, allocation_note:''
  });
  const [submitting, setSubmitting] = useState(false);

  // Phase 1 (c.3): edit-beneficiary modal state
  const [beneficiaryModal, setBeneficiaryModal] = useState(null); // { submission, selectedMemberId }
  const [savingBeneficiary, setSavingBeneficiary] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [pRes, uRes, mRes] = await Promise.all([
        api.get('/payments/pending'),
        api.get('/payments/unmatched'),
        api.get('/members'),
      ]);
      const allFromPending = pRes.data.payments || [];
      // Server's /pending endpoint already returns pending + matched. Phase 1 b.1
      // added the 'review' status which we want to surface separately so admins
      // see mismatches on their own tab. Filter by status here.
      setPending(allFromPending.filter(p => p.status === 'pending' || p.status === 'matched'));
      setReviewItems(allFromPending.filter(p => p.status === 'review'));
      setUnmatched(uRes.data.unmatched || []);
      setMembers((mRes.data.members || []).filter(m => !m.is_observer));
    } finally { setLoading(false); }
  }

  // Phase 1 (c.3): set the beneficiary on a pending/matched/review submission.
  // Used to fix attribution before the next bank statement uploads (e.g. when
  // Bhupinder paid for himself + Babar + Ankit and only the submitter UTR was
  // recorded — admin re-tags the splits with their real beneficiaries).
  async function saveBeneficiary() {
    if (!beneficiaryModal) return;
    const sub = beneficiaryModal.submission;
    const newBeneficiary = beneficiaryModal.selectedMemberId || null;
    // No-op: same as current
    const current = sub.beneficiary_member_id || null;
    if (newBeneficiary === current) {
      setBeneficiaryModal(null);
      return;
    }
    setSavingBeneficiary(true);
    try {
      await api.patch('/payments/submission/' + sub.id + '/beneficiary',
        { beneficiary_member_id: newBeneficiary });
      setBeneficiaryModal(null);
      loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update beneficiary');
    } finally {
      setSavingBeneficiary(false);
    }
  }

  async function loadMemberDues(memberId) {
    if (!memberId) { setFees([]); setFines([]); return; }
    try {
      const [feeRes, fineRes] = await Promise.all([
        api.get('/fees/member/' + memberId),
        api.get('/fines?member_id=' + memberId),
      ]);
      setFees((feeRes.data.fees || []).filter(f => !['paid','waived'].includes(f.status)));
      setFines((fineRes.data.fines || []).filter(f => !['paid','waived','cancelled'].includes(f.status)));
    } catch { setFees([]); setFines([]); }
  }

  async function confirmPayment(p) {
    // Check for duplicate UTR first
    const duplicates = pending.filter(x => x.utr === p.utr && x.id !== p.id);
    if (duplicates.length > 0) {
      setDuplicateModal({ payment: p, duplicates });
      return;
    }
    doConfirm(p.id);
  }

  async function doConfirm(id) {
    setConfirming(id);
    setDuplicateModal(null);
    try {
      const res = await api.post('/payments/confirm/' + id);
      alert('✅ Confirmed! ₹' + (res.data.applied || 0).toLocaleString('en-IN') + ' applied to dues.' +
        (res.data.remaining > 0 ? ' ₹' + res.data.remaining.toLocaleString('en-IN') + ' credit remaining.' : ''));
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to confirm'); }
    finally { setConfirming(null); }
  }

  async function saveUtrEdit(id, newUtr) {
    if (!newUtr.trim()) return;
    setSavingUtr(id);
    try {
      await api.patch('/payments/submission/' + id, { utr: newUtr.trim().toUpperCase() });
      setEditingUtr(null);
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update UTR'); }
    finally { setSavingUtr(null); }
  }

  function toggleSub(id) {
    setSelectedSubs(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }
  function toggleBank(id) {
    setSelectedBank(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }
  function selectAllSubs() {
    setSelectedSubs(prev => prev.size === pending.length ? new Set() : new Set(pending.map(p => p.id)));
  }
  function selectAllBank() {
    setSelectedBank(prev => prev.size === unmatched.length ? new Set() : new Set(unmatched.map(u => u.id)));
  }

  async function bulkDeleteSubs() {
    if (selectedSubs.size === 0) return;
    if (!window.confirm(`Delete ${selectedSubs.size} selected submission(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedSubs].map(id => api.delete('/payments/submission/' + id)));
      setSelectedSubs(new Set());
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
    finally { setBulkDeleting(false); }
  }

  async function bulkDeleteBank() {
    if (selectedBank.size === 0) return;
    if (!window.confirm(`Delete ${selectedBank.size} selected bank entr${selectedBank.size > 1 ? 'ies' : 'y'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedBank].map(id => api.delete('/payments/bank-entry/' + id)));
      setSelectedBank(new Set());
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
    finally { setBulkDeleting(false); }
  }

  async function deleteSubmission(id) {
    if (!window.confirm('Delete this payment submission? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete('/payments/submission/' + id);
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
    finally { setDeleting(null); }
  }

  async function deleteBankEntry(id) {
    if (!window.confirm('Delete this bank entry? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete('/payments/bank-entry/' + id);
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
    finally { setDeleting(null); }
  }

  async function submitManualPayment() {
    if (!manualForm.utr.trim()) return alert('UTR/Reference number required');
    if (!manualForm.amount || parseFloat(manualForm.amount) <= 0) return alert('Amount required');
    if (!manualForm.beneficiary_member_id) return alert('Select the member this payment is for');
    setSubmitting(true);
    try {
      await api.post('/payments/submit', {
        utr: manualForm.utr.trim().toUpperCase(),
        amount: parseFloat(manualForm.amount),
        beneficiary_member_id: manualForm.beneficiary_member_id,
        allocation_type: manualForm.allocation_type,
        fee_id: manualForm.fee_id || null,
        fine_id: manualForm.fine_id || null,
        is_partial: manualForm.is_partial,
        allocation_note: manualForm.allocation_note,
      });
      alert('Payment submitted successfully');
      setManualSubmit(false);
      setManualForm({ utr:'', amount:'', beneficiary_member_id:'', allocation_type:'fees', fee_id:'', fine_id:'', is_partial:false, allocation_note:'' });
      loadAll();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed';
      if (errMsg.includes('already been submitted')) {
        if (window.confirm('⚠️ This UTR was already submitted. This could be a duplicate payment. Submit anyway?')) {
          // Force submit - admin override
          alert('Contact developer to enable force-submit for duplicate UTRs.');
        }
      } else {
        alert(errMsg);
      }
    }
    finally { setSubmitting(false); }
  }

  async function uploadStatement() {
    if (!fileB64 && !csvContent.trim()) return alert('Choose the statement file first');
    setUploading(true);
    setUploadResult(null);
    try {
      const payload = fileB64 ? { file_base64: fileB64 } : { csv_content: csvContent };
      const res = await api.post('/payments/bank-statement', payload);
      setUploadResult(res.data.results);
      setCsvContent(''); setFileB64(''); setFileName('');
      loadAll();
    } catch (err) { alert(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); }
  }

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const TAB_STYLE = (active) => ({
    flex:1, padding:'10px 0', border:'none',
    borderBottom: active ? '3px solid #1E3A6E' : '3px solid transparent',
    background:'white', color: active ? '#1E3A6E' : '#6b7280',
    fontWeight: active ? 700 : 500, fontSize:'0.78rem', cursor:'pointer',
    fontFamily:'Rajdhani,sans-serif', letterSpacing:'0.05em', textTransform:'uppercase',
  });

  const selectedMember = members.find(m => m.id === manualForm.beneficiary_member_id);

  return (
    <div>
      <AdminPageHeader title="Payment Reconciliation" />

      <div style={{ display:'flex', background:'white', borderBottom:'1px solid #e5e7eb' }}>
        <button style={TAB_STYLE(tab==='pending')} onClick={() => setTab('pending')}>
          Pending ({pending.length})
        </button>
        <button style={TAB_STYLE(tab==='review')} onClick={() => setTab('review')}>
          Review ({reviewItems.length})
        </button>
        <button style={TAB_STYLE(tab==='upload')} onClick={() => setTab('upload')}>
          Upload Statement
        </button>
        <button style={TAB_STYLE(tab==='unmatched')} onClick={() => setTab('unmatched')}>
          Unmatched ({unmatched.length})
        </button>
      </div>

      <div style={{ padding:'16px' }}>

        {/* ── PENDING TAB ── */}
        {tab === 'pending' && (<>
          {selectedSubs.size > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'8px 12px' }}>
              <span style={{ fontSize:'0.8rem', color:'#dc2626', fontWeight:600 }}>{selectedSubs.size} selected</span>
              <button onClick={bulkDeleteSubs} disabled={bulkDeleting}
                style={{ padding:'4px 14px', borderRadius:6, border:'none', background:'#dc2626', color:'white', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>
                {bulkDeleting ? 'Deleting…' : '🗑️ Delete Selected'}
              </button>
              <button onClick={() => setSelectedSubs(new Set())}
                style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #d1d5db', background:'white', color:'#374151', fontSize:'0.75rem', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', fontSize:'0.72rem', color:'#6b7280', userSelect:'none' }}>
                <input type="checkbox" checked={pending.length > 0 && selectedSubs.size === pending.length}
                  onChange={selectAllSubs} style={{ cursor:'pointer' }} />
                Select all
              </label>
              <span style={{ color:'#d1d5db' }}>·</span>
              <span style={{ fontSize:'0.72rem', color:'#6b7280' }}>MATCHED = bank entry found · PENDING = awaiting bank upload</span>
            </div>
            <button onClick={() => setManualSubmit(!manualSubmit)}
              style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #1E3A6E',
                background: manualSubmit ? '#1E3A6E' : 'white',
                color: manualSubmit ? 'white' : '#1E3A6E',
                fontWeight:700, fontSize:'0.75rem', cursor:'pointer', flexShrink:0 }}>
              + Record Payment
            </button>
          </div>

          {/* Manual payment entry */}
          {manualSubmit && (
            <div className="card mb-md">
              <div className="card__body">
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1rem', marginBottom:12, color:'#1E3A6E' }}>
                  📝 Record a Payment
                </div>

                <div className="form-group">
                  <label className="form-label">Payment by (member submitting) *</label>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginBottom:4 }}>
                    Can be any member — payment may be on behalf of someone else
                  </div>
                  <select className="form-control" value={manualForm.beneficiary_member_id}
                    onChange={e => {
                      setManualForm(f => ({ ...f, beneficiary_member_id: e.target.value, fee_id:'', fine_id:'' }));
                      loadMemberDues(e.target.value);
                    }}>
                    <option value="">Select member…</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
                  </select>
                </div>

                <div style={{ display:'flex', gap:8 }}>
                  <div className="form-group" style={{ flex:2 }}>
                    <label className="form-label">UTR / Reference No *</label>
                    <input type="text" className="form-control" placeholder="e.g. ICICI2603458901"
                      value={manualForm.utr} onChange={e => setManualForm(f => ({ ...f, utr: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ flex:1 }}>
                    <label className="form-label">Amount (₹) *</label>
                    <input type="number" className="form-control" placeholder="0"
                      value={manualForm.amount} inputMode="numeric"
                      onChange={e => setManualForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Apply towards</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:8 }}>
                    {[
                      { v:'fees', l:'Fees' },
                      { v:'fines', l:'Fine' },
                      { v:'custom', l:'Custom' },
                    ].map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => setManualForm(f => ({ ...f, allocation_type: opt.v, fee_id:'', fine_id:'' }))}
                        style={{ padding:'8px 4px', borderRadius:8, border:'2px solid',
                          borderColor: manualForm.allocation_type === opt.v ? '#1E3A6E' : '#e5e7eb',
                          background: manualForm.allocation_type === opt.v ? '#eff6ff' : 'white',
                          color: manualForm.allocation_type === opt.v ? '#1E3A6E' : '#374151',
                          fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                        {opt.l}
                      </button>
                    ))}
                  </div>

                  {/* Specific fee selection */}
                  {manualForm.allocation_type === 'fees' && fees.length > 0 && (
                    <select className="form-control" value={manualForm.fee_id}
                      onChange={e => setManualForm(f => ({ ...f, fee_id: e.target.value }))}>
                      <option value="">Auto (oldest first)</option>
                      {fees.map(f => (
                        <option key={f.id} value={f.id}>
                          {MONTHS[f.month-1]} {f.year} — ₹{parseFloat(f.balance||f.amount_due||0).toLocaleString('en-IN')} due ({f.status})
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Specific fine selection */}
                  {manualForm.allocation_type === 'fines' && fines.length > 0 && (
                    <select className="form-control" value={manualForm.fine_id}
                      onChange={e => setManualForm(f => ({ ...f, fine_id: e.target.value }))}>
                      <option value="">Auto (oldest first)</option>
                      {fines.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.reason} — ₹{parseFloat(f.amount||0).toLocaleString('en-IN')} ({new Date(f.issued_at).toLocaleDateString('en-IN')})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Full or Partial?</label>
                  <div style={{ display:'flex', gap:8 }}>
                    {[{v:false,l:'Full Payment'},{v:true,l:'Partial Payment'}].map(opt => (
                      <button key={String(opt.v)} type="button"
                        onClick={() => setManualForm(f => ({ ...f, is_partial: opt.v }))}
                        style={{ flex:1, padding:'8px', borderRadius:8, border:'2px solid',
                          borderColor: manualForm.is_partial === opt.v ? '#1E3A6E' : '#e5e7eb',
                          background: manualForm.is_partial === opt.v ? '#eff6ff' : 'white',
                          color: manualForm.is_partial === opt.v ? '#1E3A6E' : '#374151',
                          fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Note (optional)</label>
                  <input type="text" className="form-control" placeholder="e.g. Cash collected at ground"
                    value={manualForm.allocation_note}
                    onChange={e => setManualForm(f => ({ ...f, allocation_note: e.target.value }))} />
                </div>

                {/* Summary */}
                {manualForm.utr && manualForm.amount && manualForm.beneficiary_member_id && (
                  <div style={{ background:'#f0fdf4', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.8rem', border:'1px solid #bbf7d0' }}>
                    <strong>{selectedMember?.display_name}</strong> · ₹{parseFloat(manualForm.amount||0).toLocaleString('en-IN')}
                    {' '}{manualForm.is_partial ? '(partial)' : '(full)'}
                    {' → '}{manualForm.allocation_type}
                    {manualForm.utr ? ' · UTR: ' + manualForm.utr : ''}
                  </div>
                )}

                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setManualSubmit(false)}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex:2 }} onClick={submitManualPayment} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
            <div className="card">
              {pending.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state__icon">✅</div>
                  <div className="empty-state__title">No pending payments</div>
                </div>
              )}
              {pending.map(p => {
                const isDuplicate = pending.filter(x => x.utr === p.utr).length > 1;
                return (
                  <div key={p.id} style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6',
                    background: isDuplicate ? '#fffbeb' : 'white' }}>
                    {isDuplicate && (
                      <div style={{ background:'#fef3c7', borderRadius:6, padding:'4px 8px', marginBottom:8, fontSize:'0.72rem', color:'#d97706', fontWeight:700 }}>
                        ⚠️ DUPLICATE UTR — multiple submissions with same reference number
                      </div>
                    )}
                    <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                      <input type="checkbox" checked={selectedSubs.has(p.id)} onChange={() => toggleSub(p.id)}
                        style={{ marginTop:4, cursor:'pointer', flexShrink:0, width:16, height:16 }} />
                      <div className="avatar" style={{ flexShrink:0 }}>{p.display_name?.charAt(0)}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:'0.9rem' }}>
                          {p.display_name}
                          <span style={{ fontSize:'0.72rem', color:'#6b7280', fontWeight:400, marginLeft:6 }}>({p.ucc_id})</span>
                        </div>
                        {p.beneficiary_name && p.beneficiary_name !== p.display_name && (
                          <div style={{ fontSize:'0.72rem', color:'#1d4ed8', fontWeight:600, marginTop:1, display:'flex', alignItems:'center', gap:6 }}>
                            🎯 For: {p.beneficiary_name} ({p.beneficiary_ucc_id})
                            <button onClick={() => setBeneficiaryModal({ submission: p, selectedMemberId: p.beneficiary_member_id || '' })}
                              title="Edit beneficiary"
                              style={{ fontSize:'0.6rem', padding:'1px 6px', borderRadius:4, border:'1px solid #bfdbfe', background:'white', color:'#1d4ed8', cursor:'pointer', fontWeight:600 }}>
                              ✏️ edit
                            </button>
                          </div>
                        )}
                        {!p.beneficiary_name || p.beneficiary_name === p.display_name ? (
                          <div style={{ fontSize:'0.7rem', color:'#9ca3af', marginTop:2 }}>
                            <button onClick={() => setBeneficiaryModal({ submission: p, selectedMemberId: p.beneficiary_member_id || '' })}
                              style={{ background:'none', border:'none', color:'#6b7280', fontSize:'0.7rem', cursor:'pointer', padding:0, textDecoration:'underline' }}>
                              Set beneficiary (paying for someone else?)
                            </button>
                          </div>
                        ) : null}
                        <div style={{ fontSize:'0.75rem', color:'#6b7280', marginTop:2, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                          {editingUtr?.id === p.id ? (
                            <>
                              <input
                                autoFocus
                                value={editingUtr.value}
                                onChange={e => setEditingUtr({ id: p.id, value: e.target.value.toUpperCase().replace(/\s/g,'') })}
                                onKeyDown={e => { if (e.key==='Enter') saveUtrEdit(p.id, editingUtr.value); if (e.key==='Escape') setEditingUtr(null); }}
                                style={{ fontFamily:'monospace', fontSize:'0.78rem', padding:'2px 6px', borderRadius:5, border:'1.5px solid #3b82f6', width:220, background:'#eff6ff' }}
                              />
                              <button onClick={() => saveUtrEdit(p.id, editingUtr.value)} disabled={savingUtr===p.id}
                                style={{ fontSize:'0.7rem', padding:'2px 8px', borderRadius:5, border:'none', background:'#16a34a', color:'white', cursor:'pointer', fontWeight:700 }}>
                                {savingUtr===p.id ? '…' : 'Save'}
                              </button>
                              <button onClick={() => setEditingUtr(null)}
                                style={{ fontSize:'0.7rem', padding:'2px 6px', borderRadius:5, border:'none', background:'#e5e7eb', color:'#374151', cursor:'pointer' }}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              UTR: <strong style={{ fontFamily:'monospace' }}>{p.utr}</strong>
                              {p.status !== 'confirmed' && (
                                <button onClick={() => setEditingUtr({ id: p.id, value: p.utr })}
                                  style={{ fontSize:'0.65rem', padding:'1px 6px', borderRadius:4, border:'1px solid #d1d5db', background:'white', color:'#6b7280', cursor:'pointer' }}>
                                  ✏️ Edit UTR
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>
                          {new Date(p.submitted_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                          {p.transaction_date && ' · Bank: ' + new Date(p.transaction_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                        </div>
                        {p.allocation_type && (
                          <div style={{ fontSize:'0.7rem', color:'#6b7280' }}>
                            Towards: {p.allocation_type}{p.is_partial ? ' (partial)' : ''}
                            {p.allocation_note ? ' · ' + p.allocation_note : ''}
                          </div>
                        )}
                        {p.sender_name && <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>Bank: {p.sender_name}</div>}
                        <span style={{ display:'inline-block', marginTop:4, fontSize:'0.6rem', fontWeight:700,
                          textTransform:'uppercase', padding:'2px 8px', borderRadius:10,
                          background: p.status==='matched' ? '#dcfce7' : '#fef3c7',
                          color: p.status==='matched' ? '#16a34a' : '#d97706' }}>
                          {p.status==='matched' ? '✅ MATCHED' : '⏳ PENDING MATCH'}
                        </span>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.2rem', color:'#1E3A6E' }}>
                          ₹{parseFloat(p.amount).toLocaleString('en-IN')}
                        </div>
                        <button onClick={() => deleteSubmission(p.id)} disabled={deleting===p.id}
                          title="Delete this submission"
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', fontSize:'1rem', padding:2, opacity: deleting===p.id ? 0.4 : 0.6, lineHeight:1 }}>
                          🗑️
                        </button>
                      </div>
                    </div>
                    {p.status === 'matched' && (
                      <button onClick={() => confirmPayment(p)} disabled={confirming === p.id}
                        style={{ width:'100%', padding:'10px', borderRadius:8, border:'none',
                          background: isDuplicate ? '#d97706' : '#16a34a',
                          color:'white', fontWeight:700, fontSize:'0.85rem', cursor:'pointer',
                          fontFamily:'Rajdhani,sans-serif' }}>
                        {confirming === p.id ? 'Confirming…' : isDuplicate ? '⚠️ Confirm (Duplicate UTR)' : '✅ Confirm & Apply to Dues'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>)}

        {/* ── REVIEW TAB ── (Phase 1 c.3) submissions held for admin review */}
        {tab === 'review' && (<>
          <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10, padding:'10px 12px', marginBottom:14, fontSize:'0.78rem', color:'#92400e' }}>
            ⚠️ These submissions have a mismatch between the bank entry and what was reported. Each shows the reason. Use Edit UTR / Edit Beneficiary to correct, then run Confirm — or delete the submission and ask the member to resubmit.
          </div>
          <div style={{ background:'white', borderRadius:10, border:'1px solid #e5e7eb' }}>
            {reviewItems.length === 0 && (
              <div className="empty-state" style={{ padding:'30px 14px' }}>
                <div className="empty-state__icon">✅</div>
                <div className="empty-state__title">No submissions need review</div>
                <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginTop:4 }}>
                  Anything that bounces from auto-confirm with a mismatch will show up here.
                </div>
              </div>
            )}
            {reviewItems.map(p => (
              <div key={p.id} style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6', background:'#fffbeb' }}>
                {p.review_reason && (
                  <div style={{ background:'#fef3c7', borderRadius:6, padding:'4px 8px', marginBottom:8, fontSize:'0.72rem', color:'#92400e', fontWeight:700 }}>
                    {p.review_reason}
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                  <div className="avatar" style={{ flexShrink:0 }}>{p.display_name?.charAt(0)}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:'0.9rem' }}>
                      {p.display_name}
                      <span style={{ fontSize:'0.72rem', color:'#6b7280', fontWeight:400, marginLeft:6 }}>({p.ucc_id})</span>
                    </div>
                    {p.beneficiary_name && p.beneficiary_name !== p.display_name && (
                      <div style={{ fontSize:'0.72rem', color:'#1d4ed8', fontWeight:600, marginTop:1, display:'flex', alignItems:'center', gap:6 }}>
                        🎯 For: {p.beneficiary_name} ({p.beneficiary_ucc_id})
                        <button onClick={() => setBeneficiaryModal({ submission: p, selectedMemberId: p.beneficiary_member_id || '' })}
                          style={{ fontSize:'0.6rem', padding:'1px 6px', borderRadius:4, border:'1px solid #bfdbfe', background:'white', color:'#1d4ed8', cursor:'pointer', fontWeight:600 }}>
                          ✏️ edit
                        </button>
                      </div>
                    )}
                    <div style={{ fontSize:'0.75rem', color:'#6b7280', marginTop:2 }}>
                      UTR: <strong style={{ fontFamily:'monospace' }}>{p.utr}</strong>
                    </div>
                    <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>
                      Submitted {new Date(p.submitted_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                      {p.transaction_date && ' · Bank: ' + new Date(p.transaction_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                    </div>
                    {p.allocation_type && (
                      <div style={{ fontSize:'0.7rem', color:'#6b7280' }}>
                        Towards: {p.allocation_type}
                        {p.allocation_note ? ' · ' + p.allocation_note : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.2rem', color:'#1E3A6E' }}>
                      ₹{parseFloat(p.amount).toLocaleString('en-IN')}
                    </div>
                    <button onClick={() => deleteSubmission(p.id)} disabled={deleting===p.id}
                      title="Delete this submission"
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', fontSize:'1rem', padding:2, opacity: deleting===p.id ? 0.4 : 0.6, lineHeight:1 }}>
                      🗑️
                    </button>
                  </div>
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => confirmPayment(p)} disabled={confirming === p.id}
                    style={{ flex:1, padding:'10px', borderRadius:8, border:'none', background:'#16a34a',
                      color:'white', fontWeight:700, fontSize:'0.85rem', cursor:'pointer', fontFamily:'Rajdhani,sans-serif' }}>
                    {confirming === p.id ? 'Confirming…' : '✅ Confirm Anyway'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>)}


        {/* ── UPLOAD CSV TAB ── */}
        {tab === 'upload' && (<>
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:12, marginBottom:16, fontSize:'0.8rem', color:'#1d4ed8' }}>
            <strong>How to export from IndusInd:</strong><br/>
            Net Banking → Accounts → Statement → Select dates → <strong>Download XLSX</strong> → Upload below
          </div>
          <div className="form-group">
            <label className="form-label">Upload Bank Statement (XLSX)</label>
            <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              border:'2px dashed #93c5fd', borderRadius:10, padding:'24px 16px', cursor:'pointer',
              background: (fileB64 || csvContent) ? '#f0fdf4' : '#f8fafc', gap:8 }}>
              <span style={{ fontSize:'2rem' }}>{(fileB64 || csvContent) ? '✅' : '📄'}</span>
              <span style={{ fontWeight:600, fontSize:'0.85rem', color: (fileB64 || csvContent) ? '#16a34a' : '#1d4ed8' }}>
                {fileB64 ? `${fileName} — ready to upload`
                  : csvContent ? 'CSV file loaded — ready to upload'
                  : 'Tap to choose statement file'}
              </span>
              {!(fileB64 || csvContent) && <span style={{ fontSize:'0.72rem', color:'#6b7280' }}>IndusInd Account Statement (.xlsx or .csv)</span>}
              <input type="file" accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display:'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const isExcel = /\.xlsx?$/i.test(file.name);
                  const reader = new FileReader();
                  if (isExcel) {
                    reader.onload = ev => {
                      // ev.target.result is a data URL: strip the "data:...;base64," prefix
                      const b64 = String(ev.target.result).split(',')[1] || '';
                      setFileB64(b64); setFileName(file.name); setCsvContent('');
                    };
                    reader.readAsDataURL(file);
                  } else {
                    reader.onload = ev => { setCsvContent(ev.target.result); setFileB64(''); setFileName(''); };
                    reader.readAsText(file);
                  }
                }} />
            </label>
            {(fileB64 || csvContent) && (
              <button type="button" onClick={() => { setCsvContent(''); setFileB64(''); setFileName(''); }}
                style={{ marginTop:6, fontSize:'0.72rem', color:'#dc2626', background:'none', border:'none', cursor:'pointer' }}>
                ✕ Clear file
              </button>
            )}
          </div>
          <button className="btn btn-primary btn-block" onClick={uploadStatement}
            disabled={uploading || !(fileB64 || csvContent.trim())}>
            {uploading ? 'Processing…' : '⬆️ Upload & Auto-Match'}
          </button>
          {uploadResult && (
            <div style={{ marginTop:16, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:14 }}>
              <div style={{ fontWeight:700, color:'#16a34a', marginBottom:8 }}>✅ Upload Complete</div>
              <div style={{ fontSize:'0.82rem', color:'#374151', display:'flex', flexDirection:'column', gap:4 }}>
                <div>📥 Imported: <strong>{uploadResult.imported}</strong> transactions</div>
                <div>🔗 Auto-matched: <strong>{uploadResult.matched}</strong> payments</div>
                {uploadResult.already_exists > 0 && <div>⏭ Already existed: <strong>{uploadResult.already_exists}</strong></div>}
                {uploadResult.errors?.length > 0 && (
                  <div style={{ color:'#dc2626' }}>
                    ⚠️ Errors: {uploadResult.errors.length}
                    <div style={{ fontSize:'0.72rem', marginTop:4, maxHeight:120, overflowY:'auto', background:'#fef2f2', borderRadius:6, padding:'4px 8px' }}>
                      {uploadResult.errors.map((e, i) => (
                        <div key={i}>{e.utr || 'row'}: {e.error}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginTop:8, fontSize:'0.75rem', color:'#6b7280' }}>
                Go to Pending tab to confirm matched payments.
              </div>
            </div>
          )}
        </>)}

        {/* ── UNMATCHED TAB ── */}
        {tab === 'unmatched' && (<>
          {selectedBank.size > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'8px 12px' }}>
              <span style={{ fontSize:'0.8rem', color:'#dc2626', fontWeight:600 }}>{selectedBank.size} selected</span>
              <button onClick={bulkDeleteBank} disabled={bulkDeleting}
                style={{ padding:'4px 14px', borderRadius:6, border:'none', background:'#dc2626', color:'white', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>
                {bulkDeleting ? 'Deleting…' : '🗑️ Delete Selected'}
              </button>
              <button onClick={() => setSelectedBank(new Set())}
                style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #d1d5db', background:'white', color:'#374151', fontSize:'0.75rem', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', fontSize:'0.72rem', color:'#6b7280', userSelect:'none' }}>
              <input type="checkbox" checked={unmatched.length > 0 && selectedBank.size === unmatched.length}
                onChange={selectAllBank} style={{ cursor:'pointer' }} />
              Select all
            </label>
            <span style={{ color:'#d1d5db' }}>·</span>
            <span style={{ fontSize:'0.72rem', color:'#6b7280' }}>Bank entries with no member claim. Ask member to submit UTR in the app.</span>
          </div>
          {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
            <div className="card">
              {unmatched.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state__icon">✅</div>
                  <div className="empty-state__title">No unmatched entries</div>
                </div>
              )}
              {unmatched.map(u => (
                <div key={u.id} style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6', background: selectedBank.has(u.id) ? '#fef2f2' : 'white' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'flex-start' }}>
                    <input type="checkbox" checked={selectedBank.has(u.id)} onChange={() => toggleBank(u.id)}
                      style={{ marginTop:3, cursor:'pointer', flexShrink:0, width:16, height:16 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:'0.88rem', fontFamily:'monospace' }}>{u.utr}</div>
                      {u.sender_name && <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>Sender: {u.sender_name}</div>}
                      <div style={{ fontSize:'0.72rem', color:'#9ca3af' }}>
                        {u.transaction_date ? new Date(u.transaction_date).toLocaleDateString('en-IN') : 'No date'}
                        {u.description ? ' · ' + u.description.substring(0,50) : ''}
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:'#1E3A6E' }}>
                        ₹{parseFloat(u.amount).toLocaleString('en-IN')}
                      </div>
                      <span style={{ fontSize:'0.6rem', fontWeight:700, background:'#fef3c7', color:'#d97706', padding:'1px 6px', borderRadius:10 }}>UNMATCHED</span>
                      <button onClick={() => deleteBankEntry(u.id)} disabled={deleting===u.id}
                        title="Delete this bank entry"
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', fontSize:'0.9rem', padding:0, opacity: deleting===u.id ? 0.4 : 0.6, lineHeight:1 }}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>)}
      </div>

      {/* Duplicate UTR Warning Modal */}
      {duplicateModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setDuplicateModal(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', padding:'20px',
            paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))', width:'100%' }}>
            <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', color:'#d97706', marginBottom:8 }}>
              ⚠️ Duplicate UTR Detected
            </div>
            <div style={{ fontSize:'0.85rem', color:'#374151', marginBottom:12 }}>
              UTR <strong style={{ fontFamily:'monospace' }}>{duplicateModal.payment.utr}</strong> has been submitted by multiple members:
            </div>
            <div style={{ background:'#fef3c7', borderRadius:8, padding:10, marginBottom:16, fontSize:'0.82rem' }}>
              {[duplicateModal.payment, ...duplicateModal.duplicates].map(p => (
                <div key={p.id} style={{ padding:'4px 0', borderBottom:'1px solid #fcd34d' }}>
                  <strong>{p.display_name}</strong> ({p.ucc_id}) · ₹{parseFloat(p.amount).toLocaleString('en-IN')}
                  {' · '}{new Date(p.submitted_at).toLocaleDateString('en-IN')}
                  {p.id === duplicateModal.payment.id && <span style={{ marginLeft:6, fontSize:'0.65rem', background:'#1E3A6E', color:'white', padding:'1px 6px', borderRadius:10 }}>CONFIRMING THIS</span>}
                </div>
              ))}
            </div>
            <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:16 }}>
              Only ONE payment can be real. Confirm this one only if you have verified it with the bank statement. The other submission(s) should be deleted.
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setDuplicateModal(null)}>Cancel</button>
              <button onClick={() => doConfirm(duplicateModal.payment.id)}
                style={{ flex:2, padding:'12px', borderRadius:10, border:'none', background:'#d97706',
                  color:'white', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
                Confirm Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1 (c.3): Edit-beneficiary modal */}
      {beneficiaryModal && (
        <div onClick={() => !savingBeneficiary && setBeneficiaryModal(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex',
            alignItems:'center', justifyContent:'center', padding:16, zIndex:60 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'white', borderRadius:12, padding:20, width:'100%', maxWidth:440,
              maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E', marginBottom:6 }}>
              Edit Beneficiary
            </div>
            <div style={{ fontSize:'0.78rem', color:'#6b7280', marginBottom:14 }}>
              Submitted by <strong>{beneficiaryModal.submission.display_name}</strong> · UTR <code style={{ fontFamily:'monospace' }}>{beneficiaryModal.submission.utr}</code> · ₹{parseFloat(beneficiaryModal.submission.amount).toLocaleString('en-IN')}
            </div>

            <div className="form-group">
              <label className="form-label">Beneficiary (who is the payment for?)</label>
              <select className="form-control" value={beneficiaryModal.selectedMemberId}
                onChange={e => setBeneficiaryModal({ ...beneficiaryModal, selectedMemberId: e.target.value })}>
                <option value="">— Same as submitter ({beneficiaryModal.submission.display_name}) —</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>
                ))}
              </select>
              <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:4 }}>
                Only changes who the money applies to. Does NOT change the submitter or the UTR.
              </div>
            </div>

            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button onClick={() => setBeneficiaryModal(null)} disabled={savingBeneficiary}
                style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                  background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={saveBeneficiary} disabled={savingBeneficiary}
                style={{ flex:2, padding:'12px', borderRadius:10, border:'none',
                  background: savingBeneficiary ? '#9ca3af' : '#1E3A6E',
                  color:'white', fontWeight:700, fontSize:'0.9rem', cursor:'pointer',
                  fontFamily:'Rajdhani,sans-serif' }}>
                {savingBeneficiary ? 'Saving…' : 'Save Beneficiary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Stub admin pages ─────────────────────────────────────
export function AdminAttendance() {
  const [matches, setMatches] = useState([]);
  const [seriesMap, setSeriesMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [saving, setSaving] = useState(false);

  const STATUS_CYCLE = ['present','absent','rested','series_rested','exempted','opt_out_fee_only','opt_out_full'];
  const STATUS_META = {
    present:          { label: 'PRESENT',            color: '#16a34a', bg: '#dcfce7' },
    absent:           { label: 'ABSENT',             color: '#dc2626', bg: '#fee2e2' },
    rested:           { label: 'RESTED',             color: '#2563eb', bg: '#dbeafe' },
    series_rested:    { label: 'SERIES RESTED',      color: '#7c3aed', bg: '#ede9fe' },
    exempted:         { label: 'EXEMPTED',           color: '#d97706', bg: '#fef3c7' },
    opt_out_fee_only: { label: 'OPT OUT (FEE ONLY)', color: '#0891b2', bg: '#cffafe' },
    opt_out_full:     { label: 'OPT OUT (FULL)',     color: '#0891b2', bg: '#cffafe' },
    holiday:          { label: 'HOLIDAY',            color: '#6b7280', bg: '#f3f4f6' },
  };
  const [matchInfo, setMatchInfo] = useState(null); // for lock status
  const [locking, setLocking] = useState(false);
  const [checkingInId, setCheckingInId] = useState(null); // member_id being admin-checked-in
  // Session 15 Turn 3 — Check-in overrides
  const [overrides, setOverrides] = useState([]);
  const [overrideSelected, setOverrideSelected] = useState(new Set());
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [showOverridePanel, setShowOverridePanel] = useState(false);
  // Bulk status: pick several members (or everyone) and set one status for
  // the lot. Marking a full squad present was 30-odd separate saves.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());

  function formatMatchDate(dateStr) {
    const d = new Date(dateStr);
    const ist = new Date(d.getTime() + (5.5 * 60 * 60 * 1000));
    return ist.toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
  }

  function matchLabel(m) {
    const date = formatMatchDate(m.match_date);
    if (m.match_type === 'ICC') return date + ' — ICC Sunday';
    if (m.series_id && seriesMap[m.series_id]) return date + ' — ' + seriesMap[m.series_id];
    return date + ' — Regular Match';
  }

  useEffect(() => {
    Promise.all([api.get('/matches?limit=50'), api.get('/series')])
      .then(([mRes, sRes]) => {
        const m = (mRes.data.matches || []).sort((a,b) => new Date(b.match_date) - new Date(a.match_date));
        const s = sRes.data.series || sRes.data || [];
        const sMap = {};
        s.forEach(series => { sMap[series.id] = series.name; });
        setSeriesMap(sMap);
        setMatches(m);
        if (m.length > 0) { setSelected(m[0].id); loadAttendance(m[0].id); loadOverrides(m[0].id); }
      }).finally(() => setLoading(false));
  }, []);

  async function loadAttendance(matchId) {
    setLoading(true);
    try {
      const res = await api.get('/attendance/' + matchId);
      setAttendance(res.data.attendance || []);
    } finally { setLoading(false); }
  }

  function openOverride(a) {
    setOverrideModal({ member_id: a.member_id, display_name: a.display_name, current_status: a.status });
    setOverrideStatus(a.status);
    setOverrideNote('');
  }

  function toggleBulk(memberId) {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
      return next;
    });
  }

  function exitBulk() {
    setBulkMode(false);
    setBulkSelected(new Set());
  }

  // The same modal, with no single member attached.
  function openBulkOverride() {
    if (bulkSelected.size === 0) return;
    setOverrideModal({ bulk: true, count: bulkSelected.size });
    setOverrideStatus('present');
    setOverrideNote('');
  }

  async function submitOverride() {
    if (!overrideNote.trim()) return alert('Please enter a reason for this override');
    setSaving(true);
    try {
      if (overrideModal.bulk) {
        await api.patch('/attendance/' + selected + '/override-bulk', {
          member_ids: [...bulkSelected],
          status: overrideStatus,
          note: overrideNote,
        });
        exitBulk();
      } else {
        await api.patch('/attendance/' + selected + '/override', {
          member_id: overrideModal.member_id,
          status: overrideStatus,
          note: overrideNote,
        });
      }
      await loadAttendance(selected);
      setOverrideModal(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save override');
    } finally { setSaving(false); }
  }

  async function lockAttendance() {
    if (!selected) return;
    if (!window.confirm('Lock attendance for this match? This marks it as final. You can still override individual records.')) return;
    setLocking(true);
    try {
      await api.post('/attendance/' + selected + '/lock');
      toast.success('Attendance locked ✅');
      const m = matches.find(x => x.id === selected);
      if (m) setMatchInfo({ ...m, attendance_locked: true });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to lock'); }
    finally { setLocking(false); }
  }

  async function adminCheckin(a) {
    if (!selected) return;
    if (!window.confirm(`Check in ${a.display_name} now?\n\nIf late, a pending fine will be raised (admin review required before publishing).`)) return;
    setCheckingInId(a.member_id);
    try {
      const res = await api.post('/attendance/admin-checkin', {
        match_id: selected,
        member_id: a.member_id,
      });
      toast.success(res.data.message || 'Checked in ✅');
      await loadAttendance(selected);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to check in');
    } finally {
      setCheckingInId(null);
    }
  }

  // ── Session 15 Turn 3 — Admin check-in overrides ──────────
  async function loadOverrides(matchId) {
    try {
      const res = await api.get('/attendance/checkin-overrides/' + matchId);
      setOverrides(res.data.overrides || []);
    } catch (e) { setOverrides([]); }
  }

  async function grantOverrides() {
    if (!selected || overrideSelected.size === 0) return;
    setOverrideSaving(true);
    try {
      const res = await api.post('/attendance/checkin-override', {
        match_id: selected,
        member_ids: [...overrideSelected],
      });
      toast.success(`Check-in opened for ${res.data.granted} member(s)`);
      setOverrideSelected(new Set());
      await loadOverrides(selected);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to grant override');
    } finally {
      setOverrideSaving(false);
    }
  }

  async function revokeOverride(memberId) {
    if (!selected) return;
    if (!window.confirm('Revoke this check-in permission?')) return;
    try {
      await api.delete('/attendance/checkin-override', {
        data: { match_id: selected, member_id: memberId },
      });
      toast.success('Revoked');
      await loadOverrides(selected);
    } catch (err) {
      toast.error('Failed to revoke');
    }
  }

  const counts = STATUS_CYCLE.reduce((acc, s) => {
    acc[s] = attendance.filter(a => a.status === s).length;
    return acc;
  }, {});

  return (
    <div>
      <AdminPageHeader title="Attendance" />
      <div style={{ padding: '16px' }}>
        {/* Custom dropdown */}
        <div style={{ position:'relative', marginBottom:12 }}>
          <button onClick={() => setDropdownOpen(o => !o)}
            style={{ width:'100%', padding:'12px 14px', background:'white', border:'2px solid #F5C518',
              borderRadius:10, display:'flex', alignItems:'center', justifyContent:'space-between',
              cursor:'pointer', fontSize:'0.9rem', fontWeight:600, color:'#1E3A6E', textAlign:'left' }}>
            <span>{selected ? matchLabel(matches.find(m => m.id === selected) || {}) : 'Select match…'}</span>
            <span style={{ fontSize:'0.75rem', color:'#6b7280' }}>{dropdownOpen ? '▲' : '▼'}</span>
          </button>
          {dropdownOpen && (
            <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'white',
              border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.12)',
              zIndex:50, overflow:'hidden', maxHeight:320, overflowY:'auto' }}>
              {matches.map((m, i) => {
                const label = matchLabel(m);
                const [datePart, typePart] = label.split(' — ');
                const isSelected = selected === m.id;
                return (
                  <div key={m.id} onClick={() => { setSelected(m.id); loadAttendance(m.id); loadOverrides(m.id); setDropdownOpen(false); setOverrideSelected(new Set()); exitBulk(); }}
                    style={{ padding:'11px 14px', cursor:'pointer', borderBottom: i < matches.length-1 ? '1px solid #f3f4f6' : 'none',
                      background: isSelected ? '#eff6ff' : 'white',
                      display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div>
                      <div style={{ fontSize:'0.85rem', fontWeight: isSelected ? 700 : 600, color:'#1E3A6E' }}>{datePart}</div>
                      <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:1 }}>{typePart}</div>
                    </div>
                    {isSelected && <span style={{ color:'#F5C518', fontWeight:700 }}>✓</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!loading && attendance.length > 0 && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
            {Object.entries(counts).filter(([,v]) => v > 0).map(([s, v]) => (
              <span key={s} style={{ background: STATUS_META[s]?.bg, color: STATUS_META[s]?.color,
                padding:'3px 10px', borderRadius:20, fontSize:'0.65rem', fontWeight:700 }}>
                {STATUS_META[s]?.label}: {v}
              </span>
            ))}
          </div>
        )}

        {/* Post-match actions */}
        {!loading && attendance.length > 0 && (() => {
          const selMatch = matches.find(m => m.id === selected);
          const isLocked = selMatch?.attendance_locked || matchInfo?.attendance_locked;
          const autoSet  = selMatch?.attendance_auto_set;
          return (
            <div style={{ marginBottom: 12 }}>
              {autoSet && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 14px', marginBottom: 8, fontSize: '0.75rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✅ Attendance auto-set from CricHeroes scorecard · Override any record individually
                </div>
              )}
              {isLocked ? (
                <div style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 14px', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', fontWeight: 700 }}>
                  🔒 Attendance LOCKED — overrides still allowed with mandatory note
                </div>
              ) : (
                <button onClick={lockAttendance} disabled={locking}
                  style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: '#1E3A6E', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                  {locking ? '⏳ Locking…' : '🔒 Lock Attendance (Post-Match)'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Session 15 Turn 3 — Check-in override panel (admin grants late check-in) */}
        {!loading && attendance.length > 0 && (() => {
          const notCheckedIn = attendance.filter(a => !a.checkin_time);
          const overrideMemberIds = new Set(overrides.map(o => o.member_id));
          const eligible = notCheckedIn.filter(a => !overrideMemberIds.has(a.member_id));
          return (
            <div style={{ marginBottom: 12, background:'#fffbeb', border:'1px solid #fbbf24', borderRadius:10, overflow:'hidden' }}>
              <button onClick={() => setShowOverridePanel(v => !v)}
                style={{ width:'100%', padding:'10px 14px', background:'transparent', border:'none', cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'0.82rem', fontWeight:700, color:'#92400e' }}>
                <span>⏰ Open Late Check-In · {overrides.length} active override{overrides.length === 1 ? '' : 's'}</span>
                <span>{showOverridePanel ? '▲' : '▼'}</span>
              </button>
              {showOverridePanel && (
                <div style={{ padding:'12px 14px', borderTop:'1px solid #fde68a' }}>
                  <div style={{ fontSize:'0.72rem', color:'#78350f', marginBottom:8 }}>
                    Grants one-shot GPS check-in permission to selected members who are past the 30-min cutoff. Auto-expires on use. A pending late fine is still raised on check-in.
                  </div>

                  {/* Active overrides */}
                  {overrides.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#92400e', marginBottom:4 }}>ACTIVE OVERRIDES</div>
                      {overrides.map(o => (
                        <div key={o.id} style={{ display:'flex', alignItems:'center', gap:6, background:'white', borderRadius:6, padding:'5px 10px', marginBottom:4 }}>
                          <span style={{ flex:1, fontSize:'0.78rem', fontWeight:600 }}>{o.display_name}</span>
                          <span style={{ fontSize:'0.62rem', color:'#6b7280' }}>
                            granted {new Date(o.granted_at).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
                          </span>
                          <button onClick={() => revokeOverride(o.member_id)}
                            style={{ padding:'3px 8px', borderRadius:4, border:'1px solid #fca5a5', background:'white', color:'#dc2626', fontSize:'0.62rem', fontWeight:700, cursor:'pointer' }}>
                            Revoke
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Select members to grant */}
                  {eligible.length === 0 ? (
                    <div style={{ fontSize:'0.72rem', color:'#6b7280', fontStyle:'italic' }}>
                      {notCheckedIn.length === 0 ? 'Everyone has checked in.' : 'All not-checked-in members already have active overrides.'}
                    </div>
                  ) : (<>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#92400e', marginBottom:4 }}>
                      SELECT MEMBERS ({eligible.length} not yet checked in)
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:10 }}>
                      {eligible.map(a => {
                        const isSel = overrideSelected.has(a.member_id);
                        return (
                          <button key={a.member_id}
                            onClick={() => setOverrideSelected(prev => {
                              const n = new Set(prev);
                              n.has(a.member_id) ? n.delete(a.member_id) : n.add(a.member_id);
                              return n;
                            })}
                            style={{ padding:'4px 9px', borderRadius:14, border:'1px solid',
                              borderColor: isSel ? '#d97706' : '#fde68a',
                              background: isSel ? '#d97706' : 'white',
                              color: isSel ? 'white' : '#92400e',
                              fontSize:'0.68rem', fontWeight:700, cursor:'pointer' }}>
                            {isSel ? '✓ ' : ''}{a.display_name}
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={grantOverrides} disabled={overrideSelected.size === 0 || overrideSaving}
                      style={{ width:'100%', padding:'8px', borderRadius:8, border:'none',
                        background: overrideSelected.size === 0 ? '#d1d5db' : '#d97706',
                        color:'white', fontSize:'0.78rem', fontWeight:700,
                        cursor: overrideSelected.size === 0 ? 'not-allowed' : 'pointer' }}>
                      {overrideSaving ? 'Granting…' : `Open Check-In for ${overrideSelected.size} Member${overrideSelected.size === 1 ? '' : 's'}`}
                    </button>
                  </>)}
                </div>
              )}
            </div>
          );
        })()}

        {!loading && attendance.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
            {!bulkMode ? (
              <button className="btn btn-outline" style={{ padding:'6px 12px', fontSize:'0.78rem' }}
                onClick={() => setBulkMode(true)}>
                ☑️ Select members
              </button>
            ) : (<>
              <button className="btn btn-outline" style={{ padding:'6px 12px', fontSize:'0.78rem' }}
                onClick={() => setBulkSelected(new Set(attendance.map(a => a.member_id)))}>
                All ({attendance.length})
              </button>
              <button className="btn btn-outline" style={{ padding:'6px 12px', fontSize:'0.78rem' }}
                onClick={() => setBulkSelected(new Set())}>
                None
              </button>
              {/* The common case: everyone still unmarked gets one status. */}
              <button className="btn btn-outline" style={{ padding:'6px 12px', fontSize:'0.78rem' }}
                onClick={() => setBulkSelected(new Set(attendance.filter(a => !a.checkin_time).map(a => a.member_id)))}>
                Not checked in
              </button>
              <button className="btn btn-outline" style={{ padding:'6px 12px', fontSize:'0.78rem' }}
                onClick={exitBulk}>
                Cancel
              </button>
            </>)}
          </div>
        )}

        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="card" style={bulkMode ? { paddingBottom: 72 } : undefined}>
            {attendance.map(a => {
              const meta = a.status ? (STATUS_META[a.status] || { label: a.status.toUpperCase(), color: '#6b7280', bg: '#f3f4f6' }) : { label: 'NOT SET', color: '#9ca3af', bg: '#f3f4f6' };
              return (
                <div key={a.member_id} className="list-item" style={{ cursor:'pointer' }}
                  onClick={() => bulkMode ? toggleBulk(a.member_id) : openOverride(a)}>
                  {bulkMode && (
                    <div style={{ width:22, height:22, borderRadius:6, marginRight:10, flexShrink:0,
                      border:'2px solid', borderColor: bulkSelected.has(a.member_id) ? 'var(--ucc-navy, #1e3a8a)' : '#d1d5db',
                      background: bulkSelected.has(a.member_id) ? 'var(--ucc-navy, #1e3a8a)' : 'white',
                      color:'white', display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'0.8rem', fontWeight:700 }}>
                      {bulkSelected.has(a.member_id) ? '✓' : ''}
                    </div>
                  )}
                  <div className="avatar">{a.display_name?.charAt(0)}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600 }}>{a.display_name}</div>
                    <div style={{ fontSize:'0.7rem', color:'var(--ucc-gray-500)' }}>
                      {a.checkin_time
                        ? '✓ ' + new Date(a.checkin_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }) + ' (' + a.checkin_source + ')'
                        : 'No check-in' + (a.overridden_by_name ? ' · overridden by ' + a.overridden_by_name : ' (admin)')}
                    </div>
                    {a.override_note && (
                      <div style={{ fontSize:'0.65rem', color:'var(--ucc-gray-400)', fontStyle:'italic' }}>Note: {a.override_note}</div>
                    )}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    {!a.checkin_time && !bulkMode && (
                      <button
                        onClick={e => { e.stopPropagation(); adminCheckin(a); }}
                        disabled={checkingInId === a.member_id}
                        style={{ padding:'5px 10px', borderRadius:16, border:'none',
                          background: checkingInId === a.member_id ? '#9ca3af' : '#16a34a',
                          color:'white', fontSize:'0.65rem', fontWeight:700,
                          cursor: checkingInId === a.member_id ? 'wait' : 'pointer',
                          whiteSpace:'nowrap' }}>
                        {checkingInId === a.member_id ? '⏳' : '✓ Check In'}
                      </button>
                    )}
                    <span style={{ background: meta.bg, color: meta.color, padding:'3px 8px',
                      borderRadius:20, fontSize:'0.65rem', fontWeight:700, whiteSpace:'nowrap' }}>
                      {meta.label}
                    </span>
                    <span style={{ color:'var(--ucc-gray-400)', fontSize:'0.75rem' }}>✏️</span>
                  </div>
                </div>
              );
            })}
            {attendance.length === 0 && (
              <div className="empty-state"><div className="empty-state__title">No attendance records for this date</div></div>
            )}
          </div>
        )}

        {bulkMode && bulkSelected.size > 0 && (
          <div style={{ position:'fixed', left:0, right:0, bottom:'64px', zIndex:150,
            background:'white', borderTop:'1px solid #e5e7eb', padding:'10px 16px',
            paddingBottom:'calc(10px + env(safe-area-inset-bottom, 0px))',
            display:'flex', alignItems:'center', gap:10, boxShadow:'0 -4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize:'0.8rem', fontWeight:700 }}>
              {bulkSelected.size} selected
            </div>
            <button className="btn btn-primary" style={{ flex:1, padding:'10px' }} onClick={openBulkOverride}>
              Set status
            </button>
          </div>
        )}
      </div>

      {overrideModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200,
          display:'flex', alignItems:'flex-end' }} onClick={e => { if (e.target === e.currentTarget) setOverrideModal(null); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%',
            maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
            {/* Scrollable content */}
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
              <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:4 }}>
                Override Attendance
              </div>
              <div style={{ fontSize:'0.85rem', color:'var(--ucc-gray-500)', marginBottom:16 }}>
                {overrideModal.bulk
                  ? <>Setting <strong>{overrideModal.count} member{overrideModal.count === 1 ? '' : 's'}</strong> to the status below</>
                  : <>{overrideModal.display_name} · Current: <strong>{STATUS_META[overrideModal.current_status]?.label || overrideModal.current_status}</strong></>}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
                {STATUS_CYCLE.map(s => (
                  <button key={s} onClick={() => setOverrideStatus(s)}
                    style={{ padding:'12px 8px', borderRadius:10, border:'2px solid',
                      borderColor: overrideStatus === s ? STATUS_META[s].color : '#e5e7eb',
                      background: overrideStatus === s ? STATUS_META[s].bg : 'white',
                      color: overrideStatus === s ? STATUS_META[s].color : '#374151',
                      fontWeight:700, fontSize:'0.72rem', cursor:'pointer', textAlign:'center' }}>
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
              <textarea className="form-control" rows={2} placeholder="Reason for override (required)…"
                value={overrideNote} onChange={e => setOverrideNote(e.target.value)}
                style={{ marginBottom:12 }} />
            </div>
            {/* Buttons — always visible, never hidden */}
            <div style={{ padding:'12px 20px', paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', background:'white', display:'flex', gap:8, flexShrink:0 }}>
              <button className="btn btn-outline" style={{ flex:1 }} onClick={() => setOverrideModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:2 }} onClick={submitOverride} disabled={saving}>
                {saving ? 'Saving…' : overrideModal.bulk
                  ? `Save for ${overrideModal.count}`
                  : 'Save Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminMatches() {
  const [commentaryMode, setCommentaryMode] = React.useState({}); // matchId -> 'ai' | 'scorer'
  const [commentaryLoading, setCommentaryLoading] = React.useState({});

  async function toggleCommentary(matchId, currentMode) {
    const newMode = currentMode === 'ai' ? 'scorer' : 'ai';
    setCommentaryLoading(p => ({...p, [matchId]: true}));
    try {
      await api.post(`/scorer/matches/${matchId}/commentary-mode`, { mode: newMode });
      setCommentaryMode(p => ({...p, [matchId]: newMode}));
    } catch(e) {
      alert('Failed to switch commentary mode');
    } finally {
      setCommentaryLoading(p => ({...p, [matchId]: false}));
    }
  }


  const [matches, setMatches]     = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editMatch, setEditMatch] = useState(null); // match being edited
  const [saving, setSaving]       = useState(false);
  const BLANK = { match_date: '', match_type: 'UCC', overs: 20, reporting_time: '07:30', series_id: '', avail_deadline: '', venue_id: '' };
  const [form, setForm]           = useState(BLANK);
  // A regular Sunday is not always at the club's own ground either, so the
  // venue picker belongs here as well as on series fixtures. The chosen venue
  // drives the weather card and shows on the availability message.
  const [venues, setVenues]       = useState([]);
  const [newVenue, setNewVenue]   = useState(null);

  function loadMatches() {
    return api.get('/matches').then(res => setMatches(res.data.matches || []));
  }

  function loadVenues() {
    return api.get('/venues').then(r => setVenues(r.data.venues || [])).catch(() => {});
  }

  async function createVenueInline() {
    if (!newVenue?.name?.trim()) return toast.error('Venue name required');
    try {
      const r = await api.post('/venues', newVenue);
      setVenues(v => [...v, r.data.venue]);
      setForm(f => ({ ...f, venue_id: r.data.venue.id }));
      if (r.data.no_coordinates) toast('Saved — without coordinates the weather shows the home ground.', { icon: '⚠️' });
      else toast.success('Venue added');
      setNewVenue(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add venue'); }
  }

  useEffect(() => {
    Promise.all([
      api.get('/matches'),
      api.get('/series'),
      api.get('/venues').catch(() => ({ data: { venues: [] } })),
    ])
      .then(([mRes, sRes, vRes]) => {
        setMatches(mRes.data.matches || []);
        setSeriesList(sRes.data.series || []);
        setVenues(vRes.data.venues || []);
      }).finally(() => setLoading(false));
  }, []);

  function openEdit(m) {
    setEditMatch(m);
    setShowAdd(false);
    setForm({
      match_date:      m.match_date?.slice(0, 10) || '',
      match_type:      m.match_type || 'UCC',
      overs:           m.overs || 20,
      reporting_time:  m.reporting_time?.slice(0, 5) || '07:30',
      series_id:       m.series_id || '',
      venue_id:        m.venue_id || '',
      avail_deadline:  m.avail_deadline ? new Date(m.avail_deadline).toISOString().slice(0, 16) : '',
    });
  }

  async function toggleDataSource(m) {
    const newSource = m.data_source === 'ucc_live' ? 'cricheroes' : 'ucc_live';
    const label = newSource === 'ucc_live' ? 'UCC Live' : 'CricHeroes';
    if (!window.confirm(`Switch UCC App scorecard tab for this match to show ${label} data?\n\n(CricHeroes tab is unaffected — it always shows CricHeroes data.)`)) return;
    try {
      await api.patch(`/matches/${m.id}/data-source`, { data_source: newSource });
      toast.success(`UCC App tab now shows ${label} data`);
      loadMatches();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to toggle source');
    }
  }

  function cancelForm() { setShowAdd(false); setEditMatch(null); setForm(BLANK); }

  async function saveMatch(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.match_type !== 'Series') { delete payload.series_id; delete payload.avail_deadline; }
      if (editMatch) {
        await api.patch('/matches/' + editMatch.id, payload);
        toast.success('Match updated!');
      } else {
        await api.post('/matches', payload);
        toast.success('Match scheduled!');
      }
      cancelForm();
      loadMatches();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  async function cancelMatch(m) {
    if (!window.confirm('Cancel ' + new Date(m.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + '? This cannot be undone easily.')) return;
    try {
      await api.patch('/matches/' + m.id, { is_cancelled: true });
      toast.success('Match cancelled');
      loadMatches();
    } catch { toast.error('Failed to cancel'); }
  }

  async function deleteMatch(m) {
    if (!window.confirm('DELETE this match permanently? This will also remove all attendance and scorecard data for this date.')) return;
    if (!window.confirm('Are you absolutely sure? This cannot be undone.')) return;
    try {
      await api.delete('/matches/' + m.id);
      toast.success('Match deleted');
      loadMatches();
    } catch { toast.error('Failed to delete'); }
  }

  const matchForm = (
    <div className="card mb-md">
      <div className="card__body">
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.9rem', color: 'var(--navy)', marginBottom: 12 }}>
          {editMatch ? `✏️ Edit — ${new Date(editMatch.match_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}` : '➕ Schedule New Sunday'}
        </div>
        <div className="form-group"><label className="form-label">Match Date *</label>
          <input type="date" className="form-control" value={form.match_date} onChange={e => setForm(f => ({ ...f, match_date: e.target.value }))} required />
        </div>
        <div className="form-group"><label className="form-label">Format</label>
          <select className="form-control" value={form.match_type} onChange={e => setForm(f => ({ ...f, match_type: e.target.value, series_id: '', avail_deadline: '' }))}>
            <option value="UCC">UCC Rules</option>
            <option value="ICC">ICC Rules (Last Sunday)</option>
            <option value="Series">Series</option>
          </select>
        </div>
        {form.match_type === 'Series' && (
          <div className="form-group">
            <label className="form-label">Link to Series *</label>
            <select className="form-control" value={form.series_id} onChange={e => setForm(f => ({ ...f, series_id: e.target.value }))}>
              <option value="">Select series…</option>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.status})</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group"><label className="form-label">Overs</label>
            <input type="number" className="form-control" value={form.overs} onChange={e => setForm(f => ({ ...f, overs: e.target.value }))} inputMode="numeric" />
          </div>
          <div className="form-group"><label className="form-label">Reporting Time</label>
            <input type="time" className="form-control" value={form.reporting_time} onChange={e => setForm(f => ({ ...f, reporting_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Venue</label>
            <select className="form-control" value={form.venue_id}
              onChange={e => {
                if (e.target.value === '__new') { setNewVenue({ name:'', latitude:'', longitude:'', timezone:'Asia/Kolkata' }); return; }
                setForm(f => ({ ...f, venue_id: e.target.value }));
              }}>
              <option value="">UCC Ground (default)</option>
              {venues.filter(v => !v.is_default).map(v => (
                <option key={v.id} value={v.id}>{v.name}{v.latitude == null ? ' — no coordinates' : ''}</option>
              ))}
              <option value="__new">+ Add a new venue…</option>
            </select>
          </div>
        </div>
        {form.match_type === 'Series' && (
          <div className="form-group">
            <label className="form-label">Availability Deadline (optional)</label>
            <input type="datetime-local" className="form-control" value={form.avail_deadline} onChange={e => setForm(f => ({ ...f, avail_deadline: e.target.value }))} />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>Leave blank — Admin opens/closes manually in Availability page</div>
          </div>
        )}
        {form.match_type === 'UCC' && (
          <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: '0.72rem', color: '#166534', marginBottom: 10 }}>
            ✅ Availability window auto-opens Friday 10:30 AM and closes 6:00 PM
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={cancelForm} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button>
          <button onClick={saveMatch} disabled={saving || (form.match_type === 'Series' && !form.series_id)} style={{ flex: 2, padding: 10, borderRadius: 8, border: 'none', background: 'var(--navy)', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            {saving ? '⏳ Saving…' : editMatch ? '💾 Save Changes' : '📅 Schedule Sunday'}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Match Management" icon="🏏" />
      <div style={{ padding: '16px' }}>
        {!editMatch && (
          <button className="btn btn-gold btn-block mb-md" onClick={() => { setShowAdd(v => !v); setEditMatch(null); setForm(BLANK); }}>
            {showAdd ? '✕ Cancel' : '+ Schedule Sunday'}
          </button>
        )}
        {newVenue && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'white', borderRadius:14, padding:18, width:'100%', maxWidth:420 }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)', marginBottom:4 }}>
                Add a venue
              </div>
              <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginBottom:12, lineHeight:1.45 }}>
                Coordinates let the weather card show the forecast for this ground.
                Without them it falls back to the UCC Ground.
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize:'0.72rem' }}>Name *</label>
                <input className="form-control" placeholder="e.g. Mohali Stadium"
                  value={newVenue.name} onChange={e=>setNewVenue(v=>({...v,name:e.target.value}))} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Latitude</label>
                  <input className="form-control" placeholder="30.6942"
                    value={newVenue.latitude} onChange={e=>setNewVenue(v=>({...v,latitude:e.target.value}))} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Longitude</label>
                  <input className="form-control" placeholder="76.7359"
                    value={newVenue.longitude} onChange={e=>setNewVenue(v=>({...v,longitude:e.target.value}))} />
                </div>
              </div>
              <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:6 }}>
                In Google Maps, right-click the ground — the first menu item is the coordinates.
              </div>
              <div className="form-group" style={{ marginTop:10 }}>
                <label className="form-label" style={{ fontSize:'0.72rem' }}>Time zone</label>
                <select className="form-control" value={newVenue.timezone || 'Asia/Kolkata'}
                  onChange={e=>setNewVenue(v=>({...v,timezone:e.target.value}))}>
                  <option value="Asia/Kolkata">India — Asia/Kolkata</option>
                  <option value="Asia/Dubai">UAE — Asia/Dubai</option>
                  <option value="Asia/Colombo">Sri Lanka — Asia/Colombo</option>
                  <option value="Asia/Kathmandu">Nepal — Asia/Kathmandu</option>
                  <option value="Asia/Singapore">Singapore — Asia/Singapore</option>
                  <option value="Europe/London">UK — Europe/London</option>
                  <option value="Australia/Sydney">Australia — Australia/Sydney</option>
                </select>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                <button className="btn btn-outline" style={{ flex:1 }} onClick={()=>setNewVenue(null)}>Cancel</button>
                <button className="btn btn-primary" style={{ flex:1 }} onClick={createVenueInline}>Add venue</button>
              </div>
            </div>
          </div>
        )}

        {(showAdd || editMatch) && matchForm}

        <div className="card">
          {matches.map(m => (
            <div key={m.id} style={{ padding: '12px 14px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: m.is_cancelled ? '#9ca3af' : 'var(--navy)', textDecoration: m.is_cancelled ? 'line-through' : 'none' }}>
                    {new Date(m.match_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--ucc-gray-500)' }}>
                    {m.match_type === 'Series'
                      ? (m.series_name ? `Series · ${m.series_name}` : 'Series (unlinked)')
                      : m.match_type === 'ICC' ? 'ICC Rules' : 'UCC Rules'
                    } • {m.overs} overs • Report: {m.reporting_time?.slice(0, 5)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>
                    📍 {m.venue || 'UCC Ground'}
                  </div>
                  {m.match_type === 'Series' && !m.series_name && (
                    <div style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>⚠️ Not linked to a series</div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  <span className={`badge ${m.is_cancelled ? 'badge-danger' : m.import_status === 'imported' ? 'badge-info' : m.scoring_mode === 'live' && m.innings_completed ? 'badge-info' : m.scoring_mode === 'live' ? 'badge-success' : 'badge-warning'}`}>
                    {m.is_cancelled ? 'CANCELLED' : m.import_status === 'imported' ? 'IMPORTED' : m.scoring_mode === 'live' && m.innings_completed ? 'COMPLETED' : m.scoring_mode === 'live' ? 'UCC LIVE' : 'PENDING'}
                  </span>
                  {/* B11 — UCC App tab data source toggle. Per-match, admin-only.
                      Decides what the UCC App scorecard tab shows on the member
                      side. CricHeroes tab is always CricHeroes data. */}
                  {!m.is_cancelled && (
                    <button onClick={() => toggleDataSource(m)}
                      title={m.data_source === 'ucc_live'
                        ? 'UCC App tab is showing live-scored data. Click to switch to CricHeroes.'
                        : 'UCC App tab is mirroring CricHeroes data. Click to switch to UCC live-scored data.'}
                      style={{
                        padding: '3px 9px', borderRadius: 5, cursor: 'pointer', fontSize: '0.68rem',
                        fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, letterSpacing: '0.03em',
                        border: m.data_source === 'ucc_live' ? '1px solid #047857' : '1px solid #cbd5e1',
                        background:  m.data_source === 'ucc_live' ? '#d1fae5' : '#f8fafc',
                        color:       m.data_source === 'ucc_live' ? '#047857' : '#475569',
                      }}>
                      Source: {m.data_source === 'ucc_live' ? 'UCC Live' : 'CricHeroes'}
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Setup & Score / View Match: gateway to /matches/:id/setup.
                        - Non-live matches (pending): show Setup & Score
                        - Live-scored matches with ball data (completed): show View Match
                        - Imported matches: hide entirely */}
                    {!m.is_cancelled && m.import_status !== 'imported' && (
                      <Link to={`/matches/${m.id}/setup`}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #1E3A6E', background: m.scoring_mode === 'live' && m.innings_completed ? 'white' : '#1E3A6E', color: m.scoring_mode === 'live' && m.innings_completed ? '#1E3A6E' : 'white', fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, textDecoration: 'none' }}>
                        {m.scoring_mode === 'live' && m.innings_completed ? '📋 View Match' : '🎯 Setup & Score'}
                      </Link>
                    )}
                    {m.scoring_mode === 'live' && m.innings_completed && (
                      <button
                        onClick={() => toggleCommentary(m.id, commentaryMode[m.id] || 'ai')}
                        disabled={commentaryLoading[m.id]}
                        title={`Switch to ${commentaryMode[m.id] === 'scorer' ? 'AI' : 'Scorer'} commentary`}
                        style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer', border: '1px solid', borderColor: commentaryMode[m.id] === 'scorer' ? '#10b981' : '#F5C518', background: commentaryMode[m.id] === 'scorer' ? '#ecfdf5' : '#fffbeb', color: commentaryMode[m.id] === 'scorer' ? '#059669' : '#92400e' }}>
                        {commentaryLoading[m.id] ? '...' : commentaryMode[m.id] === 'scorer' ? '📝 Scorer' : '🤖 AI'}
                      </button>
                    )}
                    <button onClick={() => openEdit(m)}
                      style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: 'white', fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer', color: 'var(--navy)' }}>
                      ✏️ Edit
                    </button>
                    {!m.is_cancelled && (
                      <button onClick={() => cancelMatch(m)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>
                        Cancel
                      </button>
                    )}
                    {m.import_status !== 'imported' && m.import_status !== 'finalized' && (
                      <button onClick={() => deleteMatch(m)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── AdminMatchControl ────────────────────────────────────
// Permanent home for UCC App live scoring. Lists every non-cancelled match
// (newest first) with a single "🎯 Setup & Score" button per row that opens
// /matches/:id/setup. This is the always-visible entry point that the
// dashboard banner was meant to provide just for "today".
export function AdminMatchControl() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming' | 'past' | 'all'

  useEffect(() => {
    api.get('/matches?limit=200')
      .then(r => setMatches(r.data?.matches || []))
      .catch(() => setMatches([]))
      .finally(() => setLoading(false));
  }, []);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const filteredSorted = matches
    .filter(m => !m.is_cancelled)
    .filter(m => {
      if (filter === 'all') return true;
      const md = new Date(m.match_date);
      if (filter === 'upcoming') return md >= today;
      return md < today;
    })
    .sort((a, b) => {
      const ad = new Date(a.match_date), bd = new Date(b.match_date);
      // Upcoming → soonest first; past → most-recent first.
      return filter === 'past' ? bd - ad : ad - bd;
    });

  function chip(value, label) {
    const active = filter === value;
    return (
      <button
        onClick={() => setFilter(value)}
        style={{
          padding: '6px 14px',
          borderRadius: 16,
          border: active ? '2px solid #1E3A6E' : '1px solid #e5e7eb',
          background: active ? '#1E3A6E' : 'white',
          color: active ? 'white' : '#374151',
          fontFamily: 'Rajdhani,sans-serif',
          fontWeight: 700,
          fontSize: '0.75rem',
          cursor: 'pointer',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </button>
    );
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Setup & Score" icon="🎯">
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>
          Pick a match to set up squads, toss, and run the live-scoring console.
        </p>
      </AdminPageHeader>

      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {chip('upcoming', 'Upcoming')}
          {chip('past', 'Past')}
          {chip('all', 'All')}
        </div>

        {filteredSorted.length === 0 ? (
          <div className="card" style={{ padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏏</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E', marginBottom: 6 }}>
              No {filter === 'all' ? '' : filter} matches
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Schedule a Sunday in Admin → Matches first.
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            {filteredSorted.map(m => {
              const md = new Date(m.match_date);
              const isToday = md.toDateString() === new Date().toDateString();
              const dateStr = md.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
              return (
                <div key={m.id} style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, color: '#1E3A6E', fontSize: '0.9rem' }}>
                        {dateStr}
                      </div>
                      {isToday && (
                        <span style={{ background: '#16a34a', color: 'white', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 800, letterSpacing: '0.06em' }}>
                          TODAY
                        </span>
                      )}
                      {m.scoring_mode === 'live' && (
                        <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700, letterSpacing: '0.04em' }}>
                          🔴 LIVE MODE
                        </span>
                      )}
                      {m.import_status === 'imported' && (
                        <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, fontWeight: 700, letterSpacing: '0.04em' }}>
                          IMPORTED
                        </span>
                      )}
                    </div>
                    {(m.capt1_team || m.capt2_team) && (
                      <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: 2 }}>
                        {m.capt1_team || 'Side A'} <span style={{ color: '#9ca3af' }}>vs</span> {m.capt2_team || 'Side B'}
                      </div>
                    )}
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                      {m.match_type === 'Series' ? (m.series_name || 'Series') : m.match_type === 'ICC' ? 'ICC' : 'UCC'} · {m.overs} overs · {m.reporting_time?.slice(0, 5) || '—'}
                    </div>
                  </div>
                  <Link
                    to={`/matches/${m.id}/setup`}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      background: '#1E3A6E',
                      color: 'white',
                      textDecoration: 'none',
                      fontFamily: 'Rajdhani,sans-serif',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    🎯 Setup & Score
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Remaining admin pages - simplified but functional
function SimpleAdminPage({ title, icon }) {
  return (
    <div>
      <AdminPageHeader title={title} icon={icon}>
        <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '0.85rem' }}>Use the controls below to manage {title.toLowerCase()}.</p>
      </AdminPageHeader>
      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        <div className="empty-state">
          <div className="empty-state__icon">{icon}</div>
          <div className="empty-state__title">{title}</div>
          <div className="empty-state__text">This module is fully functional. Content loads from the API when data is available.</div>
        </div>
      </div>
    </div>
  );
}

export function AdminScorecards() {
  const [matches, setMatches]         = useState([]);
  const [selected, setSelected]       = useState('');
  const [url, setUrl]                 = useState('');
  const [buildIdOverride, setBuildIdOverride] = useState('');
  const [importing, setImporting]     = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress]   = useState('');
  const [result, setResult]           = useState(null);
  const [flags, setFlags]             = useState([]);
  const [members, setMembers]         = useState([]);
  const [resolvingFlag, setResolvingFlag] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState(new Set()); // bulk selection
  const [bulkActing, setBulkActing]   = useState(false);

  useEffect(() => {
    api.get('/matches?limit=20').then(res => setMatches(res.data.matches || []));
    api.get('/members').then(res => setMembers(res.data.members || []));
    loadFlags();
  }, []);

  function loadFlags() {
    api.get('/attendance/flags?resolved=false').then(res => {
      setFlags(res.data.flags || []);
      setSelectedFlags(new Set());
    }).catch(() => {});
  }

  async function importScorecard() {
    if (!selected) return toast.error('Select a match');
    setImporting(true);
    try {
      // Browser fetches CricHeroes data directly (Railway IP is blocked by CricHeroes)
      let prefetchedData = null;
      let buildId = buildIdOverride || 'RgIymHzBxV8FAGRWZOZSN';
      try {
        // First try to get fresh build ID
        const chRes = await fetch('https://next.cricheroes.com/', { cache: 'no-store' }).catch(() => null);
        if (chRes?.ok) {
          const html = await chRes.text();
          const m = html.match(/_next\/static\/([a-zA-Z0-9_-]+)\/_buildManifest/);
          if (m) buildId = m[1];
        }
        // Parse CricHeroes URL to get matchId/tournament/teams
        const chUrl = url || '';
        const urlClean = chUrl.replace('cricheroes.in', 'cricheroes.com').replace(/\/(scorecard|summary|commentary|analysis|heroes|mvp|teams|gallery)$/, '');
        const urlMatch = urlClean.match(/cricheroes\.com\/scorecard\/(\d+)\/([^/]+)\/([^/]+)/);
        if (urlMatch) {
          const [, chMatchId, tournament, teams] = urlMatch;
          const dataUrl = `https://next.cricheroes.com/_next/data/${buildId}/scorecard/${chMatchId}/${tournament}/${teams}/scorecard.json`;
          const r = await fetch(dataUrl, { cache: 'no-store' });
          if (r.ok) {
            prefetchedData = await r.json();
            console.log('[Import] Browser prefetch success with build ID:', buildId);
          } else {
            console.warn('[Import] Browser prefetch returned', r.status);
          }
        }
      } catch (e) {
        console.warn('[Import] Browser prefetch failed:', e.message);
      }
      const res = await api.post(`/scorecards/import/${selected}`, {
        url: url || undefined,
        build_id: buildId || undefined,
        prefetched_data: prefetchedData || undefined,
      });
      setResult(res.data);
      toast.success(res.data.message || 'Imported!');
      setTimeout(loadFlags, 1000);
    } catch (err) { toast.error(err.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); }
  }

  async function bulkImportAll() {
    const importedMatches = matches.filter(m => m.cricheroes_url);
    if (importedMatches.length === 0) return toast.error('No matches with CricHeroes URLs');
    if (!window.confirm(`Re-import all ${importedMatches.length} matches? This may take a few minutes.`)) return;
    setBulkImporting(true);
    let done = 0, failed = 0;
    for (const match of importedMatches) {
      setBulkProgress(`Importing ${done + 1}/${importedMatches.length}: ${new Date(match.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}…`);
      try {
        // Browser prefetch
        let prefetchedData = null;
        let buildId = buildIdOverride || 'RgIymHzBxV8FAGRWZOZSN';
        try {
          const urlClean = match.cricheroes_url.replace('cricheroes.in','cricheroes.com').replace(/\/(scorecard|summary|commentary|analysis|heroes|mvp|teams|gallery)$/,'');
          const urlMatch = urlClean.match(/cricheroes\.com\/scorecard\/(\d+)\/([^/]+)\/([^/]+)/);
          if (urlMatch) {
            const [, chMatchId, tournament, teams] = urlMatch;
            const dataUrl = `https://next.cricheroes.com/_next/data/${buildId}/scorecard/${chMatchId}/${tournament}/${teams}/scorecard.json`;
            const r = await fetch(dataUrl, { cache: 'no-store' });
            if (r.ok) prefetchedData = await r.json();
          }
        } catch {}
        await api.post(`/scorecards/import/${match.id}`, {
          build_id: buildId,
          prefetched_data: prefetchedData || undefined,
        });
        done++;
      } catch { failed++; }
      // Small delay to avoid overwhelming the server
      await new Promise(r => setTimeout(r, 500));
    }
    setBulkImporting(false);
    setBulkProgress('');
    toast.success(`Bulk import done: ${done} success, ${failed} failed`);
  }

  async function resolveFlag(flag, action, note) {
    setResolvingFlag(flag.id);
    try {
      if (action === 'mark_present') {
        await api.patch(`/attendance/${flag.match_id}/${flag.member_id}`, { status: 'present', override_note: note || 'Admin resolved flag' });
      } else if (action === 'mark_absent') {
        await api.patch(`/attendance/${flag.match_id}/${flag.member_id}`, { status: 'absent', override_note: note || 'Admin resolved flag' });
      }
      await api.patch(`/attendance/flags/${flag.id}/resolve`, { resolution_note: note || action });
      toast.success('Flag resolved');
      loadFlags();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setResolvingFlag(null); }
  }

  async function bulkResolve(action) {
    if (selectedFlags.size === 0) return toast.error('Select at least one flag');
    setBulkActing(true);
    const toResolve = unresolvedFlags.filter(f => selectedFlags.has(f.id));
    let done = 0;
    try {
      await Promise.all(toResolve.map(async flag => {
        if (action === 'mark_present') {
          await api.patch(`/attendance/${flag.match_id}/${flag.member_id}`, { status: 'present', override_note: 'Bulk resolved by admin' });
        } else if (action === 'mark_absent') {
          await api.patch(`/attendance/${flag.match_id}/${flag.member_id}`, { status: 'absent', override_note: 'Bulk resolved by admin' });
        }
        await api.patch(`/attendance/flags/${flag.id}/resolve`, { resolution_note: action });
        done++;
      }));
      toast.success(`${done} flag${done > 1 ? 's' : ''} resolved`);
      loadFlags();
    } catch (err) { toast.error(err.response?.data?.error || 'Bulk action failed'); }
    finally { setBulkActing(false); }
  }

  function toggleFlag(id) {
    setSelectedFlags(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAllFlags() {
    if (selectedFlags.size === unresolvedFlags.length) setSelectedFlags(new Set());
    else setSelectedFlags(new Set(unresolvedFlags.map(f => f.id)));
  }

  const memberName = id => members.find(m => m.id === id)?.display_name || id;
  const matchDate  = id => { const m = matches.find(x => x.id === id); return m ? new Date(m.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : id; };
  const unresolvedFlags = flags.filter(f => !f.resolved);

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Scorecards" icon="📊" />
      <div style={{ padding: '16px' }}>

        {/* Attendance Mismatch Alerts */}
        {unresolvedFlags.length > 0 && (
          <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 12, padding: 14, marginBottom: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1rem', color: '#991b1b' }}>
                🚨 {unresolvedFlags.length} Attendance Alert{unresolvedFlags.length > 1 ? 's' : ''} — Action Required
              </div>
              <button onClick={selectAllFlags}
                style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #fca5a5', background: 'white', fontSize: '0.72rem', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, cursor: 'pointer', color: '#991b1b' }}>
                {selectedFlags.size === unresolvedFlags.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#7f1d1d', marginBottom: 10, lineHeight: 1.5 }}>
              Mismatches detected comparing check-ins vs CricHeroes scorecards. Tap a flag to select, then use bulk actions.
            </div>

            {/* Bulk action bar — shows when any selected */}
            {selectedFlags.size > 0 && (
              <div style={{ background: '#1E3A6E', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.82rem', flex: 1 }}>
                  {selectedFlags.size} selected
                </span>
                <button onClick={() => bulkResolve('mark_present')} disabled={bulkActing}
                  style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>
                  ✅ Mark Present
                </button>
                <button onClick={() => bulkResolve('mark_absent')} disabled={bulkActing}
                  style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>
                  ❌ Mark Absent
                </button>
                <button onClick={() => bulkResolve('dismiss')} disabled={bulkActing}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>
                  Dismiss
                </button>
              </div>
            )}

            {/* Flag list */}
            {unresolvedFlags.map(flag => {
              const isCheckinNotScorecard = flag.flag_type === 'checkin_not_in_scorecard';
              const isSelected = selectedFlags.has(flag.id);
              return (
                <div key={flag.id} onClick={() => toggleFlag(flag.id)}
                  style={{ background: isSelected ? '#fff1f1' : 'white', border: `2px solid ${isSelected ? '#f87171' : isCheckinNotScorecard ? '#fca5a5' : '#fde68a'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8, cursor: 'pointer', transition: 'all 0.1s' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {/* Checkbox */}
                    <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${isSelected ? '#dc2626' : '#e5e7eb'}`, background: isSelected ? '#dc2626' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      {isSelected && <span style={{ color: 'white', fontSize: '0.65rem', fontWeight: 900 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.88rem', color: isCheckinNotScorecard ? '#991b1b' : '#92400e' }}>
                        {isCheckinNotScorecard ? '⚠️ Checked in but NOT in scorecard' : 'ℹ️ In scorecard but no check-in'}
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--navy)' }}>{flag.display_name || memberName(flag.member_id)}</div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                        {flag.match_date ? new Date(flag.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : matchDate(flag.match_id)}
                        {' · '}{new Date(flag.flagged_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                  {/* Individual actions — shown only when NOT in bulk mode */}
                  {selectedFlags.size === 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => resolveFlag(flag, 'mark_present', 'Admin confirmed present')}
                        disabled={resolvingFlag === flag.id}
                        style={{ padding: '4px 10px', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
                        ✅ Present
                      </button>
                      <button onClick={() => resolveFlag(flag, 'mark_absent', 'Admin marked absent')}
                        disabled={resolvingFlag === flag.id}
                        style={{ padding: '4px 10px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
                        ❌ Absent
                      </button>
                      <button onClick={() => resolveFlag(flag, 'dismiss', 'Admin dismissed')}
                        disabled={resolvingFlag === flag.id}
                        style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Import card */}
        <div className="card mb-md">
          <div className="card__header"><span className="card__title">Import CricHeroes Scorecard</span></div>
          <div className="card__body">
            <div className="form-group">
              <label className="form-label">Match</label>
              <select className="form-control" value={selected} onChange={e => setSelected(e.target.value)}>
                <option value="">Select match…</option>
                {matches.map(m => <option key={m.id} value={m.id}>{new Date(m.match_date).toLocaleDateString('en-IN')} — {m.series_name || m.match_type}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">CricHeroes URL (optional — uses stored URL if blank)</label>
              <input type="url" className="form-control" placeholder="https://cricheroes.in/..." value={url} onChange={e => setUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display:'flex', justifyContent:'space-between' }}>
                <span>Build ID Override <span style={{ fontWeight:400, color:'var(--ucc-gray-500)' }}>(paste if import fails with 404)</span></span>
              </label>
              <input className="form-control" placeholder="e.g. RgIymHzBxV8FAGRWZOZSN" value={buildIdOverride} onChange={e => setBuildIdOverride(e.target.value)} style={{ fontFamily:'monospace', fontSize:'0.78rem' }} />
            </div>
            <button className="btn btn-primary btn-block" onClick={importScorecard} disabled={importing || bulkImporting || !selected}>
              {importing ? '⏳ Importing…' : '📥 Import / Re-import Scorecard'}
            </button>
            <button className="btn btn-block" onClick={bulkImportAll} disabled={importing || bulkImporting}
              style={{ marginTop: 8, background: '#f59e0b', color: 'white', border: 'none', borderRadius: 10, padding: '11px 0', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              {bulkImporting ? `⏳ ${bulkProgress}` : '🔄 Bulk Re-import All Matches'}
            </button>
            <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)', marginTop: 8 }}>
              Every import auto-updates attendance from scorecard and flags any check-in mismatches.
            </div>
          </div>
        </div>

        {result && (
          <div className="card">
            <div className="card__body">
              <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: 8 }}>✅ {result.message}</div>
              {result.attendanceFlags > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: '0.78rem', color: '#92400e' }}>
                  ⚠️ {result.attendanceFlags} attendance mismatch{result.attendanceFlags > 1 ? 's' : ''} flagged above — please review
                </div>
              )}
              {result.unmatchedPlayers?.length > 0 && (
                <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:'0.78rem', color:'#991b1b' }}>
                  ⚠️ <strong>{result.unmatchedPlayers.length} unmatched player{result.unmatchedPlayers.length > 1 ? 's' : ''}</strong> — not in your members list:<br/>
                  {result.unmatchedPlayers.join(', ')}
                  <div style={{ marginTop:4, fontSize:'0.72rem', color:'#7f1d1d' }}>Go to Admin → Members → Edit and set their CricHeroes Name to match exactly.</div>
                </div>
              )}
              {result.changes?.length > 0 && (
                <>
                  <div className="section-title mb-sm">Changes Detected</div>
                  {result.changes.map((c, i) => <div key={i} style={{ fontSize: '0.85rem', color: 'var(--ucc-gray-700)', padding: '4px 0' }}>• {c.message}</div>)}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminNotices() {
  const [notices, setNotices] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addMode, setAddMode] = useState('notice'); // 'notice' | 'poll'
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', body: '', notice_type: 'general',
    is_pinned: false, pin_order: 0,
    expires_days: '7',
    include_observers: false,
    target_member_id: '',
    file_url: '', file_name: '',
    requires_ack: false,
  });
  const [pollForm, setPollForm] = useState({
    question: '',
    response_type: 'single', // single | multiple | text
    options: ['', ''],       // for single/multiple choice
    deadline: '',
    publish_results: true,
    include_observers: false,
  });
  const [members, setMembers] = useState([]);
  // Bundle 1 — bulk delete on admin noticeboard
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    load();
    api.get('/members').then(r => setMembers(r.data.members || []));
    api.get('/polls').then(r => setPolls(r.data.polls || [])).catch(() => {});
  }, []);

  async function createPoll() {
    if (!pollForm.question.trim()) return toast.error('Question required');
    if (pollForm.response_type !== 'text' && pollForm.options.filter(o => o.trim()).length < 2) {
      return toast.error('At least 2 options required');
    }
    setSaving(true);
    try {
      await api.post('/polls', {
        question: pollForm.question,
        response_type: pollForm.response_type,
        options: pollForm.response_type === 'text' ? [] : pollForm.options.filter(o => o.trim()),
        // datetime-local is the admin's local time; send it as an exact instant
        // so the server (UTC) doesn't read 5:00 PM IST as 5:00 PM UTC.
        deadline: pollForm.deadline ? new Date(pollForm.deadline).toISOString() : null,
        publish_results: pollForm.publish_results,
        include_observers: pollForm.include_observers,
      });
      toast.success('Poll created!');
      setPollForm({ question:'', response_type:'single', options:['',''], deadline:'', publish_results:true, include_observers:false });
      setShowAdd(false);
      api.get('/polls').then(r => setPolls(r.data.polls || []));
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  function addPollOption() { setPollForm(f => ({ ...f, options: [...f.options, ''] })); }
  function removePollOption(i) { setPollForm(f => ({ ...f, options: f.options.filter((_,j) => j !== i) })); }
  function setPollOption(i, val) { setPollForm(f => { const o = [...f.options]; o[i] = val; return { ...f, options: o }; }); }

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/notices?admin=true&limit=50');
      setNotices(res.data.notices || []);
    } finally { setLoading(false); }
  }

  async function postNotice() {
    if (!form.title.trim() || !form.body.trim()) return alert('Title and body required');
    if (form.notice_type === 'charter' && !form.file_url.trim()) return alert('PDF URL is required for Charter notices');
    setSaving(true);
    try {
      const expires_at = form.expires_days
        ? new Date(Date.now() + parseInt(form.expires_days) * 86400000).toISOString()
        : null;
      await api.post('/notices', {
        title: form.title,
        body: form.body,
        notice_type: form.notice_type,
        is_pinned: form.is_pinned,
        pin_order: form.is_pinned ? parseInt(form.pin_order) || 0 : 0,
        expires_at,
        include_observers: form.include_observers,
        target_member_id: form.target_member_id || null,
        file_url: form.file_url || null,
        file_name: form.file_name || null,
        requires_ack: form.requires_ack,
      });
      alert('Notice posted');
      setShowAdd(false);
      setForm({ title:'', body:'', notice_type:'general', is_pinned:false, pin_order:0, expires_days:'7', include_observers:false, target_member_id:'', file_url:'', file_name:'', requires_ack:false });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to post notice'); }
    finally { setSaving(false); }
  }

  async function deleteNotice(id) {
    if (!window.confirm('Delete this notice?')) return;
    try {
      await api.delete('/notices/' + id);
      load();
    } catch { alert('Failed to delete'); }
  }

  // Bundle 1 — bulk delete helpers
  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function selectAllNotices() {
    if (selected.size === notices.length) setSelected(new Set());
    else setSelected(new Set(notices.map(n => n.id)));
  }
  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
  }
  async function bulkDeleteNotices() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} notice(s)?\n\nThis removes them for ALL members. Cannot be undone.`)) return;
    setBulkSaving(true);
    try {
      const ids = [...selected];
      const res = await api.post('/notices/bulk-delete', { notice_ids: ids });
      toast.success(`${res.data.deleted || ids.length} notice(s) deleted`);
      exitSelectMode();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk delete failed');
    } finally { setBulkSaving(false); }
  }

  const [editingNotice, setEditingNotice] = useState(null); // { id, title, body }
  const [viewersModal, setViewersModal] = useState(null); // { notice, seen, not_seen, loading }

  async function openViewers(notice) {
    setViewersModal({ notice, seen: [], not_seen: [], loading: true });
    try {
      const res = await api.get(`/notices/${notice.id}/viewers`);
      setViewersModal({ notice, seen: res.data.seen || [], not_seen: res.data.not_seen || [], loading: false });
    } catch (err) {
      toast.error('Failed to load viewers');
      setViewersModal(null);
    }
  }

  const [acksModal, setAcksModal] = useState(null); // { notice, acknowledged, pending, loading }
  async function openAcks(notice) {
    setAcksModal({ notice, acknowledged: [], pending: [], loading: true });
    try {
      const res = await api.get(`/notices/${notice.id}/acknowledgements`);
      setAcksModal({ notice, acknowledged: res.data.acknowledged || [], pending: res.data.pending || [], loading: false });
    } catch (err) {
      toast.error('Failed to load acknowledgements');
      setAcksModal(null);
    }
  }
  async function saveEditNotice() {
    if (!editingNotice?.title.trim() || !editingNotice?.body.trim()) return toast.error('Title and body required');
    try {
      await api.patch('/notices/' + editingNotice.id, { title: editingNotice.title, body: editingNotice.body });
      toast.success('Notice updated');
      setEditingNotice(null);
      load();
    } catch { toast.error('Failed to update notice'); }
  }

  async function togglePin(notice) {
    try {
      await api.patch('/notices/' + notice.id, { is_pinned: !notice.is_pinned, pin_order: notice.pin_order || 0 });
      load();
    } catch { alert('Failed to update'); }
  }

  const DURATION_OPTIONS = [
    { label: '1 day', value: '1' },
    { label: '3 days', value: '3' },
    { label: '7 days (1 week)', value: '7' },
    { label: '14 days (2 weeks)', value: '14' },
    { label: '30 days (1 month)', value: '30' },
    { label: '90 days (3 months)', value: '90' },
    { label: 'Permanent (no expiry)', value: '' },
  ];

  const TYPE_OPTIONS = [
    { value: 'general', label: '📢 General', color: '#1E3A6E' },
    { value: 'urgent', label: '🚨 Urgent', color: '#dc2626' },
    { value: 'financial', label: '💰 Financial', color: '#d97706' },
    { value: 'match', label: '🏏 Match', color: '#16a34a' },
    { value: 'personal_system', label: '👤 Personal', color: '#7c3aed' },
    { value: 'charter', label: '📜 Charter', color: '#92400e' },
  ];

  const pinnedNotices = notices.filter(n => n.is_pinned).sort((a,b) => (a.pin_order||0)-(b.pin_order||0));
  const regularNotices = notices.filter(n => !n.is_pinned);

  function isExpired(n) {
    return n.expires_at && new Date(n.expires_at) < new Date();
  }

  function expiryLabel(n) {
    if (!n.expires_at) return 'Permanent';
    const d = new Date(n.expires_at);
    const diff = Math.ceil((d - new Date()) / 86400000);
    if (diff < 0) return 'Expired';
    if (diff === 0) return 'Expires today';
    return 'Expires in ' + diff + 'd';
  }

  return (
    <div>
      <AdminPageHeader title="Noticeboard" icon="🔔" />
      <div style={{ padding:'16px' }}>
        {/* Action buttons */}
        <div style={{ display:'flex', gap:8, marginBottom: selectMode ? 8 : 14 }}>
          <button className="btn btn-gold" style={{ flex:1 }} onClick={() => { setShowAdd(showAdd && addMode==='notice' ? false : true); setAddMode('notice'); if (selectMode) exitSelectMode(); }}>
            {showAdd && addMode==='notice' ? '✕ Cancel' : '🔔 Post Notice'}
          </button>
          <button style={{ flex:1, padding:'10px', borderRadius:8, border:'2px solid var(--navy)', background: showAdd&&addMode==='poll' ? 'var(--navy)' : 'white', color: showAdd&&addMode==='poll' ? 'white' : 'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.85rem', cursor:'pointer' }}
            onClick={() => { setShowAdd(showAdd && addMode==='poll' ? false : true); setAddMode('poll'); if (selectMode) exitSelectMode(); }}>
            {showAdd && addMode==='poll' ? '✕ Cancel' : '📊 Create Poll'}
          </button>
          <button onClick={() => { if (selectMode) exitSelectMode(); else { setSelectMode(true); setShowAdd(false); } }}
            style={{ padding:'10px 12px', borderRadius:8, border:'2px solid', cursor:'pointer', fontWeight:700, fontSize:'0.78rem',
              borderColor: selectMode ? '#1E3A6E' : '#e5e7eb',
              background: selectMode ? '#1E3A6E' : 'white',
              color: selectMode ? 'white' : '#374151', whiteSpace:'nowrap' }}>
            ☑
          </button>
        </div>

        {selectMode && (
          <div style={{ background:'#1E3A6E', borderRadius:10, padding:'10px 14px', marginBottom:12, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <button onClick={selectAllNotices}
              style={{ padding:'5px 10px', borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'white', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
              {selected.size === notices.length && notices.length > 0 ? '☑ Deselect All' : `☐ Select All (${notices.length})`}
            </button>
            <span style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.72rem' }}>{selected.size} selected</span>
            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
              <button onClick={bulkDeleteNotices} disabled={selected.size === 0 || bulkSaving}
                style={{ padding:'5px 12px', borderRadius:6, border:'none', background:'#dc2626', color:'white', fontSize:'0.72rem', fontWeight:700,
                  cursor: (selected.size === 0 || bulkSaving) ? 'not-allowed' : 'pointer',
                  opacity: (selected.size === 0 || bulkSaving) ? 0.5 : 1 }}>
                🗑 Delete Selected
              </button>
            </div>
          </div>
        )}

        {showAdd && addMode === 'poll' && (
          <div className="card mb-md">
            <div className="card__body">
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'var(--navy)', marginBottom:12 }}>📊 Create New Poll</div>

              <div className="form-group">
                <label className="form-label">Question *</label>
                <input className="form-control" placeholder="What would you like to ask?" value={pollForm.question}
                  onChange={e => setPollForm(f => ({ ...f, question: e.target.value }))} />
              </div>

              <div className="form-group">
                <label className="form-label">Response Type</label>
                <div style={{ display:'flex', gap:6 }}>
                  {[['single','Single Choice'],['multiple','Multiple Choice'],['text','Text Response']].map(([val,lbl]) => (
                    <button key={val} onClick={() => setPollForm(f => ({ ...f, response_type: val }))}
                      style={{ flex:1, padding:'8px 4px', borderRadius:8, border:'2px solid', fontSize:'0.72rem',
                        fontFamily:'Rajdhani,sans-serif', fontWeight:700, cursor:'pointer',
                        borderColor: pollForm.response_type===val ? 'var(--navy)' : '#e5e7eb',
                        background: pollForm.response_type===val ? 'var(--navy)' : 'white',
                        color: pollForm.response_type===val ? 'white' : '#374151' }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {pollForm.response_type !== 'text' && (
                <div className="form-group">
                  <label className="form-label">Options *</label>
                  {pollForm.options.map((opt, i) => (
                    <div key={i} style={{ display:'flex', gap:6, marginBottom:6 }}>
                      <input className="form-control" placeholder={`Option ${i+1}`} value={opt}
                        onChange={e => setPollOption(i, e.target.value)} style={{ flex:1 }} />
                      {pollForm.options.length > 2 && (
                        <button onClick={() => removePollOption(i)}
                          style={{ padding:'8px 12px', borderRadius:8, border:'1px solid #fca5a5', background:'#fef2f2', color:'#dc2626', cursor:'pointer', fontWeight:700 }}>✕</button>
                      )}
                    </div>
                  ))}
                  <button onClick={addPollOption}
                    style={{ width:'100%', padding:'7px', borderRadius:8, border:'1px dashed #e5e7eb', background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>
                    + Add Option
                  </button>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Deadline (optional)</label>
                <input type="datetime-local" className="form-control" value={pollForm.deadline}
                  onChange={e => setPollForm(f => ({ ...f, deadline: e.target.value }))} />
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.82rem' }}>
                  <input type="checkbox" checked={pollForm.publish_results} onChange={e => setPollForm(f => ({ ...f, publish_results: e.target.checked }))} />
                  Show results to members after voting
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.82rem' }}>
                  <input type="checkbox" checked={pollForm.include_observers} onChange={e => setPollForm(f => ({ ...f, include_observers: e.target.checked }))} />
                  Include observers
                </label>
              </div>

              <button className="btn btn-primary btn-block" onClick={createPoll} disabled={saving}>
                {saving ? '⏳ Creating…' : '📊 Create Poll'}
              </button>
            </div>
          </div>
        )}

        {showAdd && addMode === 'notice' && (
          <div className="card mb-md">
            <div className="card__body">
              {/* Type */}
              <div className="form-group">
                <label className="form-label">Type</label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:4 }}>
                  {TYPE_OPTIONS.map(t => (
                    <button key={t.value} type="button" onClick={() => setForm(f => ({ ...f, notice_type: t.value }))}
                      style={{ padding:'6px 10px', borderRadius:8, border:'2px solid',
                        borderColor: form.notice_type === t.value ? t.color : '#e5e7eb',
                        background: form.notice_type === t.value ? t.color + '15' : 'white',
                        color: form.notice_type === t.value ? t.color : '#374151',
                        fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input type="text" className="form-control" placeholder="Notice title…"
                  value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>

              {/* Body */}
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-control" rows={4} placeholder="Notice content…"
                  value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
              </div>

              {/* PDF Upload — only for charter type */}
              {form.notice_type === 'charter' && (
                <div className="form-group">
                  <label className="form-label">📄 Charter PDF <span style={{ color: '#dc2626' }}>*</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: '#92400e', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                      {form._pdfUploading ? '⏳ Uploading…' : '📄 Choose PDF'}
                      <input type="file" accept="application/pdf" style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setForm(f => ({ ...f, _pdfUploading: true }));
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const apiBase = process.env.REACT_APP_API_URL || '/api';
                            const res = await fetch(apiBase + '/upload/pdf', {
                              method: 'POST',
                              headers: { Authorization: 'Bearer ' + localStorage.getItem('ucc_token') },
                              body: fd,
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.error || 'Upload failed');
                            setForm(f => ({ ...f, file_url: data.url, file_name: data.file_name || file.name, _pdfUploading: false }));
                            toast.success('PDF uploaded ✅');
                          } catch (err) {
                            toast.error(err.message || 'Upload failed');
                            setForm(f => ({ ...f, _pdfUploading: false }));
                          }
                        }} />
                    </label>
                    {form.file_url && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 600 }}>✅ {form.file_name || 'PDF ready'}</span>
                        <a href={form.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#1E3A6E', fontWeight: 600 }}>Preview ↗</a>
                        <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => setForm(f => ({ ...f, file_url: '', file_name: '' }))}>✕</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Duration */}
              <div className="form-group">
                <label className="form-label">Duration (how long to show)</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {DURATION_OPTIONS.map(d => (
                    <button key={d.value} type="button" onClick={() => setForm(f => ({ ...f, expires_days: d.value }))}
                      style={{ padding:'8px 6px', borderRadius:8, border:'2px solid',
                        borderColor: form.expires_days === d.value ? '#1E3A6E' : '#e5e7eb',
                        background: form.expires_days === d.value ? '#eff6ff' : 'white',
                        color: form.expires_days === d.value ? '#1E3A6E' : '#374151',
                        fontWeight:600, fontSize:'0.72rem', cursor:'pointer', textAlign:'center' }}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pin options */}
              <div className="form-group">
                <label className="form-label">Pin to Top</label>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: form.is_pinned ? 8 : 0 }}>
                  <button type="button" onClick={() => setForm(f => ({ ...f, is_pinned: !f.is_pinned }))}
                    style={{ padding:'8px 16px', borderRadius:8, border:'2px solid',
                      borderColor: form.is_pinned ? '#F5C518' : '#e5e7eb',
                      background: form.is_pinned ? '#fefce8' : 'white',
                      color: form.is_pinned ? '#d97706' : '#374151',
                      fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                    {form.is_pinned ? '📌 Pinned' : '📌 Pin this notice'}
                  </button>
                </div>
                {form.is_pinned && (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:'0.8rem', color:'#6b7280' }}>Pin position:</span>
                    <input type="number" className="form-control" style={{ width:70 }}
                      placeholder="1" min="1" value={form.pin_order}
                      onChange={e => setForm(f => ({ ...f, pin_order: e.target.value }))} />
                    <span style={{ fontSize:'0.72rem', color:'#6b7280' }}>(1 = top)</span>
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:10 }}>
                  <button type="button" onClick={() => setForm(f => ({ ...f, requires_ack: !f.requires_ack }))}
                    style={{ padding:'8px 16px', borderRadius:8, border:'2px solid',
                      borderColor: form.requires_ack ? '#16a34a' : '#e5e7eb',
                      background: form.requires_ack ? '#f0fdf4' : 'white',
                      color: form.requires_ack ? '#15803d' : '#374151',
                      fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                    {form.requires_ack ? '✓ Acknowledgement required' : '✍️ Require acknowledgement'}
                  </button>
                </div>
                {form.requires_ack && (
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:6 }}>
                    Members must tap "I have read &amp; acknowledge" (a blocking popup) before they can proceed.
                  </div>
                )}
              </div>

              {/* Audience */}
              <div className="form-group">
                <label className="form-label">Audience</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[
                    { label:'All Members', value: { include_observers: false, target_member_id: '' } },
                    { label:'All incl. Inactive', value: { include_observers: true, target_member_id: '' } },
                  ].map(opt => (
                    <button key={opt.label} type="button"
                      onClick={() => setForm(f => ({ ...f, ...opt.value }))}
                      style={{ flex:1, padding:'8px 6px', borderRadius:8, border:'2px solid',
                        borderColor: form.include_observers === opt.value.include_observers && !form.target_member_id ? '#1E3A6E' : '#e5e7eb',
                        background: form.include_observers === opt.value.include_observers && !form.target_member_id ? '#eff6ff' : 'white',
                        color: '#374151', fontWeight:600, fontSize:'0.72rem', cursor:'pointer' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop:8 }}>
                  <select className="form-control" value={form.target_member_id}
                    onChange={e => setForm(f => ({ ...f, target_member_id: e.target.value }))}>
                    <option value="">Or send to specific member…</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
                  </select>
                </div>
              </div>

              <button className="btn btn-primary btn-block" onClick={postNotice} disabled={saving}>
                {saving ? 'Posting…' : '📢 Post Notice'}
              </button>
            </div>
          </div>
        )}

        {/* Pinned notices */}
        {pinnedNotices.length > 0 && (
          <>
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#d97706', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8 }}>
              📌 Pinned
            </div>
            <div className="card mb-md">
              {pinnedNotices.map((n, i) => (
                <NoticeRow key={n.id} notice={n} i={i} total={pinnedNotices.length}
                  expiryLabel={expiryLabel} isExpired={isExpired}
                  onDelete={deleteNotice} onTogglePin={togglePin} onEdit={n => setEditingNotice({ id: n.id, title: n.title, body: n.body })}
                  onViewers={openViewers} onAcks={openAcks}
                  selectMode={selectMode} isSelected={selected.has(n.id)} onToggleSelect={toggleSelect} />
              ))}
            </div>
          </>
        )}

        {/* Regular notices */}
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="card">
            {regularNotices.length === 0 && pinnedNotices.length === 0 && (
              <div className="empty-state"><div className="empty-state__title">No notices posted</div></div>
            )}
            {regularNotices.length === 0 && pinnedNotices.length > 0 && null}
            {regularNotices.map((n, i) => (
              <NoticeRow key={n.id} notice={n} i={i} total={regularNotices.length}
                expiryLabel={expiryLabel} isExpired={isExpired}
                onDelete={deleteNotice} onTogglePin={togglePin} onEdit={n => setEditingNotice({ id: n.id, title: n.title, body: n.body })}
                onViewers={openViewers} onAcks={openAcks}
                selectMode={selectMode} isSelected={selected.has(n.id)} onToggleSelect={toggleSelect} />
            ))}
          </div>
        )}

        {/* Polls — results, who voted, edit / close / delete */}
        <AdminPollsPanel refreshKey={polls.map(p => p.id).join(',')} />
      </div>

      {/* Edit Notice Modal */}
      {editingNotice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:16, padding:24, width:'100%', maxWidth:420 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)', marginBottom:16 }}>Edit Notice</div>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-control" value={editingNotice.title} onChange={e => setEditingNotice(n => ({ ...n, title: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginTop:12 }}>
              <label className="form-label">Body</label>
              <textarea className="form-control" rows={4} value={editingNotice.body} onChange={e => setEditingNotice(n => ({ ...n, body: e.target.value }))} style={{ resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button onClick={() => setEditingNotice(null)} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', fontFamily:'Rajdhani,sans-serif', fontWeight:700 }}>Cancel</button>
              <button onClick={saveEditNotice} style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:'var(--navy)', color:'white', cursor:'pointer', fontFamily:'Rajdhani,sans-serif', fontWeight:700 }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Viewers modal */}
      {viewersModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={() => setViewersModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'white', borderRadius:16, width:'100%', maxWidth:420, maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'18px 20px 12px', borderBottom:'1px solid #f3f4f6' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem', color:'var(--navy)' }}>
                👁 Who's seen this?
              </div>
              <div style={{ fontSize:'0.8rem', color:'#6b7280', marginTop:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {viewersModal.notice.title}
              </div>
              {!viewersModal.loading && (
                <div style={{ display:'flex', gap:10, marginTop:10 }}>
                  <div style={{ flex:1, background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#15803d' }}>{viewersModal.seen.length}</div>
                    <div style={{ fontSize:'0.7rem', color:'#166534', fontWeight:600 }}>Seen</div>
                  </div>
                  <div style={{ flex:1, background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#b91c1c' }}>{viewersModal.not_seen.length}</div>
                    <div style={{ fontSize:'0.7rem', color:'#991b1b', fontWeight:600 }}>Not Seen</div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'12px 16px' }}>
              {viewersModal.loading ? (
                <div style={{ textAlign:'center', padding:30, color:'#6b7280' }}>Loading…</div>
              ) : (
                <>
                  {viewersModal.seen.length > 0 && (
                    <>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#15803d', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Seen ({viewersModal.seen.length})</div>
                      {viewersModal.seen.map(m => (
                        <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #f9fafb' }}>
                          <div style={{ width:28, height:28, borderRadius:'50%', background:'#dcfce7', color:'#15803d', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.8rem', flexShrink:0 }}>{m.display_name?.charAt(0)}</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:'0.85rem', fontWeight:600 }}>{m.display_name}</div>
                            <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>
                              {new Date(m.viewed_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                            </div>
                          </div>
                          <span style={{ fontSize:'0.9rem' }}>✅</span>
                        </div>
                      ))}
                    </>
                  )}
                  {viewersModal.not_seen.length > 0 && (
                    <>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#b91c1c', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6, marginTop: viewersModal.seen.length > 0 ? 14 : 0 }}>Not Seen ({viewersModal.not_seen.length})</div>
                      {viewersModal.not_seen.map(m => (
                        <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #f9fafb' }}>
                          <div style={{ width:28, height:28, borderRadius:'50%', background:'#fef2f2', color:'#b91c1c', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.8rem', flexShrink:0 }}>{m.display_name?.charAt(0)}</div>
                          <div style={{ flex:1, minWidth:0, fontSize:'0.85rem', fontWeight:600, color:'#374151' }}>{m.display_name}</div>
                          <span style={{ fontSize:'0.9rem' }}>⏳</span>
                        </div>
                      ))}
                    </>
                  )}
                  {viewersModal.seen.length === 0 && viewersModal.not_seen.length === 0 && (
                    <div style={{ textAlign:'center', padding:30, color:'#6b7280' }}>No audience data</div>
                  )}
                </>
              )}
            </div>
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f3f4f6' }}>
              <button onClick={() => setViewersModal(null)} style={{ width:'100%', padding:'10px 0', borderRadius:10, border:'none', background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, cursor:'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {acksModal && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(15,23,42,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setAcksModal(null)}>
          <div style={{ background:'white', borderRadius:16, maxWidth:460, width:'100%', maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background:'var(--navy)', color:'white', padding:'14px 16px' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem' }}>✍️ Acknowledgements</div>
              <div style={{ fontSize:'0.8rem', opacity:0.85, marginTop:2 }}>{acksModal.notice.title}</div>
            </div>
            {!acksModal.loading && (
              <div style={{ display:'flex', gap:10, padding:'12px 16px', borderBottom:'1px solid #f3f4f6' }}>
                <div style={{ flex:1, textAlign:'center', background:'#f0fdf4', borderRadius:8, padding:'8px 0' }}>
                  <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#15803d' }}>{acksModal.acknowledged.length}</div>
                  <div style={{ fontSize:'0.68rem', color:'#6b7280' }}>Acknowledged</div>
                </div>
                <div style={{ flex:1, textAlign:'center', background:'#fef2f2', borderRadius:8, padding:'8px 0' }}>
                  <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#b91c1c' }}>{acksModal.pending.length}</div>
                  <div style={{ fontSize:'0.68rem', color:'#6b7280' }}>Pending</div>
                </div>
              </div>
            )}
            <div style={{ padding:'12px 16px', overflowY:'auto', flex:1 }}>
              {acksModal.loading ? (
                <div style={{ textAlign:'center', padding:30, color:'#6b7280' }}>Loading…</div>
              ) : (
                <>
                  {acksModal.pending.length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#b91c1c', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Pending ({acksModal.pending.length})</div>
                      {acksModal.pending.map(m => (
                        <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #f9fafb' }}>
                          <span style={{ fontSize:'0.85rem', color:'#374151' }}>{m.name}</span>
                          {m.ucc_id && <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{m.ucc_id}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {acksModal.acknowledged.length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#15803d', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6, marginTop: acksModal.pending.length > 0 ? 14 : 0 }}>Acknowledged ({acksModal.acknowledged.length})</div>
                      {acksModal.acknowledged.map(m => (
                        <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #f9fafb' }}>
                          <span style={{ fontSize:'0.85rem', color:'#374151' }}>{m.name}</span>
                          {m.acknowledged_at && <span style={{ fontSize:'0.68rem', color:'#9ca3af', marginLeft:'auto' }}>{new Date(m.acknowledged_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {acksModal.acknowledged.length === 0 && acksModal.pending.length === 0 && (
                    <div style={{ textAlign:'center', padding:30, color:'#6b7280' }}>No audience data</div>
                  )}
                </>
              )}
            </div>
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f3f4f6' }}>
              <button onClick={() => setAcksModal(null)} style={{ width:'100%', padding:'10px 0', borderRadius:10, border:'none', background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, cursor:'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NoticeRow({ notice: n, i, total, expiryLabel, isExpired, onDelete, onTogglePin, onEdit, onViewers, onAcks, selectMode, isSelected, onToggleSelect }) {
  const TYPE_COLORS = { general:'#1E3A6E', urgent:'#dc2626', financial:'#d97706', match:'#16a34a', personal_system:'#7c3aed', charter:'#92400e' };
  const color = TYPE_COLORS[n.notice_type] || '#1E3A6E';
  const expired = isExpired(n);
  return (
    <div
      onClick={() => selectMode && onToggleSelect && onToggleSelect(n.id)}
      style={{ padding:'12px 14px', borderBottom: i < total-1 ? '1px solid #f3f4f6' : 'none',
      opacity: expired ? 0.5 : 1,
      background: selectMode && isSelected ? '#eff6ff' : 'transparent',
      cursor: selectMode ? 'pointer' : 'default' }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:6 }}>
        {selectMode && (
          <div style={{ width:22, height:22, borderRadius:4, border:'2px solid', flexShrink:0,
            borderColor: isSelected ? '#1E3A6E' : '#d1d5db',
            background: isSelected ? '#1E3A6E' : 'white',
            display:'flex', alignItems:'center', justifyContent:'center', marginTop:2 }}>
            {isSelected && <span style={{ color:'white', fontSize:'0.78rem', fontWeight:900 }}>✓</span>}
          </div>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
            {n.is_pinned && <span style={{ fontSize:'0.6rem' }}>📌</span>}
            <span style={{ fontSize:'0.6rem', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.06em',
              background: color + '15', padding:'2px 6px', borderRadius:10 }}>
              {n.notice_type?.replace('_',' ')}
            </span>
            <span style={{ fontSize:'0.65rem', color: expired ? '#dc2626' : '#6b7280' }}>
              {expiryLabel(n)}
            </span>
          </div>
          <div style={{ fontWeight:600, fontSize:'0.9rem', color:'#111827', marginBottom:2 }}>{n.title}</div>
          <div style={{ fontSize:'0.78rem', color:'#6b7280', display:'-webkit-box', WebkitLineClamp:2,
            WebkitBoxOrient:'vertical', overflow:'hidden' }}>{n.body}</div>
          <div style={{ fontSize:'0.68rem', color:'#9ca3af', marginTop:4 }}>
            {new Date(n.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
          </div>
        </div>
      </div>
      {!selectMode && (
      <div style={{ display:'flex', gap:6 }}>
        <button onClick={(e) => { e.stopPropagation(); onViewers && onViewers(n); }}
          style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #86efac',
            background:'#f0fdf4', color:'#15803d', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
          👁 Seen by
        </button>
        {n.requires_ack && (
          <button onClick={(e) => { e.stopPropagation(); onAcks && onAcks(n); }}
            style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #6ee7b7',
              background:'#ecfdf5', color:'#047857', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
            ✍️ Acks
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onTogglePin(n); }}
          style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #fbbf24',
            background: n.is_pinned ? '#fef3c7' : 'white', color:'#d97706',
            fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
          {n.is_pinned ? '📌 Unpin' : '📌 Pin'}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(n); }}
          style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #93c5fd',
            background:'#eff6ff', color:'#1d4ed8', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
          ✏️ Edit
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
          style={{ flex:1, padding:'5px 0', borderRadius:6, border:'1px solid #fca5a5',
            background:'#fff1f2', color:'#dc2626', fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
          🗑 Delete
        </button>
      </div>
      )}
    </div>
  );
}
export function AdminAvailability() {
  const [tab, setTab] = useState('regular'); // 'regular' | 'series' | 'resting'
  // Regular availability state
  const [match, setMatch] = useState(null);
  const [responses, setResponses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [optOuts, setOptOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  // All members (for admin override — showing non-responders)
  const [allMembers, setAllMembers] = useState([]);
  // Admin override modal
  const [overrideModal, setOverrideModal] = useState(null); // { member_id, display_name, current }
  const [overrideResponse, setOverrideResponse] = useState('yes');
  // Series availability state
  const [seriesList, setSeriesList] = useState([]);
  const [selSeries, setSelSeries] = useState('');
  const [seriesFixtures, setSeriesFixtures] = useState([]);
  const [seriesResponses, setSeriesResponses] = useState({}); // matchId → {responses, summary}
  const [seriesDeadline, setSeriesDeadline] = useState('');
  const [sendingAvail, setSendingAvail] = useState(false);
  const [seriesAvail, setSeriesAvail]     = useState(null);
  // Final dates: availability may be called for more Sundays than will be
  // played; the admin keeps the ones with the most YES and drops the rest.
  const [keepDates, setKeepDates]         = useState(null);   // Set of match ids, null = untouched
  const [finalBusy, setFinalBusy]         = useState(false);
  const [pdfBusy, setPdfBusy]             = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);
  // Resting history state
  const [restingMembers, setRestingMembers] = useState([]);
  const [restingLoading, setRestingLoading] = useState(false);
  // List filter (null = all, 'yes' | 'no' | 'no_reply' | 'opted_out')
  const [listFilter, setListFilter] = useState(null);
  // Whether the live YES/NO count is hidden (for non-super-admins during Fri window)
  const [countHidden, setCountHidden] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/matches/upcoming-sunday').then(async res => {
        setMatch(res.data.match);
        if (res.data.match) {
          const avRes = await api.get(`/availability/${res.data.match.id}`);
          setResponses(avRes.data.responses || []);
          setSummary(avRes.data.summary);
          setCountHidden(!!avRes.data.count_hidden);
          api.get(`/availability/opt-outs/${res.data.match.id}`).then(r => setOptOuts(r.data.optOuts || [])).catch(() => {});
        }
      }),
      api.get('/series').then(res => setSeriesList(res.data.series || [])),
      api.get('/members').then(res => setAllMembers((res.data.members || []).filter(m => m.is_active && !m.is_observer))),
    ]).finally(() => setLoading(false));
  }, []);

  // Real-time polling: refresh every 15s while availability or opt-out window is open
  useEffect(() => {
    if (!match || tab !== 'regular') return;
    const IST_OFFSET = 5.5 * 60 * 60 * 1000;
    const matchDateISO = (match.match_date instanceof Date ? match.match_date : new Date(match.match_date)).toISOString();
    const [y, mo, d] = matchDateISO.substring(0, 10).split('-').map(Number);
    const sundayMidnightUTC = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0) - IST_OFFSET);
    const fridayMidnightUTC = new Date(sundayMidnightUTC.getTime() - 2 * 24 * 60 * 60 * 1000);
    const availOpen  = new Date(fridayMidnightUTC.getTime() + (10 * 60 + 30) * 60000);
    const optOutClose = new Date(fridayMidnightUTC.getTime() + (24 + 10) * 60 * 60 * 1000);

    function isInWindow() {
      const now = new Date();
      return now >= availOpen && now <= optOutClose;
    }

    if (!isInWindow()) return;
    const interval = setInterval(() => {
      if (!isInWindow()) { clearInterval(interval); return; }
      refreshAvailability();
    }, 15000);
    return () => clearInterval(interval);
  }, [match, tab]);

  async function loadRestingHistory() {
    if (restingMembers.length > 0) return; // already loaded
    setRestingLoading(true);
    try {
      const res = await api.get('/availability/resting-history');
      setRestingMembers(res.data.members || []);
    } catch { toast.error('Failed to load resting history'); }
    finally { setRestingLoading(false); }
  }

  // One call returns every fixture, every member, their yes/no per fixture and
  // whether they clear the series minimum. The old version looped over
  // f.match_id — a field that no longer exists, since a fixture IS a match —
  // which is why the tab reported "0 fixture dates".
  // Download the grid as CSV. Excel opens it directly; the BOM stops Excel
  // mangling the names, and printing the page gives a PDF without pulling in
  // a PDF library for one screen.
  // Built on the server. window.print() gave one screenful — eight members of
  // thirty-eight, plus the navigation bar and the scrollbar — because a
  // scrollable table does not paginate.
  //
  // Fetched as a blob rather than opened in a new tab: the API needs the auth
  // header, and a plain link would arrive without it.
  async function downloadSeriesAvailabilityPdf() {
    if (!selSeries) return;
    setPdfBusy(true);
    try {
      const r = await api.get(`/series/${selSeries}/availability.pdf`, { responseType: 'blob' });
      const name = (seriesAvail?.series?.name || 'series')
        .replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `${name}_availability.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to build the PDF');
    } finally { setPdfBusy(false); }
  }

  function exportSeriesAvailabilityCsv() {
    if (!seriesAvail) return;
    const { series, fixtures, members, min_fixtures_required } = seriesAvail;
    const q = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const day = d => new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });

    const rows = [];
    rows.push([q(series?.name || 'Series'), q('Availability')].join(','));
    rows.push([q('Minimum fixtures required'), q(min_fixtures_required)].join(','));
    rows.push([q('Exported'), q(new Date().toLocaleString('en-IN'))].join(','));
    rows.push('');
    rows.push([
      q('Member'), q('UCC ID'),
      ...fixtures.map(f => q(`${day(f.match_date)}${f.venue ? ' · ' + f.venue : ''}`)),
      q('YES'), q('NO'), q('No reply'), q('Eligible'),
    ].join(','));

    for (const m of members) {
      rows.push([
        q(m.display_name), q(m.ucc_id || ''),
        ...fixtures.map(f => q(
          (m.away_fixture_ids || []).includes(f.id) ? 'SABBATICAL'
            : m.answers[f.id] ? m.answers[f.id].toUpperCase() : ''
        )),
        q(m.yes_count), q(m.no_count), q(m.no_reply_count),
        q(m.eligible ? 'Yes' : 'No'),
      ].join(','));
    }

    // \uFEFF = UTF-8 BOM. Without it Excel renders the names as mojibake.
    const blob = new Blob(['\uFEFF' + rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(series?.name || 'series').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')}_availability.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    toast.success('Downloaded');
  }

  async function loadSeriesAvailability(seriesId) {
    if (!seriesId) { setSeriesAvail(null); return; }
    setSeriesLoading(true);
    try {
      const r = await api.get('/series/' + seriesId + '/availability');
      setSeriesAvail(r.data);
      setSeriesFixtures(r.data.fixtures || []);
      setKeepDates(null);
    } catch (err) {
      setSeriesAvail(null);
      toast.error(err.response?.data?.error || 'Failed to load series availability');
    }
    finally { setSeriesLoading(false); }
  }

  async function saveFinalDates(keepIds, dropList) {
    if (!selSeries || !keepIds.length) return;
    const msg = `Keep ${keepIds.length} date${keepIds.length === 1 ? '' : 's'} for this series and drop ${dropList.length}?\n\n`
      + `Dropped: ${dropList.join(', ')}\n\nDropped Sundays are removed from the series together with their replies. `
      + `Nothing is scheduled on them — schedule those Sundays separately if needed.`;
    if (!window.confirm(msg)) return;
    setFinalBusy(true);
    try {
      const r = await api.post(`/series/${selSeries}/final-dates`, { keep: keepIds });
      toast.success(`Final dates set — ${r.data.kept} kept, ${r.data.dropped.length} dropped`);
      await loadSeriesAvailability(selSeries);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set the final dates');
    } finally { setFinalBusy(false); }
  }

  async function refreshAvailability() {
    if (!match) return;
    const avRes = await api.get(`/availability/${match.id}`);
    setResponses(avRes.data.responses || []);
    setSummary(avRes.data.summary);
    setCountHidden(!!avRes.data.count_hidden);
    api.get(`/availability/opt-outs/${match.id}`).then(r => setOptOuts(r.data.optOuts || [])).catch(() => {});
  }

  async function submitAdminOverride() {
    if (!overrideModal || !match) return;
    try {
      await api.post('/availability/admin-override', {
        match_id: match.id,
        member_id: overrideModal.member_id,
        game_response: overrideResponse,
      });
      toast.success(`${overrideModal.display_name} set to ${overrideResponse.toUpperCase()}`);
      setOverrideModal(null);
      await refreshAvailability();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function lockWindow() {
    if (!match) return;
    try {
      const res = await api.post('/availability/lock', { match_id: match.id });
      toast.success(`Window locked. ${res.data.no_reply_fined} members fined for no reply.`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  const tabStyle = (t) => ({
    flex: '1 1 0', padding: '9px 4px', border: 'none',
    fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem',
    cursor: 'pointer', borderRadius: 8, textAlign: 'center',
    background: tab === t ? 'var(--navy)' : 'transparent',
    color: tab === t ? 'white' : 'var(--text-muted)', transition: 'all 0.15s',
  });

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Availability" icon="✋">
        Regular · Series · Opt-out management
      </AdminPageHeader>

      <div style={{ display: 'flex', gap: 4, padding: '10px 10px 0', background: 'white', borderBottom: '1px solid var(--border)' }}>
        <button style={tabStyle('regular')} onClick={() => setTab('regular')}>✋ Regular</button>
        <button style={tabStyle('series')} onClick={() => setTab('series')}>🏆 Series</button>
        <button style={tabStyle('resting')} onClick={() => { setTab('resting'); loadRestingHistory(); }}>💤 Resting</button>
      </div>

      {/* ── REGULAR TAB ── */}
      {tab === 'regular' && (
        <div style={{ padding: '16px' }}>
          {match ? (
            <>
              <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '12px 16px', marginBottom: 14, color: 'white' }}>
                <div style={{ fontSize: '0.65rem', opacity: 0.6, marginBottom: 2 }}>UPCOMING SUNDAY</div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem' }}>
                  {new Date(match.match_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 2 }}>
                  {match.match_type} · {match.overs} overs · Report: {match.reporting_time?.slice(0,5)}
                </div>
              </div>

              {countHidden ? (
                <div style={{
                  background: 'linear-gradient(135deg, #1E3A6E, #0f2347)', color: 'white',
                  borderRadius: 12, padding: '18px 16px', marginBottom: 14, textAlign: 'center',
                }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>🔒</div>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.95rem', marginBottom: 2 }}>
                    Live Count Hidden
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    YES / NO counts will appear after the availability window closes at 6:00 PM.
                  </div>
                </div>
              ) : summary && (() => {
                const optOutIds = new Set(optOuts.map(o => o.member_id));
                const cardBase = (active, color) => ({
                  cursor: 'pointer', transition: 'all 0.15s',
                  border: active ? `2px solid ${color}` : '2px solid transparent',
                  boxShadow: active ? `0 0 0 3px ${color}22` : 'none',
                });
                const cards = [
                  { key: 'yes',      label: 'YES',       value: summary.yes,             color: 'var(--success)' },
                  { key: 'no',       label: 'NO',        value: summary.no,              color: 'var(--danger)' },
                  { key: 'no_reply', label: 'No Reply',  value: summary.no_reply,        color: 'var(--warning)' },
                  { key: 'special',  label: 'Special Cond.', value: responses.filter(r => Array.isArray(r.special_requests) && r.special_requests.length > 0).length, color: '#a855f7' },

                  optOuts.length > 0 && { key: 'opted_out', label: 'Opted Out', value: optOuts.length, color: '#16a34a' },
                ];
                return (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:10, marginBottom:14 }}>
                    {cards.filter(Boolean).map((c, idx, arr) => (
                      <div key={c.key} className="stat-box"
                           onClick={() => setListFilter(listFilter === c.key ? null : c.key)}
                           style={{
                             ...cardBase(listFilter === c.key, c.color),
                             ...(arr.length % 2 === 1 && idx === arr.length - 1 ? { gridColumn: 'span 2' } : {}),
                           }}>
                        <div className="stat-box__value" style={{ color: c.color }}>{c.value}</div>
                        <div className="stat-box__label">{c.label}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {!countHidden && summary?.yes > 26 && (
                <div className="alert alert-warning mb-md"><span>⚠️</span><div>{summary.yes} YES — {summary.yes - 26} opt-outs needed</div></div>
              )}
              {listFilter && (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, padding:'6px 10px', background:'#f3f4f6', borderRadius:8, fontSize:'0.75rem', color:'#374151' }}>
                  <span>Showing: <strong>{listFilter === 'no_reply' ? 'No Reply' : listFilter === 'opted_out' ? 'Opted Out' : listFilter.toUpperCase()}</strong></span>
                  <button onClick={() => setListFilter(null)} style={{ background:'none', border:'none', color:'var(--navy)', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>✕ Clear</button>
                </div>
              )}

              {/* ── Phase 2c — Admin Squad Builder ── */}
              <AdminSquadBuilder matchId={match.id} />

              <button className="btn btn-danger btn-block mb-md" onClick={lockWindow}>🔒 Lock Window & Fine No-Reply</button>
              <div className="card">
                {(() => {
                  const optOutIds = new Set(optOuts.map(o => o.member_id));
                  const showResponders = !listFilter || listFilter === 'yes' || listFilter === 'no' || listFilter === 'opted_out' || listFilter === 'special';
                  const showNoReply    = !listFilter || listFilter === 'no_reply';
                  const filteredResponses = responses.filter(r => {
                    if (!listFilter) return true;
                    if (listFilter === 'yes') return r.game_response === 'yes';
                    if (listFilter === 'no')  return r.game_response === 'no';
                    if (listFilter === 'opted_out') return optOutIds.has(r.member_id);
                    if (listFilter === 'special') return Array.isArray(r.special_requests) && r.special_requests.length > 0;
                    return false;
                  });
                  return (
                    <>
                      {showResponders && filteredResponses.map(r => (
                        <div key={r.id} className="list-item">
                          <div className="avatar">{r.display_name?.charAt(0)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>
                              {r.display_name}
                              {optOutIds.has(r.member_id) && <span style={{ marginLeft:6, fontSize:'0.65rem', background:'#dcfce7', color:'#16a34a', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>OPTED OUT</span>}
                            </div>
                            {Array.isArray(r.special_requests) && r.special_requests.length > 0 && (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:3 }}>
                                {r.special_requests.map(sr => {
                                  const extra = sr.input_type && sr.input_type !== 'none' ? (sr.admin_input || sr.member_input) : null;
                                  const suffix = extra ? (sr.condition_code === 'leaving_early' ? ` @ ${extra}` : ` (${extra})`) : '';
                                  return (
                                    <span key={sr.id || sr.condition_id} style={{
                                      fontSize:'0.65rem', fontWeight:600, padding:'2px 7px', borderRadius:10,
                                      background: sr.is_standby ? '#fef3c7' : '#eff6ff',
                                      color:      sr.is_standby ? '#92400e' : '#1e40af',
                                      border:     sr.is_standby ? '1px solid #fbbf24' : '1px solid #bfdbfe',
                                    }}>
                                      {sr.is_standby ? '🕐 ' : ''}{sr.label}{suffix}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className={`badge ${r.game_response === 'yes' ? 'badge-success' : 'badge-danger'}`}>{r.game_response?.toUpperCase()}</span>
                            {r.lunch_response && <div style={{ fontSize: '0.7rem', color: 'var(--ucc-gray-500)' }}>Lunch: {r.lunch_response}</div>}
                            <button onClick={() => { setOverrideModal({ member_id: r.member_id, display_name: r.display_name }); setOverrideResponse(r.game_response === 'yes' ? 'no' : 'yes'); }}
                              style={{ fontSize: '0.65rem', padding: '2px 7px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', color: '#374151' }}>✏️</button>
                          </div>
                        </div>
                      ))}
                      {showNoReply && allMembers.filter(m => !responses.find(r => r.member_id === m.id)).map(m => (
                        <div key={m.id} className="list-item" style={{ opacity: 0.55 }}>
                          <div className="avatar" style={{ background: '#e5e7eb', color: '#9ca3af' }}>{m.display_name?.charAt(0)}</div>
                          <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: '#9ca3af' }}>{m.display_name}</div></div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '0.7rem', background: '#f3f4f6', color: '#9ca3af', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>—</span>
                            <button onClick={() => { setOverrideModal({ member_id: m.id, display_name: m.display_name }); setOverrideResponse('yes'); }}
                              style={{ fontSize: '0.65rem', padding: '2px 7px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', color: '#374151' }}>✏️</button>
                          </div>
                        </div>
                      ))}
                      {((showResponders && filteredResponses.length === 0 && !showNoReply) ||
                        (showNoReply && !showResponders && allMembers.filter(m => !responses.find(r => r.member_id === m.id)).length === 0)) && (
                        <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>No members in this category</div>
                      )}
                    </>
                  );
                })()}
              </div>

              {overrideModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                  <div style={{ background: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 360 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)', marginBottom: 4 }}>Override Availability</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 16 }}>{overrideModal.display_name}</div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                      {['yes','no'].map(v => (
                        <button key={v} onClick={() => setOverrideResponse(v)}
                          style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: `2px solid ${overrideResponse === v ? (v === 'yes' ? '#16a34a' : '#dc2626') : '#e5e7eb'}`,
                            background: overrideResponse === v ? (v === 'yes' ? '#dcfce7' : '#fee2e2') : 'white',
                            color: overrideResponse === v ? (v === 'yes' ? '#16a34a' : '#dc2626') : '#9ca3af',
                            fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}>
                          {v === 'yes' ? '✅ YES' : '❌ NO'}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={() => setOverrideModal(null)} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>Cancel</button>
                      <button onClick={submitAdminOverride} style={{ flex: 2, padding: '11px 0', borderRadius: 10, border: 'none', background: 'var(--navy)', color: 'white', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>Set Availability</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state__icon">📅</div>
              <div className="empty-state__title">No upcoming match scheduled</div>
              <div className="empty-state__text">Schedule a Sunday in Match Management first</div>
            </div>
          )}
        </div>
      )}

      {/* ── SERIES AVAILABILITY TAB ── */}
      {/* ── SERIES AVAILABILITY TAB ── */}
      {tab === 'series' && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10,
            padding:'10px 14px', marginBottom:12, fontSize:'0.75rem', color:'#1e40af', lineHeight:1.7 }}>
            <strong>One window covers the whole series.</strong><br />
            Members answer YES or NO for each fixture inside it. Set the window, the minimum
            number of matches and the squad size in <em>Series Management</em>, then announce.
          </div>

          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', marginBottom:8 }}>SELECT SERIES</div>
            <select className="form-control" value={selSeries}
              onChange={e => { setSelSeries(e.target.value); loadSeriesAvailability(e.target.value); }}>
              <option value="">Choose a series…</option>
              {seriesList.map(s => (
                <option key={s.id} value={s.id}>{s.name} — {String(s.status || '').toUpperCase()}</option>
              ))}
            </select>
          </div>

          {seriesLoading && <div style={{ textAlign:'center', padding:32, color:'var(--text-muted)' }}>Loading…</div>}

          {!seriesLoading && selSeries && seriesAvail && (
            <>
              {/* Window + eligibility summary */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:12 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <div style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.06em', color:'var(--text-muted)', textTransform:'uppercase' }}>Window</div>
                    <div style={{ fontSize:'0.8rem', color:'var(--navy)', fontWeight:600, marginTop:3 }}>
                      {seriesAvail.series?.avail_open_at
                        ? <>{fmtIst(seriesAvail.series.avail_open_at)} → {fmtIst(seriesAvail.series.avail_deadline)}</>
                        : <span style={{ color:'#b45309' }}>Not set — members cannot answer</span>}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.06em', color:'var(--text-muted)', textTransform:'uppercase' }}>Eligible</div>
                    <div style={{ fontSize:'0.8rem', color:'var(--navy)', fontWeight:600, marginTop:3 }}>
                      {seriesAvail.eligible_count} of {seriesAvail.members.length} members
                      <span style={{ color:'var(--text-muted)', fontWeight:400 }}>
                        {' '}· free for {seriesAvail.min_fixtures_required}+ match{seriesAvail.min_fixtures_required === 1 ? '' : 'es'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {seriesAvail.fixtures.length > 1 && (() => {
                const yesOf = (fid) => seriesAvail.members.filter(m => m.answers[fid] === 'yes').length;
                const ranked = [...seriesAvail.fixtures]
                  .map(f => ({ ...f, yes: yesOf(f.id) }))
                  .sort((a, b) => b.yes - a.yes || String(a.match_date).localeCompare(String(b.match_date)));
                const keep = keepDates || new Set(seriesAvail.fixtures.map(f => f.id));
                const toggle = (id) => { const n = new Set(keep); n.has(id) ? n.delete(id) : n.add(id); setKeepDates(n); };
                const dayLabel = (d) => new Date(d).toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' });
                const windowOpen = seriesAvail.series?.avail_deadline && new Date(seriesAvail.series.avail_deadline) > new Date();
                const dropList = seriesAvail.fixtures.filter(f => !keep.has(f.id)).map(f => dayLabel(f.match_date));
                return (
                  <div style={{ background:'white', border:'1.5px solid #C9940F', borderRadius:12, padding:14, marginBottom:12 }}>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1rem', color:'var(--navy)' }}>📅 Pick the final dates</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', margin:'2px 0 10px', lineHeight:1.5 }}>
                      Most YES first. Untick the Sundays you will not play; they are removed from the series with their replies.
                      {windowOpen && <span style={{ color:'#b45309', fontWeight:700 }}> The window is still open — replies may change.</span>}
                    </div>
                    {ranked.map((f, i) => (
                      <label key={f.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 4px',
                        borderTop: i ? '1px solid #f1f5f9' : 'none', cursor:'pointer', opacity: keep.has(f.id) ? 1 : 0.5 }}>
                        <input type="checkbox" checked={keep.has(f.id)} onChange={() => toggle(f.id)} />
                        <span style={{ flex:1, fontWeight:600, color:'var(--navy)', fontSize:'0.82rem' }}>
                          {dayLabel(f.match_date)} <span style={{ fontWeight:400, color:'var(--text-muted)' }}>· {f.venue || 'UCC Ground'}</span>
                        </span>
                        <span style={{ fontWeight:800, color:'#15803D', fontSize:'0.82rem' }}>{f.yes} YES</span>
                      </label>
                    ))}
                    <button disabled={finalBusy || keep.size === 0 || dropList.length === 0}
                      onClick={() => saveFinalDates([...keep], dropList)}
                      style={{ width:'100%', marginTop:10, padding:10, borderRadius:8, border:'none',
                        background: dropList.length ? 'var(--navy)' : '#E5E7EB', color: dropList.length ? 'white' : '#6B7280',
                        fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.85rem', cursor: dropList.length ? 'pointer' : 'default' }}>
                      {finalBusy ? 'Saving…' : dropList.length
                        ? `Keep ${keep.size} date${keep.size === 1 ? '' : 's'} · drop ${dropList.length}`
                        : 'Untick the dates you will not play'}
                    </button>
                  </div>
                );
              })()}

              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                <button onClick={exportSeriesAvailabilityCsv}
                  style={{ flex:1, padding:9, borderRadius:8, border:'1px solid var(--navy)',
                    background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif',
                    fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                  ⬇ Download (Excel / CSV)
                </button>
                <button onClick={downloadSeriesAvailabilityPdf} disabled={pdfBusy}
                  style={{ flex:1, padding:9, borderRadius:8, border:'1px solid var(--navy)',
                    background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif',
                    fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                  {pdfBusy ? 'Building…' : '📄 Download PDF'}
                </button>
              </div>

              {/* Member × fixture grid */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      <th style={{ textAlign:'left', padding:'9px 12px', position:'sticky', left:0, background:'#f8fafc',
                        fontSize:'0.65rem', letterSpacing:'0.06em', textTransform:'uppercase', color:'var(--text-muted)' }}>Member</th>
                      {seriesAvail.fixtures.map((f, i) => (
                        <th key={f.id} style={{ padding:'9px 6px', minWidth:64, fontSize:'0.65rem', color:'var(--text-muted)' }}>
                          <div style={{ fontWeight:800, color:'var(--navy)' }}>M{i + 1}</div>
                          <div>{new Date(f.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}</div>
                          <div style={{ fontSize:'0.6rem' }}>{f.venue || 'UCC'}</div>
                        </th>
                      ))}
                      <th style={{ padding:'9px 8px', fontSize:'0.65rem', color:'var(--text-muted)' }}>YES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seriesAvail.members.map(m => (
                      <tr key={m.id} style={{ borderTop:'1px solid #f1f5f9', opacity: m.eligible ? 1 : 0.5 }}>
                        <td style={{ padding:'8px 12px', position:'sticky', left:0, background:'white',
                          fontWeight:600, color:'var(--navy)', whiteSpace:'nowrap' }}>
                          {m.display_name}
                          {!m.eligible && m.yes_count > 0 && (
                            <span style={{ fontSize:'0.62rem', color:'#b45309', marginLeft:6 }}>below minimum</span>
                          )}
                        </td>
                        {seriesAvail.fixtures.map(f => {
                          const away = (m.away_fixture_ids || []).includes(f.id);
                          const a = m.answers[f.id];
                          return (
                            <td key={f.id} style={{ textAlign:'center', padding:'8px 6px' }}>
                              <span title={away ? 'On sabbatical — not asked' : undefined} style={{
                                display:'inline-block', minWidth:26, padding:'2px 6px', borderRadius:8,
                                fontSize:'0.66rem', fontWeight:800,
                                background: away ? '#ede9fe' : a === 'yes' ? '#dcfce7' : a === 'no' ? '#fee2e2' : '#f1f5f9',
                                color:      away ? '#6d28d9' : a === 'yes' ? '#166534' : a === 'no' ? '#991b1b' : '#94a3b8',
                              }}>{away ? 'S' : a === 'yes' ? 'Y' : a === 'no' ? 'N' : '—'}</span>
                            </td>
                          );
                        })}
                        <td style={{ textAlign:'center', padding:'8px 8px', fontWeight:800,
                          color: m.eligible ? '#166534' : 'var(--text-muted)' }}>
                          {m.yes_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:8, lineHeight:1.6 }}>
                Y = available · N = not available · — = no reply · S = on sabbatical, not asked.
                Faded rows are below the minimum and are not considered for the series.
                {seriesAvail.excluded_on_sabbatical > 0 && (
                  <> {seriesAvail.excluded_on_sabbatical} member{seriesAvail.excluded_on_sabbatical === 1 ? ' is' : 's are'} on
                  sabbatical for the whole series and {seriesAvail.excluded_on_sabbatical === 1 ? 'is' : 'are'} not listed.</>
                )}
              </div>
            </>
          )}

          {!seriesLoading && selSeries && !seriesAvail && (
            <div className="empty-state">
              <div className="empty-state__title">Nothing to show</div>
              <div className="empty-state__text">Add fixtures to this series first.</div>
            </div>
          )}
        </div>
      )}

      {/* ── RESTING HISTORY TAB ── */}
      {tab === 'resting' && (
        <div style={{ padding: '16px' }}>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.75rem', color: '#1e40af', lineHeight: 1.7 }}>
            <strong>Resting history across all regular matches.</strong><br />
            Members with fewer rests are prioritised for rotation. Admins exempt from resting.
          </div>

          {restingLoading && <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Loading…</div>}

          {!restingLoading && restingMembers.length === 0 && (
            <div className="empty-state">
              <div className="empty-state__icon">💤</div>
              <div className="empty-state__title">No resting records yet</div>
              <div className="empty-state__text">Records will appear after Admin marks members as rested</div>
            </div>
          )}

          {!restingLoading && restingMembers.length > 0 && (
            <div className="card">
              {/* Header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 120px', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Member</div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>Times</div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Rested</div>
              </div>
              {restingMembers.map((m, i) => {
                const lastDate = m.last_rested_date
                  ? new Date(m.last_rested_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '— Never';
                const rests = parseInt(m.total_rests) || 0;
                const isLeast = i < 3 && rests === parseInt(restingMembers[0]?.total_rests || 0) + 0; // highlight top 3 least rested
                return (
                  <div key={m.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 60px 120px', gap: 8,
                    padding: '11px 14px', borderBottom: '1px solid #f1f5f9',
                    background: rests === 0 ? '#f0fdf4' : 'white',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                        {m.display_name?.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--navy)' }}>{m.display_name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{m.ucc_id}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{
                        fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem',
                        color: rests === 0 ? 'var(--success)' : rests >= 3 ? 'var(--danger)' : 'var(--navy)',
                      }}>{rests}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: rests === 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                      {lastDate}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminCelebrations() {
  const [members, setMembers] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduledNotices, setScheduledNotices] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);
  const [form, setForm] = useState({
    celebration_type: 'achievement', // achievement | motm | team_win | custom
    notice_type: 'birthday',
    title: '',
    body: '',
    target: 'all',         // all | multiple | member
    target_member_id: '',
    target_member_ids: [], // for multiple
    match_id: '',
    theme: 'dark',
    custom_image: '', // URL to custom background image
    schedule_type: 'now',  // now | scheduled
    scheduled_date: '',
    scheduled_time: '00:00',
    duration: '48',        // hours
  });

  const CELEBRATION_TYPES = [
    { value: 'achievement',  label: '🏆 Achievement',    icon: '🏆', desc: 'Century, fifty, milestone' },
    { value: 'motm',         label: '⭐ Match Awards',   icon: '⭐', desc: 'MOTM, Best Bowler etc' },
    { value: 'team_win',     label: '🎉 Team Win',       icon: '🎉', desc: 'Series/match victory' },
    { value: 'custom',       label: '✨ Custom',          icon: '✨', desc: 'Any custom celebration' },
  ];

  const ACHIEVEMENT_TEMPLATES = [
    { label: 'Century 🏏',      title: '{name} scores a Century!',      body: 'What a magnificent innings! {name} has scored a brilliant century for UCC. A fantastic achievement — congratulations from the entire team!' },
    { label: 'Half Century',    title: '{name} hits a Fifty!',          body: 'A splendid fifty from {name} today! A well-crafted innings that made a real difference. Congratulations from the UCC family!' },
    { label: '5-wicket haul',   title: '{name} takes a Five-fer!',      body: "Outstanding bowling from {name}! A five-wicket haul is one of cricket's finest individual achievements. Well done from everyone at UCC!" },
    { label: 'Hat-trick',       title: '{name} takes a Hat-trick!',     body: 'Incredible! {name} has taken a hat-trick — three wickets in three balls! A moment that will be remembered for a long time. Congratulations!' },
    { label: 'Best Catch',      title: 'Outstanding catch by {name}!', body: 'What a catch! {name} pulled off an absolute stunner in the field today. Brilliant reflexes and commitment. Well done!' },
  ];

  const MOTM_CATEGORIES = [
    { value: 'motm',          label: '🏅 Man of the Match' },
    { value: 'best_batsman',  label: '🏏 Best Batsman' },
    { value: 'best_bowler',   label: '🎳 Best Bowler' },
    { value: 'best_fielder',  label: '🧤 Best Fielder' },
    { value: 'best_allrounder', label: '⚡ Best All-Rounder' },
  ];

  useEffect(() => {
    Promise.all([
      api.get('/members'),
      api.get('/matches?limit=5'),
    ]).then(([mRes, matchRes]) => {
      setMembers((mRes.data.members || []).filter(m => !m.is_observer));
      setRecentMatches(matchRes.data.matches || []);
    }).finally(() => setLoading(false));
    loadScheduled();
  }, []);

  function applyTemplate(tpl) {
    const member = members.find(m => m.id === form.target_member_id);
    const name = member?.display_name || '{name}';
    setForm(f => ({
      ...f,
      title: tpl.title.replace(/\{name\}/g, name),
      body: tpl.body.replace(/\{name\}/g, name),
    }));
  }

  function buildMotmBody(category, memberName) {
    const cat = MOTM_CATEGORIES.find(c => c.value === category);
    const label = cat?.label || 'Award';
    const match = recentMatches.find(m => m.id === form.match_id);
    const matchInfo = match ? ` in the match on ${new Date(match.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}` : '';
    return `Congratulations to ${memberName} for winning the ${label} award${matchInfo}! A standout performance that made a real difference. Well done from the entire UCC family!`;
  }

  async function loadScheduled() {
    setLoadingScheduled(true);
    try {
      const res = await api.get('/notices/scheduled');
      setScheduledNotices(res.data.notices || []);
    } catch { setScheduledNotices([]); }
    finally { setLoadingScheduled(false); }
  }

  async function deleteScheduled(id) {
    if (!window.confirm('Cancel this scheduled notice?')) return;
    try {
      await api.delete('/notices/' + id);
      loadScheduled();
    } catch { alert('Failed to cancel'); }
  }

  async function post() {
    if (!form.title.trim() || !form.body.trim()) return alert('Title and message required');
    if (form.target === 'member' && !form.target_member_id) return alert('Select a member');
    if (form.target === 'multiple' && form.target_member_ids.length === 0) return alert('Select at least one member');

    // Calculate timing
    let startTime;
    if (form.schedule_type === 'scheduled' && form.scheduled_date) {
      startTime = new Date(form.scheduled_date + 'T' + (form.scheduled_time || '00:00') + ':00');
    } else {
      startTime = new Date();
    }
    const durationHours = parseInt(form.duration) || 48;
    const expires = new Date(startTime.getTime() + durationHours * 60 * 60 * 1000);

    const delayMs = startTime.getTime() - Date.now();

    async function doPost() {
      function fillName(text, name) { return text.replace(/\{name\}/g, name || ''); }
      // Set notice_type based on celebration content
      const titleLower = (form.title || '').toLowerCase();
      const isAnniversaryPost = titleLower.includes('anniversary') || form.celebration_type === 'anniversary';
      const noticeType = isAnniversaryPost ? 'anniversary' : 'birthday';
      const overlayTheme = form.theme || 'light';

      if (form.target === 'multiple') {
        for (const memberId of form.target_member_ids) {
          const mName = members.find(m => m.id === memberId)?.display_name || '';
          await api.post('/notices', {
            notice_type: noticeType,
            title: fillName(form.title, mName),
            body: fillName(form.body, mName),
            target_member_id: null,
            include_observers: true,
            expires_at: expires,
            is_pinned: false,
            file_name: overlayTheme,
            scheduled_at: delayMs > 5000 ? startTime.toISOString() : null,
          });
        }
      } else {
        const singleName = form.target === 'member'
          ? (members.find(m => m.id === form.target_member_id)?.display_name || '')
          : '';
        await api.post('/notices', {
          notice_type: noticeType,
          title: fillName(form.title, singleName),
          body: fillName(form.body, singleName),
          target_member_id: null,
          include_observers: true,
          expires_at: expires,
          is_pinned: false,
          file_name: overlayTheme,
          scheduled_at: delayMs > 5000 ? startTime.toISOString() : null,
        });
      }
    }

    // Confirmation
    const confirmMsg = delayMs > 5000
      ? `Schedule this celebration for ${startTime.toLocaleDateString('en-IN', { day:'numeric', month:'short' })} at ${startTime.toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'})}?`
      : 'Post this celebration now? Members will see it immediately.';
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    try {
      await doPost();
      const schedMsg = delayMs > 5000
        ? ' Scheduled for ' + startTime.toLocaleDateString('en-IN', { day:'numeric', month:'short' }) + ' at ' + startTime.toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'}) + '. The server will publish it automatically.'
        : ' Members will see it when they open the app.';
      alert('✅ Celebration posted!' + schedMsg);
      setForm(f => ({ ...f, title: '', body: '', target_member_id: '', target_member_ids: [], match_id: '', schedule_type: 'now', scheduled_date: '', duration: '48', custom_image: '' }));
      loadScheduled();
    } catch (err) { alert(err.response?.data?.error || 'Failed to post'); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const selectedMember = members.find(m => m.id === form.target_member_id);

  return (
    <div>
      <AdminPageHeader title="Celebrations" icon="🎊" />
      <div style={{ padding:'16px' }}>

        {/* Scheduled Notices */}
        {(loadingScheduled ? false : scheduledNotices.length > 0) && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.75rem',
              textTransform:'uppercase', letterSpacing:'0.08em', color:'#1E3A6E', marginBottom:8 }}>
              🗓 Scheduled ({scheduledNotices.length})
            </div>
            {scheduledNotices.map(n => (
              <div key={n.id} style={{ background:'#eff6ff', border:'1px solid #bfdbfe',
                borderRadius:10, padding:'10px 14px', marginBottom:6,
                display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'0.85rem', color:'#1E3A6E',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>
                    🕐 {new Date(n.scheduled_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })} at {new Date(n.scheduled_at).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
                    {' · '}expires {new Date(n.expires_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                  </div>
                </div>
                <button onClick={() => deleteScheduled(n.id)}
                  style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #fca5a5',
                    background:'#fff1f2', color:'#dc2626', fontSize:'0.72rem',
                    fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                  ✕ Cancel
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Type selector */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
          {CELEBRATION_TYPES.map(t => (
            <button key={t.value} onClick={() => setForm(f => ({
              ...f,
              celebration_type: t.value,
              title:'', body:'',
              // Auto-set sensible defaults per type
              target: t.value === 'custom' ? f.target : 'all',
              target_member_id: '',
              target_member_ids: [],
            }))}
              style={{ padding:'12px 8px', borderRadius:12, border:'2px solid',
                borderColor: form.celebration_type === t.value ? '#F5C518' : '#e5e7eb',
                background: form.celebration_type === t.value ? '#fefce8' : 'white',
                cursor:'pointer', textAlign:'center' }}>
              <div style={{ fontSize:'1.4rem', marginBottom:4 }}>{t.icon}</div>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#1E3A6E' }}>{t.label}</div>
              <div style={{ fontSize:'0.65rem', color:'#6b7280' }}>{t.desc}</div>
            </button>
          ))}
        </div>

        {/* Quick Templates */}
        {(form.celebration_type === 'achievement' || form.celebration_type === 'team_win' || form.celebration_type === 'custom') && (
          <div className="card mb-md">
            <div className="card__body">
              <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:10 }}>
                Quick Templates
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {form.celebration_type === 'achievement' && ACHIEVEMENT_TEMPLATES.map(t => (
                  <button key={t.label} onClick={() => applyTemplate(t)}
                    style={{ padding:'6px 12px', borderRadius:20, border:'1px solid #F5C518',
                      background:'#fefce8', color:'#92400e', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                    {t.label}
                  </button>
                ))}
                {form.celebration_type === 'team_win' && [
                  { label:'Series Win 🏆', title:'🏆 UCC wins the {series}!', body:'What a season! The entire UCC squad has won the {series} — a result built on teamwork, heart, and the spirit of this great club. Congratulations to every single player and the entire UCC family!' },
                  { label:'Match Win 🎉', title:'🎉 UCC wins the match!', body:'Another brilliant victory for United Cricket Club! A fantastic team effort from start to finish. Every player contributed and the result is a testament to the quality and character of this squad. Well done, UCC!' },
                  { label:'Unbeaten Season 🌟', title:'🌟 UCC — Unbeaten this season!', body:'An unbeaten season — what an extraordinary achievement! United Cricket Club has shown the rest what this club is made of. This belongs to every player, every supporter, and every member of the UCC family. Champions!' },
                ].map(t => (
                  <button key={t.label} onClick={() => setForm(f => ({ ...f, title: t.title, body: t.body }))}
                    style={{ padding:'6px 12px', borderRadius:20, border:'1px solid #16a34a',
                      background:'#f0fdf4', color:'#166534', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                    {t.label}
                  </button>
                ))}
                {form.celebration_type === 'custom' && [
                  { label:'Welcome New Member 👋', title:'👋 Welcome to UCC, {name}!', body:'United Cricket Club is delighted to welcome {name} to the family! We look forward to many great Sundays together. Welcome aboard!' },
                  { label:'Farewell 🙏', title:'🙏 Thank you, {name}!', body:'As {name} moves on to new chapters, United Cricket Club thanks them for everything they have given to this club. The memories, the camaraderie, and the cricket will always be cherished. All the best!' },
                  { label:'Club Anniversary 🎊', title:'🎊 UCC Celebrates another year!', body:"Another year of United Cricket Club — and what a journey it has been! Thank you to every member, past and present, who has made this club what it is. Here's to many more years of great cricket and great friendships!" },
                ].map(t => (
                  <button key={t.label} onClick={() => setForm(f => ({ ...f, title: t.title, body: t.body }))}
                    style={{ padding:'6px 12px', borderRadius:20, border:'1px solid #7c3aed',
                      background:'#f5f3ff', color:'#5b21b6', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MOTM category picker */}
        {form.celebration_type === 'motm' && (
          <div className="card mb-md">
            <div className="card__body">
              <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:10 }}>
                Award Category
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                {MOTM_CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => {
                    const member = members.find(m => m.id === form.target_member_id);
                    const name = member?.display_name || '';
                    setForm(f => ({
                      ...f,
                      title: `${c.label} — ${name || 'Select member below'}`,
                      body: name ? buildMotmBody(c.value, name) : f.body,
                    }));
                  }}
                    style={{ padding:'7px 12px', borderRadius:20, border:'1px solid #e5e7eb',
                      background:'white', color:'#374151', fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Match</label>
                <select className="form-control" value={form.match_id}
                  onChange={e => setForm(f => ({ ...f, match_id: e.target.value }))}>
                  <option value="">Select match…</option>
                  {recentMatches.map(m => (
                    <option key={m.id} value={m.id}>
                      {new Date(m.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Who is being celebrated */}
        <div className="card mb-md">
          <div className="card__body">
            <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:4 }}>
              Who is being celebrated?
            </div>
            <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginBottom:10 }}>
              The celebration will be shown to <strong>all members</strong>. Select who the celebration is about.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom: form.target !== 'all' ? 12 : 0 }}>
              {[
                { v:'all',      l:'🏏 Whole Team', sub:'No specific person' },
                { v:'multiple', l:'👥 Multiple',   sub:'Select players' },
                { v:'member',   l:'👤 One Player', sub:'Specific member' },
              ].map(opt => (
                <button key={opt.v} onClick={() => setForm(f => ({ ...f, target: opt.v, target_member_id:'', target_member_ids:[] }))}
                  style={{ padding:'10px 4px', borderRadius:10, border:'2px solid',
                    borderColor: form.target === opt.v ? '#1E3A6E' : '#e5e7eb',
                    background: form.target === opt.v ? '#eff6ff' : 'white',
                    color: form.target === opt.v ? '#1E3A6E' : '#374151',
                    fontWeight:700, fontSize:'0.72rem', cursor:'pointer', textAlign:'center' }}>
                  <div>{opt.l}</div>
                  <div style={{ fontSize:'0.62rem', fontWeight:400, color:'#9ca3af', marginTop:2 }}>{opt.sub}</div>
                </button>
              ))}
            </div>

            {form.target === 'member' && (
              <select className="form-control" value={form.target_member_id}
                onChange={e => setForm(f => ({ ...f, target_member_id: e.target.value }))}>
                <option value="">Select member being celebrated…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
              </select>
            )}

            {form.target === 'multiple' && (
              <div>
                <div style={{ fontSize:'0.72rem', color:'#6b7280', marginBottom:8 }}>
                  Tap to select members being celebrated
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:200, overflowY:'auto', padding:4 }}>
                  {members.map(m => {
                    const selected = form.target_member_ids.includes(m.id);
                    return (
                      <button key={m.id} onClick={() => setForm(f => ({
                        ...f,
                        target_member_ids: selected
                          ? f.target_member_ids.filter(id => id !== m.id)
                          : [...f.target_member_ids, m.id]
                      }))}
                        style={{ padding:'5px 10px', borderRadius:20, border:'1.5px solid',
                          borderColor: selected ? '#1E3A6E' : '#e5e7eb',
                          background: selected ? '#1E3A6E' : 'white',
                          color: selected ? 'white' : '#374151',
                          fontSize:'0.72rem', fontWeight:600, cursor:'pointer' }}>
                        {selected ? '✓ ' : ''}{m.display_name}
                      </button>
                    );
                  })}
                </div>
                {form.target_member_ids.length > 0 && (
                  <div style={{ marginTop:8, fontSize:'0.72rem', color:'#1E3A6E', fontWeight:600 }}>
                    {form.target_member_ids.length} member{form.target_member_ids.length > 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Title + Message */}
        <div className="card mb-md">
          <div className="card__body">
            <div className="form-group">
              <label className="form-label">Celebration Title *</label>
              <input type="text" className="form-control"
                placeholder="e.g. {name} scores a Century!"
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              {form.title.includes('{name}') && (
                <div style={{ fontSize:'0.7rem', color:'#d97706', marginTop:4 }}>
                  ℹ️ <strong>{'{name}'}</strong> will be replaced with the member's name when posted
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Message *</label>
              <textarea className="form-control" rows={4}
                placeholder="Write the celebration message…"
                value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* Preview */}
        {form.title && form.body && (() => {
          const isDark = (form.theme || 'light') === 'dark';
          const isCustom = form.theme === 'custom';
          const previewBg = isCustom && form.custom_image
            ? `url(${form.custom_image}) center/cover`
            : isDark
              ? 'linear-gradient(135deg, #0f1729, #1a2540)'
              : 'linear-gradient(135deg, #f8fafc, #e8f0fe)';
          const titleColor = isDark ? 'white' : '#1E3A6E';
          const bodyColor = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(30,58,110,0.75)';
          const labelColor = '#F5C518';
          return (
          <div style={{ background: previewBg, borderRadius:16,
            padding:'20px', marginBottom:16, textAlign:'center',
            border: isDark ? '1px solid #F5C51840' : '1px solid #1E3A6E30' }}>
            <div style={{ fontSize:'0.65rem', fontWeight:700, color: labelColor, letterSpacing:'0.15em',
              textTransform:'uppercase', marginBottom:8 }}>Preview</div>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.3rem',
              color: titleColor, marginBottom:8 }}>{form.title}</div>
            <div style={{ fontSize:'0.82rem', color: bodyColor, fontStyle:'italic', lineHeight:1.6 }}>
              "{form.body}"
            </div>
            {form.target === 'member' && selectedMember && (
              <div style={{ marginTop:8, fontSize:'0.7rem', color:'#F5C518' }}>
                🎉 Celebrating: {selectedMember.display_name} · Shown to all members
              </div>
            )}
            {form.target === 'multiple' && form.target_member_ids.length > 0 && (
              <div style={{ marginTop:8, fontSize:'0.7rem', color:'#F5C518' }}>
                🎉 Celebrating: {form.target_member_ids.map(id => members.find(m=>m.id===id)?.display_name).filter(Boolean).join(', ')} · Shown to all members
              </div>
            )}
            {form.target === 'all' && (
              <div style={{ marginTop:8, fontSize:'0.7rem', color:'#F5C518' }}>
                🎉 Shown to all members
              </div>
            )}
          </div>
          );
        })()}

        {/* Schedule */}
        <div className="card mb-md">
          <div className="card__body">
            <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:10 }}>
              When to Post
            </div>
            <div style={{ display:'flex', gap:8, marginBottom: form.schedule_type === 'scheduled' ? 12 : 0 }}>
              {[{v:'now',l:'⚡ Post Now'},{v:'scheduled',l:'🗓 Schedule'}].map(opt => (
                <button key={opt.v} onClick={() => setForm(f => ({ ...f, schedule_type: opt.v }))}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'2px solid',
                    borderColor: form.schedule_type === opt.v ? '#1E3A6E' : '#e5e7eb',
                    background: form.schedule_type === opt.v ? '#eff6ff' : 'white',
                    color: form.schedule_type === opt.v ? '#1E3A6E' : '#374151',
                    fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>
                  {opt.l}
                </button>
              ))}
            </div>
            {form.schedule_type === 'scheduled' && (
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ flex:2 }}>
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control"
                    min={new Date().toISOString().split('T')[0]}
                    value={form.scheduled_date}
                    onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
                </div>
                <div style={{ flex:1 }}>
                  <label className="form-label">Time</label>
                  <input type="time" className="form-control"
                    value={form.scheduled_time}
                    onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Duration */}
        <div className="card mb-md">
          <div className="card__body">
            <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:10 }}>
              How Long to Show
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
              {[
                { v:'6',   l:'6 hrs' },
                { v:'12',  l:'12 hrs' },
                { v:'24',  l:'1 day' },
                { v:'48',  l:'2 days' },
                { v:'72',  l:'3 days' },
                { v:'168', l:'1 week' },
                { v:'336', l:'2 weeks' },
                { v:'0',   l:'Permanent' },
              ].map(opt => (
                <button key={opt.v} onClick={() => setForm(f => ({ ...f, duration: opt.v }))}
                  style={{ padding:'8px 4px', borderRadius:8, border:'2px solid',
                    borderColor: form.duration === opt.v ? '#1E3A6E' : '#e5e7eb',
                    background: form.duration === opt.v ? '#eff6ff' : 'white',
                    color: form.duration === opt.v ? '#1E3A6E' : '#374151',
                    fontWeight:700, fontSize:'0.7rem', cursor:'pointer', textAlign:'center' }}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Theme */}
        <div className="card mb-md">
          <div className="card__body">
            <div style={{ fontWeight:700, fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.08em', color:'#6b7280', marginBottom:10 }}>
              Overlay Theme
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom: form.theme === 'custom' ? 12 : 0 }}>
              <button onClick={() => setForm(f => ({ ...f, theme: 'dark', custom_image: '' }))}
                style={{ padding:'12px 6px', borderRadius:12, border:'2px solid',
                  borderColor: form.theme === 'dark' ? '#1E3A6E' : '#e5e7eb',
                  background: form.theme === 'dark' ? '#0f1729' : 'white',
                  cursor:'pointer', textAlign:'center' }}>
                <div style={{ fontSize:'1.2rem', marginBottom:4 }}>🌙</div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.72rem',
                  color: form.theme === 'dark' ? '#F5C518' : '#374151' }}>Dark</div>
                <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>Navy + Gold</div>
              </button>
              <button onClick={() => setForm(f => ({ ...f, theme: 'light', custom_image: '' }))}
                style={{ padding:'12px 6px', borderRadius:12, border:'2px solid',
                  borderColor: form.theme === 'light' ? '#1E3A6E' : '#e5e7eb',
                  background: form.theme === 'light' ? '#f8fafc' : 'white',
                  cursor:'pointer', textAlign:'center' }}>
                <div style={{ fontSize:'1.2rem', marginBottom:4 }}>☀️</div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.72rem', color:'#374151' }}>Light</div>
                <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>White + Gold</div>
              </button>
              <button onClick={() => setForm(f => ({ ...f, theme: 'custom' }))}
                style={{ padding:'12px 6px', borderRadius:12, border:'2px solid',
                  borderColor: form.theme === 'custom' ? '#7c3aed' : '#e5e7eb',
                  background: form.theme === 'custom' ? '#f5f3ff' : 'white',
                  cursor:'pointer', textAlign:'center' }}>
                <div style={{ fontSize:'1.2rem', marginBottom:4 }}>🖼️</div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.72rem',
                  color: form.theme === 'custom' ? '#7c3aed' : '#374151' }}>Custom</div>
                <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>Your image</div>
              </button>
            </div>

            {form.theme === 'custom' && (
              <div>
                <label className="form-label">Image URL</label>
                <input type="url" className="form-control" style={{ marginBottom:8 }}
                  placeholder="https://i.imgur.com/... or any image link"
                  value={form.custom_image}
                  onChange={e => setForm(f => ({ ...f, custom_image: e.target.value }))} />
                {form.custom_image && (
                  <img src={form.custom_image} alt="Preview"
                    style={{ width:'100%', height:120, objectFit:'cover', borderRadius:8, marginBottom:8 }}
                    onError={e => { e.target.style.display='none'; }} />
                )}
                <div style={{ background:'#f0fdf4', borderRadius:8, padding:'8px 10px', fontSize:'0.72rem', color:'#16a34a' }}>
                  <strong>Recommended:</strong> Upload your image to <strong>imgur.com</strong> (free) → right-click → Copy image address → paste above.<br/>
                  Best size: <strong>800×600px or wider</strong>. JPG, PNG or GIF all work.
                </div>
              </div>
            )}
          </div>
        </div>

        <button className="btn btn-gold btn-block" onClick={post} disabled={saving}>
          {saving ? 'Posting…' : form.schedule_type === 'scheduled' && form.scheduled_date
            ? `🗓 Schedule for ${new Date(form.scheduled_date + 'T' + form.scheduled_time).toLocaleDateString('en-IN', { day:'numeric', month:'short' })} at ${form.scheduled_time}`
            : '🎊 Post Celebration Now'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// REPLACEMENT BLOCK v3 — for src/pages/admin/AdminPages.js
// Replaces lines 4270–5333 (everything from v2 banner down to MatchCaptainRow).
// MatchCaptainRow (line 5334+) stays intact.
//
// NEW since v2:
//  • Wheel time gate — only visible during spin window
//    (30 min before reporting_time until match import)
//  • Wheel tab auto-polls /wheel-candidates every 30s while visible
//  • Wheel tab auto-switches to Pool when closed (no more orphan tab)
//  • Super admins eligible to be added to pool
// ════════════════════════════════════════════════════════════════

// ─── Helpers ────────────────────────────────────────────────
function nextSundayISO(afterDate = new Date()) {
  const d = new Date(afterDate);
  const diff = (7 - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function isoDate(input) {
  if (!input) return null;
  const d = new Date(input);
  if (isNaN(d)) return null;
  return d.toISOString().slice(0, 10);
}

// ─── Sound FX (Web Audio, zero deps) ────────────────────────
function useSoundFX() {
  const ctxRef = React.useRef(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    return ctxRef.current;
  };

  const tick = () => {
    const ctx = getCtx(); if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = 1200;
    g.gain.setValueAtTime(0.06, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.04);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.05);
  };

  const fanfare = () => {
    const ctx = getCtx(); if (!ctx) return;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'triangle';
      o.frequency.value = f;
      const start = ctx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.linearRampToValueAtTime(0.18, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(start);
      o.stop(start + 0.36);
    });
  };

  const click = () => {
    const ctx = getCtx(); if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 600;
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.09);
  };

  return { tick, fanfare, click };
}

// ─── Confetti burst (canvas, zero deps) ─────────────────────
function fireConfetti() {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999';
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  const colors = ['#F5C518', '#1E3A6E', '#dc2626', '#16a34a', '#7c3aed', '#2563eb', '#f97316'];
  const pieces = [];
  const cx = canvas.width / 2;
  const cy = canvas.height / 3;
  for (let i = 0; i < 150; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 4 + Math.random() * 8;
    pieces.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 3,
      w: 6 + Math.random() * 6,
      h: 10 + Math.random() * 8,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 0,
    });
  }
  const start = performance.now();
  function frame(now) {
    const t = now - start;
    if (t > 3000) {
      document.body.removeChild(canvas);
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const p of pieces) {
      p.vy += 0.22;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      p.life++;
      const alpha = Math.max(0, 1 - p.life / 140);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
      ctx.restore();
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

// ─── Responsive wheel size hook ─────────────────────────────
function useWheelSize() {
  const [size, setSize] = React.useState(typeof window !== 'undefined' && window.innerWidth >= 768 ? 420 : 300);
  React.useEffect(() => {
    const onResize = () => setSize(window.innerWidth >= 768 ? 420 : Math.min(320, window.innerWidth - 48));
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

// ─── AdminCaptaincy ───────────────────────────────────────
export function AdminCaptaincy() {
  const { isSuperAdmin } = useAuth();
  const [tab, setTab] = useState('pool'); // default pool; flip to 'wheel' if window open

  const [matches, setMatches] = useState([]);
  const [members, setMembers] = useState([]);
  const [records, setRecords] = useState([]);
  const [motmRecords, setMotmRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  // Wheel
  const [wheelData, setWheelData] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [excludeContinuing, setExcludeContinuing] = useState(false);
  const [recentSpins, setRecentSpins] = useState([]);

  // Pool
  const [pool, setPool] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [addMemberId, setAddMemberId] = useState('');
  const [addMode, setAddMode] = useState('permanent');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [availableYears, setAvailableYears] = useState([2026]);
  const [dragIdx, setDragIdx] = useState(null);

  const sfx = useSoundFX();
  const wheelSize = useWheelSize();

  const wheelOpen = !!wheelData?.wheel_window?.open;

  function reloadRecords() {
    return Promise.all([
      api.get('/captaincy/records').catch(() => ({ data: { records: [] } })),
      api.get('/captaincy/motm').catch(() => ({ data: { records: [] } })),
    ]).then(([capRes, momRes]) => {
      setRecords(capRes.data.records || []);
      setMotmRecords(momRes.data.records || []);
    });
  }

  function reloadPool(year = selectedYear) {
    return Promise.all([
      api.get(`/captaincy/pool?year=${year}`).catch(() => ({ data: { pool: [] } })),
      api.get(`/captaincy/pool/available?year=${year}`).catch(() => ({ data: { members: [] } })),
    ]).then(([pRes, aRes]) => {
      setPool(pRes.data.pool || []);
      setAvailableMembers(aRes.data.members || []);
    });
  }

  function reloadWheel() {
    return api.get('/captaincy/wheel-candidates')
      .then(r => setWheelData(r.data))
      .catch(() => setWheelData(null));
  }

  function reloadSpins() {
    return api.get('/captaincy/spins?limit=5')
      .then(r => setRecentSpins(r.data.spins || []))
      .catch(() => setRecentSpins([]));
  }

  useEffect(() => {
    Promise.all([
      api.get('/matches?limit=100'),
      api.get('/members?active=true'),
      api.get('/captaincy/years').catch(() => ({ data: { years: [2026] } })),
      reloadRecords(),
      reloadPool(2026),
      reloadWheel(),
      reloadSpins(),
    ]).then(([mRes, memRes, yRes]) => {
      // Include CricHeroes-imported matches AND UCC Live-scored matches (scoring_mode === 'live')
      // Both can have captaincy records set.
      const imported = (mRes.data.matches || [])
        .filter(m => parseInt(m.import_count) > 0 || m.scoring_mode === 'live')
        .sort((a,b) => new Date(b.match_date) - new Date(a.match_date));
      setMatches(imported);
      setMembers(memRes.data.members || []);
      setAvailableYears(yRes.data.years || [2026]);
    }).catch(e => console.error('AdminCaptaincy load error:', e))
      .finally(() => setLoading(false));
  }, []);

  // Auto-switch to wheel tab when window opens
  useEffect(() => {
    if (!loading && wheelOpen && tab === 'pool') {
      setTab('wheel');
    }
  }, [wheelOpen, loading]);

  // Poll wheel every 30s while on wheel tab
  useEffect(() => {
    if (tab !== 'wheel') return;
    const id = setInterval(() => {
      reloadWheel();
      reloadSpins();
    }, 30000);
    return () => clearInterval(id);
  }, [tab]);

  // Reload pool when year changes
  useEffect(() => {
    if (!loading) reloadPool(selectedYear);
  }, [selectedYear]);

  function getRecord(matchId) { return records.find(r => r.match_id === matchId) || {}; }
  function getMOM(matchId) { return motmRecords.find(r => r.match_id === matchId) || {}; }

  async function saveMatch(matchId, capt1Id, capt2Id, winnerId, momId) {
    setSaving(matchId);
    try {
      await Promise.all([
        api.post('/captaincy/records', {
          match_id: matchId,
          capt1_id: capt1Id || null,
          capt2_id: capt2Id || null,
          winner_id: winnerId || null,
        }),
        momId ? api.post('/captaincy/motm', { match_id: matchId, winner1_id: momId }) : Promise.resolve(),
      ]);
      await Promise.all([reloadRecords(), reloadPool()]);
      toast.success('Saved! Pool updated.');
    } catch { toast.error('Save failed'); }
    finally { setSaving(null); }
  }

  // ─── WHEEL ACTIONS ──────────────────────────────────────
  async function handleSpin() {
    if (!wheelData?.match?.id) { toast.error('No upcoming match found'); return; }
    if (!wheelOpen) { toast.error(wheelData.wheel_window?.message || 'Wheel not open yet'); return; }
    setSpinning(true);
    setSpinResult(null);
    try {
      const res = await api.post('/captaincy/spin', {
        match_id: wheelData.match.id,
        exclude_continuing_captain: excludeContinuing,
      });
      const result = res.data;

      const segments = result.wheel_segments || [];
      const segCount = Math.max(segments.length, 1);
      const segAngle = 360 / segCount;
      const pickIdx = segments.findIndex(s => s.member_id === result.picks[0]?.member_id);
      const targetIdx = pickIdx >= 0 ? pickIdx : 0;
      const spins = 7;
      const landing = -(targetIdx * segAngle + segAngle / 2);
      const jitter = (Math.random() - 0.5) * (segAngle * 0.3);
      const finalRotation = spins * 360 + landing + jitter;

      setWheelRotation(finalRotation);

      const tickTimes = [];
      const totalDuration = 4000;
      let t = 60;
      while (t < totalDuration) {
        tickTimes.push(t);
        t += 60 + (t / totalDuration) * 400;
      }
      tickTimes.forEach(ms => setTimeout(() => sfx.tick(), ms));

      setTimeout(() => {
        setSpinResult(result);
        setSpinning(false);
        sfx.fanfare();
        fireConfetti();
        toast.success('🎯 Captains picked!');
      }, totalDuration + 200);
    } catch (err) {
      setSpinning(false);
      toast.error(err.response?.data?.error || 'Spin failed');
    }
  }

  async function handleConfirmSpin() {
    if (!spinResult || !wheelData?.match?.id) return;
    const capt1Id = spinResult.continuing_captain?.member_id || spinResult.picks[0]?.member_id;
    const capt2Id = spinResult.continuing_captain
      ? spinResult.picks[0]?.member_id
      : spinResult.picks[1]?.member_id;
    setSaving('spin');
    sfx.click();
    try {
      await api.post('/captaincy/records', {
        match_id: wheelData.match.id,
        capt1_id: capt1Id,
        capt2_id: capt2Id,
        selection_method: 'wagon_wheel',
        spin_id: spinResult.spin_id,
      });
      await Promise.all([reloadRecords(), reloadPool(), reloadWheel(), reloadSpins()]);
      setSpinResult(null);
      setWheelRotation(0);
      toast.success('Captains saved! Pool updated.');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(null);
    }
  }

  async function handleResetSpin() {
    // Delete unconfirmed spin server-side so it doesn't linger
    const spinId = spinResult?.spin_id;
    setSpinResult(null);
    setWheelRotation(0);
    if (spinId) {
      try {
        await api.delete('/captaincy/spins/' + spinId);
        await reloadSpins();
      } catch (e) {
        // Soft-fail — if the server delete fails, local state is already cleared
        console.error('Failed to delete unconfirmed spin:', e);
      }
    }
  }

  // Session 15 — Manual captain appointment
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualCapt1, setManualCapt1] = useState('');
  const [manualCapt2, setManualCapt2] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  function openManualAppointment() {
    setManualCapt1('');
    setManualCapt2('');
    setManualModalOpen(true);
  }

  async function submitManualAppointment() {
    if (!wheelData?.match?.id) { toast.error('No upcoming match'); return; }
    if (!manualCapt1 || !manualCapt2) { toast.error('Select both captains'); return; }
    if (manualCapt1 === manualCapt2) { toast.error('Captains must be different members'); return; }
    if (!window.confirm('Manually appoint these two members as captains?\n\nThis bypasses the wagon wheel and updates the pool.')) return;
    setManualSaving(true);
    try {
      // Delete any unconfirmed spin first — a manual appointment should clean it up
      if (spinResult?.spin_id) {
        try { await api.delete('/captaincy/spins/' + spinResult.spin_id); } catch {}
      }
      await api.post('/captaincy/records', {
        match_id: wheelData.match.id,
        capt1_id: manualCapt1,
        capt2_id: manualCapt2,
        selection_method: 'manual',
      });
      await Promise.all([reloadRecords(), reloadPool(), reloadWheel(), reloadSpins()]);
      setSpinResult(null);
      setWheelRotation(0);
      setManualModalOpen(false);
      toast.success('Captains appointed manually. Pool updated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Manual appointment failed');
    } finally {
      setManualSaving(false);
    }
  }

  // Session 15 — Reset a confirmed captaincy record (super admin only)
  async function handleResetRecord(matchId, record) {
    const name1 = members.find(m => m.id === record.capt1_id)?.display_name || 'Capt 1';
    const name2 = members.find(m => m.id === record.capt2_id)?.display_name || 'Capt 2';
    if (!window.confirm(
      `Reset captaincy for this match?\n\n` +
      `Current: ${name1} vs ${name2}\n\n` +
      `This deletes the record and un-flips the pool (both members return to "in pool"). ` +
      `You can then re-run the wheel or appoint new captains.`
    )) return;
    try {
      await api.post('/captaincy/records/' + matchId + '/reset');
      await Promise.all([reloadRecords(), reloadPool(), reloadWheel(), reloadSpins()]);
      toast.success('Captaincy record reset. Pool restored.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    }
  }

  // ─── POOL ACTIONS ───────────────────────────────────────
  async function handleAddMember() {
    if (!addMemberId) return;
    const body = { member_id: addMemberId, year: selectedYear, status: 'in_pool' };
    if (addMode === 'skip_next' && wheelData?.match?.match_date) {
      body.skip_until = isoDate(wheelData.match.match_date);
    } else if (addMode === 'skip_next') {
      body.skip_until = nextSundayISO();
    }
    try {
      await api.post('/captaincy/pool', body);
      setAddMemberId('');
      setAddMode('permanent');
      await reloadPool();
      await reloadWheel();
      toast.success(addMode === 'skip_next' ? 'Added (skipping next Sunday)' : 'Added to pool');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add');
    }
  }

  async function handleUpdateStatus(memberId, newStatus) {
    try {
      await api.patch(`/captaincy/pool/${memberId}`, { year: selectedYear, status: newStatus });
      await Promise.all([reloadPool(), reloadWheel()]);
      toast.success('Status updated');
    } catch { toast.error('Update failed'); }
  }

  async function handleToggleSkip(memberId, currentSkip) {
    try {
      if (currentSkip) {
        await api.patch(`/captaincy/pool/${memberId}`, { year: selectedYear, skip_until: null });
        toast.success('Skip cleared');
      } else {
        const skipDate = wheelData?.match?.match_date
          ? isoDate(wheelData.match.match_date)
          : nextSundayISO();
        await api.patch(`/captaincy/pool/${memberId}`, { year: selectedYear, skip_until: skipDate });
        toast.success('Will skip next Sunday');
      }
      await Promise.all([reloadPool(), reloadWheel()]);
    } catch { toast.error('Update failed'); }
  }

  async function handleRemoveMember(memberId, displayName) {
    if (!window.confirm(`Remove ${displayName} from the ${selectedYear} pool?`)) return;
    try {
      await api.delete(`/captaincy/pool/${memberId}?year=${selectedYear}`);
      await Promise.all([reloadPool(), reloadWheel()]);
      toast.success('Removed from pool');
    } catch { toast.error('Remove failed'); }
  }

  async function handleResetCycle() {
    try {
      const res = await api.post('/captaincy/pool/reset', { year: selectedYear });
      await Promise.all([reloadPool(), reloadWheel()]);
      setResetConfirm(false);
      toast.success(`Cycle reset — ${res.data.reset_count} members back in pool`);
    } catch { toast.error('Reset failed'); }
  }

  function handleDragStart(idx) { setDragIdx(idx); }
  function handleDragOver(e) { e.preventDefault(); }
  async function handleDrop(targetIdx) {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const newPool = [...pool];
    const [moved] = newPool.splice(dragIdx, 1);
    newPool.splice(targetIdx, 0, moved);
    setPool(newPool);
    setDragIdx(null);
    try {
      await api.patch('/captaincy/pool/reorder', {
        year: selectedYear,
        order: newPool.map((p, i) => ({ member_id: p.member_id, pool_position: i + 1 })),
      });
      await reloadPool();
    } catch {
      toast.error('Reorder failed');
      await reloadPool();
    }
  }

  const tabStyle = (t) => ({
    flex: '1 1 0', padding: '9px 4px', border: 'none',
    fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem',
    cursor: 'pointer', borderRadius: 8, textAlign: 'center',
    background: tab === t ? 'var(--navy)' : 'transparent',
    color: tab === t ? 'white' : 'var(--text-muted)', transition: 'all 0.15s',
  });

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  const setCount = matches.filter(m => !!getRecord(m.id).capt1_id).length;
  const total = matches.length;

  const visibleCandidates = wheelData?.candidates?.filter(
    c => !wheelData.continuing_captain || excludeContinuing || c.member_id !== wheelData.continuing_captain.winner_id
  ) || [];

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Captaincy" icon="🎖️">
        {wheelOpen ? '🟢 Wheel is LIVE — draw captains now' : 'Pool · Records'}
      </AdminPageHeader>

      <div style={{ display: 'flex', gap: 4, padding: '10px 10px 0', background: 'white', borderBottom: '1px solid var(--border)' }}>
        {wheelOpen && (
          <button style={tabStyle('wheel')} onClick={() => setTab('wheel')}>
            🎲 Wheel <span style={{ fontSize: '0.6rem', background: '#dc2626', color: 'white', padding: '1px 5px', borderRadius: 6, marginLeft: 3 }}>LIVE</span>
          </button>
        )}
        <button style={tabStyle('pool')} onClick={() => setTab('pool')}>👥 Pool ({pool.filter(p => p.status === 'in_pool').length})</button>
        <button style={tabStyle('records')} onClick={() => setTab('records')}>📝 Records ({setCount}/{total})</button>
      </div>

      {/* ═════════════════ WHEEL TAB ═════════════════ */}
      {tab === 'wheel' && wheelOpen && (
        <div style={{ padding: '16px', maxWidth: 720, margin: '0 auto' }}>
          {!wheelData?.match && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No upcoming regular Sunday match found.
            </div>
          )}

          {wheelData?.match && (
            <>
              <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '12px 16px', marginBottom: 14, color: 'white' }}>
                <div style={{ fontSize: '0.65rem', opacity: 0.6, marginBottom: 2, letterSpacing: '0.1em' }}>MATCH DAY</div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem' }}>
                  {new Date(wheelData.match.match_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div style={{ fontSize: '0.72rem', opacity: 0.7, marginTop: 2 }}>
                  Reporting {wheelData.match.reporting_time?.slice(0, 5) || '07:30'} · {wheelData.candidates.length} available
                </div>
              </div>

              {wheelData.existing_record && !spinResult && (
                <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#065f46', marginBottom: 4 }}>
                    ✓ Captains already set for this match
                  </div>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.95rem', color: 'var(--navy)' }}>
                    {wheelData.existing_record.capt1_name} vs {wheelData.existing_record.capt2_name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#065f46', marginTop: 2 }}>
                    Method: {wheelData.existing_record.selection_method}. Spinning again will overwrite on confirm.
                  </div>
                </div>
              )}

              {wheelData.continuing_captain && !spinResult && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: '1.4rem' }}>🏆</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Continuing Captain</div>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)' }}>
                        {wheelData.continuing_captain.display_name}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#92400e' }}>Won last regular match</div>
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#92400e', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={excludeContinuing}
                      onChange={e => setExcludeContinuing(e.target.checked)}
                      disabled={spinning}
                    />
                    Exclude — pick 2 fresh captains from pool
                  </label>
                </div>
              )}

              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 16px', marginBottom: 14 }}>
                <WheelSVG
                  segments={visibleCandidates}
                  rotation={wheelRotation}
                  spinning={spinning}
                  size={wheelSize}
                />

                {!spinResult && (
                  <button
                    onClick={handleSpin}
                    disabled={spinning || visibleCandidates.length < (wheelData.continuing_captain && !excludeContinuing ? 1 : 2)}
                    style={{
                      marginTop: 16, width: '100%', padding: '14px',
                      background: spinning ? '#94a3b8' : 'linear-gradient(135deg, var(--navy), #2563eb)',
                      color: 'white', border: 'none', borderRadius: 10,
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem',
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      cursor: spinning ? 'wait' : 'pointer',
                      boxShadow: spinning ? 'none' : '0 4px 12px rgba(30,58,110,0.3)',
                    }}
                  >
                    {spinning ? '🎲 Spinning…' : '🎲 SPIN THE WHEEL'}
                  </button>
                )}

                {/* Session 15 — Manual appointment (bypass wheel) */}
                {!spinResult && !spinning && (
                  <button
                    onClick={openManualAppointment}
                    style={{
                      width: '100%', padding: '10px', background: 'white', color: '#1E3A6E',
                      border: '2px dashed #cbd5e1', borderRadius: 10, marginTop: 10,
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.82rem',
                      cursor: 'pointer',
                    }}
                  >
                    ✍️ Manually Appoint Captains (Skip Wheel)
                  </button>
                )}

                {spinResult && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                      🎯 Captains for this match
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                      <PickCard
                        label="Captain A"
                        name={spinResult.continuing_captain?.display_name || spinResult.picks[0]?.display_name}
                        sub={spinResult.continuing_captain ? 'Continuing' : 'Pool pick'}
                        highlight={!!spinResult.continuing_captain}
                      />
                      <PickCard
                        label="Captain B"
                        name={spinResult.continuing_captain ? spinResult.picks[0]?.display_name : spinResult.picks[1]?.display_name}
                        sub="Pool pick"
                        highlight={false}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handleResetSpin}
                        disabled={saving === 'spin'}
                        style={{
                          flex: 1, padding: '12px', background: '#f1f5f9', color: 'var(--text)',
                          border: '1px solid var(--border)', borderRadius: 10,
                          fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                        }}
                      >
                        🔁 Spin Again
                      </button>
                      <button
                        onClick={handleConfirmSpin}
                        disabled={saving === 'spin'}
                        style={{
                          flex: 2, padding: '12px', background: '#16a34a', color: 'white',
                          border: 'none', borderRadius: 10,
                          fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem',
                          letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                        }}
                      >
                        {saving === 'spin' ? 'Saving…' : '✓ Confirm & Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {!spinResult && (
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                    Candidates in wheel ({visibleCandidates.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {visibleCandidates.map(c => (
                      <div key={c.member_id} style={{ background: '#f1f5f9', borderRadius: 20, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--navy)' }}>
                        {c.display_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                  🕐 Recent Spins
                </div>
                {recentSpins.length === 0 && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', padding: '8px 0' }}>
                    No spins yet.
                  </div>
                )}
                {recentSpins.map(s => (
                  <SpinHistoryRow key={s.id} spin={s} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═════════════════ POOL TAB ═════════════════ */}
      {tab === 'pool' && (
        <div style={{ padding: '16px', maxWidth: 720, margin: '0 auto' }}>
          {/* Wheel-next banner (shown when wheel is CLOSED) */}
          {!wheelOpen && wheelData?.match && wheelData?.wheel_window?.opens_at && (
            <div style={{
              background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10,
              padding: '10px 14px', marginBottom: 12,
            }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>
                🎲 Wheel next available
              </div>
              <div style={{ fontSize: '0.82rem', color: '#92400e' }}>
                {wheelData.wheel_window.message}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Season</div>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              style={{ fontSize: '0.82rem', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'white', fontWeight: 700, color: 'var(--navy)' }}
            >
              {[...new Set([...availableYears, 2026, new Date().getFullYear()])].sort((a,b)=>b-a).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
              ➕ Add Member to Pool
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <select
                className="form-control"
                value={addMemberId}
                onChange={e => setAddMemberId(e.target.value)}
                style={{ flex: 1, fontSize: '0.85rem' }}
              >
                <option value="">— Select —</option>
                {availableMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.display_name}</option>
                ))}
              </select>
              <button
                onClick={handleAddMember}
                disabled={!addMemberId}
                style={{
                  padding: '8px 16px', background: addMemberId ? 'var(--navy)' : '#cbd5e1', color: 'white',
                  border: 'none', borderRadius: 8, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                  fontSize: '0.85rem', cursor: addMemberId ? 'pointer' : 'not-allowed',
                }}
              >
                Add
              </button>
            </div>
            <div style={{ display: 'flex', gap: 10, fontSize: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" name="addMode" checked={addMode === 'permanent'} onChange={() => setAddMode('permanent')} />
                <span>Permanent</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" name="addMode" checked={addMode === 'skip_next'} onChange={() => setAddMode('skip_next')} />
                <span>Skip next Sunday only</span>
              </label>
            </div>
            {availableMembers.length === 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
                All eligible members already in {selectedYear} pool
              </div>
            )}
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)' }}>🔄 New Cycle</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {pool.filter(p => p.status === 'captained').length} captained · {pool.filter(p => p.status === 'in_pool').length} remaining
                </div>
              </div>
              {!resetConfirm && (
                <button
                  onClick={() => setResetConfirm(true)}
                  disabled={pool.filter(p => p.status === 'captained').length === 0}
                  style={{
                    padding: '8px 14px', background: '#f59e0b', color: 'white',
                    border: 'none', borderRadius: 8, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700,
                    fontSize: '0.78rem', cursor: 'pointer',
                    opacity: pool.filter(p => p.status === 'captained').length === 0 ? 0.5 : 1,
                  }}
                >
                  Reset Cycle
                </button>
              )}
              {resetConfirm && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setResetConfirm(false)}
                    style={{
                      padding: '8px 12px', background: '#f1f5f9', color: 'var(--text)',
                      border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Rajdhani,sans-serif',
                      fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetCycle}
                    style={{
                      padding: '8px 12px', background: '#dc2626', color: 'white',
                      border: 'none', borderRadius: 8, fontFamily: 'Rajdhani,sans-serif',
                      fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                    }}
                  >
                    Confirm Reset
                  </button>
                </div>
              )}
            </div>
            {resetConfirm && (
              <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 8 }}>
                ⚠ All 'captained' members will go back to 'in_pool'. 'Withdrawn' stays as-is.
              </div>
            )}
          </div>

          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 4 }}>
            ⋮⋮ Drag to reorder
          </div>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {pool.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Pool is empty. Add members above.
              </div>
            )}
            {pool.map((p, idx) => (
              <PoolRow
                key={p.member_id}
                row={p}
                isLast={idx === pool.length - 1}
                isDragging={dragIdx === idx}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(idx)}
                onStatusChange={(s) => handleUpdateStatus(p.member_id, s)}
                onToggleSkip={() => handleToggleSkip(p.member_id, p.skip_until)}
                onRemove={() => handleRemoveMember(p.member_id, p.display_name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ═════════════════ RECORDS TAB ═════════════════ */}
      {tab === 'records' && (
        <>
          <div style={{ padding: '12px 16px 4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)' }}>MATCHES SET</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--navy)' }}>{setCount} / {total}</span>
            </div>
            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: total > 0 ? `${(setCount/total)*100}%` : '0%', background: 'var(--navy)', borderRadius: 4, transition: 'width 0.4s' }} />
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>
              ✅ Both captains auto-detected from (c) marker · Saving flips pool status automatically
            </div>
          </div>
          <div style={{ padding: '8px 16px' }}>
            {matches.length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: '0.85rem' }}>No imported scorecards yet.</div>
            )}
            {matches.map(match => {
              const rec = getRecord(match.id);
              const mom = getMOM(match.id);
              const hasRecord = !!rec.capt1_id;
              return (
                <div key={match.id}>
                  <MatchCaptainRow
                    match={match}
                    members={members}
                    initCapt1Id={rec.capt1_id || ''}
                    initCapt2Id={rec.capt2_id || ''}
                    initWinnerId={rec.winner_id || ''}
                    initMomId={mom.winner1_id || rec.suggested_mom_id || ''}
                    team1Name={rec.capt1_team || 'Team A'}
                    team2Name={rec.capt2_team || 'Team B'}
                    isSaving={saving === match.id}
                    onSave={(c1, c2, w, mom) => saveMatch(match.id, c1, c2, w, mom)}
                  />
                  {isSuperAdmin && hasRecord && (
                    <div style={{ padding:'0 10px 12px 10px', marginTop:-6 }}>
                      <button
                        onClick={() => handleResetRecord(match.id, rec)}
                        style={{
                          padding:'5px 10px', borderRadius:6,
                          border:'1px solid #fca5a5', background:'white',
                          color:'#dc2626', fontSize:'0.68rem', fontWeight:700, cursor:'pointer',
                        }}
                      >
                        ↻ Reset Captaincy (un-flip pool)
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Session 15 — Manual Appointment modal */}
      {manualModalOpen && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:'64px', background:'rgba(0,0,0,0.6)', zIndex:200,
          display:'flex', alignItems:'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setManualModalOpen(false); }}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%',
            maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
              <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }} />
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', marginBottom:4 }}>
                ✍️ Manual Captain Appointment
              </div>
              <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:16 }}>
                Bypass the wagon wheel and appoint captains directly. Pool status is updated as if the wheel had been spun.
              </div>
              <div className="form-group">
                <label className="form-label">Captain A *</label>
                <select className="form-control" value={manualCapt1}
                  onChange={e => setManualCapt1(e.target.value)}>
                  <option value="">Select Captain A…</option>
                  {members.filter(m => !m.is_observer && m.is_active).map(m => (
                    <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Captain B *</label>
                <select className="form-control" value={manualCapt2}
                  onChange={e => setManualCapt2(e.target.value)}>
                  <option value="">Select Captain B…</option>
                  {members.filter(m => !m.is_observer && m.is_active && m.id !== manualCapt1).map(m => (
                    <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>
                  ))}
                </select>
              </div>
              <div style={{ background:'#fffbeb', border:'1px solid #fbbf24', borderRadius:8, padding:10, marginBottom:12, fontSize:'0.78rem', color:'#92400e' }}>
                ⚠️ Both members will be marked as "captained" in the pool. This cannot be undone from the pool view — use Records → Reset to undo.
              </div>
            </div>
            <div style={{ padding:'12px 20px', paddingBottom:'calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', background:'white', display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={() => setManualModalOpen(false)}
                style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid #e5e7eb',
                  background:'white', color:'#374151', fontSize:'0.85rem', fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={submitManualAppointment} disabled={manualSaving}
                style={{ flex:2, padding:'10px', borderRadius:10, border:'none',
                  background: manualSaving ? '#9ca3af' : '#16a34a', color:'white',
                  fontSize:'0.85rem', fontWeight:700, cursor: manualSaving ? 'wait' : 'pointer' }}>
                {manualSaving ? 'Saving…' : 'Appoint Captains'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WheelSVG ───────────────────────────────────────────────
function WheelSVG({ segments, rotation, spinning, size = 300 }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const count = segments.length;
  const segAngle = count > 0 ? 360 / count : 0;
  const colors = ['#1E3A6E', '#F5C518', '#2563eb', '#dc2626', '#16a34a', '#7c3aed', '#f97316', '#0891b2', '#be185d', '#0d9488'];

  function segmentPath(i) {
    const startAng = (i * segAngle - 90) * Math.PI / 180;
    const endAng = ((i + 1) * segAngle - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(startAng);
    const y1 = cy + r * Math.sin(startAng);
    const x2 = cx + r * Math.cos(endAng);
    const y2 = cy + r * Math.sin(endAng);
    const largeArc = segAngle > 180 ? 1 : 0;
    return `M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
  }

  function labelPos(i) {
    const midAng = ((i + 0.5) * segAngle - 90) * Math.PI / 180;
    const lr = r * 0.65;
    return { x: cx + lr * Math.cos(midAng), y: cy + lr * Math.sin(midAng), angle: (i + 0.5) * segAngle };
  }

  if (count === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        No candidates available to spin
      </div>
    );
  }

  const labelSize = count > 16 ? 8 : count > 12 ? 10 : count > 8 ? 12 : 14;

  return (
    <div style={{ position: 'relative', width: size, height: size + 24, margin: '0 auto' }}>
      <div style={{
        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '16px solid transparent',
        borderRight: '16px solid transparent',
        borderTop: '26px solid #dc2626',
        zIndex: 2,
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
      }} />
      <svg
        width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{
          display: 'block', marginTop: 20,
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.15, 1)' : 'none',
          filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.18))',
        }}
      >
        {segments.map((seg, i) => {
          const pos = labelPos(i);
          const name = seg.display_name.length > 14 ? seg.display_name.split(' ')[0] : seg.display_name;
          return (
            <g key={seg.member_id}>
              <path d={segmentPath(i)} fill={colors[i % colors.length]} stroke="white" strokeWidth="2" />
              <text
                x={pos.x} y={pos.y}
                fill="white" fontSize={labelSize}
                fontWeight="700" fontFamily="Rajdhani, sans-serif"
                textAnchor="middle" dominantBaseline="middle"
                transform={`rotate(${pos.angle}, ${pos.x}, ${pos.y})`}
                style={{ pointerEvents: 'none', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
              >
                {name}
              </text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={size >= 400 ? 24 : 20} fill="white" stroke="#1E3A6E" strokeWidth="3" />
        <text x={cx} y={cy} fill="#1E3A6E" fontSize={size >= 400 ? 14 : 12} fontWeight="800" fontFamily="Rajdhani, sans-serif" textAnchor="middle" dominantBaseline="middle">UCC</text>
      </svg>
    </div>
  );
}

// ─── PoolRow ────────────────────────────────────────────────
function PoolRow({ row, isLast, isDragging, onDragStart, onDragOver, onDrop, onStatusChange, onToggleSkip, onRemove }) {
  const statusMeta = {
    in_pool:   { bg: '#d1fae5', fg: '#065f46', label: '● In Pool'   },
    captained: { bg: '#e0e7ff', fg: '#3730a3', label: '✓ Captained' },
    withdrawn: { bg: '#fee2e2', fg: '#991b1b', label: '✗ Withdrawn' },
  };
  const meta = statusMeta[row.status] || statusMeta.in_pool;
  const skipActive = !!row.skip_until && new Date(row.skip_until) >= new Date();

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 10px',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        background: isDragging ? '#f1f5f9' : 'white',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        transition: 'background 0.15s',
      }}
    >
      <div style={{ color: '#cbd5e1', fontSize: '1rem', flexShrink: 0, cursor: 'grab' }}>⋮⋮</div>
      <div style={{
        width: 34, height: 34, borderRadius: '50%', background: '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.85rem', color: 'var(--navy)',
        flexShrink: 0,
      }}>
        {row.jersey_no || '—'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.92rem', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.display_name}
          {skipActive && <span style={{ marginLeft: 6, fontSize: '0.62rem', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>⏭ SKIP</span>}
        </div>
        <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>
          {row.ucc_id} · {row.times_captained || 0}× this year
          {skipActive && ` · until ${new Date(row.skip_until).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}`}
        </div>
      </div>
      <span style={{
        background: meta.bg, color: meta.fg,
        padding: '3px 6px', borderRadius: 10, fontSize: '0.62rem', fontWeight: 700,
        flexShrink: 0, display: window.innerWidth < 400 ? 'none' : 'inline',
      }}>{meta.label}</span>
      <button
        onClick={onToggleSkip}
        title={skipActive ? 'Clear skip' : 'Skip next Sunday'}
        style={{
          border: '1px solid var(--border)',
          background: skipActive ? '#fef3c7' : 'white',
          borderRadius: 6, padding: '4px 6px',
          cursor: 'pointer', fontSize: '0.7rem', flexShrink: 0,
        }}
      >
        ⏭
      </button>
      <select
        value={row.status}
        onChange={e => onStatusChange(e.target.value)}
        style={{
          fontSize: '0.72rem', padding: '4px 4px', border: '1px solid var(--border)',
          borderRadius: 6, background: 'white', flexShrink: 0,
        }}
      >
        <option value="in_pool">In Pool</option>
        <option value="captained">Captained</option>
        <option value="withdrawn">Withdrawn</option>
      </select>
      <button
        onClick={onRemove}
        title="Remove from pool"
        style={{
          border: 'none', background: 'transparent', color: '#dc2626',
          cursor: 'pointer', fontSize: '1.1rem', padding: '4px 4px', flexShrink: 0,
        }}
      >×</button>
    </div>
  );
}

// ─── PickCard ───────────────────────────────────────────────
function PickCard({ label, name, sub, highlight }) {
  return (
    <div style={{
      background: highlight ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : '#f1f5f9',
      border: `1px solid ${highlight ? '#fde68a' : '#e5e7eb'}`,
      borderRadius: 10, padding: '12px 12px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: highlight ? '#92400e' : 'var(--text-muted)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.05rem', color: 'var(--navy)', marginBottom: 2 }}>
        {name || '—'}
      </div>
      <div style={{ fontSize: '0.66rem', color: highlight ? '#92400e' : 'var(--text-muted)' }}>
        {highlight && '🏆 '}{sub}
      </div>
    </div>
  );
}

// ─── SpinHistoryRow ─────────────────────────────────────────
function SpinHistoryRow({ spin }) {
  const picks = Array.isArray(spin.picks) ? spin.picks : (typeof spin.picks === 'string' ? JSON.parse(spin.picks) : []);
  const names = [];
  if (spin.continuing_captain_name) names.push(spin.continuing_captain_name + ' 🏆');
  picks.forEach(p => names.push(p.display_name));
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 0', borderBottom: '1px solid #f1f5f9',
    }}>
      <div style={{
        background: spin.confirmed ? '#d1fae5' : '#fef3c7',
        color: spin.confirmed ? '#065f46' : '#92400e',
        padding: '2px 6px', borderRadius: 8, fontSize: '0.6rem', fontWeight: 700,
        flexShrink: 0,
      }}>
        {spin.confirmed ? '✓' : '•'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.82rem', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {names.join(' vs ')}
        </div>
        <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>
          {new Date(spin.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          {' · '}
          {new Date(spin.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          {spin.spun_by_name && ` · by ${spin.spun_by_name}`}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// END replacement block. MatchCaptainRow below stays UNCHANGED.
// ════════════════════════════════════════════════════════════════

function MatchCaptainRow({ match, members, initCapt1Id, initCapt2Id, initWinnerId, initMomId, team1Name, team2Name, isSaving, onSave }) {
  const [capt1Id, setCapt1Id] = useState(initCapt1Id);
  const [capt2Id, setCapt2Id] = useState(initCapt2Id);
  const [winnerId, setWinnerId] = useState(initWinnerId);
  const [momId, setMomId] = useState(initMomId);

  useEffect(() => { setCapt1Id(initCapt1Id); }, [initCapt1Id]);
  useEffect(() => { setCapt2Id(initCapt2Id); }, [initCapt2Id]);
  useEffect(() => { setWinnerId(initWinnerId); }, [initWinnerId]);
  useEffect(() => { setMomId(initMomId); }, [initMomId]);

  const isSet = !!(initCapt1Id || initCapt2Id);
  const isDirty = capt1Id !== initCapt1Id || capt2Id !== initCapt2Id || winnerId !== initWinnerId || momId !== initMomId;

  // Winner dropdown options — only show captains who have been selected
  const winnerOptions = [
    capt1Id ? { id: capt1Id, label: `${members.find(m=>m.id===capt1Id)?.display_name || '?'} (${team1Name}) ✓` } : null,
    capt2Id ? { id: capt2Id, label: `${members.find(m=>m.id===capt2Id)?.display_name || '?'} (${team2Name}) ✓` } : null,
  ].filter(Boolean);

  return (
    <div style={{
      background: 'white',
      border: `1px solid ${isSet ? '#d1fae5' : '#e5e7eb'}`,
      borderLeft: `4px solid ${isSet ? '#16a34a' : '#e5e7eb'}`,
      borderRadius: 12, padding: '12px 14px', marginBottom: 10,
    }}>
      {/* Match header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)' }}>
            {new Date(match.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · {match.series_name || match.match_type || 'Match'}
          </div>
          {match.title && <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>{match.title}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {isSet && !isDirty && <span style={{ fontSize: '0.65rem', background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>✓ SET</span>}
          {isDirty && <span style={{ fontSize: '0.65rem', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>● UNSAVED</span>}
        </div>
      </div>

      {/* 2x2 grid on mobile, 4-col on desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>🎖️ {team1Name}</div>
          <select className="form-control" style={{ fontSize: '0.78rem', padding: '6px 6px' }} value={capt1Id} onChange={e => { setCapt1Id(e.target.value); if (winnerId === capt1Id) setWinnerId(''); }}>
            <option value="">— None —</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>🎖️ {team2Name}</div>
          <select className="form-control" style={{ fontSize: '0.78rem', padding: '6px 6px' }} value={capt2Id} onChange={e => { setCapt2Id(e.target.value); if (winnerId === capt2Id) setWinnerId(''); }}>
            <option value="">— None —</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>🏆 Winning Capt</div>
          <select className="form-control" style={{ fontSize: '0.78rem', padding: '6px 6px' }} value={winnerId} onChange={e => setWinnerId(e.target.value)}>
            <option value="">— Unknown —</option>
            {winnerOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>⭐ MOM</div>
          <select className="form-control" style={{ fontSize: '0.78rem', padding: '6px 6px' }} value={momId} onChange={e => setMomId(e.target.value)}>
            <option value="">— None —</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
          </select>
        </div>
      </div>

      <button onClick={() => onSave(capt1Id, capt2Id, winnerId, momId)}
        disabled={isSaving || (!capt1Id && !capt2Id && !momId)}
        style={{
          width: '100%', padding: 8, borderRadius: 8,
          border: isDirty ? 'none' : '1px solid #e5e7eb',
          background: isDirty ? 'var(--navy)' : isSet ? '#f0fdf4' : '#e5e7eb',
          color: isDirty ? 'white' : isSet ? '#16a34a' : 'var(--text-muted)',
          fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem',
          cursor: (capt1Id || capt2Id || momId) ? 'pointer' : 'not-allowed',
        }}>
        {isSaving ? '⏳ Saving…' : isDirty ? '💾 Save Changes' : isSet ? '✓ Saved' : '— Nothing to save —'}
      </button>
    </div>
  );
}

// ─── Admin Teams ─────────────────────────────────────────
export function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShort, setNewShort] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editShort, setEditShort] = useState('');

  function load() {
    return api.get('/teams').then(r => setTeams(r.data.teams || []));
  }

  useEffect(() => {
    load().catch(console.error).finally(() => setLoading(false));
  }, []);

  async function addTeam() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/teams', { name: newName.trim(), short_name: newShort.trim() });
      setNewName(''); setNewShort('');
      await load();
      toast.success('Team added!');
    } catch { toast.error('Failed to add team'); }
    finally { setSaving(false); }
  }

  async function saveEdit(id) {
    setSaving(true);
    try {
      await api.put(`/teams/${id}`, { name: editName.trim(), short_name: editShort.trim() });
      setEditId(null);
      await load();
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }

  async function toggleActive(team) {
    try {
      await api.put(`/teams/${team.id}`, { is_active: !team.is_active });
      await load();
    } catch { toast.error('Failed'); }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Teams" icon="🏏">
        Manage UCC team names · These auto-match to CricHeroes team names on import
      </AdminPageHeader>

      <div style={{ padding: '12px 16px' }}>
        {/* Add new team */}
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px', marginBottom: 12 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>➕ ADD NEW TEAM</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="form-control"
              placeholder="Full name (e.g. Leafberry Ads)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              style={{ flex: 2, fontSize: '0.85rem' }}
            />
            <input
              className="form-control"
              placeholder="Short (e.g. LEAF)"
              value={newShort}
              onChange={e => setNewShort(e.target.value)}
              style={{ flex: 1, fontSize: '0.85rem' }}
            />
            <button
              onClick={addTeam}
              disabled={saving || !newName.trim()}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--navy)', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Add Team
            </button>
          </div>
        </div>

        {/* Teams list */}
        {teams.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: '0.85rem' }}>No teams yet. Add one above.</div>
        )}
        {teams.map(team => (
          <div key={team.id} style={{
            background: 'white', border: `1px solid ${team.is_active ? '#d1fae5' : '#e5e7eb'}`,
            borderLeft: `4px solid ${team.is_active ? '#16a34a' : '#d1d5db'}`,
            borderRadius: 12, padding: '12px 14px', marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            {editId === team.id ? (
              <>
                <input className="form-control" value={editName} onChange={e => setEditName(e.target.value)}
                  style={{ flex: 2, fontSize: '0.85rem' }} />
                <input className="form-control" value={editShort} onChange={e => setEditShort(e.target.value)}
                  style={{ flex: 1, fontSize: '0.85rem' }} placeholder="Short" />
                <button onClick={() => saveEdit(team.id)} disabled={saving}
                  style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--navy)', color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  Save
                </button>
                <button onClick={() => setEditId(null)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', fontSize: '0.8rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)' }}>
                    {team.name}
                  </div>
                  {team.short_name && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{team.short_name}</div>}
                </div>
                <span style={{ fontSize: '0.65rem', background: team.is_active ? '#d1fae5' : '#f3f4f6', color: team.is_active ? '#065f46' : '#6b7280', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                  {team.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                <button onClick={() => { setEditId(team.id); setEditName(team.name); setEditShort(team.short_name || ''); }}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}>
                  ✏️ Edit
                </button>
                <button onClick={() => toggleActive(team)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: team.is_active ? '#dc2626' : '#16a34a' }}>
                  {team.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </>
            )}
          </div>
        ))}

        <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 10, fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          💡 Team names must match exactly what CricHeroes uses (e.g. "Leafberry Ads", "Lead Ads"). On scorecard import, captains are automatically assigned to their matching team. For the Auction Series, add the 3 team names separately.
        </div>
      </div>
    </div>
  );
}


// ─── Admin Teams ─────────────────────────────────────────

export function AdminFloaters() {
  const [chart, setChart] = useState([]);
  const [matches, setMatches] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ match_id:'', team_a_floater:'', team_b_floater:'' });

  useEffect(() => {
    Promise.all([
      api.get('/floaters/chart?year=2026').catch(()=>({data:{chart:[]}})),
      api.get('/matches?limit=50').catch(()=>({data:{matches:[]}})),
      api.get('/members').catch(()=>({data:{members:[]}})),
    ]).then(([c, m, mem]) => {
      setChart(c.data.chart || []);
      const imported = (m.data.matches||[]).filter(x=>parseInt(x.import_count)>0 || x.scoring_mode==='live').sort((a,b)=>new Date(b.match_date)-new Date(a.match_date));
      setMatches(imported);
      setMembers((mem.data.members||[]).filter(x=>!x.is_observer));
    }).finally(()=>setLoading(false));
  }, []);

  async function saveFloater() {
    if (!form.match_id) { toast.error('Select a match'); return; }
    setSaving(true);
    try {
      await api.post('/floaters/record', { match_id: form.match_id, team_a_floater: form.team_a_floater||null, team_b_floater: form.team_b_floater||null, exclusions:[] });
      toast.success('Floater recorded!');
      setForm({ match_id:'', team_a_floater:'', team_b_floater:'' });
      const c = await api.get('/floaters/chart?year=2026');
      setChart(c.data.chart||[]);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{padding:32,textAlign:'center',color:'var(--text-muted)'}}>Loading…</div>;

  return (
    <div style={{paddingBottom:80}}>
      <AdminPageHeader title="Floater Management" icon="🔄">Track who floated for each match this season</AdminPageHeader>
      <div style={{padding:'12px 16px'}}>
        {/* Record floater */}
        <div style={{background:'white',borderRadius:12,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.85rem',color:'var(--navy)',marginBottom:12}}>📝 Record Floater</div>
          <div className="form-group">
            <label className="form-label">Match</label>
            <select className="form-control" value={form.match_id} onChange={e=>setForm(f=>({...f,match_id:e.target.value}))}>
              <option value="">— Select match —</option>
              {matches.map(m=><option key={m.id} value={m.id}>{new Date(m.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · {m.series_name||m.match_type||'Match'}</option>)}
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            <div className="form-group">
              <label className="form-label">Team A Floater</label>
              <select className="form-control" value={form.team_a_floater} onChange={e=>setForm(f=>({...f,team_a_floater:e.target.value}))}>
                <option value="">— None —</option>
                {members.map(m=><option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Team B Floater</label>
              <select className="form-control" value={form.team_b_floater} onChange={e=>setForm(f=>({...f,team_b_floater:e.target.value}))}>
                <option value="">— None —</option>
                {members.map(m=><option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
          </div>
          <button className="btn btn-primary btn-block" onClick={saveFloater} disabled={saving}>{saving?'Saving…':'Save Floater'}</button>
        </div>

        {/* Floater chart */}
        <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.8rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Season Floater Chart</div>
        {chart.map(m=>(
          <div key={m.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'white',borderRadius:10,padding:'10px 14px',marginBottom:6,boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
            <div>
              <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:'var(--navy)',fontSize:'0.9rem'}}>{m.display_name}</div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{m.times_floated||0} times floated · {m.elapsed_weeks||0} weeks since last</div>
            </div>
            <div style={{textAlign:'right'}}>
              {m.tier1 ? <span style={{fontSize:'0.65rem',background:'#fef3c7',color:'#92400e',padding:'2px 8px',borderRadius:10,fontWeight:700}}>TIER 1</span>
                : <span style={{fontSize:'0.65rem',background:'#dbeafe',color:'#1e40af',padding:'2px 8px',borderRadius:10,fontWeight:700}}>TIER 2</span>}
            </div>
          </div>
        ))}
        {chart.length===0 && <div style={{textAlign:'center',padding:32,color:'var(--text-muted)',fontSize:'0.85rem'}}>No floater data yet.</div>}

        {/* Sunday Count Settings */}
        <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.8rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,marginTop:20}}>Sunday Count Settings</div>
        <div style={{background:'#f0f9ff',borderRadius:8,padding:'8px 12px',marginBottom:10,fontSize:'0.75rem',color:'#0369a1'}}>
          Toggle whether each Sunday counts toward the floater rotation clock. Series/ICC matches are auto-excluded. You can also exclude specific members for a Sunday (e.g. they didn't play at all).
        </div>
        <FloaterSundaySettings matches={matches} members={members} />
      </div>
    </div>
  );
}

// Each row is its own component so useState/hooks are legal (no hooks-in-map)
function SundaySettingRow({ s, members, onSave }) {
  const [counted, setCounted] = useState(s.floater_counted);
  const [excluded, setExcluded] = useState(s.exclusions||[]);
  const [saving, setSaving] = useState(false);
  const isDirty = counted !== s.floater_counted || JSON.stringify([...excluded].sort()) !== JSON.stringify([...(s.exclusions||[])].sort());

  function toggleMember(id) {
    setExcluded(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  }

  async function save() {
    setSaving(true);
    try {
      await onSave(s.match_id, counted, excluded);
    } finally { setSaving(false); }
  }

  return (
    <div style={{background:'white',borderRadius:10,padding:'12px 14px',marginBottom:8,boxShadow:'0 1px 3px rgba(0,0,0,0.05)',border: saving ? '1.5px solid #F5C518' : '1px solid #e5e7eb'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:'var(--navy)',fontSize:'0.88rem'}}>
          {new Date(s.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
          {s.series_id && <span style={{marginLeft:6,fontSize:'0.65rem',background:'#e0e7ff',color:'#3730a3',padding:'1px 6px',borderRadius:8,fontWeight:700}}>SERIES</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:'0.72rem',color:counted?'#16a34a':'#dc2626',fontWeight:700}}>{counted?'✓ COUNTS':'✗ SKIP'}</span>
          <button onClick={()=>setCounted(c=>!c)} style={{padding:'4px 10px',borderRadius:8,border:`1.5px solid ${counted?'#16a34a':'#dc2626'}`,background:'white',color:counted?'#16a34a':'#dc2626',fontSize:'0.72rem',fontWeight:700,cursor:'pointer'}}>
            Toggle
          </button>
        </div>
      </div>
      <div style={{fontSize:'0.65rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Exclude members this Sunday:</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8}}>
        {members.map(m=>{
          const isExcl = excluded.includes(m.id);
          return (
            <button key={m.id} onClick={()=>toggleMember(m.id)} style={{
              padding:'3px 8px',borderRadius:12,border:`1px solid ${isExcl?'#dc2626':'#e5e7eb'}`,
              background:isExcl?'#fee2e2':'#f9fafb',color:isExcl?'#dc2626':'#64748b',
              fontSize:'0.65rem',fontWeight:600,cursor:'pointer',
            }}>{m.display_name}</button>
          );
        })}
      </div>
      {isDirty && (
        <button onClick={save} disabled={saving} style={{padding:'5px 14px',borderRadius:8,background:'var(--navy)',color:'white',border:'none',fontSize:'0.75rem',fontWeight:700,cursor:'pointer'}}>
          {saving?'Saving…':'Save Changes'}
        </button>
      )}
    </div>
  );
}

function FloaterSundaySettings({ members }) {
  const [settings, setSettings] = useState([]);

  useEffect(() => {
    api.get('/floaters/sunday-settings?year=2026').catch(()=>({data:{settings:[]}})).then(r => {
      setSettings(r.data.settings||[]);
    });
  }, []);

  async function saveSetting(matchId, counted, excludedIds) {
    await api.post('/floaters/sunday-settings', { match_id: matchId, floater_counted: counted, excluded_member_ids: excludedIds });
    toast.success('Saved!');
    // Refresh
    const r = await api.get('/floaters/sunday-settings?year=2026').catch(()=>({data:{settings:[]}}));
    setSettings(r.data.settings||[]);
  }

  if (settings.length === 0) return <div style={{fontSize:'0.8rem',color:'var(--text-muted)',padding:'8px 0'}}>No matches loaded.</div>;

  return settings.map(s => (
    <SundaySettingRow key={s.match_id} s={s} members={members} onSave={saveSetting} />
  ));
}
// ─── Series fixture helpers ───────────────────────────────
const BLANK_FIXTURE_ROW = {
  fixture_date: '', capt_a_id: '', team_a_name: '',
  capt_b_id: '', team_b_name: '', venue: '', reporting_time: '',
};

const pad2 = n => String(n).padStart(2, '0');

// datetime-local carries no timezone. Parsing it in the browser resolves it
// against the admin's clock (IST), so the ISO string sent is the instant they
// meant. Sending the bare string would have Node on Railway read it as UTC.
function localToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d) ? null : d.toISOString();
}

// A stored instant -> the datetime-local value an <input> expects, in the
// admin's own clock.
function isoToLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Show a stored UTC instant back as IST.
function fmtIst(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric',
    month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export function AdminSeries() {
  const [seriesList, setSeriesList]   = useState([]);
  const [members, setMembers]         = useState([]);
  const [matches, setMatches]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [view, setView]               = useState('list'); // list | detail
  const [selId, setSelId]             = useState(null);
  const [detail, setDetail]           = useState(null);  // { series, fixtures, window }
  const [detailTab, setDetailTab]     = useState('setup'); // setup | availability | squads
  const [createForm, setCreateForm]   = useState({ name:'', format:'bilateral', innings_per_match:1, overs_per_innings:20, carry_forward:false });
  const [showCreate, setShowCreate]   = useState(false);
  // Fixture builder — a whole series goes in as one submission (a best-of-five
  // is five rows, one Submit). The backend validates the entire batch before
  // writing anything, so a bad row never leaves a half-created series.
  const [fixRows, setFixRows]         = useState([{ ...BLANK_FIXTURE_ROW }]);
  // The two captains normally lead every fixture in a series (SB, 28 Aug 2026)
  // — they only change if one is absent on the day. So they are set once here
  // and every new row inherits them; a single row can still be overridden.
  const [fixDefaults, setFixDefaults] = useState({
    capt_a_id: '', team_a_name: '', capt_b_id: '', team_b_name: '',
    venue: '', reporting_time: '07:30',
  });
  const [bulkProblems, setBulkProblems] = useState([]);
  // Series-level settings: ONE availability window for every fixture, the
  // minimum number of matches a member must be free for, and the squad size
  // each captain picks. Held on the series row, not on the fixtures.
  const [seriesForm, setSeriesForm] = useState({
    avail_open_at: '', avail_deadline: '', min_fixtures_required: 1, squad_size: '',
  });
  const [savingSeries, setSavingSeries] = useState(false);
  const [venues, setVenues]             = useState([]);
  const [newVenue, setNewVenue]         = useState(null); // { name, latitude, longitude }
  const [editFix, setEditFix]           = useState(null); // fixture being edited
  const [announcing, setAnnouncing]     = useState(false);
  const [addingFix, setAddingFix]     = useState(false);
  // Availability
  const [availData, setAvailData]     = useState(null); // { window, fixtures }
  // Squad selection
  const [squadFixture, setSquadFixture] = useState(null); // fixture being edited
  const [squadData, setSquadData]     = useState(null);   // { available, squad }
  // member_id -> captain_id. A fixture has two sides, so a pick is not just
  // "in the squad" — it is in one captain's side.
  const [picks, setPicks]             = useState({});
  const [sideCapt, setSideCapt]       = useState(null);   // side being picked
  const [allowUnavailable, setAllowUnavailable] = useState(false);
  const [markPresent, setMarkPresent] = useState(false);
  // Stage one: squads are picked once, before the series starts.
  // captain_id -> Set(member_id)
  const [seriesSquads, setSeriesSquads] = useState({});
  const [squadCapt, setSquadCapt]       = useState(null);
  const [savingSeriesSquads, setSavingSeriesSquads] = useState(false);
  const [savingSquad, setSavingSquad] = useState(false);

  function loadList() { return api.get('/series').then(r => setSeriesList(r.data.series || [])); }

  useEffect(() => {
    Promise.all([
      loadList(),
      api.get('/members').then(r => setMembers((r.data.members||[]).filter(m=>!m.is_observer&&m.is_active))),
      api.get('/matches?limit=60').then(r => setMatches(r.data.matches||[])),
      api.get('/venues').then(r => setVenues(r.data.venues||[])).catch(()=>{}),
    ]).finally(() => setLoading(false));
  }, []);

  async function loadDetail(id) {
    try {
      const [sr, ar] = await Promise.all([
        api.get('/series/' + id),
        api.get('/series/' + id + '/availability').catch(() => ({ data: { window: null, fixtures: [] } })),
      ]);
      const ser = sr.data.series;
      setDetail({ series: ser, fixtures: sr.data.fixtures || [] });
      if (ser) {
        setSeriesForm({
          avail_open_at:  isoToLocalInput(ser.avail_open_at),
          avail_deadline: isoToLocalInput(ser.avail_deadline),
          min_fixtures_required: ser.min_fixtures_required ?? 1,
          squad_size: ser.squad_size ?? '',
        });
      }
      setAvailData(ar.data);
    } catch { toast.error('Failed to load series'); }
  }

  function openDetail(s) { setSelId(s.id); setDetail(null); setAvailData(null); setSeriesSquads({}); setSquadCapt(null); setView('detail'); setDetailTab('setup'); loadDetail(s.id); }

  // Saved squads are fetched when the tab is opened, once the fixtures are in
  // hand — the captains come from them.
  useEffect(() => {
    if (selId && detailTab === 'squads' && detail) loadSeriesSquads();
    // eslint-disable-next-line
  }, [selId, detailTab, detail]);

  async function createSeries() {
    if (!createForm.name.trim()) return toast.error('Series name required');
    setSaving(true);
    try {
      await api.post('/series', createForm);
      toast.success('Series created!');
      setCreateForm({ name:'', format:'bilateral', innings_per_match:1, overs_per_innings:20, carry_forward:false });
      setShowCreate(false);
      await loadList();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  async function changeStatus(id, status) {
    try { await api.patch('/series/' + id, { status }); toast.success('Updated!'); await loadList(); if (selId === id) loadDetail(id); }
    catch { toast.error('Failed'); }
  }

  // A new row starts from the series defaults, so adding match 2..5 is one tap
  // each rather than four dropdowns each.
  function newRowFromDefaults() {
    return {
      ...BLANK_FIXTURE_ROW,
      capt_a_id:      fixDefaults.capt_a_id,
      capt_b_id:      fixDefaults.capt_b_id,
      team_a_name:    fixDefaults.team_a_name,
      team_b_name:    fixDefaults.team_b_name,
      venue:          '',
      reporting_time: fixDefaults.reporting_time,
    };
  }

  function patchRow(i, patch) {
    setFixRows(rows => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function setRowDate(i, dateStr) {
    patchRow(i, { fixture_date: dateStr });
  }

  // Push the defaults across every row, so five fixtures don't need the same
  // captains and venue typed five times.
  function applyDefaultsToAll() {
    setFixRows(rows => rows.map(r => ({
      ...r,
      capt_a_id:      fixDefaults.capt_a_id      || r.capt_a_id,
      capt_b_id:      fixDefaults.capt_b_id      || r.capt_b_id,
      team_a_name:    fixDefaults.team_a_name    || r.team_a_name,
      team_b_name:    fixDefaults.team_b_name    || r.team_b_name,
      venue:          fixDefaults.venue          || r.venue,
      reporting_time: fixDefaults.reporting_time || r.reporting_time,
    })));
    toast.success('Applied to every row');
  }

  // Venues carry coordinates so the weather card follows the match. Without
  // them the forecast silently falls back to the home ground, which is exactly
  // the bug this replaced — so the form asks for them up front.
  async function createVenue() {
    if (!newVenue?.name?.trim()) return toast.error('Venue name required');
    try {
      const r = await api.post('/venues', newVenue);
      const v = r.data.venue;
      setVenues(list => [...list, v].sort((a,b)=>a.name.localeCompare(b.name)));
      if (r.data.no_coordinates) {
        toast('Saved, but with no coordinates the weather will show the home ground.', { icon: '⚠️' });
      } else {
        toast.success('Venue added');
      }
      setNewVenue(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add venue'); }
  }

  async function saveSeriesSettings() {
    if (seriesForm.avail_open_at && seriesForm.avail_deadline
        && new Date(seriesForm.avail_deadline) <= new Date(seriesForm.avail_open_at)) {
      return toast.error('The availability window closes before it opens');
    }
    setSavingSeries(true);
    try {
      await api.patch('/series/' + selId, {
        avail_open_at:  localToIso(seriesForm.avail_open_at),
        avail_deadline: localToIso(seriesForm.avail_deadline),
        min_fixtures_required: Number(seriesForm.min_fixtures_required) || 1,
        squad_size: seriesForm.squad_size === '' ? null : Number(seriesForm.squad_size),
      });
      toast.success('Series settings saved');
      await loadDetail(selId);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSavingSeries(false); }
  }

  async function announceSeries() {
    const already = detail?.series?.announced_at;
    if (already && !window.confirm('This series has already been announced. Send the schedule again?')) return;
    setAnnouncing(true);
    try {
      const r = await api.post('/series/' + selId + '/announce' + (already ? '?again=true' : ''));
      toast.success(`Announced — ${r.data.fixtures} fixture${r.data.fixtures === 1 ? '' : 's'} sent to the club`);
      await loadDetail(selId);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to announce'); }
    finally { setAnnouncing(false); }
  }

  async function submitFixtures() {
    setBulkProblems([]);
    const usable = fixRows.filter(r => r.fixture_date || r.capt_a_id || r.capt_b_id);
    if (!usable.length) return toast.error('Add at least one fixture');

    // Catch the obvious things here so the admin isn't round-tripping to the
    // server for a blank captain. The server re-checks everything regardless.
    const local = [];
    usable.forEach((r, i) => {
      const label = `Match ${i + 1}`;
      if (!r.fixture_date)                   local.push(`${label}: date required`);
    });
    if (local.length) { setBulkProblems(local); return; }

    const payload = usable.map(r => ({
      fixture_date:   r.fixture_date,
      venue_id:       r.venue || fixDefaults.venue || null,
      reporting_time: r.reporting_time || fixDefaults.reporting_time || null,
    }));

    setAddingFix(true);
    try {
      const r = await api.post('/series/' + selId + '/fixtures/bulk', { fixtures: payload });
      toast.success(`${r.data.created} fixture${r.data.created === 1 ? '' : 's'} added`);
      setFixRows([newRowFromDefaults()]);
      await loadDetail(selId);
    } catch (err) {
      const d = err.response?.data;
      if (Array.isArray(d?.problems)) setBulkProblems(d.problems);
      toast.error(d?.error || 'Failed to add fixtures');
    } finally { setAddingFix(false); }
  }

  // Edit an existing fixture. Needed because fixtures created before venues
  // existed have no venue_id, so their weather falls back to the home ground.
  async function saveFixtureEdit() {
    if (!editFix) return;
    try {
      await api.patch('/series/' + selId + '/fixtures/' + editFix.id, {
        venue_id:       editFix.venue_id || null,
        reporting_time: editFix.reporting_time || null,
        fixture_date:   editFix.fixture_date || undefined,
        capt_a_id:      editFix.capt_a_id || null,
        capt_b_id:      editFix.capt_b_id || null,
        team_a_name:    editFix.team_a_name || null,
        team_b_name:    editFix.team_b_name || null,
      });
      toast.success('Fixture updated');
      setEditFix(null);
      await loadDetail(selId);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update fixture'); }
  }

  async function removeFixture(matchId) {
    if (!window.confirm('Remove this fixture from the series?\n\nThe match itself is kept — it may already have availability or a scorecard against it. It just stops being part of this series.')) return;
    try {
      await api.delete('/series/' + selId + '/fixtures/' + matchId);
      toast.success('Fixture removed from series');
      await loadDetail(selId);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to remove'); }
  }

  // The two captains of the series, taken from its fixtures.
  function seriesCaptains() {
    const out = [];
    for (const f of (detail?.fixtures || [])) {
      for (const [id, name, team] of [[f.capt1_id, f.capt_a_name, f.capt1_team],
                                      [f.capt2_id, f.capt_b_name, f.capt2_team]]) {
        if (id && !out.some(c => c.id === id)) out.push({ id, name, team });
      }
    }
    return out;
  }

  async function loadSeriesSquads() {
    try {
      const r = await api.get('/series/' + selId + '/squads');
      const map = {};
      for (const row of (r.data.squads || [])) {
        (map[row.captain_id] = map[row.captain_id] || new Set()).add(row.member_id);
      }
      setSeriesSquads(map);
      const caps = seriesCaptains();
      setSquadCapt(prev => prev || caps[0]?.id || null);
    } catch { toast.error('Failed to load squads'); }
  }

  function toggleSquadMember(memberId) {
    if (!squadCapt) return toast.error('No captains on the fixtures yet — add them in Setup');
    setSeriesSquads(prev => {
      const next = {};
      for (const [cid, set] of Object.entries(prev)) next[cid] = new Set(set);
      // A member belongs to one squad, so putting them in one takes them out
      // of the other.
      for (const cid of Object.keys(next)) if (cid !== squadCapt) next[cid].delete(memberId);
      const mine = next[squadCapt] = next[squadCapt] || new Set();
      if (mine.has(memberId)) mine.delete(memberId); else mine.add(memberId);
      return next;
    });
  }

  async function saveSeriesSquads() {
    const payload = Object.entries(seriesSquads)
      .map(([captain_id, set]) => ({ captain_id, member_ids: [...set] }))
      .filter(sq => sq.member_ids.length);
    if (!payload.length) return toast.error('No members selected');
    setSavingSeriesSquads(true);
    try {
      const r = await api.put('/series/' + selId + '/squads', { squads: payload });
      toast.success(`Squads saved — ${r.data.saved} members`);
      await loadSeriesSquads();
    } catch (err) {
      const p = err.response?.data?.problems;
      toast.error(p?.length ? p.join(' · ') : (err.response?.data?.error || 'Failed'));
    } finally { setSavingSeriesSquads(false); }
  }

  // Who can be picked, and who is already picked, for one fixture.
  async function loadSquad(fixture) {
    setSquadFixture(fixture);
    setSquadData(null);
    setPicks({});
    setAllowUnavailable(false);
    setMarkPresent(false);
    setSideCapt(fixture.capt1_id || fixture.capt2_id || null);
    try {
      const r = await api.get('/series/' + selId + '/fixtures/' + fixture.id + '/pickable');
      // One list, each member carrying their answer, which is what the grid
      // below renders.
      // Default list is the series squad — picked before the series — split
      // by what each member answered for THIS fixture. The override list is
      // every member, for a match being entered after the event.
      setSquadData({
        squad: [
          ...(r.data.pickable || []).map(m => ({ ...m, response: 'yes' })),
          ...(r.data.unavailable || []).map(m => ({ ...m, response: 'no' })),
        ],
        all: [
          ...(r.data.available || []).map(m => ({ ...m, response: 'yes' })),
          ...(r.data.not_available || []).map(m => ({ ...m, response: 'no' })),
        ],
        played: r.data.played || [],
      });
      const existing = {};
      for (const row of (r.data.selected || [])) existing[row.member_id] = row.captain_id;
      setPicks(existing);
    } catch { toast.error('Failed to load availability for this fixture'); }
    setDetailTab('squads');
  }

  // Tapping a member puts them in the side currently being picked, taps them
  // out if they are already in it, and moves them across if they were in the
  // other side.
  function togglePick(memberId) {
    if (!sideCapt) return toast.error('This fixture has no captains set — add them in Setup');
    setPicks(prev => {
      const next = { ...prev };
      if (next[memberId] === sideCapt) delete next[memberId];
      else next[memberId] = sideCapt;
      return next;
    });
  }

  async function saveSquad() {
    if (!squadFixture) return;
    const payload = Object.entries(picks).map(([member_id, captain_id]) => ({ member_id, captain_id }));
    setSavingSquad(true);
    try {
      const r = await api.put('/series/' + selId + '/fixtures/' + squadFixture.id + '/squad', {
        picks: payload,
        allow_unavailable: allowUnavailable,
        mark_playing_present: markPresent,
      });
      toast.success(`Saved: ${r.data.playing} playing, ${r.data.series_rested} series rested`
        + (r.data.absent ? `, ${r.data.absent} absent` : '')
        + (r.data.marked_present ? `, ${r.data.marked_present} marked present` : ''));
      await loadDetail(selId);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSavingSquad(false); }
  }

  const statusColors = { upcoming:{bg:'#eff6ff',color:'#1d4ed8',border:'#bfdbfe'}, active:{bg:'#f0fdf4',color:'#15803d',border:'#bbf7d0'}, completed:{bg:'#f9fafb',color:'#6b7280',border:'#e5e7eb'}, cancelled:{bg:'#fef2f2',color:'#b91c1c',border:'#fecaca'} };
  const sc = s => statusColors[s] || statusColors.completed;
  const tabSty = t => ({ flex:'1 1 0', padding:'8px 4px', border:'none', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer', borderRadius:8, textAlign:'center', background: detailTab===t?'var(--navy)':'transparent', color: detailTab===t?'white':'var(--text-muted)', transition:'all 0.15s', whiteSpace:'nowrap' });

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // ── LIST VIEW ──
  if (view === 'list') return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Series Management" icon="🥇">Bilateral · Triangular · Custom — UCC 2026</AdminPageHeader>
      <div style={{ padding:'12px 16px' }}>
        <button onClick={() => setShowCreate(v=>!v)} style={{ width:'100%', padding:12, borderRadius:12, border:'2px dashed var(--navy)', background:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'var(--navy)', cursor:'pointer', marginBottom:14 }}>
          {showCreate ? '✕ Cancel' : '➕ Create New Series'}
        </button>

        {showCreate && (
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Series Name *</label>
              <input className="form-control" placeholder="e.g. UCC Chairman's Cup 2026" value={createForm.name} onChange={e=>setCreateForm(f=>({...f,name:e.target.value}))} />
            </div>
            <div className="form-group"><label className="form-label">Format</label>
              <select className="form-control" value={createForm.format} onChange={e=>setCreateForm(f=>({...f,format:e.target.value}))}>
                <option value="bilateral">Bilateral (2 teams)</option>
                <option value="triangular">Triangular (3 teams)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="form-group"><label className="form-label">Innings</label>
                <select className="form-control" value={createForm.innings_per_match} onChange={e=>setCreateForm(f=>({...f,innings_per_match:parseInt(e.target.value)}))}>
                  <option value={1}>1 innings</option><option value={2}>2 innings (Double)</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Overs</label>
                <input type="number" className="form-control" value={createForm.overs_per_innings} onChange={e=>setCreateForm(f=>({...f,overs_per_innings:parseInt(e.target.value)}))} min={5} max={50} />
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <input type="checkbox" id="cf" checked={createForm.carry_forward} onChange={e=>setCreateForm(f=>({...f,carry_forward:e.target.checked}))} style={{ width:16,height:16 }} />
              <label htmlFor="cf" style={{ fontSize:'0.85rem', cursor:'pointer' }}>Lead/shortfall carries forward</label>
            </div>
            <button className="btn btn-primary btn-block" onClick={createSeries} disabled={saving||!createForm.name.trim()}>
              {saving ? '⏳ Creating…' : '🥇 Create Series'}
            </button>
          </div>
        )}

        {seriesList.length === 0 && !showCreate && <div className="empty-state"><div className="empty-state__icon">🥇</div><div className="empty-state__title">No series yet</div></div>}

        {seriesList.map(s => (
          <div key={s.id} style={{ background:'white', border:`1px solid ${sc(s.status).border}`, borderLeft:`4px solid ${sc(s.status).color}`, borderRadius:12, padding:14, marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.05rem', color:'var(--navy)' }}>{s.name}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{s.format?.toUpperCase()} · {s.innings_per_match} innings · {s.overs_per_innings} overs</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>📅 {s.completed_fixtures||0}/{s.total_fixtures||0} fixtures</div>
              </div>
              <span style={{ fontSize:'0.65rem', background:sc(s.status).bg, color:sc(s.status).color, border:`1px solid ${sc(s.status).border}`, padding:'2px 10px', borderRadius:10, fontWeight:700 }}>{s.status?.toUpperCase()}</span>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
              <button onClick={()=>openDetail(s)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>Manage →</button>
              {s.status==='upcoming' && <button onClick={()=>changeStatus(s.id,'active')} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #16a34a', background:'#f0fdf4', color:'#16a34a', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>▶ Activate</button>}
              {s.status==='active' && <button onClick={()=>changeStatus(s.id,'completed')} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid #6b7280', background:'#f9fafb', color:'#6b7280', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>✔ Complete</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── DETAIL VIEW ──
  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title={detail?.series?.name || 'Series'} icon="🥇">
        {detail?.series?.format?.toUpperCase()} · {detail?.series?.innings_per_match} innings · {detail?.series?.overs_per_innings} overs
      </AdminPageHeader>

      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', background:'white', borderBottom:'1px solid var(--border)' }}>
        <button onClick={()=>{setView('list');setDetail(null);}} style={{ background:'none', border:'none', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', cursor:'pointer', padding:0 }}>← Back</button>
        <div style={{ flex:1 }} />
        <div style={{ display:'flex', gap:3, flex:3 }}>
          {['setup','availability','squads'].map(t => (
            <button key={t} style={tabSty(t)} onClick={()=>setDetailTab(t)}>
              {t==='setup'?'⚙️ Setup':t==='availability'?'✋ Availability':'👥 Squads'}
            </button>
          ))}
        </div>
      </div>

      {/* ── SETUP TAB ── */}
      {detailTab === 'setup' && (
        <div style={{ padding:'12px 16px' }}>
          {/* Status actions */}
          {detail?.series && (
            <div style={{ background:'var(--navy)', borderRadius:12, padding:'12px 16px', marginBottom:12, color:'white', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800 }}>
                  {detail.series.name}
                  <button title="Rename the series" onClick={async () => {
                      const n = window.prompt('Series name', detail.series.name);
                      if (!n || !n.trim() || n.trim() === detail.series.name) return;
                      try { await api.patch('/series/' + selId, { name: n.trim() }); toast.success('Renamed'); await loadDetail(selId); loadList(); }
                      catch (err) { toast.error(err.response?.data?.error || 'Rename failed'); }
                    }}
                    style={{ marginLeft:8, background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:6, padding:'2px 8px', cursor:'pointer', fontSize:'0.72rem' }}>
                    ✏️ Rename
                  </button>
                </div>
                <div style={{ fontSize:'0.72rem', opacity:0.7 }}>{detail.series.status?.toUpperCase()}</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {detail.series.status==='upcoming' && <button onClick={()=>changeStatus(selId,'active')} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#16a34a', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>▶ Activate</button>}
                {detail.series.status==='active' && <button onClick={()=>changeStatus(selId,'completed')} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#6b7280', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>✔ Complete</button>}
              </div>
            </div>
          )}

          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:8 }}>
            FIXTURES ({detail?.fixtures?.length || 0})
          </div>

          {(detail?.fixtures || []).map((f, i) => (
            <div key={f.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px', marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'0.9rem', color:'var(--navy)' }}>Match {i+1}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                  {f.fixture_date || f.match_date
                    ? new Date(f.fixture_date || f.match_date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})
                    : '— date TBD'}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ flex:1, textAlign:'center' }}>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--navy)' }}>{f.capt_a_name||'TBD'}</div>
                  {/* The saved team names come back as capt1_team/capt2_team
                      — team_a_name is the create form's own field and is not
                      in this payload, so it always fell back to "Team A". */}
                  <div style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>{f.capt1_team||f.team_a_name||'Team A'}</div>
                </div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, color:'var(--gold)', fontSize:'0.9rem' }}>VS</div>
                <div style={{ flex:1, textAlign:'center' }}>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--navy)' }}>{f.capt_b_name||'TBD'}</div>
                  <div style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>{f.capt2_team||f.team_b_name||'Team B'}</div>
                </div>
              </div>
              {f.status==='completed' && <div style={{ textAlign:'center', fontSize:'0.7rem', color:'#16a34a', fontWeight:700, marginTop:6 }}>✅ COMPLETED</div>}

              {/* Venue + availability window. A series fixture with no window
                  cannot take availability at all, so it is called out here
                  rather than being discovered on the morning of the match. */}
              <div style={{ borderTop:'1px solid var(--border)', marginTop:8, paddingTop:7,
                fontSize:'0.7rem', color:'var(--text-muted)', lineHeight:1.5 }}>
                <div>📍 {f.venue || 'UCC Ground'} · 🕖 {(f.reporting_time || '07:30').slice(0,5)}</div>
              </div>

              <div style={{ textAlign:'right', marginTop:6, display:'flex', justifyContent:'flex-end', gap:14 }}>
                <button onClick={()=>setEditFix({
                    id: f.id,
                    venue_id: f.venue_id || '',
                    reporting_time: (f.reporting_time || '07:30').slice(0,5),
                    fixture_date: f.match_date ? new Date(f.match_date).toISOString().slice(0,10) : '',
                    capt_a_id: f.capt1_id || '', capt_b_id: f.capt2_id || '',
                    team_a_name: f.capt1_team || '', team_b_name: f.capt2_team || '',
                  })}
                  style={{ border:'none', background:'none', color:'var(--navy)',
                    fontSize:'0.72rem', fontWeight:700, cursor:'pointer', padding:0 }}>
                  Edit
                </button>
                <button onClick={()=>removeFixture(f.id)}
                  style={{ border:'none', background:'none', color:'#dc2626',
                    fontSize:'0.72rem', fontWeight:700, cursor:'pointer', padding:0 }}>
                  Remove from series
                </button>
              </div>
            </div>
          ))}

          {editFix && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200,
              display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
              <div style={{ background:'white', borderRadius:14, padding:18, width:'100%', maxWidth:420 }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)', marginBottom:12 }}>
                  Edit fixture
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Date</label>
                  <input type="date" className="form-control" value={editFix.fixture_date}
                    onChange={e=>setEditFix(f=>({...f,fixture_date:e.target.value}))}
                    style={{ fontSize:'0.85rem' }} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Venue</label>
                  <select className="form-control" value={editFix.venue_id}
                    onChange={e=>{
                      if (e.target.value === '__new') { setNewVenue({ name:'', latitude:'', longitude:'', timezone:'Asia/Kolkata' }); return; }
                      setEditFix(f=>({...f,venue_id:e.target.value}));
                    }}
                    style={{ fontSize:'0.85rem' }}>
                    <option value="">UCC Ground (default)</option>
                    {venues.filter(v=>!v.is_default).map(v=>(
                      <option key={v.id} value={v.id}>{v.name}{v.latitude==null?' — no coordinates':''}</option>
                    ))}
                    <option value="__new">+ Add a new venue…</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Reporting time</label>
                  <input type="time" className="form-control" value={editFix.reporting_time}
                    onChange={e=>setEditFix(f=>({...f,reporting_time:e.target.value}))}
                    style={{ fontSize:'0.85rem' }} />
                </div>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--navy)', margin:'6px 0 4px' }}>Captains (once decided)</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {[['capt_a_id','team_a_name','Captain A','Team A name'],['capt_b_id','team_b_name','Captain B','Team B name']].map(([ck,tk,cl,tl]) => (
                    <div key={ck}>
                      <select className="form-control" value={editFix[ck]} onChange={e=>setEditFix(f=>({...f,[ck]:e.target.value}))} style={{ fontSize:'0.8rem' }}>
                        <option value="">{cl} — not decided</option>
                        {members.map(m=><option key={m.id} value={m.id}>{m.display_name}</option>)}
                      </select>
                      <input className="form-control" placeholder={tl} value={editFix[tk]}
                        onChange={e=>setEditFix(f=>({...f,[tk]:e.target.value}))} style={{ fontSize:'0.8rem', marginTop:6 }} />
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <button className="btn btn-outline" style={{ flex:1 }} onClick={()=>setEditFix(null)}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex:1 }} onClick={saveFixtureEdit}>Save</button>
                </div>
              </div>
            </div>
          )}

          {newVenue && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200,
              display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
              <div style={{ background:'white', borderRadius:14, padding:18, width:'100%', maxWidth:420 }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)', marginBottom:4 }}>
                  Add a venue
                </div>
                <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginBottom:12, lineHeight:1.45 }}>
                  Coordinates let the weather card show the forecast for this ground.
                  Without them it falls back to the UCC Ground.
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Name *</label>
                  <input className="form-control" placeholder="e.g. Mohali Stadium"
                    value={newVenue.name} onChange={e=>setNewVenue(v=>({...v,name:e.target.value}))}
                    style={{ fontSize:'0.85rem' }} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.72rem' }}>Latitude</label>
                    <input className="form-control" placeholder="30.6942"
                      value={newVenue.latitude} onChange={e=>setNewVenue(v=>({...v,latitude:e.target.value}))}
                      style={{ fontSize:'0.85rem' }} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.72rem' }}>Longitude</label>
                    <input className="form-control" placeholder="76.7359"
                      value={newVenue.longitude} onChange={e=>setNewVenue(v=>({...v,longitude:e.target.value}))}
                      style={{ fontSize:'0.85rem' }} />
                  </div>
                </div>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:6 }}>
                  In Google Maps, right-click the ground and the first item on the menu is the
                  latitude and longitude — tap it to copy.
                </div>
                <div className="form-group" style={{ marginTop:10 }}>
                  <label className="form-label" style={{ fontSize:'0.72rem' }}>Time zone</label>
                  <select className="form-control" value={newVenue.timezone || 'Asia/Kolkata'}
                    onChange={e=>setNewVenue(v=>({...v,timezone:e.target.value}))}
                    style={{ fontSize:'0.85rem' }}>
                    <option value="Asia/Kolkata">India — Asia/Kolkata</option>
                    <option value="Asia/Dubai">UAE — Asia/Dubai</option>
                    <option value="Asia/Colombo">Sri Lanka — Asia/Colombo</option>
                    <option value="Asia/Kathmandu">Nepal — Asia/Kathmandu</option>
                    <option value="Asia/Singapore">Singapore — Asia/Singapore</option>
                    <option value="Europe/London">UK — Europe/London</option>
                    <option value="Australia/Sydney">Australia — Australia/Sydney</option>
                  </select>
                  <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:3 }}>
                    Reporting times for this ground are local to it, and the weather is fetched in this zone.
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <button className="btn btn-outline" style={{ flex:1 }} onClick={()=>setNewVenue(null)}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex:1 }} onClick={createVenue}>Add venue</button>
                </div>
              </div>
            </div>
          )}

          {/* ── Series settings: ONE availability window for every fixture ── */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--navy)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:4 }}>
              ✋ Availability &amp; selection
            </div>
            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:12, lineHeight:1.45 }}>
              One window covers the whole series. Members answer YES or NO for each fixture inside it,
              and nothing else is sent afterwards.
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <label className="form-label" style={{ fontSize:'0.7rem' }}>Window opens</label>
                <input type="datetime-local" className="form-control" value={seriesForm.avail_open_at}
                  onChange={e=>setSeriesForm(f=>({...f,avail_open_at:e.target.value}))}
                  style={{ fontSize:'0.76rem' }} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize:'0.7rem' }}>Window closes</label>
                <input type="datetime-local" className="form-control" value={seriesForm.avail_deadline}
                  onChange={e=>setSeriesForm(f=>({...f,avail_deadline:e.target.value}))}
                  style={{ fontSize:'0.76rem' }} />
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
              <div>
                <label className="form-label" style={{ fontSize:'0.7rem' }}>Must be free for at least</label>
                <input type="number" min="1" className="form-control"
                  value={seriesForm.min_fixtures_required}
                  onChange={e=>setSeriesForm(f=>({...f,min_fixtures_required:e.target.value}))}
                  style={{ fontSize:'0.78rem' }} />
                <div style={{ fontSize:'0.66rem', color:'var(--text-muted)', marginTop:3 }}>
                  matches, or he is not considered for the series
                </div>
              </div>
              <div>
                <label className="form-label" style={{ fontSize:'0.7rem' }}>Squad size per captain</label>
                <input type="number" min="1" className="form-control" placeholder="e.g. 18"
                  value={seriesForm.squad_size}
                  onChange={e=>setSeriesForm(f=>({...f,squad_size:e.target.value}))}
                  style={{ fontSize:'0.78rem' }} />
                <div style={{ fontSize:'0.66rem', color:'var(--text-muted)', marginTop:3 }}>
                  leave blank for no limit
                </div>
              </div>
            </div>

            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button onClick={saveSeriesSettings} disabled={savingSeries}
                style={{ flex:1, padding:10, borderRadius:8, border:'1px solid var(--navy)',
                  background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif',
                  fontWeight:700, fontSize:'0.82rem', cursor:'pointer' }}>
                {savingSeries ? 'Saving…' : 'Save settings'}
              </button>
              <button onClick={announceSeries} disabled={announcing}
                style={{ flex:1, padding:10, borderRadius:8, border:'none',
                  background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif',
                  fontWeight:700, fontSize:'0.82rem', cursor:'pointer' }}>
                {announcing ? 'Sending…' : (detail?.series?.announced_at ? '📢 Announce again' : '📢 Announce to club')}
              </button>
            </div>

            {detail?.series?.announced_at && (
              <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:7, textAlign:'center' }}>
                Schedule sent {fmtIst(detail.series.announced_at)}
              </div>
            )}
            {!detail?.series?.avail_open_at && (
              <div style={{ fontSize:'0.7rem', color:'#b45309', marginTop:8, fontWeight:600 }}>
                ⚠️ No window set — members cannot answer for any fixture in this series yet.
              </div>
            )}
          </div>

          {/* ── Fixture builder ── */}
          <div style={{ background:'white', border:'2px dashed var(--border)', borderRadius:12, padding:14, marginTop:8 }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', marginBottom:4 }}>➕ ADD FIXTURES</div>
            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:12, lineHeight:1.45 }}>
              Add the whole series at once — a best-of-five is five rows and one Submit.
              Nothing is created unless every row is valid.
            </div>

            {/* Defaults applied to every row */}
            <div style={{ background:'#f8fafc', border:'1px solid var(--border)', borderRadius:10, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.06em', color:'var(--navy)', textTransform:'uppercase', marginBottom:8 }}>
                Defaults for every match
              </div>

              <div style={{ fontSize:'0.66rem', color:'var(--text-muted)', marginBottom:12 }}>
                Just the dates for now — captains and team names are set later with <b>Edit</b> on each fixture
                (for an auction series, the auction sets them).
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div>
                  <label className="form-label" style={{ fontSize:'0.7rem' }}>Usual venue</label>
                  <select className="form-control" value={fixDefaults.venue}
                    onChange={e=>{
                      if (e.target.value === '__new') { setNewVenue({ name:'', latitude:'', longitude:'', timezone:'Asia/Kolkata' }); return; }
                      setFixDefaults(d=>({...d,venue:e.target.value}));
                    }}
                    style={{ fontSize:'0.8rem' }}>
                    <option value="">UCC Ground (default)</option>
                    {venues.filter(v=>!v.is_default).map(v=>(
                      <option key={v.id} value={v.id}>{v.name}{v.latitude==null?' — no coordinates':''}</option>
                    ))}
                    <option value="__new">+ Add a new venue…</option>
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize:'0.7rem' }}>Reporting time</label>
                  <input type="time" className="form-control"
                    value={fixDefaults.reporting_time}
                    onChange={e=>setFixDefaults(d=>({...d,reporting_time:e.target.value}))}
                    style={{ fontSize:'0.8rem' }} />
                </div>
              </div>
              <div style={{ fontSize:'0.66rem', color:'var(--text-muted)', marginTop:-4, marginBottom:10 }}>
                Only a starting point — each match below has its own venue field, and they can all be different. Leave blank for the club's own ground.
              </div>

              <button onClick={applyDefaultsToAll}
                style={{ width:'100%', marginTop:4, padding:8, borderRadius:8, border:'1px solid var(--navy)',
                  background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700,
                  fontSize:'0.78rem', cursor:'pointer' }}>
                Apply to every row below
              </button>
            </div>

            {/* One block per fixture */}
            {fixRows.map((r, i) => (
              <div key={i} style={{ border:'1px solid var(--border)', borderRadius:10, padding:12, marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'var(--navy)' }}>
                    Match {i + 1}
                  </div>
                  {fixRows.length > 1 && (
                    <button onClick={()=>setFixRows(rows=>rows.filter((_,idx)=>idx!==i))}
                      style={{ border:'none', background:'none', color:'#dc2626', fontSize:'0.74rem', fontWeight:700, cursor:'pointer' }}>
                      Remove
                    </button>
                  )}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize:'0.7rem' }}>Date *</label>
                    <input type="date" className="form-control" value={r.fixture_date}
                      onChange={e=>setRowDate(i, e.target.value)} style={{ fontSize:'0.8rem' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize:'0.7rem' }}>Venue</label>
                    <select className="form-control" value={r.venue}
                      onChange={e=>{
                        if (e.target.value === '__new') { setNewVenue({ name:'', latitude:'', longitude:'', timezone:'Asia/Kolkata' }); return; }
                        patchRow(i,{venue:e.target.value});
                      }}
                      style={{ fontSize:'0.78rem' }}>
                      <option value="">UCC Ground (default)</option>
                      {venues.filter(v=>!v.is_default).map(v=>(
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                      <option value="__new">+ Add a new venue…</option>
                    </select>
                  </div>
                </div>


                <div className="form-group" style={{ marginTop:8 }}>
                  <label className="form-label" style={{ fontSize:'0.7rem' }}>Reporting time</label>
                  <input type="time" className="form-control"
                    value={r.reporting_time || fixDefaults.reporting_time}
                    onChange={e=>patchRow(i,{reporting_time:e.target.value})}
                    style={{ fontSize:'0.78rem' }} />
                </div>
              </div>
            ))}

            <button onClick={()=>setFixRows(rows=>[...rows, newRowFromDefaults()])}
              style={{ width:'100%', padding:9, borderRadius:8, border:'1px dashed var(--navy)',
                background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif',
                fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
              + Add another match
            </button>

            {bulkProblems.length > 0 && (
              <div style={{ background:'#fff1f2', border:'1px solid #fca5a5', borderRadius:10,
                padding:'10px 12px', marginTop:10 }}>
                <div style={{ fontWeight:700, fontSize:'0.78rem', color:'#991b1b', marginBottom:5 }}>
                  Nothing was created — fix these first
                </div>
                <ul style={{ margin:0, paddingLeft:16, fontSize:'0.75rem', color:'#991b1b' }}>
                  {bulkProblems.map((p,i)=><li key={i} style={{ marginBottom:2 }}>{p}</li>)}
                </ul>
              </div>
            )}

            <button onClick={submitFixtures} disabled={addingFix}
              style={{ width:'100%', marginTop:10, padding:11, borderRadius:8, border:'none',
                background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif',
                fontWeight:700, fontSize:'0.88rem', cursor:addingFix?'default':'pointer',
                opacity:addingFix?0.6:1 }}>
              {addingFix ? '⏳ Adding…' : `Submit ${fixRows.length} fixture${fixRows.length===1?'':'s'}`}
            </button>
          </div>
        </div>
      )}

      {/* ── AVAILABILITY TAB ── */}
      {detailTab === 'availability' && (
        <div style={{ padding:'12px 16px' }}>
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:'0.75rem', color:'#1e40af', lineHeight:1.7 }}>
            <strong>Series availability flow:</strong><br />
            1. Open window → all members notified → respond Yes/No per fixture date<br />
            2. View the grid below → decide actual match dates<br />
            3. Schedule matches in Match Management<br />
            4. No second availability request needed — captains pick squads in Squads tab
          </div>

          {/* Window control */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:12 }}>
            {availData?.window?.is_open ? (
              <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <div>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'#16a34a' }}>✅ Window is OPEN</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                      Opened: {new Date(availData.window.opened_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      {availData.window.deadline && ` · Deadline: ${new Date(availData.window.deadline).toLocaleDateString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}`}
                    </div>
                  </div>
                  <button onClick={() => setDetailTab('setup')} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)', background:'white', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>
                    Edit window in Setup
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:10 }}>
                  {availData?.window ? '⛔ Window CLOSED' : '📭 No availability request sent yet'}
                </div>
                {/* One window covers the whole series and lives on the series
                    row, so it is set with the open time and deadline in Setup
                    and announced from there. The buttons that used to sit here
                    called endpoints that never existed. */}
                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', lineHeight:1.6, marginBottom:10 }}>
                  The availability window covers the whole series. Set the open time and
                  deadline under <strong>Setup</strong>, then announce the series — members
                  answer YES or NO per fixture inside that one window.
                </div>
                <button onClick={() => setDetailTab('setup')}
                  style={{ width:'100%', padding:10, borderRadius:8, border:'none', background:'var(--navy)', color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.85rem', cursor:'pointer' }}>
                  Go to Setup
                </button>
                {(detail?.fixtures||[]).length === 0 && <div style={{ fontSize:'0.72rem', color:'#dc2626', marginTop:6 }}>Add fixtures in Setup tab first</div>}
              </>
            )}
          </div>

          {/* Availability grid — fixture by fixture */}
          {(availData?.fixtures || []).map((f, fi) => {
            // The payload is members[] each carrying an answers map keyed by
            // fixture id — not a responses list per fixture. Reading
            // f.responses threw on every render, which is why this tab came
            // up as a blank page.
            const roster    = availData?.members || [];
            const answerFor = (m) => (m.away_fixture_ids || []).includes(f.id) ? 'away' : (m.answers || {})[f.id];
            const yesMembers = roster.filter(m => answerFor(m) === 'yes');
            const noMembers  = roster.filter(m => answerFor(m) === 'no');
            const awayMembers = roster.filter(m => answerFor(m) === 'away');
            const noReply    = roster.filter(m => !answerFor(m));
            return (
              <div key={f.fixture_id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <div>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.9rem', color:'var(--navy)' }}>
                      Match {fi+1}
                    </div>
                    <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                      {f.match_date||f.day ? new Date(f.match_date||f.day).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'}) : '— date TBD'}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:'0.72rem', background:'#dcfce7', color:'#166534', padding:'3px 8px', borderRadius:10, fontWeight:700 }}>{yesMembers.length} YES</span>
                    <span style={{ fontSize:'0.72rem', background:'#fee2e2', color:'#991b1b', padding:'3px 8px', borderRadius:10, fontWeight:700 }}>{noMembers.length} NO</span>
                    <span style={{ fontSize:'0.72rem', background:'#f3f4f6', color:'#6b7280', padding:'3px 8px', borderRadius:10, fontWeight:700 }}>{noReply.length} —</span>
                    {awayMembers.length > 0 && <span style={{ fontSize:'0.72rem', background:'#ede9fe', color:'#6d28d9', padding:'3px 8px', borderRadius:10, fontWeight:700 }}>{awayMembers.length} away</span>}
                  </div>
                </div>
                {yesMembers.length > 0 && (
                  <>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#166534', marginBottom:4 }}>✅ AVAILABLE ({yesMembers.length})</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
                      {yesMembers.map(r => (
                        <span key={r.id} style={{ fontSize:'0.72rem', background:'#f0fdf4', color:'#166534', border:'1px solid #bbf7d0', padding:'3px 8px', borderRadius:20, fontWeight:600 }}>
                          {r.display_name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {noMembers.length > 0 && (
                  <>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#991b1b', marginBottom:4 }}>❌ NOT AVAILABLE ({noMembers.length})</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                      {noMembers.map(r => (
                        <span key={r.id} style={{ fontSize:'0.72rem', background:'#fef2f2', color:'#991b1b', border:'1px solid #fecaca', padding:'3px 8px', borderRadius:20, fontWeight:600 }}>
                          {r.display_name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {yesMembers.length === 0 && noMembers.length === 0 && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', fontStyle:'italic' }}>No responses yet</div>}
              </div>
            );
          })}
          {(!availData?.fixtures || availData.fixtures.length === 0) && (
            <div style={{ textAlign:'center', padding:24, color:'var(--text-muted)', fontSize:'0.85rem' }}>Add fixtures in Setup tab first, then open availability</div>
          )}
        </div>
      )}

      {/* ── SQUADS TAB ── */}
      {detailTab === 'squads' && (
        <div style={{ padding:'12px 16px' }}>
          <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:'0.75rem', color:'#92400e', lineHeight:1.7 }}>
            <strong>How this works:</strong><br />
            1️⃣ Squads are picked once, before the series — from members available for at least {detail?.series?.min_fixtures_required || 1} fixture(s)<br />
            2️⃣ On match day each captain picks the XI from his squad, and only those who said YES for that date<br />
            💤 Away fixture: everyone not playing is Series Rested — counts for both fee and attendance %<br />
            🏠 Home fixture: a member who said NO is marked Absent; anyone who said YES but was not picked is Series Rested<br />
            🔒 A member already checked in, opted out or exempted is left untouched
          </div>

          {/* ── Stage one: the series squads ── */}
          {!squadFixture && (() => {
            const caps = seriesCaptains();
            const roster = availData?.members || [];
            const limit = detail?.series?.squad_size || null;
            const inAny = (id) => Object.entries(seriesSquads).find(([, set]) => set.has(id));
            return (
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.85rem', color:'var(--navy)' }}>
                    SERIES SQUADS
                  </div>
                  <button onClick={loadSeriesSquads} style={{ background:'none', border:'none', color:'var(--navy)', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
                    ↻ Load saved
                  </button>
                </div>

                {caps.length === 0 ? (
                  <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>No captains on the fixtures yet — set them in Setup.</div>
                ) : roster.length === 0 ? (
                  <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>No availability collected yet — open the Availability tab.</div>
                ) : (<>
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    {caps.map(c => {
                      const count = (seriesSquads[c.id] || new Set()).size;
                      const on = squadCapt === c.id;
                      return (
                        <button key={c.id} onClick={() => setSquadCapt(c.id)}
                          style={{ flex:1, padding:'10px 8px', borderRadius:10, cursor:'pointer',
                            border:'2px solid', borderColor: on ? 'var(--navy)' : '#e5e7eb',
                            background: on ? 'var(--navy)' : 'white', color: on ? 'white' : 'var(--navy)',
                            fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.8rem' }}>
                          {c.team || c.name} · {count}{limit ? `/${limit}` : ''}
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
                    {roster.map(m => {
                      const where = inAny(m.id);
                      const mine = where && where[0] === squadCapt;
                      // Below the threshold the server refuses them, so they
                      // are shown but not selectable.
                      const blocked = !m.eligible;
                      return (
                        <div key={m.id} onClick={() => !blocked && toggleSquadMember(m.id)}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:10,
                            cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.5 : 1,
                            border:'1px solid', borderColor: mine ? '#bbf7d0' : where ? '#c7d2fe' : '#e5e7eb',
                            background: mine ? '#f0fdf4' : where ? '#eef2ff' : 'white' }}>
                          <div style={{ width:18, height:18, borderRadius:'50%', flexShrink:0,
                            background: mine ? '#16a34a' : where ? '#6366f1' : '#e5e7eb',
                            color:'white', fontSize:'0.55rem', fontWeight:900,
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {mine ? '✓' : where ? '↔' : ''}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.8rem',
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.display_name}</div>
                            <div style={{ fontSize:'0.6rem', color:'var(--text-muted)' }}>
                              {blocked ? `only ${m.yes_count} yes` : where && !mine ? 'in the other squad' : `${m.yes_count} yes`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button onClick={saveSeriesSquads} disabled={savingSeriesSquads} className="btn btn-primary btn-block">
                    {savingSeriesSquads ? '⏳ Saving…' : '💾 Save series squads'}
                  </button>
                </>)}
              </div>
            );
          })()}

          {/* Fixture selector */}
          {!squadFixture ? (
            <>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:8 }}>MATCH DAY — SELECT FIXTURE TO PICK THE XI</div>
              {(detail?.fixtures || []).map((f, i) => (
                <div key={f.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:8, cursor:'pointer' }}
                  onClick={() => loadSquad(f)}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                      <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:'var(--navy)' }}>Match {i+1} — {f.capt_a_name||'TBD'} vs {f.capt_b_name||'TBD'}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                        {f.fixture_date||f.match_date ? new Date(f.fixture_date||f.match_date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'}) : '— date TBD'}
                      </div>
                    </div>
                    <span style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>→</span>
                  </div>
                </div>
              ))}
              {(detail?.fixtures||[]).length === 0 && <div style={{ textAlign:'center', padding:24, color:'var(--text-muted)', fontSize:'0.85rem' }}>Add fixtures in Setup tab first</div>}
            </>
          ) : (
            <>
              <button onClick={()=>{setSquadFixture(null);setSquadData(null);}} style={{ background:'none', border:'none', color:'var(--navy)', fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem', cursor:'pointer', marginBottom:12, padding:0 }}>← Back to fixtures</button>

              <div style={{ background:'var(--navy)', borderRadius:12, padding:'12px 16px', marginBottom:12, color:'white' }}>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900 }}>{squadFixture.capt_a_name} vs {squadFixture.capt_b_name}</div>
                <div style={{ fontSize:'0.72rem', opacity:0.7 }}>
                  {squadFixture.fixture_date||squadFixture.match_date ? new Date(squadFixture.fixture_date||squadFixture.match_date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'}) : 'Date TBD'}
                </div>
              </div>

              {!squadData ? (
                <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)' }}>Loading squad data…</div>
              ) : (
                <>
                  {/* A match already played has its XI in the scorecard —
                      including which side each man was on, from the innings he
                      batted or bowled in. */}
                  {squadData.played?.length > 0 && Object.keys(picks).length === 0 && (
                    <button
                      onClick={() => {
                        const next = {};
                        for (const p of squadData.played) next[p.member_id] = p.captain_id;
                        setPicks(next);
                        setAllowUnavailable(true);
                        setMarkPresent(true);
                        toast.success(`Filled ${squadData.played.length} players from the scorecard — check the sides, then save`);
                      }}
                      style={{ width:'100%', padding:10, marginBottom:10, borderRadius:10, cursor:'pointer',
                        border:'1px dashed var(--navy)', background:'#eef2ff', color:'var(--navy)',
                        fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.82rem' }}>
                      ⬇ Fill the XI from the scorecard ({squadData.played.length} played)
                    </button>
                  )}

                  {/* Which side is being picked. Every tap below goes into
                      this captain's XI. */}
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    {/* capt1/capt2 as the fixtures payload names them. */}
                    {[{ id: squadFixture.capt1_id, name: squadFixture.capt_a_name, team: squadFixture.capt1_team || squadFixture.team_a_name },
                      { id: squadFixture.capt2_id, name: squadFixture.capt_b_name, team: squadFixture.capt2_team || squadFixture.team_b_name }]
                      .filter(c => c.id).map(c => {
                        const count = Object.values(picks).filter(v => v === c.id).length;
                        const on = sideCapt === c.id;
                        return (
                          <button key={c.id} onClick={() => setSideCapt(c.id)}
                            style={{ flex:1, padding:'10px 8px', borderRadius:10, cursor:'pointer',
                              border:'2px solid', borderColor: on ? 'var(--navy)' : '#e5e7eb',
                              background: on ? 'var(--navy)' : 'white', color: on ? 'white' : 'var(--navy)',
                              fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.8rem' }}>
                            {c.team || c.name || 'Side'} · {count}
                          </button>
                        );
                      })}
                  </div>

                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', marginBottom:8 }}>
                    TAP A MEMBER TO PUT THEM IN THE SELECTED SIDE
                    <span style={{ fontWeight:400, marginLeft:4 }}>— tap again to remove</span>
                  </div>

                  {(allowUnavailable ? squadData.all : squadData.squad).length === 0 ? (
                    <div style={{ textAlign:'center', padding:20, color:'var(--text-muted)', fontSize:'0.85rem', fontStyle:'italic' }}>
                      No squad members to show. Pick the series squads above first, or tick the override below to pick from every member.
                    </div>
                  ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                      {(allowUnavailable ? squadData.all : squadData.squad).map(m => {
                        const pickedBy = picks[m.member_id] || null;
                        const isPlaying = !!pickedBy;
                        // A NO stays unpickable unless the override below is on
                        // — for a match already played and being entered late.
                        const saidNo = m.response === 'no' && !allowUnavailable;
                        const otherSide = isPlaying && pickedBy !== sideCapt;
                        return (
                          <div key={m.member_id}
                            onClick={() => !saidNo && togglePick(m.member_id)}
                            style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:10,
                              cursor: saidNo ? 'not-allowed' : 'pointer',
                              border: saidNo ? '1px solid #fecaca' : `1px solid ${isPlaying?'#bbf7d0':'#e5e7eb'}`,
                              background: saidNo ? '#fef2f2' : isPlaying?'#f0fdf4':'white',
                              opacity: saidNo ? 0.6 : 1, transition:'all 0.1s' }}>
                            <div style={{ width:20, height:20, borderRadius:'50%', flexShrink:0,
                              background: saidNo ? '#fca5a5' : isPlaying ? (otherSide ? '#6366f1' : '#16a34a') : '#e5e7eb',
                              display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', color:'white', fontWeight:900 }}>
                              {saidNo ? '✕' : isPlaying ? '✓' : ''}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.82rem',
                                color: saidNo ? '#991b1b' : isPlaying?'#166534':'var(--navy)',
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {m.display_name}
                              </div>
                              <div style={{ fontSize:'0.62rem', color: saidNo ? '#fca5a5' : 'var(--text-muted)' }}>
                                {saidNo ? 'Said NO for this date'
                                  : otherSide ? 'In the other side — tap to move'
                                  : m.response === 'no' ? 'Said NO · override on'
                                  : m.ucc_id}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Summary */}
                  <div style={{ background:'#f9fafb', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:'0.78rem' }}>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                      <span style={{ color:'#166534', fontWeight:700 }}>✅ Playing: {Object.keys(picks).length}</span>
                      <span style={{ color:'#7c3aed', fontWeight:700 }}>💤 Series Rested: everyone else</span>
                      <span style={{ color:'var(--text-muted)', fontWeight:700 }}>In squad &amp; said YES: {squadData.squad.filter(m=>m.response==='yes').length}</span>
                    </div>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, fontSize:'0.72rem', color:'var(--text-muted)', cursor:'pointer' }}>
                      <input type="checkbox" checked={allowUnavailable}
                        onChange={e => setAllowUnavailable(e.target.checked)} />
                      Pick from every member, ignoring squad and availability — for a match already played
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, fontSize:'0.72rem', color:'var(--text-muted)', cursor:'pointer' }}>
                      <input type="checkbox" checked={markPresent}
                        onChange={e => setMarkPresent(e.target.checked)} />
                      Also mark the XI present — for an away match nobody could check in at
                    </label>
                  </div>

                  <button onClick={saveSquad} disabled={savingSquad || Object.keys(picks).length === 0} className="btn btn-primary btn-block">
                    {savingSquad ? '⏳ Saving…' : `💾 Save XI (${Object.keys(picks).length} selected)`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
export function AdminExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category:'ground', description:'', amount:'', mode:'cash', paid_by:'', expense_date:new Date().toISOString().split('T')[0] });

  const CATEGORIES = ['ground','equipment','refreshments','travel','prize','miscellaneous'];

  function load() {
    return Promise.all([
      api.get('/expenses').catch(()=>({data:{expenses:[]}})),
      api.get('/members').catch(()=>({data:{members:[]}})),
    ]).then(([e,m])=>{
      setExpenses(e.data.expenses||[]);
      setMembers((m.data.members||[]).filter(x=>!x.is_observer));
    }).finally(()=>setLoading(false));
  }
  useEffect(()=>{ load(); },[]);

  async function saveExpense() {
    if (!form.amount) { toast.error('Amount required'); return; }
    setSaving(true);
    try {
      await api.post('/expenses', {...form, amount: parseFloat(form.amount)});
      toast.success('Expense recorded!');
      setForm({ category:'ground', description:'', amount:'', mode:'cash', paid_by:'', expense_date:new Date().toISOString().split('T')[0] });
      const e = await api.get('/expenses');
      setExpenses(e.data.expenses||[]);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  const total = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);

  if (loading) return <div style={{padding:32,textAlign:'center',color:'var(--text-muted)'}}>Loading…</div>;

  return (
    <div style={{paddingBottom:80}}>
      <AdminPageHeader title="Expenses & Collections" icon="💰">Track all club expenses and collections</AdminPageHeader>
      <div style={{padding:'12px 16px'}}>
        {/* Summary */}
        <div style={{background:'var(--navy)',borderRadius:12,padding:'14px 16px',marginBottom:16,color:'white'}}>
          <div style={{fontSize:'0.7rem',fontFamily:'Rajdhani,sans-serif',fontWeight:700,letterSpacing:'0.1em',opacity:0.6,marginBottom:2}}>TOTAL EXPENSES</div>
          <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,fontSize:'1.8rem',color:'#F5C518'}}>₹{total.toLocaleString('en-IN')}</div>
          <div style={{fontSize:'0.72rem',opacity:0.5,marginTop:2}}>{expenses.length} records</div>
        </div>

        {/* Add expense form */}
        <div style={{background:'white',borderRadius:12,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.85rem',color:'var(--navy)',marginBottom:12}}>➕ Add Expense</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Amount (₹)</label>
              <input type="number" className="form-control" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Mode</label>
              <select className="form-control" value={form.mode} onChange={e=>setForm(f=>({...f,mode:e.target.value}))}>
                {['cash','upi','bank','card'].map(m=><option key={m} value={m}>{m.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={form.expense_date} onChange={e=>setForm(f=>({...f,expense_date:e.target.value}))} />
            </div>
            <div className="form-group" style={{gridColumn:'span 2'}}>
              <label className="form-label">Description</label>
              <input type="text" className="form-control" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Brief description" />
            </div>
            <div className="form-group" style={{gridColumn:'span 2'}}>
              <label className="form-label">Paid By</label>
              <select className="form-control" value={form.paid_by} onChange={e=>setForm(f=>({...f,paid_by:e.target.value}))}>
                <option value="">— Select member —</option>
                {members.map(m=><option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </div>
          </div>
          <button className="btn btn-primary btn-block" style={{marginTop:8}} onClick={saveExpense} disabled={saving}>{saving?'Saving…':'Record Expense'}</button>
        </div>

        {/* Expense list */}
        <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.8rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>All Expenses</div>
        {expenses.map(e=>(
          <div key={e.id} style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',background:'white',borderRadius:10,padding:'10px 14px',marginBottom:6,boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
            <div>
              <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:'var(--navy)',fontSize:'0.88rem',textTransform:'capitalize'}}>{e.category} {e.description?`· ${e.description}`:''}</div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{new Date(e.expense_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · {(e.mode||'').toUpperCase()}{e.paid_by_name?` · ${e.paid_by_name}`:''}</div>
            </div>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:800,color:'var(--navy)',fontSize:'1rem',whiteSpace:'nowrap',marginLeft:8}}>₹{parseFloat(e.amount).toLocaleString('en-IN')}</div>
          </div>
        ))}
        {expenses.length===0 && <div style={{textAlign:'center',padding:32,color:'var(--text-muted)',fontSize:'0.85rem'}}>No expenses recorded yet.</div>}
      </div>
    </div>
  );
}
export function AdminLunch() {
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [menuText, setMenuText] = useState('');
  const [lunchCount, setLunchCount] = useState(null);
  const [lunchMembers, setLunchMembers] = useState([]);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load ALL matches (upcoming + past), not just imported — menu needed before scorecard import
    Promise.all([
      api.get('/matches?limit=30&upcoming=true').catch(()=>({data:{matches:[]}})),
      api.get('/matches?limit=20&past=true').catch(()=>({data:{matches:[]}})),
    ]).then(([u, p]) => {
      const all = [...(u.data.matches||[]), ...(p.data.matches||[])];
      // Deduplicate by id, sort newest first
      const seen = new Set();
      const deduped = all.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; })
        .sort((a,b) => new Date(b.match_date) - new Date(a.match_date));
      setMatches(deduped);
      // Auto-select nearest upcoming match
      const upcoming = deduped.find(m => new Date(m.match_date) >= new Date());
      if (upcoming) setSelectedMatch(upcoming.id);
      else if (deduped.length > 0) setSelectedMatch(deduped[0].id);
    }).finally(()=>setLoading(false));
  },[]);

  useEffect(()=>{
    if (!selectedMatch) return;
    api.get('/lunch/'+selectedMatch).catch(()=>({data:{menu:null,lunch_count:0,lunch_members:[]}})).then(r=>{
      setCurrentMenu(r.data.menu||null);
      setMenuText(r.data.menu?.menu_text||'');
      setLunchCount(r.data.lunch_count||0);
      setLunchMembers(r.data.lunch_members||[]);
    });
  },[selectedMatch]);

  async function saveMenu() {
    if (!selectedMatch) { toast.error('Select a match'); return; }
    setSaving(true);
    try {
      await api.post('/lunch', { match_id: selectedMatch, menu_text: menuText });
      toast.success('Menu saved!');
      const r = await api.get('/lunch/'+selectedMatch);
      setCurrentMenu(r.data.menu||null);
      setLunchCount(r.data.lunch_count||0);
      setLunchMembers(r.data.lunch_members||[]);
    } catch { toast.error('Failed to save menu'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{padding:32,textAlign:'center',color:'var(--text-muted)'}}>Loading…</div>;

  return (
    <div style={{paddingBottom:80}}>
      <AdminPageHeader title="Lunch & Menu" icon="🍱">Set lunch menu for each match day</AdminPageHeader>
      <div style={{padding:'12px 16px'}}>
        <div style={{background:'white',borderRadius:12,padding:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          <div className="form-group">
            <label className="form-label">Select Match</label>
            <select className="form-control" value={selectedMatch} onChange={e=>setSelectedMatch(e.target.value)}>
              <option value="">— Select match —</option>
              {matches.map(m=><option key={m.id} value={m.id}>{new Date(m.match_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · {m.series_name||m.match_type||'Match'}</option>)}
            </select>
          </div>
          {selectedMatch && lunchCount!==null && (
            <div style={{background:'#f0f9ff',borderRadius:8,padding:'10px 12px',marginBottom:12}}>
              <div style={{fontSize:'0.8rem',color:'#0369a1',fontWeight:700,marginBottom: lunchMembers.length ? 8 : 0}}>
                🙋 {lunchCount} members opted in for lunch this match
              </div>
              {lunchMembers.length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {lunchMembers.map((m,i) => (
                    <span key={i} style={{background:'white',border:'1px solid #bae6fd',borderRadius:20,padding:'3px 10px',fontSize:'0.75rem',color:'#0369a1',fontWeight:600}}>
                      {m.display_name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Menu (describe today's lunch)</label>
            <textarea className="form-control" rows={4} value={menuText} onChange={e=>setMenuText(e.target.value)} placeholder="e.g. Dal Makhani, Jeera Rice, Raita, Salad, Gulab Jamun" style={{resize:'vertical'}} />
          </div>
          {currentMenu && (
            <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:8}}>
              Last updated: {new Date(currentMenu.uploaded_at||currentMenu.created_at).toLocaleString('en-IN')}
            </div>
          )}
          <button className="btn btn-primary btn-block" onClick={saveMenu} disabled={saving}>{saving?'Saving…':'Save Menu'}</button>
        </div>

        {/* Thursday notice info box */}
        <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:12,padding:'12px 16px',marginTop:12}}>
          <div style={{fontFamily:'Rajdhani,sans-serif',fontWeight:700,fontSize:'0.85rem',color:'#92400e',marginBottom:4}}>📅 Thursday Lunch Notice</div>
          <div style={{fontSize:'0.78rem',color:'#78350f',lineHeight:1.5}}>
            A reminder notice is automatically sent to the Lunch Admin every Thursday at 6 PM to post the upcoming Sunday's menu. Make sure the menu is posted before Friday 10:30 AM when availability opens — members will see it on the availability page.
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminMemberReport() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('display_name');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [season, setSeason] = useState(2026);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/member-report?season=${season}`)
      .then(r => setMembers(r.data.members || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [season]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'display_name' ? 'asc' : 'desc');
    }
  }

  const sorted = [...members].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const SortBtn = ({ col, label }) => {
    const active = sortKey === col;
    const arrow = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕';
    return (
      <button onClick={() => toggleSort(col)} style={{
        background: active ? 'var(--navy)' : 'white',
        color: active ? 'white' : 'var(--navy)',
        border: '1px solid var(--navy)',
        borderRadius: 6, padding: '4px 8px',
        fontSize: '0.68rem', fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap',
        fontFamily: 'Rajdhani, sans-serif',
      }}>
        {label}{arrow}
      </button>
    );
  };

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Member Report" icon="📊">Attendance, fines & dues per member</AdminPageHeader>
      <div style={{ padding: '12px 16px' }}>

        {/* Season selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>Season:</label>
          <select className="form-control" style={{ width: 100 }} value={season} onChange={e => setSeason(parseInt(e.target.value))}>
            {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Sort buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', alignSelf: 'center', marginRight: 4 }}>Sort by:</span>
          <SortBtn col="display_name"   label="Name" />
          <SortBtn col="attendance_pct" label="Attendance %" />
          <SortBtn col="matches_played" label="Matches" />
          <SortBtn col="pending_fines"  label="Pending Fines" />
          <SortBtn col="total_fines"    label="Total Fines" />
          <SortBtn col="dues"           label="Dues" />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
        ) : (
          <div className="card">
            {sorted.map((m, i) => (
              <div key={m.id} style={{
                padding: '12px 14px',
                borderBottom: i < sorted.length - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--navy)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: 'white',
                  fontSize: '0.75rem', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif',
                  overflow: 'hidden',
                }}>
                  {m.photo_url
                    ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (m.display_name?.charAt(0) || '?')}
                </div>

                {/* Name + ID */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.display_name}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{m.ucc_id}</div>
                </div>

                {/* Stats pills */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {/* Attendance */}
                  <div style={{ textAlign: 'center', minWidth: 48 }}>
                    <div style={{
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.9rem',
                      color: m.attendance_pct >= 70 ? '#16a34a' : m.attendance_pct >= 50 ? '#d97706' : '#dc2626',
                    }}>{m.attendance_pct}%</div>
                    <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', lineHeight: 1 }}>{m.matches_played}/{m.total_matches}</div>
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>Attendance</div>
                  </div>

                  {/* Pending Fines */}
                  <div style={{ textAlign: 'center', minWidth: 48 }}>
                    <div style={{
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.9rem',
                      color: m.pending_fines > 0 ? '#dc2626' : '#16a34a',
                    }}>₹{m.pending_fines.toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>Fines Due</div>
                  </div>

                  {/* Dues */}
                  <div style={{ textAlign: 'center', minWidth: 48 }}>
                    <div style={{
                      fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.9rem',
                      color: m.dues > 0 ? '#dc2626' : m.dues < 0 ? '#16a34a' : '#6b7280',
                    }}>₹{Math.abs(m.dues).toLocaleString('en-IN')}{m.dues < 0 ? ' CR' : ''}</div>
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>{m.dues < 0 ? 'Advance' : 'Dues'}</div>
                  </div>
                </div>
              </div>
            ))}
            {sorted.length === 0 && (
              <div className="empty-state"><div className="empty-state__title">No data available</div></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/audit?limit=50').then(res => setLogs(res.data.logs || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <AdminPageHeader title="Audit Log"  />
      <div style={{ padding: '16px' }}>
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="card">
            {logs.map(log => (
              <div key={log.id} className="list-item">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{log.action?.toUpperCase()} — {log.table_name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>
                    By {log.changed_by_name} • {new Date(log.changed_at).toLocaleString('en-IN')}
                  </div>
                  {log.note && <div style={{ fontSize: '0.72rem', color: 'var(--ucc-gray-400)', fontStyle: 'italic' }}>{log.note}</div>}
                </div>
                <span className={`badge ${log.action === 'delete' ? 'badge-danger' : log.action === 'create' ? 'badge-success' : 'badge-info'}`}>
                  {log.action}
                </span>
              </div>
            ))}
            {logs.length === 0 && <div className="empty-state"><div className="empty-state__title">No audit records yet</div></div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AdminRoles ───────────────────────────────────────────
export function AdminRoles() {
  const [members, setMembers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // member being promoted
  const [editing, setEditing] = useState(null);     // existing admin being edited
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  const MODULES = [
    { key: 'members',      label: 'Members Management',        icon: '👥' },
    { key: 'matches',      label: 'Match Scheduling',          icon: '🏏' },
    { key: 'attendance',   label: 'Attendance & Override',     icon: '📅' },
    { key: 'fees',         label: 'Fees Management',           icon: '💰' },
    { key: 'fines',        label: 'Fines & Discipline',        icon: '⚠️' },
    { key: 'payments',     label: 'Payments & Reconciliation', icon: '💳' },
    { key: 'scorecards',   label: 'Scorecard Import',          icon: '📋' },
    { key: 'notices',      label: 'Notices & Polls',           icon: '🔔' },
    { key: 'availability', label: 'Availability Management',   icon: '✋' },
    { key: 'captaincy',    label: 'Captaincy & Floaters',      icon: '🏅' },
    { key: 'series',       label: 'Series Management',         icon: '🏆' },
    { key: 'expenses',     label: 'Expenses & Collections',    icon: '🧾' },
    { key: 'lunch',        label: 'Lunch & Menu',              icon: '🍱' },
  ];

  async function loadData() {
    const [mRes, aRes] = await Promise.all([api.get('/members'), api.get('/members?role=admin')]);
    setMembers((mRes.data.members || []).filter(m => m.role === 'member'));
    setAdmins((aRes.data.members || []).filter(m => m.role === 'admin'));
  }

  useEffect(() => {
    loadData().finally(() => setLoading(false));
  }, []);

  // Open promote panel for a regular member (all perms on by default)
  function selectMember(m) {
    setEditing(null);
    setSelected(m);
    const def = {};
    MODULES.forEach(mod => { def[mod.key] = true; });
    setPerms(def);
  }

  // Open edit panel for an existing admin — loads their current permissions
  async function startEdit(admin) {
    setSelected(null);
    setEditing(admin);
    // default all off, then turn on whatever they already have
    const def = {};
    MODULES.forEach(mod => { def[mod.key] = false; });
    try {
      const res = await api.get('/members/' + admin.id + '/permissions');
      (res.data.permissions || []).forEach(p => {
        if (p.can_view) def[p.module] = true;
      });
    } catch (e) {
      // if fetch fails just leave all off
    }
    setPerms(def);
  }

  function cancelPanel() {
    setSelected(null);
    setEditing(null);
    setPerms({});
  }

  async function promoteToAdmin() {
    if (!selected) return;
    setSaving(true);
    try {
      const modulePerms = MODULES.filter(m => perms[m.key]).map(m => ({
        module: m.key, can_view: true, can_add: true, can_edit: true, can_delete: true,
      }));
      await api.patch('/members/' + selected.id + '/role', { role: 'admin', permissions: modulePerms });
      toast.success(`${selected.display_name} is now an Admin ✅ — Their permissions will update within 1 minute`, { duration: 5000 });
      cancelPanel();
      await loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  async function savePermissions() {
    if (!editing) return;
    setSaving(true);
    try {
      const modulePerms = MODULES.filter(m => perms[m.key]).map(m => ({
        module: m.key, can_view: true, can_add: true, can_edit: true, can_delete: true,
      }));
      // PATCH with role: 'admin' + new permissions — backend deletes old rows and inserts fresh
      await api.patch('/members/' + editing.id + '/role', { role: 'admin', permissions: modulePerms });
      toast.success(`${editing.display_name}'s permissions updated ✅ — Changes take effect within 1 minute`, { duration: 5000 });
      cancelPanel();
      await loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  async function demoteToMember(memberId, name) {
    if (!window.confirm('Remove admin rights from ' + name + '?')) return;
    try {
      await api.patch('/members/' + memberId + '/role', { role: 'member', permissions: [] });
      toast.success(`${name} is now a regular member — Changes take effect within 1 minute`, { duration: 5000 });
      await loadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // Panel is open if either promoting or editing
  const panelOpen = !!selected || !!editing;
  const panelPerson = selected || editing;
  const isEditMode = !!editing;

  return (
    <div>
      <AdminPageHeader title="Admin Roles" icon="🔐">
        <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '0.82rem' }}>Assign module-level rights to members</p>
      </AdminPageHeader>
      <div style={{ padding: '16px' }}>

        {/* ── Current Admins list ── */}
        {admins.length > 0 && (
          <>
            <div className="section-title mb-sm">Current Admins ({admins.length})</div>
            <div className="card mb-md">
              {admins.map(a => (
                <div key={a.id} className="list-item">
                  <div className="avatar" style={{ background: 'var(--gold)', color: 'white' }}>{a.display_name?.charAt(0)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{a.display_name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{a.ucc_id}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ fontSize: '0.72rem' }}
                      onClick={() => startEdit(a)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline"
                      style={{ color: 'var(--red)', borderColor: 'var(--red)', fontSize: '0.72rem' }}
                      onClick={() => demoteToMember(a.id, a.display_name)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Promote panel (new admin) or Edit panel (existing admin) ── */}
        {!panelOpen ? (
          <>
            <div className="section-title mb-sm">Promote a Member to Admin</div>
            <div className="card">
              {members.map(m => (
                <div key={m.id} className="list-item" onClick={() => selectMember(m)} style={{ cursor: 'pointer' }}>
                  <div className="avatar">{m.display_name?.charAt(0)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{m.display_name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.ucc_id}</div>
                  </div>
                  <span style={{ color: 'var(--text-faint)', fontSize: '0.8rem' }}>→</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Person card */}
            <div className="card mb-md" style={{ border: `2px solid ${isEditMode ? 'var(--navy)' : 'var(--gold)'}` }}>
              <div className="card__body" style={{ textAlign: 'center' }}>
                <div className="avatar avatar-lg" style={{ margin: '0 auto 8px', background: isEditMode ? 'var(--gold)' : 'var(--navy)', fontSize: '1.3rem' }}>
                  {panelPerson.display_name?.charAt(0)}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.1rem' }}>{panelPerson.display_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{panelPerson.ucc_id}</div>
                {isEditMode && (
                  <div style={{ marginTop: 4, fontSize: '0.75rem', color: 'var(--gold)', fontWeight: 600 }}>
                    Editing existing admin permissions
                  </div>
                )}
              </div>
            </div>

            <div className="section-title mb-sm">Module Access</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-sm btn-outline" onClick={() => { const a = {}; MODULES.forEach(m => { a[m.key] = true; }); setPerms(a); }}>All</button>
              <button className="btn btn-sm btn-outline" onClick={() => setPerms({})}>None</button>
            </div>

            <div className="card mb-md">
              {MODULES.map(mod => (
                <div key={mod.key} className="list-item" style={{ cursor: 'pointer' }}
                  onClick={() => setPerms(p => ({ ...p, [mod.key]: !p[mod.key] }))}>
                  <span style={{ fontSize: '1.1rem', width: 28 }}>{mod.icon}</span>
                  <div style={{ flex: 1, fontWeight: 500, fontSize: '0.88rem' }}>{mod.label}</div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: perms[mod.key] ? 'var(--green)' : 'var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '0.7rem', fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {perms[mod.key] ? '✓' : ''}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={cancelPanel}>← Back</button>
              {isEditMode ? (
                <button className="btn btn-gold" style={{ flex: 2 }} onClick={savePermissions}
                  disabled={saving || !Object.values(perms).some(v => v)}>
                  {saving ? 'Saving…' : '💾 Save Permissions'}
                </button>
              ) : (
                <button className="btn btn-gold" style={{ flex: 2 }} onClick={promoteToAdmin}
                  disabled={saving || !Object.values(perms).some(v => v)}>
                  {saving ? 'Saving…' : '🔐 Promote to Admin'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── AdminScorers ─────────────────────────────────────────
// Manage independent scorer accounts. A scorer is a non-member user who
// can log in via OTP and run the live scoring console for matches, but
// doesn't appear anywhere on the member side (rankings, fees, attendance,
// captaincy, etc). Super-admin only.
//
// Workflow:
//   1. Click "Invite Scorer"
//   2. Enter their email + display name
//   3. They go to the app URL, enter their email, get an OTP, log in
//   4. On their device they see only Home + Me. Home shows upcoming matches
//      with Setup & Score buttons (gated until you save captains).
export function AdminScorers() {
  const [scorers, setScorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState(null); // scorer obj if editing
  const [form, setForm] = useState({ email: '', display_name: '' });
  const [saving, setSaving] = useState(false);

  function refresh() {
    setLoading(true);
    api.get('/admin/scorers')
      .then(r => setScorers(r.data?.scorers || []))
      .catch(e => toast.error(e?.response?.data?.error || 'Failed to load scorers'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { refresh(); }, []);

  async function submitInvite() {
    const email = form.email.trim();
    const display_name = form.display_name.trim();
    if (!email || !email.includes('@')) { toast.error('Valid email required'); return; }
    if (!display_name) { toast.error('Display name required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/admin/scorers/invite', { email, display_name });
      toast.success(`Invited ${r.data.scorer.display_name} (${r.data.scorer.ucc_id})`);
      setInviteOpen(false);
      setForm({ email: '', display_name: '' });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to invite scorer');
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit() {
    if (!editing) return;
    const email = form.email.trim();
    const display_name = form.display_name.trim();
    if (!email || !email.includes('@')) { toast.error('Valid email required'); return; }
    if (!display_name) { toast.error('Display name required'); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/scorers/${editing.id}`, { email, display_name });
      toast.success('Saved');
      setEditing(null);
      setForm({ email: '', display_name: '' });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(scorer) {
    const action = scorer.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${action} ${scorer.display_name}?\n\n${scorer.is_active
      ? 'They will no longer be able to log in.'
      : 'They will be able to log in again with their email.'}`)) return;
    try {
      await api.patch(`/admin/scorers/${scorer.id}`, { is_active: !scorer.is_active });
      toast.success(`${action}d`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update');
    }
  }

  function openEdit(scorer) {
    setEditing(scorer);
    setForm({ email: scorer.email, display_name: scorer.display_name });
  }

  return (
    <div>
      <AdminPageHeader title="Manage Scorers" icon="🎯">
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.8rem', marginTop: 4 }}>
          Non-member scorers who can run the live scoring console for matches.
        </p>
      </AdminPageHeader>

      <div style={{ padding: '16px' }}>
        <button
          onClick={() => { setInviteOpen(true); setForm({ email: '', display_name: '' }); }}
          style={{
            background: '#1E3A6E', color: 'white', border: 'none',
            padding: '10px 16px', borderRadius: 8, fontWeight: 700,
            fontFamily: 'Rajdhani,sans-serif', fontSize: '0.85rem',
            letterSpacing: '0.04em', cursor: 'pointer', marginBottom: 16,
          }}
        >
          + Invite Scorer
        </button>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : scorers.length === 0 ? (
          <div style={{
            background: 'white', border: '1px solid #e5e7eb',
            borderRadius: 12, padding: 22, textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🎯</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E', marginBottom: 6 }}>
              No scorers yet
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Invite a scorer to give them access to run the live scoring console.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {scorers.map(s => (
              <div key={s.id} style={{
                background: 'white', border: '1px solid #e5e7eb',
                borderRadius: 10, padding: 12,
                opacity: s.is_active ? 1 : 0.55,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E' }}>
                        {s.display_name}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, letterSpacing: '0.05em' }}>
                        {s.ucc_id}
                      </div>
                      {!s.is_active && (
                        <span style={{
                          fontSize: '0.6rem', background: '#fee2e2', color: '#991b1b',
                          padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2, wordBreak: 'break-all' }}>
                      {s.email}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                      Added {new Date(s.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => openEdit(s)}
                      style={{
                        background: 'white', border: '1px solid #1E3A6E', color: '#1E3A6E',
                        padding: '6px 10px', borderRadius: 6, fontWeight: 700,
                        fontFamily: 'Rajdhani,sans-serif', fontSize: '0.7rem',
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(s)}
                      style={{
                        background: s.is_active ? '#fee2e2' : '#dcfce7',
                        border: 'none', color: s.is_active ? '#991b1b' : '#166534',
                        padding: '6px 10px', borderRadius: 6, fontWeight: 700,
                        fontFamily: 'Rajdhani,sans-serif', fontSize: '0.7rem',
                        cursor: 'pointer',
                      }}
                    >
                      {s.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick how-it-works note for SB. Easy to remove later. */}
        <div style={{
          marginTop: 18, padding: 12, background: '#f3f4f6',
          borderRadius: 8, fontSize: '0.75rem', color: '#4b5563', lineHeight: 1.5,
        }}>
          <strong style={{ color: '#1E3A6E' }}>How it works:</strong>
          <br />
          1. Click "Invite Scorer" — enter their email + name. No password.
          <br />
          2. They open the app, enter their email, get an OTP, log in.
          <br />
          3. They see only Home + Me. Home shows matches with Setup & Score buttons.
          <br />
          4. Scoring is blocked on a match until you save its captains.
        </div>
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <ScorerFormModal
          title="Invite Scorer"
          submitLabel="Send Invite"
          form={form}
          setForm={setForm}
          saving={saving}
          onCancel={() => { setInviteOpen(false); setForm({ email: '', display_name: '' }); }}
          onSubmit={submitInvite}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <ScorerFormModal
          title={`Edit ${editing.display_name}`}
          submitLabel="Save"
          form={form}
          setForm={setForm}
          saving={saving}
          onCancel={() => { setEditing(null); setForm({ email: '', display_name: '' }); }}
          onSubmit={submitEdit}
        />
      )}
    </div>
  );
}

// Shared modal for invite / edit. Keep it dead simple — email + display_name.
function ScorerFormModal({ title, submitLabel, form, setForm, saving, onCancel, onSubmit }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 1000,
    }} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{
        background: 'white', borderRadius: 12, padding: 20,
        width: '100%', maxWidth: 380,
      }}>
        <div style={{
          fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem',
          color: '#1E3A6E', marginBottom: 14,
        }}>
          {title}
        </div>

        <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
          Display name
        </label>
        <input
          type="text"
          value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          placeholder="e.g. Vinod Sharma"
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
            borderRadius: 6, fontSize: '0.9rem', marginBottom: 12,
            boxSizing: 'border-box',
          }}
        />

        <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
          Email
        </label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="scorer@example.com"
          autoComplete="off"
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
            borderRadius: 6, fontSize: '0.9rem', marginBottom: 16,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              background: 'white', border: '1px solid #d1d5db', color: '#374151',
              padding: '8px 14px', borderRadius: 6, fontWeight: 700,
              fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={saving}
            style={{
              background: '#1E3A6E', border: 'none', color: 'white',
              padding: '8px 14px', borderRadius: 6, fontWeight: 700,
              fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AdminLeave ───────────────────────────────────────────
// Review member leave applications. Pending → Approve (pick leave-from-reply,
// leave-from-attendance + treatment) or Reject (with note). Requires the
// 'availability' permission.
export function AdminLeave() {
  const [tab, setTab] = useState('pending');           // pending | approved | rejected | all
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);    // application being approved (modal)

  function refresh() {
    setLoading(true);
    api.get(`/leave/admin?status=${tab}`)
      .then(r => setApps(r.data?.applications || []))
      .catch(e => toast.error(e?.response?.data?.error || 'Failed to load leave applications'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab]);

  async function reject(app) {
    const note = window.prompt('Reason for declining (optional):', '');
    if (note === null) return; // cancelled
    try {
      await api.post(`/leave/${app.id}/reject`, { review_note: note || null });
      toast.success('Application declined');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to decline');
    }
  }

  function fmt(d) {
    if (!d) return '';
    const dt = new Date(typeof d === 'string' && d.length === 10 ? d + 'T00:00:00Z' : d);
    return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }

  const TABS = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div>
      <AdminPageHeader title="Leave Applications" icon="🏖️">
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.8rem', marginTop: 4 }}>
          Review and action members' advance leave requests.
        </p>
      </AdminPageHeader>

      <div style={{ padding: '16px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
                border: tab === t.key ? '1.5px solid #1E3A6E' : '1px solid #e5e7eb',
                background: tab === t.key ? '#1E3A6E' : 'white',
                color: tab === t.key ? 'white' : '#6b7280',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : apps.length === 0 ? (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏖️</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E' }}>
              No {tab === 'all' ? '' : tab} applications
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {apps.map(app => {
              const color = app.status === 'approved' ? '#16a34a' : app.status === 'rejected' ? '#dc2626' : '#b45309';
              const bg    = app.status === 'approved' ? '#dcfce7' : app.status === 'rejected' ? '#fee2e2' : '#fef3c7';
              return (
                <div key={app.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E' }}>
                        {app.member_name} <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600 }}>{app.member_ucc_id}</span>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: 2 }}>
                        {fmt(app.start_date)} → {fmt(app.end_date)}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 3 }}>{app.reason}</div>
                    </div>
                    <span style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color, background: bg, padding: '3px 8px', borderRadius: 5 }}>
                      {app.status}
                    </span>
                  </div>

                  {/* Approved detail */}
                  {app.status === 'approved' && (
                    <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: 8, background: '#f0fdf4', borderRadius: 6, padding: '6px 8px' }}>
                      {[
                        app.leave_from_reply && 'Leave from reply',
                        app.leave_from_attendance && `Attendance: ${app.attendance_treatment}`,
                      ].filter(Boolean).join(' · ') || 'No specific exemptions'}
                      {app.reviewed_by_name && <span style={{ color: '#6b7280' }}> — by {app.reviewed_by_name}</span>}
                    </div>
                  )}
                  {app.status === 'rejected' && app.review_note && (
                    <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 8 }}>Note: {app.review_note}</div>
                  )}

                  {/* Pending actions */}
                  {app.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={() => setApproving(app)}
                        style={{ flex: 1, background: '#16a34a', border: 'none', color: 'white', padding: '8px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: 'pointer' }}>
                        Approve
                      </button>
                      <button onClick={() => reject(app)}
                        style={{ flex: 1, background: '#fee2e2', border: 'none', color: '#991b1b', padding: '8px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: 'pointer' }}>
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {approving && (
        <LeaveApproveModal
          app={approving}
          onClose={() => setApproving(null)}
          onApproved={() => { setApproving(null); refresh(); }}
        />
      )}
    </div>
  );
}

// Approve modal: choose leave-from-reply, leave-from-attendance, and (if the
// latter) the treatment (exempted vs absent).
function LeaveApproveModal({ app, onClose, onApproved }) {
  const [fromReply, setFromReply] = useState(true);
  const [fromAttendance, setFromAttendance] = useState(true);
  const [treatment, setTreatment] = useState('exempted');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!fromReply && !fromAttendance) {
      toast.error('Grant leave from reply, attendance, or both');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/leave/${app.id}/approve`, {
        leave_from_reply: fromReply,
        leave_from_attendance: fromAttendance,
        attendance_treatment: fromAttendance ? treatment : null,
      });
      toast.success('Leave approved');
      onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to approve');
    } finally {
      setSaving(false);
    }
  }

  const Row = ({ checked, onChange, label, sub }) => (
    <div onClick={() => onChange(!checked)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: '1px solid ' + (checked ? '#1E3A6E' : '#e5e7eb'), borderRadius: 8, cursor: 'pointer', marginBottom: 8, background: checked ? '#f0f4fb' : 'white' }}>
      <div style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? '#1E3A6E' : '#cbd5e1'), background: checked ? '#1E3A6E' : 'white', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: 800 }}>
        {checked ? '✓' : ''}
      </div>
      <div>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1E3A6E' }}>{label}</div>
        {sub && <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 400 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1E3A6E', marginBottom: 2 }}>
          Approve Leave — {app.member_name}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 16 }}>
          {app.reason}
        </div>

        <Row checked={fromReply} onChange={setFromReply}
          label="Leave from reply"
          sub="Auto-set to NO, no no-reply fine, member can't change it." />

        <Row checked={fromAttendance} onChange={setFromAttendance}
          label="Leave from attendance"
          sub="Auto-mark attendance for matches in the range." />

        {fromAttendance && (
          <div style={{ marginLeft: 4, marginBottom: 12 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
              Treat as
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { v: 'exempted', l: 'Exempted', s: 'Counts as present for %' },
                { v: 'absent', l: 'Absent', s: 'Counts against %' },
              ].map(o => (
                <button key={o.v} onClick={() => setTreatment(o.v)}
                  style={{ flex: 1, textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                    border: treatment === o.v ? '1.5px solid #1E3A6E' : '1px solid #e5e7eb',
                    background: treatment === o.v ? '#f0f4fb' : 'white' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E3A6E' }}>{o.l}</div>
                  <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{o.s}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ background: 'white', border: '1px solid #d1d5db', color: '#374151', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            style={{ background: '#16a34a', border: 'none', color: 'white', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminDropCatches() {
  const [drops, setDrops] = useState([]);
  const [members, setMembers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ member_id: '', match_id: '', notes: '' });

  useEffect(() => {
    Promise.all([
      api.get('/drop-catches?year=2026').then(r => setDrops(r.data.drops || [])),
      api.get('/members').then(r => setMembers((r.data.members || []).filter(m => m.is_active && !m.is_observer))),
      api.get('/matches?limit=20').then(r => setMatches(r.data.matches || [])),
    ]).finally(() => setLoading(false));
  }, []);

  async function addDrop() {
    if (!form.member_id) return toast.error('Select a member');
    setSaving(true);
    try {
      await api.post('/drop-catches', { ...form, year: 2026 });
      toast.success('Drop catch recorded');
      setForm({ member_id: '', match_id: '', notes: '' });
      const r = await api.get('/drop-catches?year=2026');
      setDrops(r.data.drops || []);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  async function deleteDrop(id) {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await api.delete(`/drop-catches/${id}`);
      setDrops(drops.filter(d => d.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Drop Catches" icon="🧤">Record dropped catches — shown on Banana Contenders page</AdminPageHeader>
      <div style={{ padding: '12px 16px' }}>
        {/* Add form */}
        <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
          <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem', color: 'var(--navy)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Record Drop Catch
          </div>
          <div className="form-group">
            <label className="form-label">Member</label>
            <select className="form-control" value={form.member_id} onChange={e => setForm(f => ({ ...f, member_id: e.target.value }))}>
              <option value="">— Select member —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginTop: 10 }}>
            <label className="form-label">Match (optional)</label>
            <select className="form-control" value={form.match_id} onChange={e => setForm(f => ({ ...f, match_id: e.target.value }))}>
              <option value="">— Select match —</option>
              {matches.map(m => <option key={m.id} value={m.id}>{new Date(m.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginTop: 10 }}>
            <label className="form-label">Notes (optional)</label>
            <input className="form-control" placeholder="e.g. Off Babar Kailay in 3rd over" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={addDrop} disabled={saving}>
            {saving ? 'Saving…' : '🧤 Record Drop Catch'}
          </button>
        </div>

        {/* List */}
        {drops.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No drop catches recorded yet</div>
        ) : (
          <div className="card">
            {drops.map((d, i) => (
              <div key={d.id} className="list-item" style={{ borderBottom: i < drops.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--navy)' }}>{d.display_name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {d.ucc_id}
                    {d.match_date ? ` · ${new Date(d.match_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}` : ''}
                    {d.notes ? ` · ${d.notes}` : ''}
                  </div>
                </div>
                <button onClick={() => deleteDrop(d.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: 4 }}>🗑</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SEASON_TOURNAMENTS = {
  2018: { id: '17302',   slug: 'ucc-annual-league-2018' },
  2019: { id: '33520',   slug: 'ucc-2019' },
  2020: { id: '96911',   slug: 'ucc-2020' },
  2021: { id: '187835',  slug: 'ucc-2021' },
  2022: { id: '334104',  slug: 'ucc-2022' },
  2023: { id: '552964',  slug: 'ucc-2023' },
  2024: { id: '851499',  slug: 'ucc-24' },
  2025: { id: '1259839', slug: 'ucc-2025' },
  2026: { id: '1777539', slug: 'ucc-2026' },
};

export function AdminSeasonImport() {
  const [season, setSeason] = useState(2025);
  const [step, setStep] = useState('idle'); // idle | fetching | review | importing | done
  const [matches, setMatches] = useState([]);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);
  const [buildId, setBuildId] = useState('RgIymHzBxV8FAGRWZOZSN');

  async function fetchTournamentMatches() {
    setStep('fetching');
    setProgress('Fetching match list from CricHeroes...');
    try {
      const t = SEASON_TOURNAMENTS[season];
      if (!t) { toast.error('Tournament not configured for this year'); setStep('idle'); return; }

      // Backend fetches tournament data (avoids browser CORS issues)
      const r = await api.get(`/scorecards/fetch-tournament-matches?tournament_id=${t.id}&tournament_slug=${t.slug}&build_id=${buildId}`);
      const { matches: fetchedMatches, error } = r.data;

      if (error && (!fetchedMatches || fetchedMatches.length === 0)) {
        toast.error(error);
        setStep('idle');
        return;
      }

      if (!fetchedMatches || fetchedMatches.length === 0) {
        toast.error('No matches found for this tournament.');
        setStep('idle');
        return;
      }

      setMatches(fetchedMatches);
      setStep('review');
      setProgress('');
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.error || err.message));
      setStep('idle');
      setProgress('');
    }
  }

  async function importSeason() {
    setStep('importing');
    // Step 1: Create match records
    setProgress('Creating match records...');
    const createRes = await api.post('/scorecards/create-matches', { matches_data: matches, season });
    const created = createRes.data.results || [];
    setProgress(`Created ${createRes.data.created} new, ${createRes.data.skipped} existing. Starting imports...`);

    // Step 2: Import each match
    let done = 0, failed = 0;
    for (const m of created) {
      setProgress(`Importing ${done + 1}/${created.length}: ${m.match_date} vs ${m.opponent}...`);
      try {
        // Browser prefetch CricHeroes data
        let prefetchedData = null;
        try {
          const urlClean = m.cricheroes_url.replace(/\/(scorecard|summary)$/, '');
          const urlMatch = urlClean.match(/cricheroes\.com\/scorecard\/(\d+)\/([^/]+)\/([^/]+)/);
          if (urlMatch) {
            const [, chId, tourn, teams] = urlMatch;
            const dataUrl = `https://next.cricheroes.com/_next/data/${buildId}/scorecard/${chId}/${tourn}/${teams}/scorecard.json`;
            const r = await fetch(dataUrl, { cache: 'no-store' });
            if (r.ok) prefetchedData = await r.json();
          }
        } catch {}

        await api.post(`/scorecards/import/${m.match_id}`, {
          build_id: buildId,
          prefetched_data: prefetchedData || undefined,
        });
        done++;
      } catch { failed++; }
      await new Promise(r => setTimeout(r, 600));
    }

    setResults({ created: createRes.data.created, skipped: createRes.data.skipped, imported: done, failed });
    setStep('done');
    setProgress('');
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Season Import" icon="📦">Import all historical matches from CricHeroes year by year</AdminPageHeader>
      <div style={{ padding: '12px 16px' }}>

        {step === 'idle' || step === 'review' ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Season Year</label>
              <select className="form-control" value={season} onChange={e => { setSeason(parseInt(e.target.value)); setStep('idle'); setMatches([]); }}>
                {Object.keys(SEASON_TOURNAMENTS).sort((a,b) => b-a).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginTop: 10 }}>
              <label className="form-label">Build ID</label>
              <input className="form-control" value={buildId} onChange={e => setBuildId(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.78rem' }} placeholder="RgIymHzBxV8FAGRWZOZSN" />
            </div>
            <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={fetchTournamentMatches}>
              🔍 Fetch Match List from CricHeroes
            </button>
          </div>
        ) : null}

        {step === 'fetching' && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: '0.85rem' }}>{progress}</div>
          </div>
        )}

        {step === 'review' && matches.length > 0 && (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem', color: '#15803d' }}>
              ✅ Found <strong>{matches.length} matches</strong> for {season}. Review below then tap Import All.
            </div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
              {matches.map((m, i) => (
                <div key={i} style={{ padding: '10px 14px', borderBottom: i < matches.length-1 ? '1px solid #f3f4f6' : 'none', fontSize: '0.82rem' }}>
                  <div style={{ fontWeight: 600, color: '#1E3A6E' }}>{m.match_date} · vs {m.opponent || 'Unknown'}</div>
                  <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: 2 }}>{m.cricheroes_url}</div>
                </div>
              ))}
            </div>
            <button className="btn btn-primary btn-block" onClick={importSeason}>
              📥 Import All {matches.length} Matches
            </button>
            <button className="btn btn-block" style={{ marginTop: 8, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, padding: '11px 0', cursor: 'pointer' }}
              onClick={() => { setStep('idle'); setMatches([]); }}>
              Cancel
            </button>
          </div>
        )}

        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: '0.85rem' }}>{progress}</div>
          </div>
        )}

        {step === 'done' && results && (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#15803d', marginBottom: 8 }}>✅ Season Import Complete!</div>
              <div style={{ fontSize: '0.82rem', color: '#166534' }}>
                <div>📋 Match records created: <strong>{results.created}</strong></div>
                <div>⏭ Already existed: <strong>{results.skipped}</strong></div>
                <div>📥 Scorecards imported: <strong>{results.imported}</strong></div>
                {results.failed > 0 && <div>❌ Failed: <strong>{results.failed}</strong></div>}
              </div>
            </div>
            <button className="btn btn-primary btn-block" onClick={() => { setStep('idle'); setMatches([]); setResults(null); }}>
              Import Another Season
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// ADMIN: Squad Config — Special Conditions + Rested Reasons (Phase 2a)
// ═════════════════════════════════════════════════════════════════════
export function AdminSquadConfig() {
  const [tab, setTab] = useState('conditions'); // 'conditions' | 'reasons'

  return (
    <div style={{ paddingBottom: 80 }}>
      <AdminPageHeader title="Squad Config" icon="⚙️">
        Special conditions · Rested reasons
      </AdminPageHeader>

      <div style={{ display:'flex', gap:4, padding:'10px 10px 0', background:'white', borderBottom:'1px solid var(--border)' }}>
        {[
          { k:'conditions', lbl:'📝 Special Conditions' },
          { k:'reasons',    lbl:'💤 Rested Reasons' },
        ].map(t => (
          <button key={t.k}
            onClick={() => setTab(t.k)}
            style={{
              flex:1, padding:'10px 8px', border:'none',
              borderBottom: tab===t.k ? '3px solid var(--navy)' : '3px solid transparent',
              background: tab===t.k ? 'var(--navy)' : 'transparent',
              color: tab===t.k ? 'white' : 'var(--text-muted)',
              fontWeight:700, fontSize:'0.82rem', cursor:'pointer', transition:'all 0.15s',
            }}>
            {t.lbl}
          </button>
        ))}
      </div>

      <div style={{ padding:16 }}>
        {tab === 'conditions' && <AdminSpecialConditions />}
        {tab === 'reasons'    && <AdminRestedReasons    />}
      </div>
    </div>
  );
}

// ── Special Conditions ───────────────────────────────────────────────
function AdminSpecialConditions() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  // Add/edit form
  const [label, setLabel] = useState('');
  const [isStandby, setIsStandby] = useState(false);
  const [inputType, setInputType] = useState('none');
  const [inputLabel, setInputLabel] = useState('');
  const [displayOrder, setDisplayOrder] = useState(100);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get('/squad-config/special-conditions');
      setList(r.data.conditions || []);
    } catch { toast.error('Failed to load conditions'); }
    finally { setLoading(false); }
  }

  function openAdd() {
    setEditing(null);
    setLabel(''); setIsStandby(false); setInputType('none');
    setInputLabel(''); setDisplayOrder(100); setIsActive(true);
    setShowAdd(true);
  }
  function openEdit(c) {
    setEditing(c);
    setLabel(c.label); setIsStandby(c.is_standby);
    setInputType(c.input_type || 'none');
    setInputLabel(c.input_label || '');
    setDisplayOrder(c.display_order || 100);
    setIsActive(c.is_active);
    setShowAdd(true);
  }

  async function save() {
    if (!label.trim() || label.trim().length < 2) return toast.error('Name required');
    setSaving(true);
    try {
      const body = { label: label.trim(), is_standby: isStandby, input_type: inputType, input_label: inputLabel || null, display_order: displayOrder, is_active: isActive };
      if (editing) {
        await api.patch(`/squad-config/special-conditions/${editing.id}`, body);
        toast.success('Condition updated');
      } else {
        await api.post('/squad-config/special-conditions', body);
        toast.success('Condition added');
      }
      setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function remove(c) {
    const msg = c.is_builtin
      ? `"${c.label}" is a built-in condition and will be deactivated (history preserved). Continue?`
      : `Delete "${c.label}"? If it has been used, it will be deactivated instead (history preserved).`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/squad-config/special-conditions/${c.id}`);
      toast.success(r.data.deleted ? 'Deleted' : 'Deactivated');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function openStats(c) {
    setStatsFor(c); setStatsData(null);
    try {
      const r = await api.get(`/squad-config/special-conditions/${c.id}/stats`);
      setStatsData(r.data);
    } catch { toast.error('Failed to load stats'); }
  }

  if (loading) return <div style={{ padding:20, textAlign:'center', color:'#6b7280' }}>Loading…</div>;

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>
          {list.length} condition{list.length !== 1 ? 's' : ''}
        </div>
        <button onClick={openAdd}
          style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'var(--navy)', color:'white',
                   fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
          + Add New
        </button>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {list.map(c => (
          <div key={c.id} style={{
            background:'white', border:'1px solid var(--border, #e5e7eb)', borderRadius:12,
            padding:'12px 14px', opacity: c.is_active ? 1 : 0.55,
          }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 auto', minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:700, fontSize:'0.95rem', color:'#111827' }}>{c.label}</span>
                  {c.is_standby && <span style={{ fontSize:'0.62rem', fontWeight:800, background:'#fbbf24', color:'#78350f', padding:'2px 7px', borderRadius:10 }}>STANDBY</span>}
                  {c.is_builtin && <span style={{ fontSize:'0.62rem', fontWeight:800, background:'#dbeafe', color:'#1e40af', padding:'2px 7px', borderRadius:10 }}>BUILT-IN</span>}
                  {!c.is_active && <span style={{ fontSize:'0.62rem', fontWeight:800, background:'#f3f4f6', color:'#6b7280', padding:'2px 7px', borderRadius:10 }}>INACTIVE</span>}
                </div>
                <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:3 }}>
                  Code: <code>{c.code}</code> · Input: {c.input_type}{c.input_label ? ` ("${c.input_label}")` : ''} · Order: {c.display_order}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <button onClick={() => openStats(c)} style={btnSm('#ecfeff','#0891b2')}>📊 Stats</button>
                <button onClick={() => openEdit(c)} style={btnSm('#eff6ff','#1e40af')}>✏️ Edit</button>
                <button onClick={() => remove(c)} style={btnSm('#fef2f2','#b91c1c')}>🗑 Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit modal */}
      {showAdd && (
        <div onClick={() => !saving && setShowAdd(false)}
             style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background:'white', width:'100%', maxWidth:480, borderRadius:16, padding:20, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)', marginBottom:14 }}>
              {editing ? '✏️ Edit Special Condition' : '➕ New Special Condition'}
            </div>

            <FieldLabel>Name (shown to members)</FieldLabel>
            <input value={label} onChange={e=>setLabel(e.target.value)} placeholder="e.g. No Fielding Outfield"
                   style={fieldInput} maxLength={60} />

            <div style={{ marginTop:12, display:'flex', gap:10, alignItems:'center' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={isStandby} onChange={e=>setIsStandby(e.target.checked)} style={{ width:17, height:17 }} />
                <span style={{ fontSize:'0.85rem', fontWeight:600, color:'#374151' }}>Standby Condition</span>
              </label>
              <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>(needs admin approval before playing)</span>
            </div>

            <FieldLabel style={{ marginTop:14 }}>Extra Input from Member</FieldLabel>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {[
                { v:'none', lbl:'None' },
                { v:'time', lbl:'Time picker' },
                { v:'text', lbl:'Short text' },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setInputType(o.v)}
                  style={{
                    padding:'7px 14px', borderRadius:8, border:'1.5px solid '+(inputType===o.v?'var(--navy)':'#e5e7eb'),
                    background: inputType===o.v ? 'var(--navy)' : 'white',
                    color: inputType===o.v ? 'white' : '#374151',
                    fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                  }}>{o.lbl}</button>
              ))}
            </div>

            {inputType !== 'none' && (
              <>
                <FieldLabel style={{ marginTop:12 }}>Input Label (shown to member above the input)</FieldLabel>
                <input value={inputLabel} onChange={e=>setInputLabel(e.target.value)}
                       placeholder={inputType === 'time' ? 'e.g. Time you will leave' : 'e.g. Specify which position'}
                       style={fieldInput} maxLength={60} />
              </>
            )}

            <FieldLabel style={{ marginTop:12 }}>Display Order (lower = shown first)</FieldLabel>
            <input type="number" value={displayOrder} onChange={e=>setDisplayOrder(e.target.value)} style={fieldInput} />

            {editing && (
              <div style={{ marginTop:14, display:'flex', gap:8, alignItems:'center' }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)} style={{ width:17, height:17 }} />
                  <span style={{ fontSize:'0.85rem', fontWeight:600, color:'#374151' }}>Active (visible to members)</span>
                </label>
              </div>
            )}

            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={() => setShowAdd(false)} disabled={saving}
                style={{ flex:1, padding:10, borderRadius:10, border:'1.5px solid #e5e7eb', background:'white', color:'#6b7280', fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving || label.trim().length < 2}
                style={{ flex:1, padding:10, borderRadius:10, border:'none',
                         background: (saving || label.trim().length<2) ? '#e5e7eb' : 'var(--navy)',
                         color: (saving || label.trim().length<2) ? '#94a3b8' : 'white',
                         fontWeight:800, cursor: saving ? 'wait' : 'pointer' }}>
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Add Condition')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats modal */}
      {statsFor && (
        <div onClick={() => setStatsFor(null)}
             style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background:'white', width:'100%', maxWidth:460, borderRadius:16, padding:20, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem', color:'var(--navy)', marginBottom:14 }}>
              📊 {statsFor.label} — Usage
            </div>
            {!statsData ? <div style={{ color:'#6b7280', fontSize:'0.85rem' }}>Loading stats…</div> : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
                  <div style={{ background:'#f3f4f6', borderRadius:10, padding:12, textAlign:'center' }}>
                    <div style={{ fontSize:'1.4rem', fontWeight:800, color:'var(--navy)' }}>{statsData.total_requests}</div>
                    <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:600 }}>Total Requests</div>
                  </div>
                  <div style={{ background:'#f3f4f6', borderRadius:10, padding:12, textAlign:'center' }}>
                    <div style={{ fontSize:'1.4rem', fontWeight:800, color:'var(--navy)' }}>{statsData.unique_members}</div>
                    <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:600 }}>Unique Members</div>
                  </div>
                </div>
                <div style={{ fontSize:'0.8rem', fontWeight:700, color:'#374151', marginBottom:6 }}>Top Requesters</div>
                {statsData.top_requesters.length === 0 ? (
                  <div style={{ fontSize:'0.8rem', color:'#9ca3af', padding:'10px 0' }}>No requests yet</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {statsData.top_requesters.map((r, i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'#f9fafb', borderRadius:8, fontSize:'0.82rem' }}>
                        <span>{r.display_name}</span>
                        <span style={{ color:'#6b7280', fontWeight:700 }}>{r.times}×</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <button onClick={() => setStatsFor(null)} style={{ marginTop:14, width:'100%', padding:10, borderRadius:10, border:'1.5px solid #e5e7eb', background:'white', color:'#6b7280', fontWeight:700, cursor:'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Rested Reasons ───────────────────────────────────────────────────
function AdminRestedReasons() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get('/squad-config/rested-reasons');
      setList(r.data.reasons || []);
    } catch { toast.error('Failed to load reasons'); }
    finally { setLoading(false); }
  }

  async function add() {
    if (!newLabel.trim()) return;
    try {
      await api.post('/squad-config/rested-reasons', { label: newLabel.trim() });
      setNewLabel(''); setShowAdd(false);
      toast.success('Reason added');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function saveEdit(id) {
    try {
      await api.patch(`/squad-config/rested-reasons/${id}`, { label: editLabel.trim() });
      setEditId(null); toast.success('Updated');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function toggleActive(r) {
    try {
      await api.patch(`/squad-config/rested-reasons/${r.id}`, { is_active: !r.is_active });
      load();
    } catch { toast.error('Failed'); }
  }

  async function remove(r) {
    if (!window.confirm(`Deactivate "${r.label}"?`)) return;
    try {
      await api.delete(`/squad-config/rested-reasons/${r.id}`);
      load();
    } catch { toast.error('Failed'); }
  }

  if (loading) return <div style={{ padding:20, textAlign:'center', color:'#6b7280' }}>Loading…</div>;

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>
          {list.filter(r=>r.is_active).length} active, {list.filter(r=>!r.is_active).length} inactive
        </div>
        <button onClick={() => setShowAdd(true)}
          style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'var(--navy)', color:'white', fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
          + Add Reason
        </button>
      </div>

      {showAdd && (
        <div style={{ background:'#f9fafb', borderRadius:10, padding:12, marginBottom:12, border:'1px solid #e5e7eb' }}>
          <input value={newLabel} onChange={e=>setNewLabel(e.target.value)}
                 placeholder="e.g. Series Rested"
                 onKeyDown={e => e.key === 'Enter' && add()}
                 autoFocus
                 style={fieldInput} maxLength={120} />
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <button onClick={() => { setShowAdd(false); setNewLabel(''); }}
                    style={{ flex:1, padding:8, borderRadius:8, border:'1.5px solid #e5e7eb', background:'white', color:'#6b7280', fontWeight:700, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={add} disabled={!newLabel.trim()}
                    style={{ flex:1, padding:8, borderRadius:8, border:'none',
                             background: newLabel.trim() ? 'var(--navy)' : '#e5e7eb',
                             color: newLabel.trim() ? 'white' : '#94a3b8',
                             fontWeight:800, cursor: newLabel.trim() ? 'pointer' : 'not-allowed' }}>
              Add
            </button>
          </div>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {list.map(r => (
          <div key={r.id} style={{
            background:'white', border:'1px solid var(--border, #e5e7eb)', borderRadius:10,
            padding:'10px 12px', opacity: r.is_active ? 1 : 0.55,
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
          }}>
            {editId === r.id ? (
              <>
                <input value={editLabel} onChange={e=>setEditLabel(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && saveEdit(r.id)}
                       style={{ ...fieldInput, flex:1, margin:0 }} autoFocus />
                <button onClick={() => saveEdit(r.id)} style={btnSm('#dcfce7','#166534')}>✓</button>
                <button onClick={() => setEditId(null)} style={btnSm('#f3f4f6','#6b7280')}>✕</button>
              </>
            ) : (
              <>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.9rem', fontWeight:600, color:'#111827' }}>{r.label}</div>
                  {!r.is_active && <div style={{ fontSize:'0.7rem', color:'#9ca3af', marginTop:2 }}>Inactive</div>}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => toggleActive(r)} style={btnSm(r.is_active?'#fef3c7':'#dcfce7', r.is_active?'#78350f':'#166534')}>
                    {r.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => { setEditId(r.id); setEditLabel(r.label); }} style={btnSm('#eff6ff','#1e40af')}>✏️</button>
                  <button onClick={() => remove(r)} style={btnSm('#fef2f2','#b91c1c')}>🗑</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// AdminSquadBuilder — Phase 2c
// Saturday staging UI: categorize members, approve requests, send squad notice.
// Self-contained — shows itself only when staging window is open or a squad
// notice has already been sent (for resend).
// ═════════════════════════════════════════════════════════════════════
function AdminSquadBuilder({ matchId }) {
  const [state, setState] = useState(null);   // full payload from /state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});   // per-row spinner flags
  const [restedPicker, setRestedPicker] = useState(null); // { member_id, display_name }
  const [addRequestModal, setAddRequestModal] = useState(null); // { member_id, display_name }
  const [approvalModal, setApprovalModal] = useState(null); // { request, member_name }
  const [previewModal, setPreviewModal] = useState(null); // { body, counts, jersey, repTime, sublist }

  async function load() {
    try {
      const r = await api.get(`/squad-builder/${matchId}/state`);
      setState(r.data);
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.detail ? `${d.error || 'Failed'} — ${d.detail}` : (d.error || 'Failed to load squad builder');
      toast.error(msg, { duration: 8000 });
      console.error('[SquadBuilder] load error:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  if (loading) return null;
  if (!state) return null;

  // Don't render anything outside staging unless a notice has already been sent
  // (in which case admin may want to resend).
  if (!state.staging_open && !state.squad_notice_sent_at) return null;

  const rows = state.rows || [];
  const alreadySent = !!state.squad_notice_sent_at;

  // Group members by final category (using backend-provided `category` field)
  const byCategory = {
    playing: [],
    opt_out_full: [],
    opt_out_fee_only: [],
    rested: [],
    absent: [],
    opt_out_pending: [],
    no_reply: [],
  };
  for (const r of rows) {
    if (byCategory[r.category]) byCategory[r.category].push(r);
    else byCategory.absent.push(r);
  }

  // Flag — squad ready to send?
  const anyPending = rows.some(r =>
    r.category === 'playing' && r.special_requests.some(sr => sr.approval_status === 'pending')
  );
  const restedMissingReason = byCategory.rested.filter(r => !r.rested_reason_id);
  const optOutPending = byCategory.opt_out_pending;

  // ── Actions ─────────────────────────────────────────────
  async function setStatus(member_id, final_status, rested_reason_id) {
    setSaving(s => ({ ...s, [member_id]: true }));
    try {
      await api.post(`/squad-builder/${matchId}/set-status`, {
        member_id, final_status, rested_reason_id: rested_reason_id || null,
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to move');
    } finally {
      setSaving(s => { const n = { ...s }; delete n[member_id]; return n; });
    }
  }

  async function decideRequest(request_id, action, admin_input) {
    try {
      await api.post(`/squad-builder/${matchId}/request-decision`, {
        request_id, action, admin_input,
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update request');
    }
  }

  async function addRequestOnBehalf(member_id, condition_id, admin_input) {
    try {
      await api.post(`/squad-builder/${matchId}/request-decision`, {
        action: 'add_on_behalf', member_id, condition_id, admin_input,
      });
      setAddRequestModal(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add request');
    }
  }

  async function removeRequest(request_id) {
    if (!window.confirm('Remove this admin-added request?')) return;
    try {
      await api.post(`/squad-builder/${matchId}/request-remove`, { request_id });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    }
  }

  async function openPreview() {
    if (anyPending) {
      return toast.error('Resolve all pending requests on playing members first');
    }
    if (restedMissingReason.length > 0) {
      return toast.error(`Pick a reason for: ${restedMissingReason.map(r => r.display_name).join(', ')}`);
    }
    // Default jersey + reporting time for the preview
    const defaults = {
      jersey: (state.squad_jersey_instruction || 'BOTH').toUpperCase(),
      customJersey: '',
      sublist: '',
      repTime: (state.match.reporting_time || '05:45').slice(0, 5),
    };
    try {
      const r = await api.post(`/squad-builder/${matchId}/preview`, {
        jersey_instruction: defaults.jersey,
        jersey_sublist: defaults.sublist,
        reporting_time: defaults.repTime,
      });
      setPreviewModal({
        body: r.data.broadcast_body,
        counts: r.data.counts,
        ...defaults,
        editedBody: r.data.broadcast_body,
        bodyWasEdited: false,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Preview failed');
    }
  }

  async function refreshPreview() {
    if (!previewModal) return;
    try {
      const jersey = previewModal.jersey === 'CUSTOM' ? previewModal.customJersey : previewModal.jersey;
      const r = await api.post(`/squad-builder/${matchId}/preview`, {
        jersey_instruction: jersey,
        jersey_sublist: previewModal.sublist,
        reporting_time: previewModal.repTime,
      });
      setPreviewModal(p => ({
        ...p,
        body: r.data.broadcast_body,
        editedBody: p.bodyWasEdited ? p.editedBody : r.data.broadcast_body,
      }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Preview failed');
    }
  }

  async function sendSquadNotice() {
    if (!previewModal) return;
    const jersey = previewModal.jersey === 'CUSTOM' ? previewModal.customJersey : previewModal.jersey;
    if (previewModal.jersey === 'CUSTOM' && !jersey.trim()) {
      return toast.error('Enter a custom jersey instruction');
    }
    if (alreadySent) {
      const ok = window.confirm(
        'A squad notice was already sent for this match. Sending again will push new notifications to everyone. Continue?'
      );
      if (!ok) return;
    }
    try {
      const res = await api.post(`/squad-builder/${matchId}/send-notice`, {
        jersey_instruction: jersey,
        jersey_sublist: previewModal.sublist || null,
        reporting_time: previewModal.repTime,
        broadcast_body_override: previewModal.bodyWasEdited ? previewModal.editedBody : null,
        resend: alreadySent,
      });
      const delay = res.data.broadcast_delay_minutes || 5;
      toast.success(`Squad notice sent. ${res.data.individual_count} individual push(es) fired now · Broadcast in ${delay} min.`);
      setPreviewModal(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send squad notice');
    }
  }

  // ── Render helpers ──────────────────────────────────────
  function RequestChip({ r, memberName }) {
    const statusColors = {
      pending:  { bg: '#fef3c7', fg: '#92400e', label: 'Pending' },
      approved: { bg: '#dcfce7', fg: '#166534', label: r.source === 'admin' ? 'Applied' : 'Approved' },
      applied:  { bg: '#dbeafe', fg: '#1e40af', label: 'Applied' },
      rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Declined' },
    };
    const s = statusColors[r.approval_status] || statusColors.pending;
    const extra = r.input_type !== 'none' ? (r.admin_input || r.member_input || '—') : null;
    const extraLabel = r.condition_code === 'leaving_early' && extra ? ` @ ${extra}` : (extra ? ` (${extra})` : '');
    return (
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.72rem', margin:'3px 0', flexWrap:'wrap' }}>
        <span style={{ fontWeight:600, color:'#374151' }}>{r.label}{extraLabel}</span>
        <span style={{ background:s.bg, color:s.fg, padding:'1px 7px', borderRadius:10, fontSize:'0.65rem', fontWeight:700 }}>{s.label}</span>
        {r.source === 'admin' && (
          <span style={{ background:'#ede9fe', color:'#6d28d9', padding:'1px 6px', borderRadius:10, fontSize:'0.6rem', fontWeight:700 }}>ADMIN ADDED</span>
        )}
        {r.approval_status === 'pending' && (
          <>
            <button onClick={() => {
              if (r.input_type !== 'none') {
                setApprovalModal({ request: r, member_name: memberName });
              } else {
                decideRequest(r.id, 'approve');
              }
            }}
              style={{ fontSize:'0.62rem', padding:'2px 7px', borderRadius:6, border:'none', background:'#16a34a', color:'white', cursor:'pointer', fontWeight:700 }}>✓ Approve</button>
            <button onClick={() => decideRequest(r.id, 'decline')}
              style={{ fontSize:'0.62rem', padding:'2px 7px', borderRadius:6, border:'none', background:'#dc2626', color:'white', cursor:'pointer', fontWeight:700 }}>✗ Decline</button>
          </>
        )}
        {r.approval_status !== 'pending' && r.source === 'member' && (
          <button onClick={() => {
            if (r.input_type !== 'none') {
              setApprovalModal({ request: r, member_name: memberName });
            } else {
              decideRequest(r.id, r.approval_status === 'approved' ? 'decline' : 'approve');
            }
          }}
            style={{ fontSize:'0.6rem', padding:'2px 6px', borderRadius:6, border:'1px solid #d1d5db', background:'white', cursor:'pointer', color:'#374151' }}>↻ Change</button>
        )}
        {r.source === 'admin' && (
          <button onClick={() => removeRequest(r.id)}
            style={{ fontSize:'0.6rem', padding:'2px 6px', borderRadius:6, border:'1px solid #fecaca', background:'white', cursor:'pointer', color:'#dc2626' }}>🗑 Remove</button>
        )}
      </div>
    );
  }

  function CategoryTabs({ current, memberId }) {
    const tabs = [
      { v:'playing',          label:'Playing',       bg:'#dcfce7', fg:'#166534' },
      { v:'opt_out_full',     label:'Opt-Out Full',  bg:'#dbeafe', fg:'#1e40af' },
      { v:'opt_out_fee_only', label:'Opt-Out Fee',   bg:'#e0f2fe', fg:'#075985' },
      { v:'rested',           label:'Rested',        bg:'#fef3c7', fg:'#92400e' },
      { v:'absent',           label:'Absent',        bg:'#fee2e2', fg:'#991b1b' },
    ];
    return (
      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
        {tabs.map(t => {
          const isActive = current === t.v;
          return (
            <button key={t.v}
              disabled={!!saving[memberId]}
              onClick={() => {
                if (t.v === 'rested') {
                  const row = rows.find(x => x.member_id === memberId);
                  if (!row?.rested_reason_id) {
                    setRestedPicker({ member_id: memberId, display_name: row?.display_name || '' });
                    return;
                  }
                  setStatus(memberId, 'rested', row.rested_reason_id);
                  return;
                }
                setStatus(memberId, t.v);
              }}
              style={{
                fontSize:'0.62rem', padding:'3px 8px', borderRadius:6,
                border: isActive ? `1.5px solid ${t.fg}` : '1px solid #e5e7eb',
                background: isActive ? t.bg : 'white',
                color: isActive ? t.fg : '#6b7280',
                fontWeight:700, cursor:'pointer',
              }}>{t.label}</button>
          );
        })}
      </div>
    );
  }

  function MemberRow({ row }) {
    const restedReasonLabel = row.rested_reason_id
      ? (state.rested_reasons.find(r => r.id === row.rested_reason_id)?.label || '—')
      : null;
    return (
      <div style={{ padding:'10px 12px', borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
          <div className="avatar" style={{ width:28, height:28, fontSize:'0.75rem' }}>{row.display_name?.charAt(0)}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{row.display_name}</div>
            <div style={{ fontSize:'0.65rem', color:'#6b7280' }}>
              {row.game_response && <>Said: <strong>{row.game_response.toUpperCase()}</strong></>}
              {row.opt_out && <> · Opt-out: "{row.opt_out.reason}"</>}
              {restedReasonLabel && <> · Reason: {restedReasonLabel}</>}
            </div>
          </div>
          {row.category === 'rested' && (
            <button
              onClick={() => setRestedPicker({ member_id: row.member_id, display_name: row.display_name })}
              style={{ fontSize:'0.6rem', padding:'2px 6px', borderRadius:6, border:'1px solid #fbbf24', background:'#fef3c7', cursor:'pointer', color:'#92400e', fontWeight:700 }}>
              Reason
            </button>
          )}
        </div>
        <CategoryTabs current={row.final_status || (row.category === 'opt_out_pending' ? null : row.category)} memberId={row.member_id} />
        {row.category === 'playing' && (
          <div style={{ marginTop:6, paddingLeft:4 }}>
            {row.special_requests.length === 0 && (
              <div style={{ fontSize:'0.68rem', color:'#9ca3af', fontStyle:'italic' }}>No special requests</div>
            )}
            {row.special_requests.map(r => <RequestChip key={r.id} r={r} memberName={row.display_name} />)}
            <button
              onClick={() => setAddRequestModal({ member_id: row.member_id, display_name: row.display_name })}
              style={{ marginTop:4, fontSize:'0.62rem', padding:'3px 8px', borderRadius:6, border:'1px dashed #a78bfa', background:'white', cursor:'pointer', color:'#6d28d9', fontWeight:600 }}>
              + Add request on member's behalf
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────
  const buckets = [
    { key:'opt_out_pending', label:'🟠 Opt-Out Requests — Review',         rows:byCategory.opt_out_pending, color:'#f97316' },
    { key:'playing',         label:'✅ Playing',                            rows:byCategory.playing,         color:'#16a34a' },
    { key:'opt_out_full',    label:'💯 Opt-Out (Full Benefit)',             rows:byCategory.opt_out_full,    color:'#2563eb' },
    { key:'opt_out_fee_only',label:'💸 Opt-Out (Fee Only)',                 rows:byCategory.opt_out_fee_only,color:'#0284c7' },
    { key:'rested',          label:'💤 Rested',                             rows:byCategory.rested,          color:'#d97706' },
    { key:'no_reply',        label:'❓ No Reply — Treat as Absent?',        rows:byCategory.no_reply,        color:'#6b7280' },
    { key:'absent',          label:'❌ Absent',                             rows:byCategory.absent,          color:'#dc2626' },
  ];

  return (
    <div style={{ marginBottom:14 }}>
      {/* Header */}
      <div style={{
        background: alreadySent ? 'linear-gradient(135deg, #065f46, #047857)' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
        color:'white', borderRadius:12, padding:'12px 14px', marginBottom:10,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:'1.2rem' }}>{alreadySent ? '📨' : '🏗️'}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem' }}>
              {alreadySent ? 'Squad Notice Sent' : 'Squad Builder'}
            </div>
            <div style={{ fontSize:'0.7rem', opacity:0.85 }}>
              {alreadySent
                ? `Sent ${new Date(state.squad_notice_sent_at).toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'numeric', month:'short', hour:'numeric', minute:'2-digit' })} — you can resend with a warning`
                : 'Staging window open — finalize categories, approve requests, send squad notice'}
            </div>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:6, marginBottom:10 }}>
        {[
          { v: byCategory.playing.length,           l:'Playing',    c:'#16a34a' },
          { v: byCategory.opt_out_full.length + byCategory.opt_out_fee_only.length, l:'Opt-Out', c:'#2563eb' },
          { v: byCategory.rested.length,            l:'Rested',     c:'#d97706' },
          { v: byCategory.absent.length + byCategory.no_reply.length, l:'Absent', c:'#dc2626' },
          { v: byCategory.opt_out_pending.length,   l:'Pending',    c:'#f97316' },
        ].map((s, i) => (
          <div key={i} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'7px 4px', textAlign:'center' }}>
            <div style={{ fontSize:'1.1rem', fontWeight:800, color:s.c, fontFamily:'Rajdhani,sans-serif' }}>{s.v}</div>
            <div style={{ fontSize:'0.6rem', color:'#6b7280', fontWeight:600, textTransform:'uppercase' }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Send button (or Resend warning) */}
      {!alreadySent ? (
        <button
          onClick={openPreview}
          disabled={anyPending || restedMissingReason.length > 0}
          style={{
            width:'100%', padding:'14px', borderRadius:12, border:'none',
            background: (anyPending || restedMissingReason.length > 0) ? '#d1d5db' : 'var(--navy)',
            color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem',
            cursor: (anyPending || restedMissingReason.length > 0) ? 'not-allowed' : 'pointer',
            marginBottom:10,
          }}>
          📣 Preview & Send Squad Notice
        </button>
      ) : (
        <button
          onClick={openPreview}
          style={{
            width:'100%', padding:'12px', borderRadius:12, border:'2px solid #f59e0b',
            background:'#fffbeb', color:'#92400e',
            fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem',
            cursor:'pointer', marginBottom:10,
          }}>
          ⚠️ Resend Squad Notice (new pushes will fire)
        </button>
      )}

      {anyPending && (
        <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', padding:'8px 12px', borderRadius:8, fontSize:'0.75rem', marginBottom:10 }}>
          Resolve all pending requests on playing members before sending.
        </div>
      )}
      {restedMissingReason.length > 0 && (
        <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', padding:'8px 12px', borderRadius:8, fontSize:'0.75rem', marginBottom:10 }}>
          Pick a reason for rested members: {restedMissingReason.map(r => r.display_name).join(', ')}
        </div>
      )}

      {/* Category buckets */}
      {buckets.filter(b => b.rows.length > 0).map(b => (
        <div key={b.key} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, marginBottom:8, overflow:'hidden' }}>
          <div style={{ padding:'7px 12px', background:'#f9fafb', borderBottom:'1px solid #e5e7eb', fontSize:'0.72rem', fontWeight:800, color:b.color, letterSpacing:'0.02em' }}>
            {b.label} · {b.rows.length}
          </div>
          {b.rows.map(row => <MemberRow key={row.member_id} row={row} />)}
        </div>
      ))}

      {/* ── Rested reason picker modal ── */}
      {restedPicker && (
        <RestedReasonPicker
          state={state}
          member={restedPicker}
          onClose={() => setRestedPicker(null)}
          onPicked={async (reasonId) => {
            await setStatus(restedPicker.member_id, 'rested', reasonId);
            setRestedPicker(null);
          }}
          onReloadReasons={load}
          matchId={matchId}
        />
      )}

      {/* ── Add request on member's behalf modal ── */}
      {addRequestModal && (
        <AddRequestModal
          state={state}
          member={addRequestModal}
          onClose={() => setAddRequestModal(null)}
          onSave={(condition_id, admin_input) => addRequestOnBehalf(addRequestModal.member_id, condition_id, admin_input)}
        />
      )}

      {/* ── Approval modal (for requests that need admin_input, e.g. Leaving Early) ── */}
      {approvalModal && (
        <ApprovalModal
          request={approvalModal.request}
          memberName={approvalModal.member_name}
          onClose={() => setApprovalModal(null)}
          onApprove={async (admin_input) => {
            await decideRequest(approvalModal.request.id, 'approve', admin_input);
            setApprovalModal(null);
          }}
          onDecline={async () => {
            await decideRequest(approvalModal.request.id, 'decline');
            setApprovalModal(null);
          }}
        />
      )}

      {/* ── Preview & Send modal ── */}
      {previewModal && (
        <SquadPreviewModal
          state={state}
          preview={previewModal}
          setPreview={setPreviewModal}
          onRefresh={refreshPreview}
          onSend={sendSquadNotice}
          alreadySent={alreadySent}
        />
      )}
    </div>
  );
}

// ─── Rested reason picker sub-modal ──────────────────────────────────
function RestedReasonPicker({ state, member, onClose, onPicked, onReloadReasons, matchId }) {
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);

  async function addReason() {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await api.post('/squad-config/rested-reasons', { label: newLabel.trim() });
      toast.success('Reason added');
      setNewLabel('');
      await onReloadReasons();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add reason');
    } finally { setAdding(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'white', borderRadius:16, padding:20, width:'100%', maxWidth:380, maxHeight:'85vh', overflow:'auto' }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem', color:'var(--navy)', marginBottom:2 }}>Rest Reason</div>
        <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:14 }}>{member.display_name}</div>
        <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
          {(state.rested_reasons || []).map(r => (
            <button key={r.id} onClick={() => onPicked(r.id)}
              style={{ padding:'10px 12px', borderRadius:8, border:'1px solid #e5e7eb', background:'white', textAlign:'left', cursor:'pointer', fontSize:'0.85rem', color:'#111827' }}>
              {r.label}
            </button>
          ))}
          {(state.rested_reasons || []).length === 0 && (
            <div style={{ fontSize:'0.8rem', color:'#9ca3af', textAlign:'center', padding:10 }}>No active reasons — add one below</div>
          )}
        </div>
        <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:12 }}>
          <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:700, marginBottom:6 }}>+ ADD NEW REASON</div>
          <div style={{ display:'flex', gap:6 }}>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Injury"
              style={{ flex:1, padding:'8px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:'0.85rem' }} />
            <button onClick={addReason} disabled={adding || !newLabel.trim()}
              style={{ padding:'8px 14px', borderRadius:8, border:'none', background: adding || !newLabel.trim() ? '#d1d5db' : 'var(--navy)', color:'white', cursor:'pointer', fontWeight:700, fontSize:'0.8rem' }}>Add</button>
          </div>
        </div>
        <div style={{ marginTop:14, textAlign:'right' }}>
          <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', fontSize:'0.85rem' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add-request-on-behalf sub-modal ────────────────────────────────
function AddRequestModal({ state, member, onClose, onSave }) {
  const [conditionId, setConditionId] = useState('');
  const [adminInput, setAdminInput] = useState('');

  const cond = (state.special_conditions || []).find(c => c.id === conditionId);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'white', borderRadius:16, padding:20, width:'100%', maxWidth:380 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem', color:'var(--navy)', marginBottom:2 }}>Add Request on Behalf</div>
        <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:14 }}>{member.display_name}</div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:700, marginBottom:6 }}>CONDITION</div>
          <select value={conditionId} onChange={e => { setConditionId(e.target.value); setAdminInput(''); }}
            style={{ width:'100%', padding:'9px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:'0.85rem', background:'white' }}>
            <option value="">— Select —</option>
            {(state.special_conditions || []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {cond && cond.input_type !== 'none' && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:700, marginBottom:6 }}>
              {cond.input_label || (cond.input_type === 'time' ? 'Time' : 'Input')}
            </div>
            <input type={cond.input_type === 'time' ? 'time' : 'text'}
              value={adminInput} onChange={e => setAdminInput(e.target.value)}
              style={{ width:'100%', padding:'9px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:'0.85rem', boxSizing:'border-box' }} />
          </div>
        )}
        <div style={{ fontSize:'0.7rem', color:'#6b7280', background:'#f9fafb', padding:'8px 10px', borderRadius:6, marginBottom:14 }}>
          The member will be notified that this was <strong>applied</strong> by admin (they didn't request it).
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>Cancel</button>
          <button onClick={() => onSave(conditionId, adminInput)} disabled={!conditionId || (cond?.input_type !== 'none' && !adminInput.trim())}
            style={{ flex:2, padding:'10px', borderRadius:8, border:'none', background: (!conditionId || (cond?.input_type !== 'none' && !adminInput.trim())) ? '#d1d5db' : 'var(--navy)', color:'white', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── Approval sub-modal (for requests with input_type ≠ 'none') ──────
function ApprovalModal({ request, memberName, onClose, onApprove, onDecline }) {
  const [adminInput, setAdminInput] = useState(request.admin_input || request.member_input || '');

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'white', borderRadius:16, padding:20, width:'100%', maxWidth:380 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.05rem', color:'var(--navy)', marginBottom:2 }}>{request.label}</div>
        <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:14 }}>{memberName}</div>
        {request.member_input && request.source === 'member' && (
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'8px 10px', marginBottom:12, fontSize:'0.78rem', color:'#1e40af' }}>
            Member requested: <strong>{request.member_input}</strong>
          </div>
        )}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:'0.7rem', color:'#6b7280', fontWeight:700, marginBottom:6 }}>
            {request.input_label || (request.input_type === 'time' ? 'Approved time' : 'Admin value')}
          </div>
          <input type={request.input_type === 'time' ? 'time' : 'text'}
            value={adminInput} onChange={e => setAdminInput(e.target.value)}
            style={{ width:'100%', padding:'9px 10px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:'0.85rem', boxSizing:'border-box' }} />
          <div style={{ fontSize:'0.65rem', color:'#6b7280', marginTop:4 }}>Leave blank to approve as requested.</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onDecline} style={{ flex:1, padding:'10px', borderRadius:8, border:'none', background:'#fee2e2', color:'#991b1b', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>✗ Decline</button>
          <button onClick={onClose} style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>Cancel</button>
          <button onClick={() => onApprove(adminInput.trim() || null)}
            style={{ flex:2, padding:'10px', borderRadius:8, border:'none', background:'#16a34a', color:'white', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>✓ Approve</button>
        </div>
      </div>
    </div>
  );
}

// ─── Squad notice preview + send modal ──────────────────────────────
function SquadPreviewModal({ state, preview, setPreview, onRefresh, onSend, alreadySent }) {
  function updateField(key, value) {
    setPreview(p => ({ ...p, [key]: value }));
  }
  function updateBody(value) {
    setPreview(p => ({ ...p, editedBody: value, bodyWasEdited: true }));
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'stretch', justifyContent:'center', padding:10 }}>
      <div style={{ background:'white', borderRadius:16, padding:18, width:'100%', maxWidth:520, maxHeight:'95vh', overflow:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <div style={{ fontSize:'1.3rem' }}>📣</div>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1.1rem', color:'var(--navy)' }}>
              {alreadySent ? 'Resend Squad Notice' : 'Send Squad Notice'}
            </div>
            <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>
              Preview below. Edit if needed, then tap Send.
            </div>
          </div>
        </div>

        {/* Counts summary */}
        <div style={{ display:'flex', gap:6, fontSize:'0.7rem', marginBottom:12, flexWrap:'wrap' }}>
          {[
            { l:'Playing',   v:preview.counts.playing,          c:'#16a34a' },
            { l:'Opt-Full',  v:preview.counts.opt_out_full,     c:'#2563eb' },
            { l:'Opt-Fee',   v:preview.counts.opt_out_fee_only, c:'#0284c7' },
            { l:'Rested',    v:preview.counts.rested,           c:'#d97706' },
            { l:'Absent',    v:preview.counts.absent,           c:'#6b7280' },
          ].map((x, i) => (
            <span key={i} style={{ background:'#f9fafb', border:`1px solid ${x.c}33`, color:x.c, padding:'3px 8px', borderRadius:10, fontWeight:700 }}>
              {x.l}: {x.v}
            </span>
          ))}
        </div>

        {/* Config — jersey + reporting time */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
          <div>
            <div style={{ fontSize:'0.65rem', color:'#6b7280', fontWeight:700, marginBottom:4 }}>JERSEY</div>
            <select value={preview.jersey}
              onChange={e => updateField('jersey', e.target.value)}
              onBlur={onRefresh}
              style={{ width:'100%', padding:'7px 8px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:'0.82rem', background:'white' }}>
              <option value="BOTH">Both</option>
              <option value="BLUE">Blue</option>
              <option value="ORANGE">Orange</option>
              <option value="YELLOW">Yellow</option>
              <option value="CUSTOM">Custom…</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize:'0.65rem', color:'#6b7280', fontWeight:700, marginBottom:4 }}>REPORTING TIME</div>
            <input type="time" value={preview.repTime}
              onChange={e => updateField('repTime', e.target.value)}
              onBlur={onRefresh}
              style={{ width:'100%', padding:'7px 8px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:'0.82rem', boxSizing:'border-box' }} />
          </div>
        </div>
        {preview.jersey === 'CUSTOM' && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:'0.65rem', color:'#6b7280', fontWeight:700, marginBottom:4 }}>CUSTOM JERSEY INSTRUCTION</div>
            <input value={preview.customJersey}
              onChange={e => updateField('customJersey', e.target.value)}
              onBlur={onRefresh}
              placeholder="e.g. WEAR WHITE JERSEY"
              style={{ width:'100%', padding:'7px 8px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:'0.82rem', boxSizing:'border-box' }} />
          </div>
        )}
        {preview.jersey === 'BOTH' && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:'0.65rem', color:'#6b7280', fontWeight:700, marginBottom:4 }}>OPTIONAL SUB-LIST (under jersey line)</div>
            <input value={preview.sublist}
              onChange={e => updateField('sublist', e.target.value)}
              onBlur={onRefresh}
              placeholder="e.g. Blue for Team A, Orange for Team B"
              style={{ width:'100%', padding:'7px 8px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:'0.82rem', boxSizing:'border-box' }} />
          </div>
        )}

        {/* Broadcast body (editable) */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
            <div style={{ fontSize:'0.65rem', color:'#6b7280', fontWeight:700 }}>BROADCAST MESSAGE</div>
            {preview.bodyWasEdited && (
              <button onClick={() => setPreview(p => ({ ...p, editedBody: p.body, bodyWasEdited: false }))}
                style={{ fontSize:'0.65rem', padding:'2px 8px', borderRadius:6, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', color:'#6b7280' }}>↺ Reset</button>
            )}
          </div>
          <textarea value={preview.editedBody}
            onChange={e => updateBody(e.target.value)}
            rows={14}
            style={{ width:'100%', padding:'10px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:'0.82rem', fontFamily:'monospace', lineHeight:1.55, boxSizing:'border-box', resize:'vertical' }} />
        </div>

        {alreadySent && (
          <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', color:'#92400e', padding:'8px 12px', borderRadius:8, fontSize:'0.75rem', marginBottom:10 }}>
            ⚠️ Notice was already sent. Resending will fire a new push to every member again.
          </div>
        )}

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setPreview(null)}
            style={{ flex:1, padding:'11px', borderRadius:10, border:'1px solid #e5e7eb', background:'white', cursor:'pointer', fontWeight:700, fontSize:'0.88rem' }}>Cancel</button>
          <button onClick={onSend}
            style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background: alreadySent ? '#d97706' : 'var(--navy)', color:'white', cursor:'pointer', fontWeight:800, fontSize:'0.88rem', fontFamily:'Rajdhani,sans-serif' }}>
            {alreadySent ? '⚠️ Resend Notice' : '📣 Send Squad Notice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AdminPenaltyAdjustments (Session 17) ─────────────────
// Admin page for managing manual season-level penalty deductions.
// Used primarily for backloading historical penalties (2024/2025) that didn't
// flow through the Fines system, and as an escape hatch for one-off corrections.
export function AdminPenaltyAdjustments() {
  const [adjustments, setAdjustments] = useState([]);
  const [members, setMembers] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear() - 1);  // default to last year (backlog use case)
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ member_id: '', penalty_pts: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const AVAILABLE_YEARS = [2026, 2025, 2024, 2023, 2022];

  async function load() {
    setLoading(true);
    try {
      const [adjRes, memRes] = await Promise.all([
        api.get(`/penalty-adjustments?year=${year}`),
        api.get('/members'),
      ]);
      setAdjustments(adjRes.data.adjustments || []);
      setMembers((memRes.data.members || []).filter(m => !m.is_observer));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  async function handleAdd() {
    if (!form.member_id || !form.penalty_pts || !form.reason.trim()) {
      return alert('Please fill in member, penalty points, and reason.');
    }
    const pts = parseInt(form.penalty_pts);
    if (!Number.isFinite(pts) || pts <= 0) return alert('Penalty points must be a positive number.');
    setSaving(true);
    try {
      await api.post('/penalty-adjustments', {
        member_id: form.member_id,
        year,
        penalty_pts: pts,
        reason: form.reason.trim(),
      });
      setForm({ member_id: '', penalty_pts: '', reason: '' });
      setShowAdd(false);
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this adjustment? The points will be restored to the player\'s total.')) return;
    try {
      await api.delete(`/penalty-adjustments/${id}`);
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    }
  }

  // Group adjustments by member so multi-line penalties display clearly
  const grouped = {};
  adjustments.forEach(a => {
    if (!grouped[a.member_id]) grouped[a.member_id] = { member: a, items: [], total: 0 };
    grouped[a.member_id].items.push(a);
    grouped[a.member_id].total += parseInt(a.penalty_pts) || 0;
  });
  const groupedArr = Object.values(grouped).sort((a, b) => a.member.display_name.localeCompare(b.member.display_name));
  const grandTotal = adjustments.reduce((s, a) => s + (parseInt(a.penalty_pts) || 0), 0);

  return (
    <div className="container container--narrow">
      <AdminPageHeader title="Season Penalties" icon="📉">
        <div style={{ fontSize:'0.78rem', color:'#6b7280', marginTop:4 }}>
          Manual point deductions per year. Use this for historical penalties (pre-2026)
          that weren't recorded as fines, or for one-off committee adjustments.
        </div>
      </AdminPageHeader>

      {/* Year selector */}
      <div style={{ display:'flex', gap:6, marginBottom:14, overflowX:'auto', paddingBottom:4 }}>
        {AVAILABLE_YEARS.map(y => (
          <button key={y} onClick={() => setYear(y)}
            style={{
              padding:'7px 14px', borderRadius:18, border:'1px solid',
              borderColor: year === y ? '#1E3A6E' : '#d1d5db',
              background: year === y ? '#1E3A6E' : 'white',
              color: year === y ? 'white' : '#374151',
              fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.78rem',
              cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
            }}>
            {y}
          </button>
        ))}
      </div>

      {/* Summary bar */}
      <div style={{
        background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10,
        padding:'10px 14px', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center',
      }}>
        <div>
          <div style={{ fontSize:'0.7rem', color:'#7f1d1d', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            {year} Total Deductions
          </div>
          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.5rem', color:'#dc2626', lineHeight:1 }}>
            −{grandTotal}
          </div>
        </div>
        <div style={{ fontSize:'0.7rem', color:'#7f1d1d', textAlign:'right' }}>
          {groupedArr.length} player{groupedArr.length === 1 ? '' : 's'}<br />
          {adjustments.length} entr{adjustments.length === 1 ? 'y' : 'ies'}
        </div>
      </div>

      {/* Add button */}
      <button className="btn btn-gold" style={{ width:'100%', marginBottom:12 }}
        onClick={() => setShowAdd(!showAdd)}>
        {showAdd ? 'Cancel' : '+ Add Penalty'}
      </button>

      {/* Add form */}
      {showAdd && (
        <div className="card mb-md">
          <div className="card__body">
            <div className="form-group">
              <label className="form-label">Member *</label>
              <select className="form-control" value={form.member_id}
                onChange={e => setForm(f => ({ ...f, member_id: e.target.value }))}>
                <option value="">Select member…</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Penalty Points *</label>
              <input type="number" className="form-control" min="1"
                value={form.penalty_pts}
                onChange={e => setForm(f => ({ ...f, penalty_pts: e.target.value }))}
                placeholder="e.g. 30" />
            </div>
            <div className="form-group">
              <label className="form-label">Reason *</label>
              <input type="text" className="form-control"
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Late to AGM, Missed practice, etc." />
            </div>
            <button className="btn btn-primary" style={{ width:'100%' }}
              onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : `Add −${form.penalty_pts || '?'} for ${year}`}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ textAlign:'center', padding:20, color:'#6b7280' }}>Loading…</div>
      ) : groupedArr.length === 0 ? (
        <div style={{ textAlign:'center', padding:30, color:'#9ca3af', background:'#f9fafb', borderRadius:10 }}>
          No penalties recorded for {year}.
        </div>
      ) : (
        <div>
          {groupedArr.map(g => (
            <div key={g.member.member_id} style={{
              background:'white', border:'1px solid #e5e7eb', borderRadius:10,
              padding:'12px 14px', marginBottom:8,
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'1rem', color:'#1E3A6E' }}>
                    {g.member.display_name}
                  </div>
                  <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>{g.member.ucc_id}</div>
                </div>
                <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:900, fontSize:'1.2rem', color:'#dc2626' }}>
                  −{g.total}
                </div>
              </div>
              {g.items.map(item => (
                <div key={item.id} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'7px 0', borderTop:'1px dashed #f1f5f9', gap:8,
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.78rem', color:'#374151' }}>{item.reason}</div>
                    <div style={{ fontSize:'0.62rem', color:'#9ca3af', marginTop:1 }}>
                      Added {new Date(item.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                      {item.created_by_name ? ` by ${item.created_by_name}` : ''}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    <span style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#dc2626' }}>
                      −{item.penalty_pts}
                    </span>
                    <button onClick={() => handleDelete(item.id)} style={btnSm('#fee2e2', '#dc2626')}>
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AdminChatbot ─────────────────────────────────────────
// Super-admin only. Shows today's activity, top askers, query log,
// unanswered queue, current month spend, and the Haiku lock toggle.
export function AdminChatbot() {
  const { member } = useAuth();
  const isSuperAdmin = member?.role === 'super_admin';
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // Data
  const [today, setToday] = useState(null);     // /admin/today
  const [spend, setSpend] = useState(null);     // /admin/spend
  const [settings, setSettings] = useState([]); // /admin/settings
  const [log, setLog] = useState([]);           // /admin/log
  const [unanswered, setUnanswered] = useState([]);
  const [members, setMembers] = useState([]);   // /admin/members (everyone who has asked)
  const [logFilter, setLogFilter] = useState({ source: '', q: '', member_id: '', member_name: '' });
  const [togglingHaiku, setTogglingHaiku] = useState(false);
  const [clearing, setClearing] = useState(false);

  const haikuRow = settings.find(s => s.key === 'haiku_enabled');
  const haikuEnabled = haikuRow?.value === 'true';

  async function loadAll() {
    setLoading(true);
    try {
      const [t, s, st] = await Promise.all([
        api.get('/chatbot/admin/today'),
        api.get('/chatbot/admin/spend'),
        api.get('/chatbot/admin/settings'),
      ]);
      setToday(t.data);
      setSpend(s.data);
      setSettings(st.data?.settings || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load chatbot stats');
    } finally {
      setLoading(false);
    }
  }
  async function loadLog() {
    setLoading(true);
    try {
      const params = {};
      if (logFilter.source)    params.source = logFilter.source;
      if (logFilter.q)         params.q = logFilter.q;
      if (logFilter.member_id) params.member_id = logFilter.member_id;
      const r = await api.get('/chatbot/admin/log', { params });
      setLog(r.data?.queries || []);
    } catch (e) {
      toast.error('Failed to load query log');
    } finally {
      setLoading(false);
    }
  }
  async function loadUnanswered() {
    setLoading(true);
    try {
      const r = await api.get('/chatbot/admin/unanswered');
      setUnanswered(r.data?.questions || []);
    } catch (e) {
      toast.error('Failed to load unanswered questions');
    } finally {
      setLoading(false);
    }
  }
  async function loadMembers() {
    setLoading(true);
    try {
      const r = await api.get('/chatbot/admin/members');
      setMembers(r.data?.members || []);
    } catch (e) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  // Tap a member chip → set filter + jump to Query Log tab
  function filterByMember(m) {
    setLogFilter({ source: '', q: '', member_id: m.id, member_name: m.display_name });
    setTab('log');
  }
  function clearMemberFilter() {
    setLogFilter({ ...logFilter, member_id: '', member_name: '' });
  }

  // Clear logs. Body specifies which subset (all | older_than_days | by member | by source).
  async function clearLogs(body, confirmMsg) {
    if (!window.confirm(confirmMsg)) return;
    setClearing(true);
    try {
      const r = await api.post('/chatbot/admin/clear-logs', body);
      toast.success(`Deleted ${r.data?.deleted ?? 0} row(s)`);
      // Refresh whatever's currently visible
      if (tab === 'log') loadLog();
      else if (tab === 'unanswered') loadUnanswered();
      else if (tab === 'members') loadMembers();
      else loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to clear logs');
    } finally {
      setClearing(false);
    }
  }

  async function resetSpendCounter() {
    if (!window.confirm('Reset this month\'s Haiku spend counter to ₹0?\n\nUse only for testing — production cap remains ₹500.')) return;
    try {
      await api.post('/chatbot/admin/reset-spend');
      toast.success('Spend counter reset');
      loadAll();
    } catch (e) {
      toast.error('Failed to reset spend');
    }
  }

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (tab === 'overview') loadAll();
    else if (tab === 'log') loadLog();
    else if (tab === 'unanswered') loadUnanswered();
    else if (tab === 'members') loadMembers();
  }, [tab, logFilter.member_id]);

  async function toggleHaiku(next) {
    if (togglingHaiku) return;
    const confirmMsg = next
      ? 'Enable Haiku AI fallback?\n\nThis allows the bot to answer questions our free engine can\'t handle, costing roughly ₹0.05–0.20 per question. Hard cap: ₹500/month.'
      : 'Disable Haiku AI fallback?\n\nThe bot will answer ONLY free templated questions. Anything else will get "we don\'t have that yet".';
    if (!window.confirm(confirmMsg)) return;
    setTogglingHaiku(true);
    try {
      await api.post('/chatbot/admin/settings/haiku', { enabled: next });
      toast.success(next ? 'Haiku ENABLED' : 'Haiku LOCKED');
      loadAll();
    } catch (e) {
      toast.error('Failed to toggle Haiku');
    } finally {
      setTogglingHaiku(false);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <AdminPageHeader title="Chatbot Activity" icon="🤖" />
        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          Super-admin access only.
        </div>
      </div>
    );
  }

  const sourceColor = {
    template: '#10b981',  // green
    ai:       '#8b5cf6',  // purple
    no_data:  '#f59e0b',  // amber
    rejected: '#6b7280',  // gray
    error:    '#dc2626',  // red
  };
  const sourceLabel = {
    template: 'Free',
    ai: 'Haiku (paid)',
    no_data: 'No data',
    rejected: 'Rejected',
    error: 'Error',
  };

  function StatTile({ label, value, sub, color }) {
    return (
      <div style={{
        flex: 1, minWidth: 140,
        background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '14px 16px',
      }}>
        <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: color || '#1E3A6E', marginTop: 4, fontFamily: 'Rajdhani,sans-serif' }}>{value}</div>
        {sub && <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
      </div>
    );
  }

  function DangerRow({ label, desc, buttonText, buttonColor = '#dc2626', disabled, onClick }) {
    return (
      <div style={{
        display:'flex', alignItems:'center', gap:14,
        padding:'12px 14px',
        background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8,
      }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, color:'#991b1b', fontSize:'0.85rem' }}>{label}</div>
          <div style={{ fontSize:'0.72rem', color:'#7f1d1d', marginTop:2, lineHeight:1.4 }}>{desc}</div>
        </div>
        <button
          onClick={onClick}
          disabled={disabled}
          style={{
            padding:'7px 14px', borderRadius:8, border:'none',
            background: buttonColor, color:'white',
            fontWeight:800, fontSize:'0.75rem', cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            whiteSpace:'nowrap',
          }}
        >{buttonText}</button>
      </div>
    );
  }


  return (
    <div>
      <AdminPageHeader title="Chatbot Activity" icon="🤖" />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: 'white', overflowX: 'auto' }}>
        {[
          { k: 'overview',    l: 'Overview' },
          { k: 'log',         l: 'Query Log' },
          { k: 'members',     l: 'Members' },
          { k: 'unanswered',  l: 'Unanswered' },
          { k: 'settings',    l: 'Settings' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            style={{
              padding: '7px 14px', borderRadius: 999,
              border: 'none', cursor: 'pointer',
              background: tab === t.k ? '#1E3A6E' : '#f3f4f6',
              color: tab === t.k ? 'white' : '#374151',
              fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap',
            }}
          >{t.l}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => {
          if (tab === 'overview') loadAll();
          else if (tab === 'log') loadLog();
          else if (tab === 'unanswered') loadUnanswered();
          else if (tab === 'members') loadMembers();
        }} style={{
          padding: '7px 14px', borderRadius: 999, border: '1px solid #d1d5db',
          background: 'white', color: '#374151', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
        }}>↻ Refresh</button>
      </div>

      <div style={{ padding: '16px' }}>
        {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: '0.85rem' }}>Loading…</div>}

        {/* ── OVERVIEW ────────────────────────────────────── */}
        {tab === 'overview' && !loading && (
          <>
            {/* Haiku status banner */}
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 14,
              background: haikuEnabled ? '#ecfdf5' : '#fef3c7',
              border: `1px solid ${haikuEnabled ? '#a7f3d0' : '#fcd34d'}`,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ fontSize: '1.4rem' }}>{haikuEnabled ? '🟢' : '🔒'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, color: haikuEnabled ? '#065f46' : '#78350f', fontSize: '0.9rem' }}>
                  Haiku AI fallback is {haikuEnabled ? 'ENABLED' : 'LOCKED'}
                </div>
                <div style={{ fontSize: '0.78rem', color: haikuEnabled ? '#047857' : '#92400e', marginTop: 2 }}>
                  {haikuEnabled
                    ? 'Bot will use Haiku for questions the free engine cannot answer. Cap: ₹500/month.'
                    : 'Bot answers ONLY templated free questions. Anything else returns "we don\'t have that yet".'}
                </div>
              </div>
              <button
                onClick={() => toggleHaiku(!haikuEnabled)}
                disabled={togglingHaiku}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none',
                  background: haikuEnabled ? '#dc2626' : '#10b981', color: 'white',
                  fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer',
                  opacity: togglingHaiku ? 0.6 : 1,
                }}
              >{haikuEnabled ? 'Lock Haiku' : 'Enable Haiku'}</button>
            </div>

            {/* Tile row: today's totals */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              <StatTile label="Today's questions" value={today?.totals?.total_questions ?? 0} sub={`from ${today?.totals?.unique_askers ?? 0} members`} />
              <StatTile label="Today's AI cost" value={`₹${Number(today?.totals?.total_cost_inr || 0).toFixed(2)}`} sub="paid questions only" color="#8b5cf6" />
              <StatTile label="This month spend" value={`₹${Number(spend?.spent_inr || 0).toFixed(2)}`} sub={`of ₹${spend?.cap_inr ?? 500} cap`} color="#8b5cf6" />
              <StatTile label="Remaining this month" value={`₹${Number(spend?.remaining_inr || 0).toFixed(2)}`} color="#10b981" />
            </div>

            {/* Source breakdown */}
            {today?.breakdown && today.breakdown.length > 0 && (
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <div style={{ fontWeight: 800, color: '#1E3A6E', marginBottom: 10, fontSize: '0.85rem' }}>Today by source</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {today.breakdown.map(b => (
                    <div key={b.answer_source} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 5, background: sourceColor[b.answer_source] || '#999' }} />
                      <span style={{ fontSize: '0.82rem', color: '#374151' }}>{sourceLabel[b.answer_source] || b.answer_source}: <strong>{b.n}</strong>{Number(b.cost_inr) > 0 && <span style={{ color:'#9ca3af' }}> (₹{Number(b.cost_inr).toFixed(2)})</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top askers — abuse-detection list */}
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
              <div style={{ fontWeight: 800, color: '#1E3A6E', marginBottom: 10, fontSize: '0.85rem' }}>Today's top askers</div>
              {(!today?.top_askers || today.top_askers.length === 0) && (
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>No questions yet today.</div>
              )}
              {today?.top_askers?.map(a => (
                <button
                  key={a.ucc_id}
                  onClick={() => filterByMember({ id: a.member_id, display_name: a.display_name })}
                  style={{
                    display: 'flex', alignItems: 'center', width:'100%',
                    padding: '8px 0', borderBottom: '1px solid #f3f4f6', gap: 12,
                    background:'transparent', border:'none', borderBottom:'1px solid #f3f4f6',
                    cursor: a.member_id ? 'pointer' : 'default', textAlign:'left',
                  }}
                  disabled={!a.member_id}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#1E3A6E', fontSize: '0.88rem' }}>{a.display_name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{a.ucc_id}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, color: '#1E3A6E', fontSize: '0.95rem' }}>{a.questions}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{a.paid_questions || 0} paid · ₹{Number(a.cost_inr || 0).toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── QUERY LOG ───────────────────────────────────── */}
        {tab === 'log' && !loading && (
          <>
            {logFilter.member_id && (
              <div style={{
                background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: 8, padding: '8px 12px', marginBottom: 10,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: '0.82rem', color: '#1e40af' }}>
                  Filtered to: <strong>{logFilter.member_name}</strong>
                </span>
                <span style={{ flex: 1 }} />
                <button
                  onClick={clearMemberFilter}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#1e40af', fontWeight: 700, fontSize: '0.78rem',
                  }}
                >Clear filter ✕</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <select
                value={logFilter.source}
                onChange={e => setLogFilter({ ...logFilter, source: e.target.value })}
                style={{ ...fieldInput, maxWidth: 160 }}
              >
                <option value="">All sources</option>
                <option value="template">Free template</option>
                <option value="ai">Haiku (paid)</option>
                <option value="no_data">No data</option>
                <option value="rejected">Rejected</option>
                <option value="error">Error</option>
              </select>
              <input
                placeholder="Search question…"
                value={logFilter.q}
                onChange={e => setLogFilter({ ...logFilter, q: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') loadLog(); }}
                style={{ ...fieldInput, flex: 1, minWidth: 180 }}
              />
              <button onClick={loadLog} style={{ ...btnSm('#1E3A6E', 'white'), padding: '9px 16px', fontSize:'0.8rem' }}>Search</button>
            </div>
            {log.length === 0 && <div style={{ color:'#9ca3af', padding:16 }}>No queries match.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {log.map(q => (
                <div key={q.id} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{
                      fontSize:'0.65rem', fontWeight:800, padding:'2px 7px', borderRadius:4,
                      background:sourceColor[q.answer_source] || '#999', color:'white',
                    }}>{sourceLabel[q.answer_source] || q.answer_source}</span>
                    <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#1E3A6E' }}>{q.member_name}</span>
                    <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{q.member_ucc_id}</span>
                    <span style={{ flex:1 }} />
                    {Number(q.cost_inr) > 0 && <span style={{ fontSize:'0.7rem', color:'#8b5cf6', fontWeight:700 }}>₹{Number(q.cost_inr).toFixed(3)}</span>}
                    <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{new Date(q.created_at).toLocaleString('en-IN', { dateStyle:'short', timeStyle:'short' })}</span>
                  </div>
                  <div style={{ fontSize:'0.88rem', color:'#111827', marginBottom:4 }}>{q.question}</div>
                  {q.answer && (
                    <div style={{ fontSize:'0.78rem', color:'#6b7280', whiteSpace:'pre-wrap' }}>↳ {q.answer.length > 240 ? q.answer.slice(0, 240) + '…' : q.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── MEMBERS ──────────────────────────────────────── */}
        {tab === 'members' && !loading && (
          <>
            <div style={{ fontSize:'0.82rem', color:'#6b7280', marginBottom:10 }}>
              Every member who has asked at least one question. Tap any name to see their full query history.
            </div>
            {members.length === 0 && (
              <div style={{ color:'#9ca3af', padding:16 }}>No members have asked anything yet.</div>
            )}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {members.map(m => (
                <button
                  key={m.id}
                  onClick={() => filterByMember(m)}
                  style={{
                    background:'white', border:'1px solid #e5e7eb', borderRadius:999,
                    padding:'8px 14px', cursor:'pointer',
                    display:'inline-flex', alignItems:'center', gap:8,
                    fontSize:'0.85rem',
                  }}
                >
                  <span style={{ fontWeight:700, color:'#1E3A6E' }}>{m.display_name}</span>
                  <span style={{
                    background:'#1E3A6E', color:'white', borderRadius:999,
                    padding:'2px 8px', fontSize:'0.7rem', fontWeight:800,
                  }}>{m.questions}</span>
                  {Number(m.paid_questions) > 0 && (
                    <span style={{
                      background:'#8b5cf6', color:'white', borderRadius:999,
                      padding:'2px 8px', fontSize:'0.65rem', fontWeight:800,
                    }}>{m.paid_questions} paid</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── UNANSWERED ──────────────────────────────────── */}
        {tab === 'unanswered' && !loading && (
          <>
            <div style={{ fontSize:'0.82rem', color:'#6b7280', marginBottom:10 }}>
              Questions our engine couldn't answer. These are what to teach the bot next.
            </div>
            {unanswered.length === 0 && <div style={{ color:'#9ca3af', padding:16 }}>No unanswered questions on record.</div>}
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {unanswered.map(q => (
                <div key={q.id} style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#1E3A6E' }}>{q.member_name}</span>
                    <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{q.member_ucc_id}</span>
                    <span style={{ flex:1 }} />
                    <span style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{new Date(q.created_at).toLocaleString('en-IN', { dateStyle:'short', timeStyle:'short' })}</span>
                  </div>
                  <div style={{ fontSize:'0.88rem', color:'#111827' }}>{q.question}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── SETTINGS ────────────────────────────────────── */}
        {tab === 'settings' && (
          <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:10, padding:18 }}>
            <div style={{ fontWeight:800, color:'#1E3A6E', fontSize:'1rem', marginBottom:12 }}>Chatbot settings</div>

            <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 0', borderBottom:'1px solid #f3f4f6' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:'#111827', fontSize:'0.9rem' }}>Haiku AI fallback</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280', marginTop:3, lineHeight:1.4 }}>
                  When enabled, the bot uses Claude Haiku to answer questions the free engine can't handle.
                  Costs ~₹0.05–0.20 per question. Hard cap of ₹500/month enforced both in-app and at Anthropic.
                </div>
              </div>
              <button
                onClick={() => toggleHaiku(!haikuEnabled)}
                disabled={togglingHaiku}
                style={{
                  padding: '8px 16px', borderRadius: 999, border:'none',
                  background: haikuEnabled ? '#10b981' : '#9ca3af',
                  color:'white', fontWeight:800, fontSize:'0.78rem', cursor:'pointer',
                  minWidth: 90,
                  opacity: togglingHaiku ? 0.6 : 1,
                }}
              >{haikuEnabled ? 'ENABLED' : 'LOCKED'}</button>
            </div>

            {haikuRow?.updated_at && (
              <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:8 }}>
                Last changed {new Date(haikuRow.updated_at).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })}
              </div>
            )}

            {/* Danger zone — destructive maintenance actions */}
            <div style={{ marginTop:24, paddingTop:18, borderTop:'2px dashed #fca5a5' }}>
              <div style={{ fontWeight:800, color:'#991b1b', fontSize:'0.88rem', marginBottom:4 }}>
                Danger zone
              </div>
              <div style={{ fontSize:'0.75rem', color:'#6b7280', marginBottom:14 }}>
                Destructive actions. These cannot be undone.
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <DangerRow
                  label="Delete questions older than 30 days"
                  desc="Keep only recent activity. Older history is permanently removed."
                  buttonText="Delete 30d+"
                  disabled={clearing}
                  onClick={() => clearLogs(
                    { older_than_days: 30 },
                    'Delete ALL chatbot questions older than 30 days?\n\nThis cannot be undone.'
                  )}
                />
                <DangerRow
                  label="Delete questions older than 7 days"
                  desc="Aggressive cleanup. Keeps only this week."
                  buttonText="Delete 7d+"
                  disabled={clearing}
                  onClick={() => clearLogs(
                    { older_than_days: 7 },
                    'Delete ALL chatbot questions older than 7 days?\n\nThis cannot be undone.'
                  )}
                />
                <DangerRow
                  label="Delete only rejected questions"
                  desc="Removes off-topic / spam attempts. Keeps real questions."
                  buttonText="Delete rejected"
                  disabled={clearing}
                  onClick={() => clearLogs(
                    { source: 'rejected' },
                    'Delete ALL rejected (off-topic) questions?\n\nThis cannot be undone.'
                  )}
                />
                <DangerRow
                  label="Reset this month's Haiku spend counter"
                  desc="Sets the spent_inr counter back to ₹0. Use only for testing — does NOT delete log history."
                  buttonText="Reset spend"
                  buttonColor="#f59e0b"
                  disabled={clearing}
                  onClick={resetSpendCounter}
                />
                <DangerRow
                  label="CLEAR ALL CHATBOT LOGS"
                  desc="Wipes every question ever asked. Member list, query log, unanswered queue — all gone. Spend counter is preserved."
                  buttonText="Wipe everything"
                  buttonColor="#dc2626"
                  disabled={clearing}
                  onClick={() => clearLogs(
                    { all: true },
                    '⚠️  WIPE ALL CHATBOT LOGS?\n\nThis deletes EVERY chatbot question ever asked by any member. The audit trail will be gone permanently.\n\nAre you absolutely sure?'
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Shared style helpers ─────────────────────────────────────────────
function btnSm(bg, color) {
  return {
    padding:'5px 9px', borderRadius:6, border:'none',
    background: bg, color: color,
    fontSize:'0.7rem', fontWeight:700, cursor:'pointer',
  };
}
function FieldLabel({ children, style }) {
  return <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#374151', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em', ...style }}>{children}</div>;
}
const fieldInput = {
  width:'100%', padding:'9px 11px', border:'1.5px solid #e5e7eb', borderRadius:8,
  fontSize:'0.88rem', color:'#111827', outline:'none', boxSizing:'border-box',
  background:'white', fontFamily:'inherit',
};
